// Import dependencies
import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import polylabel from 'polylabel';
import * as THREE from 'three';
import { CSG } from 'three-csg-ts';
import * as turf from '@turf/turf';
import { SkeletonBuilder } from 'straight-skeleton';
import { useDispatch } from 'react-redux';

// Import CSS & assets
import 'mapbox-gl/dist/mapbox-gl.css';
import './Map.css';
import ico_map_layers from '../../../assets/ico/ico_map_layers.svg';
import ico_map_layerstop from '../../../assets/ico/ico_map_layerstop.svg';
import ico_map_center from '../../../assets/ico/ico_map_center.svg';
import ico_map_expand from '../../../assets/ico/ico_map_expand.svg';
import ico_map_minimize from '../../../assets/ico/ico_map_minimize.svg';
import ico_map_close from '../../../assets/ico/ico_map_close.svg';
import ico_map_spin from '../../../assets/ico/ico_map_spin.svg';
import ico_map_spinstop from '../../../assets/ico/ico_map_spinstop.svg';
import ico_map_measure from '../../../assets/ico/ico_map_measure.svg';
import ico_map_measurestop from '../../../assets/ico/ico_map_measurestop.svg';
import ico_map_export from '../../../assets/ico/ico_map_export.svg';
import ico_map_exportstop from '../../../assets/ico/ico_map_exportstop.svg';
import ico_map_locate from '../../../assets/ico/ico_map_locate.svg';
import ico_map_locatestop from '../../../assets/ico/ico_map_locatestop.svg';

// Import external functions
import * as map_helpers from './Map_helpers';
import * as search_helpers from '../search_elements/Search_helpers';
import * as helpers from '../../../helpers/Other_helpers';
import * as node_helpers from '../ruleEditor_elements/helpers/NodeHelpers';
import * as fetch_helpers from '../../../helpers/Fetch_helpers';
import * as map_helpers_trees from './Map_helpers_trees';
import * as map_helpers_buildings from './data_helpers/Map_helpers_buildings';
import * as map_helpers_division from './data_helpers/Map_helpers_division';
import * as map_helpers_combinations from './data_helpers/Map_helpers_combinations';
import * as map_helpers_commodities_car_park from './data_helpers/Map_helpers_commodities_car_park';
import * as map_helpers_commodities_charging from './data_helpers/Map_helpers_commodities_charging';
import * as map_helpers_commodities_transport from './data_helpers/Map_helpers_commodities_transport';
import * as map_helpers_commodities_train from './data_helpers/Map_helpers_commodities_train';
import * as map_helpers_commodities_bike from './data_helpers/Map_helpers_commodities_bike';
import * as map_helpers_commodities_hospital from './data_helpers/Map_helpers_commodities_hospital';
import * as map_helpers_commodities_doctor from './data_helpers/Map_helpers_commodities_doctor';
import * as map_helpers_commodities_pharmacy from './data_helpers/Map_helpers_commodities_pharmacy';
import * as map_helpers_commodities_edu_baby from './data_helpers/Map_helpers_commodities_edu_baby';
import * as map_helpers_commodities_edu_prim from './data_helpers/Map_helpers_commodities_edu_prim';
import * as map_helpers_commodities_edu_sec from './data_helpers/Map_helpers_commodities_edu_sec';
import * as map_helpers_commodities_edu_sup from './data_helpers/Map_helpers_commodities_edu_sup';
import * as map_helpers_commodities_mall from './data_helpers/Map_helpers_commodities_mall';
import * as map_helpers_commodities_supermarket from './data_helpers/Map_helpers_commodities_supermarket';
import * as map_helpers_commodities_parc from './data_helpers/Map_helpers_commodities_parc';
import * as map_helpers_commodities_restaurant from './data_helpers/Map_helpers_commodities_restaurant';
import * as map_helpers_commodities_culture from './data_helpers/Map_helpers_commodities_culture';


var search_popup = null;
var dvf_popup = null;
var mapRotation = false;
var mapLocating = false;
var mapUserLocation = null;
var currentBearing = 0;
var isMeasureVisible_var = false;
var isDivision_var = false;
var division_memory = null;
var drawMarkers = [];
var divisionMarkers = [];

// Set mapbox
mapboxgl.accessToken = 'pk.eyJ1IjoidGlqb3giLCJhIjoiY2s4azRibzF6MDEzZDNuczduZW1jbzB0biJ9.JAGdImb16q69S3kEn0KcCg';

const Map = ({ isMapReady, setIsMapReady, isSectionDataHidden, setIsSectionDataHidden, isSectionChatHidden, setIsSectionChatHidden, isHeaderBanner, currentSection, currentStep, capacity, setCapacity, mapResize, setTargetSection, mapLayers, setMapLayers, ruleUpdateTracker, highlightedRule, setHighlightedRule, volumeUpdateTracker, highlightedCombination, setHighlightedCombination, setErrorPage, globalUpdateTracker, mapDrawMode, setMapDrawMode, hiddenRule, setHiddenRule, user, setHighlightedSitadel, handleToaster, zoneUrba_mapShow, searchParameters, setSearchParameters, highlightedLand }) => {

    const dispatch = useDispatch();

    // __________ MAP PARAMETERS

    // MAP : Global variables
    const mapContainer = useRef(null);
    const map = useRef(null);
    const draw = useRef(null);

    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [boundsMarkers, setBoundsMarkers] = useState([]);
    const [pipesMarkers, setPipesMarkers] = useState([]);
    const [basiasMarkers, setBasiasMarkers] = useState([]);
    const [merimeeMarkers, setMerimeeMarkers] = useState([]);
    const [sitadelMarkers, setSitadelMarkers] = useState([]);
    const [isodistanceMarkers, setIsodistanceMarkers] = useState([]);
    const [dvfMarkers, setDvfMarkers] = useState([]);
    const [contourLinesMarkers, setContourLinesMarkers] = useState([]);
    const [zoneUrbaMarkers, setZoneUrbaMarkers] = useState([]);

    // MAP : Initial values
    const [lng, setLng] = useState(2.20);
    const [lat, setLat] = useState(46.4);
    const getInitialZoom = () => {
        if (window.innerWidth < 550) {
            return 3.85
        }
        else {
            return 5.15
        }
    }
    const [zoom, setZoom] = useState(getInitialZoom());
    const [pitch, setPitch] = useState(0);
    const [bearing, setBearing] = useState(0);

    // MAP : Loading
    const [isMapLoading, setIsMapLoading] = useState(false);

    // MAP : On load 
    useEffect(() => {
        // Check if map is already initialized
        if (map.current) return;
        // If not => Initialize it
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/tijox/cke0dn2v61bfq19lb2p69mei2',
            center: [lng, lat],
            zoom: zoom,
            pitch: pitch,
            antialias: true,
        });
        // Add controls
        // map.current.addControl(new mapboxgl.FullscreenControl());
        map.current.addControl(new mapboxgl.NavigationControl());
        // map.current.addControl(new mapboxgl.GeolocateControl());
        map.current.addControl(new mapboxgl.ScaleControl({
            maxWidth: 100,
            unit: 'metric'
        }), 'bottom-right');

        // Set loaded state and ready state
        map.current.on('load', () => {
            setIsMapLoaded(true);
            setIsMapReady(true);
            if (window.innerWidth > 600) {
                map.current.easeTo({
                    padding: { left: 600 },
                    duration: 800 // In ms. This matches the CSS transition duration property.
                });
            }
        });

        // Add events
        // MAP : On move
        map.current.on('moveend', () => {
            // Update lng, lat, zoom, pitch & bearing
            setLng(map.current.getCenter().lng.toFixed(4));
            setLat(map.current.getCenter().lat.toFixed(4));
            setZoom(map.current.getZoom().toFixed(2));
            setPitch(map.current.getPitch().toFixed(2));
            setBearing(map.current.getBearing().toFixed(2));
        });

        // DRAW
        draw.current = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                // line_string: true,
                // polygon: true,
                // trash: true
            },
        });
        map.current.addControl(draw.current);
        map.current.on('draw.create', draw_create);
        map.current.on('draw.update', draw_update);
        map.current.on('draw.modechange', draw_modeChange);
        map.current.on('draw.selectionchange', draw_selectionChange);

    });

    // MAP : On move
    useEffect(() => {
        currentBearing = parseFloat(bearing);
        landPolygons_Check();
        landLabels_Show();
    }, [lng, lat, zoom, pitch, bearing]);

    // MAP : Pitch change
    const pitchChange = () => {
        if (pitch > 0) {
            map.current.flyTo({
                pitch: 0,
                essential: true
            });
        }
        else {
            map.current.flyTo({
                pitch: 50,
                essential: true
            });
        }
    }

    // MAP : Resize
    useEffect(() => {
        map.current.resize();
    }, [mapResize]);

    function mapExpand_Switch() {
        if (isSectionDataHidden) {
            setIsSectionDataHidden(false);
            setIsSectionChatHidden(false);
        }
        else {
            setIsSectionDataHidden(true);
            setIsSectionChatHidden(true);
        }
    }
    useEffect(() => {
        if (window.innerWidth > 600) {
            if (isSectionDataHidden === false && isSectionChatHidden === false) {
                map.current.easeTo({
                    padding: { left: 600, right: 400 },
                    duration: 800 // In ms. This matches the CSS transition duration property.
                });
            }
            else if (isSectionDataHidden === false && isSectionChatHidden === true) {
                map.current.easeTo({
                    padding: { left: 600, right: 0 },
                    duration: 800 // In ms. This matches the CSS transition duration property.
                });
            }
            else if (isSectionDataHidden === true && isSectionChatHidden === false) {
                map.current.easeTo({
                    padding: { left: 0, right: 400 },
                    duration: 800 // In ms. This matches the CSS transition duration property.
                });
            }
            else if (isSectionDataHidden === true && isSectionChatHidden === true) {
                map.current.easeTo({
                    padding: { left: 0, right: 0 },
                    duration: 800 // In ms. This matches the CSS transition duration property.
                });
            }
            else {
                map.current.easeTo({
                    padding: { left: 0 },
                    duration: 800 // In ms. This matches the CSS transition duration property.
                });
            }
        }
    }, [isSectionDataHidden, isSectionChatHidden]);

    // MAP : Center
    const mapCenter = () => {
        flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.00006);
    }

    // MAP : Center for drawing
    const mapCenterToDraw = () => {
        var bbox = capacity?.landBase?.union_old?.bbox?.coordinates || capacity.landBase.union.bbox.coordinates;
        var offset = 0.00006;
        map.current.fitBounds(
            [
                [bbox[0][0][0] - offset, bbox[0][0][1] - offset], // southwestern corner of the bounds
                [bbox[0][2][0] + offset, bbox[0][2][1] + offset], // northeastern corner of the bounds
            ],
            {
                offset: [0, -80],
                pitch: 0
            }
        );
    }

    // MAP : Fit bbox
    const flyTo_FitBbox = (bbox, offset) => {
        map.current.fitBounds([
            [bbox[0][0][0] - offset, bbox[0][0][1] - offset], // southwestern corner of the bounds
            [bbox[0][2][0] + offset, bbox[0][2][1] + offset], // northeastern corner of the bounds
        ]);
    }

    // MAP : GeoLocate
    const [isLocating, setIsLocating] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    function mapUserLocation_Set(position) {
        setUserLocation(position);
        mapUserLocation = position;
        if (position !== null) {
            userLocation_Show(position);
        }
        else {
            userLocation_Hide();
        }
    }
    function mapLocating_Switch(value) {
        var value_i = value;
        if (!value) {
            value_i = !mapLocating;
        }
        if (value_i === false) {
            mapLocating = false;
            setIsLocating(false);
            mapUserLocation_Set(null);
        }
        else if (value_i === true) {
            mapLocating = true;
            setIsLocating(true);
            mapLocating_getUserLocation();
        }
    }
    function mapLocating_getUserLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(mapLocating_showPosition, mapLocating_showError);
        } else {
            console.log("Geolocation is not supported by this browser.");
            handleToaster([{ key: "message", status: "info", type: "static", style: "error", title: "Géolocalisation", text: "Le navigateur ne permet pas la géolocalisation." }]);
            mapLocating_Switch(false);
        }
    }
    function mapLocating_showPosition(position) {
        console.log("POSIITON", position);
        mapUserLocation_Set(position);
    }

    function mapLocating_showError(error) {
        mapLocating_Switch(false);
        switch (error.code) {
            case error.PERMISSION_DENIED:
                console.log("User denied the request for Geolocation.");
                handleToaster([{ key: "message", status: "info", type: "static", style: "error", title: "Géolocalisation", text: "L'utilisateur a refusé la géolocalisation." }]);
                break;
            case error.POSITION_UNAVAILABLE:
                console.log("Location information is unavailable.");
                handleToaster([{ key: "message", status: "info", type: "static", style: "error", title: "Géolocalisation", text: "La position actuelle est inconnue." }]);
                break;
            case error.TIMEOUT:
                console.log("The request to get user location timed out.");
                handleToaster([{ key: "message", status: "info", type: "static", style: "error", title: "Géolocalisation", text: "La géolocalisation a mis trop de temps." }]);
                break;
            case error.UNKNOWN_ERROR:
                console.log("An unknown error occurred.");
                handleToaster([{ key: "message", status: "info", type: "static", style: "error", title: "Géolocalisation", text: "Une erreur inconnue empêche la géolocalisation." }]);
                break;
            default:
                console.log("An unknown error occurred.");
                handleToaster([{ key: "message", status: "info", type: "static", style: "error", title: "Géolocalisation", text: "Une erreur inconnue empêche la géolocalisation." }]);
        }
    }
    // MAP : Rotate
    const [isRotating, setIsRotating] = useState(false);
    function mapRotation_Switch() {
        if (mapRotation === true) {
            mapRotation = false;
            setIsRotating(false);
        }
        else {
            mapRotation = true;
            setIsRotating(true);
            // flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.0001);
            // setTimeout(function () {
            //     mapRotation_Rotate(0);
            // }, 600);
            map.current.flyTo({
                center: capacity.landBase.union.center.geometry.coordinates,
                essential: true
            });
            setTimeout(function () {
                mapRotation_Rotate(currentBearing);
            }, 400);
            // mapRotation_Rotate(currentBearing);
        }
    }
    function mapRotation_Rotate(timestamp) {
        // console.log("still rotate", mapRotation);
        if (mapRotation === true) {
            timestamp = timestamp + 0.4;
            if (timestamp > 180) {
                timestamp = timestamp - 360;
            }
            // map.current.rotateTo((timestamp / 100) % 360, { duration: 1000 });
            map.current.rotateTo(timestamp, { duration: 50 });
            setTimeout(function () {
                mapRotation_Rotate(timestamp)
            }, 50);
        }
    }

    // __________ SEARCH
    useEffect(() => {
        // console.log("MAP SEARCH PARAMS USE EFFECT", searchParameters);
        if (searchParameters?.map_update && searchParameters.map_update[0] === "-") {
            var land_list_updated = search_helpers.search_apply_filters(searchParameters, setSearchParameters);
            console.log("UPDATED LAND LIST", land_list_updated);
            landPolygons_UpdateFromSearch(land_list_updated);
        }
    }, [searchParameters]);
    useEffect(() => {
        // console.log("MAP ZOMM TO LAND", highlightedLand);
        if (highlightedLand?.bbox) {
            // Close other search popup
            if (search_popup !== null) {
                search_popup.remove();
                search_popup = null;
            }
            // Fly
            // map.current.flyTo({
            //     center: highlightedLand.com,
            //     zoom: 20,
            //     essential: true
            // });
            var bbox_formated = [[[highlightedLand.bbox[0], highlightedLand.bbox[1]], [], [highlightedLand.bbox[2], highlightedLand.bbox[3]], [], []]];
            flyTo_FitBbox(bbox_formated, 0.00006);
            // Create new search popup
            setTimeout(() => {
                var center_coords = [(highlightedLand.bbox[0] + highlightedLand.bbox[2]) / 2, (highlightedLand.bbox[1] + highlightedLand.bbox[3]) / 2];
                var section = highlightedLand.id.substring(8, 10);
                if (section.substring(0, 1) === "0") { section = section.substring(1) };
                var numero = parseInt(highlightedLand.id.substring(10));
                var label = "Parcelle " + section + "-" + numero;
                var txt_label = '<span style="color:#062134;font-size:15px;font-weight:700"><strong>' + label + '</strong></span>';
                var txt_land_area = highlightedLand?.s ? '<br><span style="font-size:12px;font-weight: 600;color: grey;">Superficie :</span><br><span style="color:#062134;font-weight:500;">' + helpers.get_pretty_num(highlightedLand.s) + ' m²</span>' : "";
                var txt_land_compacity = highlightedLand?.Im ? '<br><span style="font-size:12px;font-weight: 600;color: grey;">Compacité :</span><br><span style="color:#062134;font-weight:500;">' + helpers.get_pretty_num(highlightedLand.Im) + '</span>' : "";

                search_popup = new mapboxgl.Popup({ offset: 0 })
                    .setLngLat(center_coords)
                    .setHTML('<div style="font-family:Manrope; color:#062134;">' + txt_label + txt_land_area + txt_land_compacity + '</div>')
                    .addTo(map.current);
            }, 1000);
        }
    }, [highlightedLand]);


    // __________ DRAW
    const [isMeasureVisible, setIsMeasureVisible] = useState(false);
    const closeMeasure = () => {
        if (isMeasureVisible === true) {
            draw.current.changeMode("simple_select");
            draw_delete_all();
            setIsMeasureVisible(false);
            isMeasureVisible_var = false;
        }
    }
    const openMeasure = () => {
        draw.current.changeMode("simple_select");
        draw_delete_all();
        setIsMeasureVisible(true);
        isMeasureVisible_var = true;
    }
    const handleMeasureSwitch = () => {
        if (isMeasureVisible === true) {
            closeMeasure();
        }
        else {
            openMeasure();
        }
    }
    const [isMeasureLength, setIsMeasureLength] = useState(false);
    var isMeasureLength_var = false;
    const startMeasureLength = () => {
        setIsMeasureLength(true);
        isMeasureLength_var = true;
        draw_delete_all();
        draw.current.changeMode("draw_line_string");
    }
    const stopMeasureLength = () => {
        setIsMeasureLength(false);
        isMeasureLength_var = false;
    }
    const [isMeasureArea, setIsMeasureArea] = useState(false);
    var isMeasureArea_var = false;
    const startMeasureArea = () => {
        setIsMeasureArea(true);
        isMeasureArea_var = true;
        draw_delete_all();
        draw.current.changeMode("draw_polygon");
    }
    const stopMeasureArea = () => {
        setIsMeasureArea(false);
        isMeasureArea_var = false;
    }

    const draw_create = () => {
        const draw_data = draw.current.getAll();
        console.log("DRAW CREATE", draw_data);
        // console.log("isMeasureVisible_var", isMeasureVisible_var);
        // console.log("isMeasureVisible", isMeasureVisible);
        // MEASURMENT : LENGTH
        if (isMeasureVisible_var === true && draw_data?.features.length > 0 && draw_data?.features[0]?.geometry?.type === "LineString") {
            var length = turf.length(draw_data?.features[0]) * 1000;
            var midPoint = turf.along(draw_data?.features[0], length / 2000);
            var markers = [];
            var el = document.createElement('div');
            el.className = 'marker';
            el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;color:#FFF;background-color:#062134;border-radius:6px"><span style="">Longueur : ' + helpers.get_pretty_num(length.toFixed(2)) + ' m</span></div>';
            markers.push(new mapboxgl.Marker(el, { offset: [0, -20] })
                .setLngLat(turf.getCoord(midPoint))
                .addTo(map.current))
            drawMarkers = markers;
        }
        // MEASURMENT : AREA
        if (isMeasureVisible_var === true && draw_data?.features.length > 0 && draw_data?.features[0]?.geometry?.type === "Polygon") {
            var area = turf.area(draw_data?.features[0]);
            var midPoint = turf.centroid(draw_data?.features[0]);
            var markers = [];
            var el = document.createElement('div');
            el.className = 'marker';
            el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;color:#FFF;background-color:#062134;border-radius:6px"><span style="">Surface : ' + helpers.get_pretty_num(area.toFixed(2)) + ' m²</span></div>';
            markers.push(new mapboxgl.Marker(el)
                .setLngLat(turf.getCoord(midPoint))
                .addTo(map.current))
            drawMarkers = markers;
        }
        // DIVISION
        if (isDivision_var === true) {
            divisionDraw_check_validity();
        }
    }
    const draw_modeChange = (e) => {
        // console.log("DRAW MODE CHANGE", e);
        if (e.mode !== "draw_line_string") {
            stopMeasureLength();
        }
        if (e.mode !== "draw_polygon") {
            stopMeasureArea();
        }
    }
    const [isDivisionValid, setIsDivisionValid] = useState(false);
    const [drawSelection, setDrawSelection] = useState([]);
    const draw_selectionChange = () => {
        const draw_data = draw.current.getSelectedIds();
        // console.log("DRAW SELECTION CHANGE", draw_data);
        setDrawSelection(draw_data);
    }
    const draw_update = () => {
        // console.log("DRAW UPDATE");
        // Remove markers
        for (var i = 0; i < drawMarkers.length; i++) {
            drawMarkers[i].remove();
        }
        draw_create();
    }
    const draw_delete_all = () => {
        draw.current.deleteAll();
        // Remove markers
        for (var i = 0; i < drawMarkers.length; i++) {
            drawMarkers[i].remove();
        }
        // Hide union layer
        drawUnionPolygonLine_Hide();
    }

    // DRAW FROM DIVISION
    const divisionDraw_check_validity = () => {
        const draw_data = draw.current.getAll();
        var check_division = map_helpers_division.checkDivisionLines(refCapacity.current, draw_data);
        console.log("TEST DIVISION LINE CREATED", check_division);
        // Update drawed lines to stick points if modification needed
        if (check_division?.isModified === true && check_division?.geo_data?.type === "FeatureCollection") {
            draw.current.set(check_division.geo_data);
        }
        // Set validity status
        setIsDivisionValid(check_division.isValid);
        // Show division elements
        if (check_division?.isValid && check_division?.divisionAreas) {
            console.log("Show buffer polys");
            // LINES BUFFER
            // Remove if exists already
            // Remove layer
            // if (map.current.getLayer('layerDivisionPolys')) {
            //     map.current.removeLayer('layerDivisionPolys');
            // }
            // // Remove source
            // if (map.current.getSource('sourceDivisionPolys')) {
            //     map.current.removeSource('sourceDivisionPolys');
            // }
            // // Add source
            // var division_poly_data = {
            //     type: 'FeatureCollection',
            //     features: [
            //         check_division.divisionAreas.lines_buffer,
            //     ],
            // };
            // if (check_division.divisionAreas.lines_buffer.type === "FeatureCollection") {
            //     division_poly_data = check_division.divisionAreas.lines_buffer;
            // }
            // map.current.addSource('sourceDivisionPolys', {
            //     type: 'geojson',
            //     data: division_poly_data
            // });
            // // Add layer
            // map.current.addLayer({
            //     id: 'layerDivisionPolys',
            //     type: 'fill',
            //     source: 'sourceDivisionPolys',
            //     paint: {
            //         'fill-color': 'orange',
            //         'fill-opacity': 0.6,
            //     },
            // });
            // DIVISION AREAS
            // Remove if exists already
            // Remove layer
            // if (map.current.getLayer('layerDivisionPolys2')) {
            //     map.current.removeLayer('layerDivisionPolys2');
            // }
            // // Remove source
            // if (map.current.getSource('sourceDivisionPolys2')) {
            //     map.current.removeSource('sourceDivisionPolys2');
            // }
            // Remove markers
            for (var i = 0; i < drawMarkers.length; i++) {
                drawMarkers[i].remove();
            }
            // Add source
            // var division_poly_data2 = {
            //     type: 'FeatureCollection',
            //     features: [
            //         check_division.divisionAreas.division_areas,
            //     ],
            // };
            // if (check_division.divisionAreas.division_areas.type === "FeatureCollection") {
            //     division_poly_data2 = check_division.divisionAreas.division_areas;
            // }
            // map.current.addSource('sourceDivisionPolys2', {
            //     type: 'geojson',
            //     data: division_poly_data2
            // });
            // // Add layer
            // map.current.addLayer({
            //     id: 'layerDivisionPolys2',
            //     type: 'fill',
            //     source: 'sourceDivisionPolys2',
            //     paint: {
            //         'fill-color': 'green',
            //         'fill-opacity': 0.4,
            //     },
            // });
            // Add markers
            var markers = [];
            for (var i = 0; i < check_division.divisionAreas.division_areas.features.length; i++) {
                var area_label = check_division.divisionAreas.division_areas.features[i]?.properties?.label || "D";
                var area_value = check_division.divisionAreas.division_areas.features[i]?.properties?.area ? "<br />" + helpers.get_pretty_num(check_division.divisionAreas.division_areas.features[i].properties.area.toFixed(0)) + " m²" : "";
                var midPoint = turf.centroid(check_division.divisionAreas.division_areas.features[i]);
                var el = document.createElement('div');
                el.className = 'marker';
                el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;color:#FFF;background-color:#062134;border-radius:6px;text-align:center"><span style=""><strong>' + area_label + '</strong>' + area_value + '</span></div>';
                markers.push(new mapboxgl.Marker(el, { offset: [0, -20] })
                    .setLngLat(turf.getCoord(midPoint))
                    .addTo(map.current))
            }
            drawMarkers = markers;
            console.log("DIVISION MARKER 001");
            // Update capacity
            var landBase = { ...refCapacity.current.landBase };
            landBase.division = {
                geo_data: check_division?.geo_data || null,
                lines_buffer: check_division?.divisionAreas?.lines_buffer || null,
                division_areas: check_division?.divisionAreas?.division_areas || null,
            };
            setCapacity({ ...refCapacity.current, landBase: landBase });
        }
        else {
            // // Remove layer
            // if (map.current.getLayer('layerDivisionPolys')) {
            //     map.current.removeLayer('layerDivisionPolys');
            // }
            // // Remove source
            // if (map.current.getSource('sourceDivisionPolys')) {
            //     map.current.removeSource('sourceDivisionPolys');
            // }
            // // Remove layer
            // if (map.current.getLayer('layerDivisionPolys2')) {
            //     map.current.removeLayer('layerDivisionPolys2');
            // }
            // // Remove source
            // if (map.current.getSource('sourceDivisionPolys2')) {
            //     map.current.removeSource('sourceDivisionPolys2');
            // }
            // Remove markers
            for (var i = 0; i < drawMarkers.length; i++) {
                drawMarkers[i].remove();
            }
        }
    }
    // DRAW UNION POLY : Hide
    const drawUnionPolygonLine_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerDrawUnionPolys')) {
            map.current.removeLayer('layerDrawUnionPolys');
        }
        // Remove source
        if (map.current.getSource('sourceDrawUnionPolys')) {
            map.current.removeSource('sourceDrawUnionPolys');
        }
        // Remove layer
        if (map.current.getLayer('layerDrawUnionLines')) {
            map.current.removeLayer('layerDrawUnionLines');
        }
        // Remove source
        if (map.current.getSource('sourceDrawUnionLines')) {
            map.current.removeSource('sourceDrawUnionLines');
        }
    }
    // DRAW UNION POLY : Show
    const drawUnionPolygonLine_Show = () => {
        // POLYGONS
        if (capacity?.landBase?.union_old?.geometry) {
            // Set union color
            var union_color = "#062134";
            // Remove if exists already
            drawUnionPolygonLine_Hide();
            // Add source
            var union_poly_data = {
                type: 'FeatureCollection',
                features: [
                    capacity?.landBase?.union_old?.geometry || capacity.landBase.union.geometry,
                ],
            };
            if (capacity.landBase.union.geometry.type === "FeatureCollection") {
                union_poly_data = capacity.landBase.union.geometry;
            }
            if (capacity?.landBase?.union_old?.geometry?.type === "FeatureCollection") {
                union_poly_data = capacity.landBase.union_old.geometry;
            }
            map.current.addSource('sourceDrawUnionPolys', {
                type: 'geojson',
                data: union_poly_data
            });
            // Add layer
            map.current.addLayer({
                id: 'layerDrawUnionPolys',
                type: 'fill',
                source: 'sourceDrawUnionPolys',
                paint: {
                    'fill-color': union_color,
                    'fill-opacity': 0.4,
                },
            }, layer_get_position('layerUnionPolys'));
        }
        // LINES
        if (capacity?.landBase?.union_old?.outerLine) {
            // Add source
            var union_line_data = {
                type: 'FeatureCollection',
                features: [
                    capacity.landBase.union_old.outerLine,
                ],
            };
            map.current.addSource('sourceDrawUnionLines', {
                type: 'geojson',
                data: union_line_data
            });
            // Add layer
            map.current.addLayer({
                id: 'layerDrawUnionLines',
                type: 'line',
                source: 'sourceDrawUnionLines',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#062134',
                    'line-width': 2.5,
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerUnionLines'));
        }
    }


    // DRAW FROM RULE
    useEffect(() => {
        setDrawSelection([]);

        if (mapDrawMode?.mode === null) {
            const draw_data = draw.current.getAll();
            isDivision_var = false;
            division_memory = null;
            // console.log("CLOSING DRAW RULE MODE", draw_data);
        }
        else if (mapDrawMode?.mode === "point" || mapDrawMode?.mode === "line" || mapDrawMode?.mode === "polygon") {
            // console.log("SETTING DRAW RULE MODE", mapDrawMode);
            // Delete existing draw data
            draw_delete_all();
            // Add current draw data
            if (mapDrawMode?.data?.features && mapDrawMode.data.features.length > 0) {
                draw.current.set(mapDrawMode.data);
                // Show markers if division
                if (mapDrawMode?.target?.division === true) {
                    divisionDraw_check_validity();
                }
            }
            // Set division default values
            if (mapDrawMode?.target?.division === true) {
                // Set unvalid to avoid saving same values
                setIsDivisionValid(false);
                // Show Union layer
                drawUnionPolygonLine_Show();
            }
            // Set draw mode
            draw.current.changeMode("simple_select");
            // Move map to draw
            mapCenterToDraw();
            if (mapDrawMode?.target?.division === true) {
                isDivision_var = true;
                division_memory = {
                    union: capacity?.landBase?.union ? JSON.parse(JSON.stringify(capacity.landBase.union)) : null,
                    division: capacity?.landBase?.division ? JSON.parse(JSON.stringify(capacity.landBase.division)) : null,
                };
                console.log("DIVISION MEMORY", division_memory);
            }
        }
    }, [mapDrawMode.mode]);

    const ruleDraw_validate = () => {
        const draw_data = draw.current.getAll();
        setMapDrawMode({ ...mapDrawMode, mode: "update", data: draw_data });
        draw_delete_all();
        draw.current.changeMode("simple_select");
        setHiddenRule(null);
    }

    const ruleDraw_cancel = () => {
        if (isDivision_var === true) {
            // restore division memory
            console.log("RESTORE DIVISION MEMORY", division_memory);
            var landBase = { ...refCapacity.current.landBase };
            landBase.union = division_memory.union;
            landBase.division = division_memory.division;
            setCapacity({ ...refCapacity.current, landBase: landBase });
        }
        setMapDrawMode({ mode: null, data: null, target: null });
        draw_delete_all();
        draw.current.changeMode("simple_select");
        setHiddenRule(null);
    }

    const ruleDraw_add = () => {
        if (mapDrawMode?.mode === "point") {
            // Set draw mode
            draw.current.changeMode("draw_point");
        }
        if (mapDrawMode?.mode === "line") {
            // Set draw mode
            draw.current.changeMode("draw_line_string");
        }
        if (mapDrawMode?.mode === "polygon") {
            // Set draw mode
            draw.current.changeMode("draw_polygon");
        }

        // console.log("mode", draw.current.getMode());
    }

    const ruleDraw_delete = () => {
        const draw_selection = draw.current.getSelectedIds();
        draw.current.delete(draw_selection);
        setDrawSelection([]);
        // DIVISION
        if (isDivision_var === true) {
            divisionDraw_check_validity();
        }
    }

    const ruleDraw_delete_all = () => {
        draw_delete_all();
        setDrawSelection([]);
    }

    // __________ EXPORT
    const [isExportVisible, setIsExportVisible] = useState(false);



    // __________ LAYERS

    // LAYERS : Layer container
    const [isLayersVisible, setIsLayersVisible] = useState(false);
    const handleLayersSwitch = () => {
        setIsLayersVisible(!isLayersVisible);
    }

    // LAYERS : Organisation
    const layers_order = [
        "layerSatellite",
        "layerRisksEarthquake",
        "layerRisksClay",
        "layerRisksBasias_Buffer",
        "layerRisksBasias_Polys",
        "layerRisksBasias_Lines",
        "layerZonesUrbaPoly",
        "aeroway-polygon",
        "layerLandsPolys2",
        "layerLandsLabels",
        "layerUserLocation",
        "layerRisksPipes_Buffer",
        "layerRisksPipes_Polys",
        "layerRisksPipes_Lines",
        "layerContextMerimee_Buffer",
        "layerContextIsodistance_Polys",
        "layerContextIsodistance_Lines",
        "layerUnionPolys",
        "layerUnionLines",
        "layerDivisionPolys",
        "layerDivisionLines",
        "layerUnionLinesType",
        "layer3D_land",
        "layerTopoPoints_Line",
        "layerTopoProfile_Line",
        "layerTopoProfile_Points",
        "layerTrees_Points",
        "building-extrusion",
        "layerContextDVF_Clusters",
        "layerContextDVF_ClusterLabels",
        "layerContextDVF_Points",
        "layerContextSitadel_Points",
        "layerIGNBuildings_land",
        "layerIGNBuildings_noland",
        "layerIGNBuildings_Click_land",
        "layerIGNBuildings_Click_noland",
        "combinaton",
        "layerRules",
        "layer3Dbuilding",
        "layerBuildableVolume",
    ]

    const layer_get_position = (layer_id) => {
        var index = layers_order.indexOf(layer_id);
        // console.log("LAYER INDEX", index);
        if (index > -1) {
            var check_string = layers_order.slice(index).join("|");
            // console.log("LAYER check_string", check_string);
            var current_layers = map.current.getStyle().layers;
            for (var i = 0; i < current_layers.length; i++) {
                if (check_string.includes("|" + current_layers[i].id + "|")) {
                    // console.log("LAYER FOUND", current_layers[i].id);
                    return current_layers[i].id
                }
            }
        }

        return ""

    }

    // LAYERS : All layers
    const [layerSatellite, setLayerSatellite] = useState(false);
    const [layerOSMBuildings, setLayerOSMBuildings] = useState(false);
    const [layerCityLands, setLayerCityLands] = useState(false);
    const [layerIGNBuildings, setLayerIGNBuildings] = useState(false);
    const [layerIGNBuildingsNoland, setLayerIGNBuildingsNoland] = useState(false);
    const [layerIGNBuildingsLand_keep, setLayerIGNBuildingsLand_keep] = useState(false);
    const [layerIGNBuildingsLand_erase, setLayerIGNBuildingsLand_erase] = useState(false);
    const [layerUnion, setLayerUnion] = useState(false);
    const [layerDivision, setLayerDivision] = useState(false);
    const [layerUnionBounds, setLayerUnionBounds] = useState(false);
    const [layerTopo3D, setLayerTopo3D] = useState(false);
    const [layerTopoPoints, setLayerTopoPoints] = useState(false);
    const [layerTopoProfile, setLayerTopoProfile] = useState(false);
    const [layerTrees, setLayerTrees] = useState(false);
    const [layerRisksEarthquake, setLayerRisksEarthquake] = useState(false);
    const [layerRisksClay, setLayerRisksClay] = useState(false);
    const [layerRisksPipes, setLayerRisksPipes] = useState(false);
    const [layerRisksBasias, setLayerRisksBasias] = useState(false);
    const [layerContextMerimee, setLayerContextMerimee] = useState(false);
    const [layerContextSitadel, setLayerContextSitadel] = useState(false);
    const [layerContextIsodistance, setLayerContextIsodistance] = useState(false);
    const [layerContextDVF, setLayerContextDVF] = useState(false);
    const [layerContextCommoditiesCarPark, setLayerContextCommoditiesCarPark] = useState(false);
    const [layerContextCommoditiesCarCharging, setLayerContextCommoditiesCarCharging] = useState(false);
    const [layerContextCommoditiesTransport, setLayerContextCommoditiesTransport] = useState(false);
    const [layerContextCommoditiesTrain, setLayerContextCommoditiesTrain] = useState(false);
    const [layerContextCommoditiesBike, setLayerContextCommoditiesBike] = useState(false);
    const [layerContextCommoditiesHospital, setLayerContextCommoditiesHospital] = useState(false);
    const [layerContextCommoditiesDoctor, setLayerContextCommoditiesDoctor] = useState(false);
    const [layerContextCommoditiesPharmacy, setLayerContextCommoditiesPharmacy] = useState(false);
    const [layerContextCommoditiesBaby, setLayerContextCommoditiesBaby] = useState(false);
    const [layerContextCommoditiesPrimarySchool, setLayerContextCommoditiesPrimarySchool] = useState(false);
    const [layerContextCommoditiesSecondarySchool, setLayerContextCommoditiesSecondarySchool] = useState(false);
    const [layerContextCommoditiesUniversity, setLayerContextCommoditiesUniversity] = useState(false);
    const [layerContextCommoditiesMall, setLayerContextCommoditiesMall] = useState(false);
    const [layerContextCommoditiesSupermarket, setLayerContextCommoditiesSupermarket] = useState(false);
    const [layerContextCommoditiesParc, setLayerContextCommoditiesParc] = useState(false);
    const [layerContextCommoditiesRestaurant, setLayerContextCommoditiesRestaurant] = useState(false);
    const [layerContextCommoditiesCulture, setLayerContextCommoditiesCulture] = useState(false);
    const [layerRules, setLayerRules] = useState(false);
    const [layerZonesUrba, setLayerZonesUrba] = useState(false);
    const [layerBuildableVolume, setLayerBuildableVolume] = useState(false);
    const [layerCombinationsSelected, setLayerCombinationsSelected] = useState(false);

    // LAYER : Toggle
    const layer_Toggle = (layer) => {
        // console.log("TOGGLE LAYER", layer);
        // Get state
        var mapLayersState = mapLayers;
        // Update state
        mapLayersState[layer]["isVisible"] = !mapLayersState[layer]["isVisible"];
        // Update original
        setMapLayers({ ...mapLayersState });
    }
    const layer_Set = (layer, bool) => {
        // console.log("TOGGLE SET", layer);
        // Get state
        var mapLayersState = mapLayers;
        // Update state
        mapLayersState[layer]["isVisible"] = bool;
        // Update original
        setMapLayers({ ...mapLayersState });
    }
    const layer_Set_All = (layer_list) => {
        // Get state
        var mapLayersState = mapLayers;
        // Loop th list
        layer_list.forEach(layer_set_data => {
            if (layer_set_data.length === 2) {
                // Update state
                try {
                    mapLayersState[layer_set_data[0]]["isVisible"] = layer_set_data[1];
                } catch (error) {
                    console.log("ERROR LAYER SET ALL");
                }
            }
        })
        // Update original
        setMapLayers({ ...mapLayersState });
    }

    // LAYER : On change
    useEffect(() => {
        setLayerSatellite(mapLayers.satellite.isVisible);
        setLayerOSMBuildings(mapLayers.OSM_Buildings.isVisible);
        setLayerCityLands(mapLayers.cityLands.isVisible);
        setLayerIGNBuildings(mapLayers.IGN_Buildings.isVisible);
        setLayerIGNBuildingsNoland(mapLayers.IGN_BuildingsNoland.isVisible);
        setLayerIGNBuildingsLand_keep(mapLayers.IGN_BuildingsLand_keep.isVisible);
        setLayerIGNBuildingsLand_erase(mapLayers.IGN_BuildingsLand_erase.isVisible);
        setLayerUnion(mapLayers.union.isVisible);
        setLayerDivision(mapLayers.division.isVisible);
        setLayerUnionBounds(mapLayers.boundsType.isVisible);
        setLayerTopo3D(mapLayers.topo_3D.isVisible);
        setLayerTopoPoints(mapLayers.topo_points.isVisible);
        setLayerTopoProfile(mapLayers.topo_profile.isVisible);
        setLayerRisksEarthquake(mapLayers.risks_earthquake.isVisible);
        setLayerTrees(mapLayers.trees.isVisible);
        setLayerRisksClay(mapLayers.risks_clay.isVisible);
        setLayerRisksPipes(mapLayers.risks_pipes.isVisible);
        setLayerRisksBasias(mapLayers.risks_basias.isVisible);
        setLayerContextMerimee(mapLayers.context_merimee.isVisible);
        setLayerContextSitadel(mapLayers.context_sitadel.isVisible);
        setLayerContextIsodistance(mapLayers.context_isodistance.isVisible);
        setLayerContextDVF(mapLayers.context_dvf.isVisible);
        setLayerContextCommoditiesCarPark(mapLayers.context_commodities_car_park.isVisible);
        setLayerContextCommoditiesCarCharging(mapLayers.context_commodities_car_charging.isVisible);
        setLayerContextCommoditiesTransport(mapLayers.context_commodities_transport.isVisible);
        setLayerContextCommoditiesTrain(mapLayers.context_commodities_train.isVisible);
        setLayerContextCommoditiesBike(mapLayers.context_commodities_bike.isVisible);
        setLayerContextCommoditiesHospital(mapLayers.context_commodities_hospital.isVisible);
        setLayerContextCommoditiesDoctor(mapLayers.context_commodities_doctor.isVisible);
        setLayerContextCommoditiesPharmacy(mapLayers.context_commodities_pharmacy.isVisible);
        setLayerContextCommoditiesBaby(mapLayers.context_commodities_baby.isVisible);
        setLayerContextCommoditiesPrimarySchool(mapLayers.context_commodities_primary_school.isVisible);
        setLayerContextCommoditiesSecondarySchool(mapLayers.context_commodities_secondary_school.isVisible);
        setLayerContextCommoditiesUniversity(mapLayers.context_commodities_university.isVisible);
        setLayerContextCommoditiesMall(mapLayers.context_commodities_mall.isVisible);
        setLayerContextCommoditiesSupermarket(mapLayers.context_commodities_supermarket.isVisible);
        setLayerContextCommoditiesParc(mapLayers.context_commodities_parc.isVisible);
        setLayerContextCommoditiesRestaurant(mapLayers.context_commodities_restaurant.isVisible);
        setLayerContextCommoditiesCulture(mapLayers.context_commodities_culture.isVisible);
        setLayerRules(mapLayers.rules.isVisible);
        setLayerZonesUrba(mapLayers.zones_urba.isVisible);
        setLayerBuildableVolume(mapLayers.buildable_volume.isVisible);
        setLayerCombinationsSelected(mapLayers.combinations_selected.isVisible);
    }, [mapLayers]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER SATELLITE");
        if (layerSatellite) { satellite_Show(); }
        else { satellite_Hide(); }
    }, [layerSatellite]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER OSM BUILDINGS");
        if (layerOSMBuildings) { OSMBuildings_Show(); layer_Set('IGN_Buildings', false); }
        // if (layerOSMBuildings) { OSMBuildings_Show(); }
        else { OSMBuildings_Hide(); }
    }, [layerOSMBuildings]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER CITY LANDS");
        if (layerCityLands) { landPolygons2_Show(); }
        else { landPolygons2_Hide(); landLabels_Hide(); }
    }, [layerCityLands]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER IGN BUILDINGS");
        if (layerIGNBuildings) {
            // layer_Set('OSM_Buildings', false);
            var layer_set_list = [['OSM_Buildings', false]];
            // Activate all sub layers if they were all to false
            if (!layerIGNBuildingsNoland && !layerIGNBuildingsLand_keep && !layerIGNBuildingsLand_erase) {
                // layer_Set('IGN_BuildingsNoland', true); layer_Set('IGN_BuildingsLand_keep', true); layer_Set('IGN_BuildingsLand_erase', true);
                layer_set_list = [['OSM_Buildings', false], ['IGN_BuildingsNoland', true], ['IGN_BuildingsLand_keep', true], ['IGN_BuildingsLand_erase', true]];
            }
            layer_Set_All(layer_set_list);
        }
        // if (layerIGNBuildings) { IGNBuildings_Show(); layer_Set('OSM_Buildings', false); }
        // if (layerIGNBuildings) { IGNBuildings_Show(); }
        // else { layer_Set('IGN_BuildingsNoland', false); layer_Set('IGN_BuildingsLand_keep', false); layer_Set('IGN_BuildingsLand_erase', false); }
        else { layer_Set_All([['IGN_BuildingsNoland', false], ['IGN_BuildingsLand_keep', false], ['IGN_BuildingsLand_erase', false]]) }
    }, [layerIGNBuildings]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER IGN BUILDINGS NO LAND");
        // if (layerIGNBuildings) { IGNBuildings_Show(); layer_Set('OSM_Buildings', false); }
        if (layerIGNBuildingsNoland) {
            // layer_Set('OSM_Buildings', false); layer_Set('IGN_Buildings', true);
            layer_Set_All([['OSM_Buildings', false], ['IGN_Buildings', true]]);
            IGNBuildingsNoLand_Show();
        }
        else {
            if (!layerIGNBuildingsLand_erase && (!capacity?.buildable?.volume?.parameters?.builtArea > 0 || !layerIGNBuildingsLand_keep)) {
                // Close parent if all children are false
                layer_Set('IGN_Buildings', false);
            }
            IGNBuildingsNoLand_Hide();
        }
    }, [layerIGNBuildingsNoland]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER IGN BUILDINGS LAND");
        if (layerIGNBuildingsLand_keep) {
            // layer_Set('OSM_Buildings', false); layer_Set('IGN_Buildings', true);
            layer_Set_All([['OSM_Buildings', false], ['IGN_Buildings', true]]);
            IGNBuildingsLandKeep_Show();
        }
        else {
            if (!layerIGNBuildingsNoland && !layerIGNBuildingsLand_erase) {
                // Close parent if all children are false
                layer_Set('IGN_Buildings', false);
            }
            IGNBuildingsLandKeep_Hide();
        }
    }, [layerIGNBuildingsLand_keep]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER IGN BUILDINGS LAND");
        if (layerIGNBuildingsLand_erase) {
            // layer_Set('OSM_Buildings', false); layer_Set('IGN_Buildings', true);
            layer_Set_All([['OSM_Buildings', false], ['IGN_Buildings', true]]);
            IGNBuildingsLandErase_Show();
        }
        else {
            if (!layerIGNBuildingsNoland && (!capacity?.buildable?.volume?.parameters?.builtArea > 0 || !layerIGNBuildingsLand_keep)) {
                // Close parent if all children are false
                layer_Set('IGN_Buildings', false);
            }
            IGNBuildingsLandErase_Hide();
        }
    }, [layerIGNBuildingsLand_erase]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER UNION");
        if (layerUnion) { unionPolygon_Show(); unionLines_Show(); }
        else { unionPolygon_Hide(); unionLines_Hide(); }
    }, [layerUnion]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER DIVISION");
        if (layerDivision) { divisionPolygon_Show(); divisionLines_Show(); }
        else { divisionPolygon_Hide(); divisionLines_Hide(); }
    }, [layerDivision]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER BOUNDS TYPE");
        if (layerUnionBounds) { unionLinesType_Show(); }
        else { unionLinesType_Hide(); }
    }, [layerUnionBounds]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER TOPO 3D");
        if (layerTopo3D) { topo3D_Show(); }
        else { topo3D_Hide(); }
    }, [layerTopo3D]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER TOPO 3D");
        if (layerTopoPoints) { topoPoints_Show(); }
        else { topoPoints_Hide(); }
    }, [layerTopoPoints]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER TOPO PROFILE");
        if (layerTopoProfile) { topoProfile_Show(); }
        else { topoProfile_Hide(); }
    }, [layerTopoProfile]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER TREES");
        if (layerTrees) { trees_Show(); }
        else { trees_Hide(); }
    }, [layerTrees]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER RISKS EARTHQUAKE");
        if (layerRisksEarthquake) { risksEarthquake_Show(); }
        else { risksEarthquake_Hide(); }
    }, [layerRisksEarthquake]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER RISKS CLAY");
        if (layerRisksClay) { risksClay_Show(); }
        else { risksClay_Hide(); }
    }, [layerRisksClay]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER RISKS PIPES");
        if (layerRisksPipes) { risksPipes_Show(); }
        else { risksPipes_Hide(); }
    }, [layerRisksPipes]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER RISKS BASIAS");
        if (layerRisksBasias) { risksBasias_Show(); }
        else { risksBasias_Hide(); }
    }, [layerRisksBasias]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER CONTEXT MERIMEE");
        if (layerContextMerimee) { contextMerimee_Show(); }
        else { contextMerimee_Hide(); }
    }, [layerContextMerimee]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER CONTEXT SITADEL");
        if (layerContextSitadel) { contextSitadel_Show(); }
        else { contextSitadel_Hide(); }
    }, [layerContextSitadel]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER CONTEXT ISODISTANCE");
        if (layerContextIsodistance) { contextIsodistance_Show(); }
        else { contextIsodistance_Hide(); }
    }, [layerContextIsodistance]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER CONTEXT DVF");
        if (layerContextDVF) { contextDVF_Show(); }
        else { contextDVF_Hide(); }
    }, [layerContextDVF]);

    useEffect(() => {
        if (layerContextCommoditiesCarPark) { contextCommoditiesCarPark_Show(); }
        else { contextCommoditiesCarPark_Hide(); }
    }, [layerContextCommoditiesCarPark]);

    useEffect(() => {
        if (layerContextCommoditiesCarCharging) { contextCommoditiesCarCharging_Show(); }
        else { contextCommoditiesCarCharging_Hide(); }
    }, [layerContextCommoditiesCarCharging]);

    useEffect(() => {
        if (layerContextCommoditiesTransport) { contextCommoditiesTransport_Show(); }
        else { contextCommoditiesTransport_Hide(); }
    }, [layerContextCommoditiesTransport]);

    useEffect(() => {
        if (layerContextCommoditiesTrain) { contextCommoditiesTrain_Show(); }
        else { contextCommoditiesTrain_Hide(); }
    }, [layerContextCommoditiesTrain]);

    useEffect(() => {
        if (layerContextCommoditiesBike) { contextCommoditiesBike_Show(); }
        else { contextCommoditiesBike_Hide(); }
    }, [layerContextCommoditiesBike]);

    useEffect(() => {
        if (layerContextCommoditiesHospital) { contextCommoditiesHospital_Show(); }
        else { contextCommoditiesHospital_Hide(); }
    }, [layerContextCommoditiesHospital]);

    useEffect(() => {
        if (layerContextCommoditiesDoctor) { contextCommoditiesDoctor_Show(); }
        else { contextCommoditiesDoctor_Hide(); }
    }, [layerContextCommoditiesDoctor]);

    useEffect(() => {
        if (layerContextCommoditiesPharmacy) { contextCommoditiesPharmacy_Show(); }
        else { contextCommoditiesPharmacy_Hide(); }
    }, [layerContextCommoditiesPharmacy]);

    useEffect(() => {
        if (layerContextCommoditiesBaby) { contextCommoditiesBaby_Show(); }
        else { contextCommoditiesBaby_Hide(); }
    }, [layerContextCommoditiesBaby]);

    useEffect(() => {
        if (layerContextCommoditiesPrimarySchool) { contextCommoditiesPrimarySchool_Show(); }
        else { contextCommoditiesPrimarySchool_Hide(); }
    }, [layerContextCommoditiesPrimarySchool]);

    useEffect(() => {
        if (layerContextCommoditiesSecondarySchool) { contextCommoditiesSecondarySchool_Show(); }
        else { contextCommoditiesSecondarySchool_Hide(); }
    }, [layerContextCommoditiesSecondarySchool]);

    useEffect(() => {
        if (layerContextCommoditiesUniversity) { contextCommoditiesUniversity_Show(); }
        else { contextCommoditiesUniversity_Hide(); }
    }, [layerContextCommoditiesUniversity]);

    useEffect(() => {
        if (layerContextCommoditiesMall) { contextCommoditiesMall_Show(); }
        else { contextCommoditiesMall_Hide(); }
    }, [layerContextCommoditiesMall]);

    useEffect(() => {
        if (layerContextCommoditiesSupermarket) { contextCommoditiesSupermarket_Show(); }
        else { contextCommoditiesSupermarket_Hide(); }
    }, [layerContextCommoditiesSupermarket]);

    useEffect(() => {
        if (layerContextCommoditiesParc) { contextCommoditiesParc_Show(); }
        else { contextCommoditiesParc_Hide(); }
    }, [layerContextCommoditiesParc]);

    useEffect(() => {
        if (layerContextCommoditiesRestaurant) { contextCommoditiesRestaurant_Show(); }
        else { contextCommoditiesRestaurant_Hide(); }
    }, [layerContextCommoditiesRestaurant]);

    useEffect(() => {
        if (layerContextCommoditiesCulture) { contextCommoditiesCulture_Show(); }
        else { contextCommoditiesCulture_Hide(); }
    }, [layerContextCommoditiesCulture]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER RULES");
        if (layerRules) {
            rules_Show();
            setHighlightedRule("all");
        }
        else {
            rules_Hide();
            setHighlightedRule(null);
        }
    }, [layerRules]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER ZONES URBA");
        if (layerZonesUrba) { zonesUrba_Show(); }
        else { zonesUrba_Hide(); }
    }, [layerZonesUrba]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER BUILDABLE VOLUME");
        if (layerBuildableVolume) { buildableVolume_Show(); }
        else { buildableVolume_Hide(); }
    }, [layerBuildableVolume]);

    useEffect(() => {
        // console.log("CHANGE ON LAYER COMBINATIONS SELECTED");
        if (layerCombinationsSelected) { combinationsSelected_Show(); }
        else { combinationsSelected_Hide(); }
    }, [layerCombinationsSelected]);

    // Layers Update Tracker
    useEffect(() => {
        // console.log("GLOBAL UPDATE TRACKED for " + globalUpdateTracker.item, globalUpdateTracker.value);
        if (globalUpdateTracker.item.includes("_union_") && layerUnion) {
            unionPolygon_Show();
            unionLines_Show();
        }
        if (globalUpdateTracker.item.includes("_division_") && layerDivision) {
            divisionPolygon_Show();
            divisionLines_Show();
        }
        if (globalUpdateTracker.item.includes("_bound_") && layerUnionBounds) {
            unionLinesType_Show();
        }
        if (globalUpdateTracker.item.includes("_rule_") && layerRules) {
            rules_Show();
        }
        if (globalUpdateTracker.item.includes("_volume_") && layerBuildableVolume) {
            buildableVolume_Show();
        }
        if (globalUpdateTracker.item.includes("_building_")) {
            if (layerIGNBuildingsNoland) {
                IGNBuildingsNoLand_Show();
            }
            if (layerIGNBuildingsLand_keep) {
                IGNBuildingsLandKeep_Show();
            }
            if (layerIGNBuildingsLand_erase) {
                IGNBuildingsLandErase_Show();
            }
        }
    }, [globalUpdateTracker]);

    // Zones Urba
    useEffect(() => {
        // console.log("UPDATED ZONE URBA MAP SHOW", zoneUrba_mapShow);
        if (zoneUrba_mapShow.length > 0) { zoneUrba_Show(zoneUrba_mapShow) }
        else { zoneUrba_Hide() }
    }, [zoneUrba_mapShow]);



    // __________ CAPACITY

    // Set states
    const [capacity_step, setCapacity_step] = useState(undefined);
    const [capacity_city, setCapacity_city] = useState(undefined);
    const [capacity_geocoderResult, setCapacity_geocoderResult] = useState(undefined);
    const [capacity_landBase, setCapacity_landBase] = useState(undefined);

    // Set ref
    const refCapacity = useRef(capacity);

    // CAPA : Update states
    useEffect(() => {
        // console.log("------CHANGING CAPA", capacity);

        refCapacity.current = capacity;

        setCapacity_step(capacity?.step);
        setCapacity_city(capacity?.city);
        setCapacity_geocoderResult(capacity?.geocoderResult);
        setCapacity_landBase(capacity?.landBase);
    }, [capacity]);

    // CAPA : On step change
    // useEffect(() => {
    //     console.log("------CHANGING CAPA_STEP", capacity?.step);

    //     if (capacity?.step) {
    //         console.log("-UPDATING STEP- : Step ", capacity.step);
    //     }

    //     // STEP 0
    //     if (capacity?.step === 0) {
    //         // Set lands polygons interactions
    //         landPolygons_SetInteractions();
    //     }

    //     // STEP 1 - A REVOIR /\ ________________________________________________________________________________________________________________________________
    //     if (capacity?.step === 1) {
    //         // Update map for step 1
    //         // Remove city lands if shown
    //         if (map.current.getLayer('layerLandsPolys')) {
    //             map.current.removeLayer('layerLandsPolys');
    //         }
    //         if (map.current.getSource('sourceLandsPolys')) {
    //             map.current.removeSource('sourceLandsPolys');
    //         }
    //         // Remove lands number if shown
    //         if (map.current.getLayer('layerLandsLabels')) {
    //             map.current.removeLayer('layerLandsLabels');
    //         }
    //         if (map.current.getSource('sourceLandsLabels')) {
    //             map.current.removeSource('sourceLandsLabels');
    //         }
    //         // Remove union bounds if shown
    //         if (map.current.getLayer('layerLandBounds')) {
    //             map.current.removeLayer('layerLandBounds');
    //         }
    //         if (map.current.getSource('sourceLandBounds')) {
    //             map.current.removeSource('sourceLandBounds');
    //         }
    //         // Remove union poly if shown
    //         if (map.current.getLayer('layerLandPoly')) {
    //             map.current.removeLayer('layerLandPoly');
    //         }
    //         if (map.current.getSource('sourceLandPoly')) {
    //             map.current.removeSource('sourceLandPoly');
    //         }
    //         // Show landbase bounds
    //         if (map.current.getLayer('layerLandbaseBounds')) {
    //             map.current.removeLayer('layerLandbaseBounds');
    //         }
    //         if (map.current.getSource('sourceLandbaseBounds')) {
    //             map.current.removeSource('sourceLandbaseBounds');
    //         }
    //         // _Add source
    //         var union_data = {
    //             type: 'FeatureCollection',
    //             features: [
    //                 capacity.landBase.union.outerLine,
    //             ],
    //         };
    //         var union_color = "#062134";
    //         if (capacity.landBase.union.outerLine.type === "FeatureCollection") {
    //             union_data = capacity.landBase.union.outerLine;
    //             union_color = "#DD1C1A";
    //         }
    //         map.current.addSource('sourceLandbaseBounds', {
    //             type: 'geojson',
    //             data: union_data
    //         });
    //         // _Add layer
    //         map.current.addLayer({
    //             id: 'layerLandbaseBounds',
    //             type: 'line',
    //             source: 'sourceLandbaseBounds',
    //             layout: {
    //                 'line-cap': 'round',
    //                 'line-join': 'round'
    //             },
    //             paint: {
    //                 'line-color': union_color,
    //                 'line-width': 2.5,
    //             },
    //             filter: ['in', '$type', 'LineString']
    //         }, 'building-extrusion');
    //         // Show landbase poly
    //         if (map.current.getLayer('layerLandbasePoly')) {
    //             map.current.removeLayer('layerLandbasePoly');
    //         }
    //         if (map.current.getSource('sourceLandbasePoly')) {
    //             map.current.removeSource('sourceLandbasePoly');
    //         }
    //         // _Add source
    //         var union_poly_data = {
    //             type: 'FeatureCollection',
    //             features: [
    //                 capacity.landBase.union.geometry,
    //             ],
    //         };
    //         if (capacity.landBase.union.geometry.type === "FeatureCollection") {
    //             union_poly_data = capacity.landBase.union.geometry;
    //         }
    //         map.current.addSource('sourceLandbasePoly', {
    //             type: 'geojson',
    //             data: union_poly_data
    //         });
    //         // _Add layer
    //         map.current.addLayer({
    //             id: 'layerLandbasePoly',
    //             type: 'fill',
    //             source: 'sourceLandbasePoly',
    //             paint: {
    //                 'fill-color': union_color,
    //                 'fill-opacity': 0.4,
    //             },
    //         }, 'building-extrusion');
    //         // Fly to
    //         map.current.fitBounds([
    //             [capacity.landBase.union.bbox.coordinates[0][3][0] - 0.00001, capacity.landBase.union.bbox.coordinates[0][3][1] - 0.00001], // southwestern corner of the bounds
    //             [capacity.landBase.union.bbox.coordinates[0][1][0] + 0.00001, capacity.landBase.union.bbox.coordinates[0][1][1] + 0.00001], // northeastern corner of the bounds
    //         ]);
    //     }
    // }, [capacity_step]);

    // CAPA : On change step
    useEffect(() => {
        // console.log("-UPDATING STEP- : Step ", capacity?.step);

        try {

            // STEP 0
            if (currentStep === 0) {
                // Set lands polygons interactions
                landPolygons_SetInteractions();
            }
            else if (currentStep === 1) {
                // Hide lands polygons
                landPolygons_Hide();
                // Hide lands labels
                landLabels_Hide();
                // Hide union dash lines
                unionDashLines_Hide();
                // Show union polygon
                unionPolygon_Show();
                // Show union lines
                unionLines_Show();
                // Fit view to union
                flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.00006);
            }

            if (currentStep > 0) {
                // Hide geolocate control
                // document.getElementsByClassName("mapboxgl-ctrl-geolocate")[0].parentNode.style.display = "none";
                userLocation_Hide();
                // Replace custom controls
                // document.getElementsByClassName("app__map_button_container")[0].style.top = "107px";
                if (window.innerWidth > 550) {
                    document.getElementsByClassName("app__map_button_container2")[0].style.top = "270px";
                    document.getElementsByClassName("app__map_button_container3")[0].style.top = "309px";
                }
                // Set 3D buildings interactions
                IGNBuildings_SetInteractions("land");
                IGNBuildings_SetInteractions("noland");
                // Set DVF interactions
                contextDVF_SetInteractions();
            }

        } catch (error) {
            console.log("Error", error);
            setErrorPage(error?.message);
        }


    }, [currentStep]);

    // CAPA : On city change
    useEffect(() => {
        // Only triggered if SETP === 0
        if (currentStep === 0) {
            // console.log("------CHANGING CAPA_CITY", capacity?.city);

            // console.log("-UPDATING CITY- : Success", capacity?.city);
            if (!capacity?.city || capacity.city === null || capacity?.city === "fetching") {
                landPolygons_Hide();
                landLabels_Hide();
            }
            else {
                landPolygons_Show();
                landLabels_Show();
            }
            // Reset landBase
            if (capacity?.city) {
                // console.log("RESET LANDBASE");
                setCapacity({ ...capacity, landBase: { lands: [], union: {} }, toSave: { autoSave: false, action: "", value: "" } });
            }
        }
    }, [capacity_city]);

    // CAPA : On geocoderResult change
    useEffect(() => {
        // Only triggered if SETP === 0
        if (currentStep === 0) {
            // console.log("------CHANGING CAPA_GEOCODERRESULT", capacity?.geocoderResult);

            if (capacity?.geocoderResult) {
                var target_zoom = 13;
                var target_center = [];
                // If search_type = "land"
                if (capacity.geocoderResult?.geometry_name) {
                    target_zoom = 19.5;
                    // target_center = [
                    //     (capacity.geocoderResult.bbox[0] + capacity.geocoderResult.bbox[2]) / 2,
                    //     (capacity.geocoderResult.bbox[1] + capacity.geocoderResult.bbox[3]) / 2,
                    // ]
                    target_center = turf.getCoord(turf.centroid(capacity.geocoderResult));
                }
                // If search_type = "address"
                else {
                    if (capacity.geocoderResult.properties.type === "street") { target_zoom = 17 }
                    else if (capacity.geocoderResult.properties.type === "housenumber") { target_zoom = 19 };
                    target_center = capacity.geocoderResult.geometry.coordinates;
                }
                // console.log("FLY TO");
                map.current.flyTo({
                    center: target_center,
                    zoom: target_zoom,
                    essential: true
                });
                // Preload target city
                // console.log("CALL FROM GEOCODER CHANGE");
                getMunicipality(target_center[0], target_center[1]);
            }
        }
    }, [capacity_geocoderResult]);

    // CAPA : On landBase change
    useEffect(() => {
        // Only triggered if SETP === 0
        if (currentStep === 0) {
            // console.log("------CHANGING CAPA_LANDBASE", capacity?.landBase);

            if (capacity?.landBase && capacity?.landBase?.lands) {
                if (capacity.landBase.lands.length === 0) {
                    unionPolygon_Hide();
                    unionDashLines_Hide();
                }
                else {
                    unionPolygon_Show();
                    unionDashLines_Show();
                }
            }

            // Get calculated values
            // if (capacity?.landBase?.union?.bounds_groups && !capacity?.landBase?.calculated_values) {
            //     var landBase = capacity.landBase;
            //     var limit_total_length = 0;
            //     var limit_separation_length = 0;
            //     var limit_public_length = 0;
            //     var limit_nc_length = 0;
            //     for (var i = 0; i < capacity.landBase.union.bounds_groups.length; i++) {
            //         limit_total_length += capacity.landBase.union.bounds_groups[i].length;
            //         if (capacity.landBase.union.bounds_groups[i].context_analysis.result.type === "separation") {
            //             limit_separation_length += capacity.landBase.union.bounds_groups[i].length;
            //         }
            //         else if (capacity.landBase.union.bounds_groups[i].context_analysis.result.type === "public") {
            //             limit_public_length += capacity.landBase.union.bounds_groups[i].length;
            //         }
            //         else {
            //             limit_nc_length += capacity.landBase.union.bounds_groups[i].length;
            //         }
            //     }
            //     landBase.calculated_values = {
            //         limit_total_length: limit_total_length,
            //         limit_separation_length: limit_separation_length,
            //         limit_public_length: limit_public_length,
            //         limit_nc_length: limit_nc_length
            //     }
            //     console.log("JUST FOR CALCULATED VALUES");
            //     setCapacity({ ...capacity, landBase: landBase, toSave: { autoSave: false, action: "", value: "" } });
            // }
        }
    }, [capacity_landBase]);

    // CAPA : On Section change
    // useEffect(() => {
    //     console.log('New Section', currentSection);
    //     currentSection_var = currentSection;-
    // }, [currentSection]);


    // __________ SATELLITE

    // SATELLITE : Hide
    const satellite_Hide = () => {
        if (map.current.getLayer('layerSatellite')) {
            map.current.removeLayer('layerSatellite');
        }
        if (map.current.getSource('sourceSatellite')) {
            map.current.removeSource('sourceSatellite');
        }
    }

    // SATELLITE : Show
    const satellite_Show = () => {
        // Remove if exists already
        satellite_Hide();
        // Add source
        map.current.addSource('sourceSatellite', {
            type: 'raster',
            url: 'mapbox://mapbox.satellite',
            tileSize: 256,
        });
        // Add layer
        map.current.addLayer({
            id: 'layerSatellite',
            source: 'sourceSatellite',
            type: 'raster',
            layout: {
                visibility: 'visible',
            },
        }, layer_get_position('layerSatellite'));
        // }, 'aeroway-polygon');
    }


    // __________ LANDS POLYGONS

    // LANDS POLYGONS : Hide
    const landPolygons_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerLandsPolys')) {
            map.current.removeLayer('layerLandsPolys');
        }
        // Remove source
        if (map.current.getSource('sourceLandsPolys')) {
            map.current.removeSource('sourceLandsPolys');
        }
        // Remove search popup
        if (search_popup !== null) {
            search_popup.remove();
            search_popup = null;
        }
    }

    // LANDS POLYGONS : Show
    const landPolygons_Show = () => {
        if (capacity?.city && capacity.city !== null && capacity.city !== "fetching" && capacity.city?.bounds) {
            // Fetch land polygons
            fetch(capacity.city["url_lands"])
                .then(res => res.json())
                .then(
                    (result) => {
                        console.log("-FETCHING LANDS- : Success", result);
                        // Create landsDB
                        map_helpers.create_landsDB(result.features, setSearchParameters, dispatch, capacity);
                        // Remove Layer and Source if already loaded
                        landPolygons_Hide();
                        // Add source to map
                        map.current.addSource('sourceLandsPolys', {
                            'type': 'geojson',
                            'data': result,
                            'generateId': true
                        });
                        // Add layers to map
                        map.current.addLayer({
                            'id': 'layerLandsPolys',
                            'type': 'fill',
                            'source': 'sourceLandsPolys',
                            'layout': {
                                'visibility': 'visible'
                            },
                            'paint': {
                                'fill-color': [
                                    'case',
                                    ['boolean', ['feature-state', 'clicked'], false],
                                    '#062134',
                                    '#888888'
                                ],
                                // 'fill-color': '#888888',
                                // 'fill-opacity': 0.4,
                                // 'fill-opacity': [
                                //     'case',
                                //     ['boolean', ['get', 'notMatched'], true],
                                //     0.1,
                                //     0.4, //all other cases
                                // ],
                                'fill-opacity': [
                                    'match',
                                    ['get', 'search_match'],
                                    '100',
                                    0.4,
                                    '50',
                                    0.25,
                                    '0',
                                    0.18,
                                    0.4 /*other*/
                                ],
                                'fill-outline-color': 'rgba(6, 33, 52, 1)'
                            }
                        }, layer_get_position('layerLandsPolys'));
                        // Scroll to section 1
                        if (currentSection !== 1) {
                            setTargetSection(1);
                        }
                    },
                    (error) => {
                        console.log("Error fetching lands", error);
                    }
                )
        }
    }

    // LANDS POLYGONS : Update from search
    const landPolygons_UpdateFromSearch = (features) => {
        // Check if source
        if (map.current.getSource('sourceLandsPolys')) {
            console.log("SOURCE", features);
            map.current.getSource('sourceLandsPolys').setData({ type: "FeatureCollection", features: features });
            // Close search popup
            if (search_popup !== null) {
                search_popup.remove();
                search_popup = null;
            }
        }
    }

    // LAND POLYGONS : Check
    const landPolygons_Check = () => {
        // Check if step === 0
        if (capacity?.step === 0) {
            // Check if no land search currently running
            if ((!capacity?.search?.currentFiltersIds || capacity.search.currentFiltersIds.length === 0)) {
                // Check if zoom is big enought to show land polygons
                if (zoom >= 13) {
                    // Set city to "fetching" if null to avoid multiple fetch
                    if (capacity.city === null) {
                        setCapacity({ ...capacity, city: "fetching", toSave: { autoSave: false, action: "", value: "" } });
                    }
                    // Call the getMunicipality function
                    // console.log("CALL FROM LAND POLYGONS CHECK");
                    getMunicipality(lng, lat);
                }
                // If zoom is not big enought => hide land polygons
                else {
                    // Remove land polygons
                    landPolygons_Hide();
                    // Reset city to null
                    if (capacity.city !== null) {
                        setCapacity({ ...capacity, city: null, toSave: { autoSave: false, action: "", value: "" } });
                    }
                    // Reset landSelection
                    // if (landSelection.area !== 0) {
                    //     setLandSelection({ lands: [], area: 0 });
                    //     map_helpers.land_rest();
                    // }
                }
            }
            else {
                console.log("MAP CITY FETCH DISABLED DURING LAND SEARCH");
            }
        }
    }

    // LAND POLYGONS : Fetch Municipality
    const getMunicipality = async (longitude, latitude) => {
        await map_helpers.get_municipality(longitude, latitude, capacity, setCapacity);
    }

    // LAND POLYGONS : Set Interactions
    const landPolygons_SetInteractions = () => {
        map.current.on('mouseenter', 'layerLandsPolys', () => { map.current.getCanvas().style.cursor = 'pointer' });
        map.current.on('mouseleave', 'layerLandsPolys', () => { map.current.getCanvas().style.cursor = '' });
        map.current.on('click', 'layerLandsPolys', (e) => { landPolygons_Click(e) });
        map.current.on('contextmenu', 'layerLandsPolys', (e) => { landPolygons_RightClick(e) });
    }

    // LAND POLYGONS : Cancel Interactions
    const landPolygons_CancelInteractions = () => {
        map.current.off('mouseenter', 'layerLandsPolys', () => { map.current.getCanvas().style.cursor = 'pointer' });
        map.current.off('mouseleave', 'layerLandsPolys', () => { map.current.getCanvas().style.cursor = '' });
        map.current.off('click', 'layerLandsPolys', (e) => { landPolygons_Click(e) });
    }

    // LAND POLYGONS : Click
    const landPolygons_Click = (e) => {
        try {
            // Scroll to Terrain section
            if (currentSection !== 2) {
                setTargetSection(2);
                // console.log("SCROLL TO TERRAIN");
            }
            // Query landsPolys and roads
            var visibleLandsAndRoads = map.current.queryRenderedFeatures({
                layers: ['layerLandsPolys', 'road-simple']
            });
            console.log("QueryResult Land & Roads:", visibleLandsAndRoads);
            // Query buildings
            var visibleBuildings = map.current.queryRenderedFeatures({
                layers: ['building-extrusion'],
            });
            // console.log("QueryResult Buildings:", visibleBuildings);
            // Query landsDB
            var landDB_geometry = map_helpers.get_landsDB(e.features[0].properties);
            console.log("landDB_geometry:", landDB_geometry);
            // Launch select script
            // map_helpers.land_select(e.features[0].id, e.features[0].properties, e.features[0].geometry, refCapacity.current, setCapacity, visibleLandsAndRoads, setIsMapLoading, map);
            console.log("Clicked land:", e.features[0].id, e.features[0].properties);
            map_helpers.land_select(e.features[0].id, e.features[0].properties, landDB_geometry, refCapacity.current, setCapacity, visibleLandsAndRoads, visibleBuildings, setIsMapLoading, map);
        } catch (error) {
            console.error("ERR", error);
            setErrorPage(error?.message);
        }
    }

    const landPolygons_RightClick = (e) => {
        try {
            console.log("RIGHT CLICK", e.features);
            if (e.features.length > 0) {
                var polygon = e.features[0];
                var centroid = turf.centroid(polygon);
                var coordinates = turf.getCoord(centroid);
                var section = polygon?.properties?.section || "";
                var numero = polygon?.properties?.numero ? "-" + parseInt(polygon.properties.numero) : "";
                if (section.substring(0, 1) === "0") { section = section.substring(1) };
                var label = "Parcelle " + section + numero;
                var txt_label = '<span style="color:#062134;font-size:15px;font-weight:700"><strong>' + label + '</strong></span>';
                var txt_land_area = polygon?.properties?.s ? '<br><span style="font-size:12px;font-weight: 600;color: grey;">Superficie :</span><br><span style="color:#062134;font-weight:500;">' + helpers.get_pretty_num(polygon.properties.s) + ' m²</span>' : (polygon?.properties?.contenance ? '<br><span style="font-size:12px;font-weight: 600;color: grey;">Superficie :</span><br><span style="color:#062134;font-weight:500;">' + helpers.get_pretty_num(polygon.properties.contenance) + ' m²</span>' : "");
                var txt_land_compacity = polygon?.properties?.Im ? '<br><span style="font-size:12px;font-weight: 600;color: grey;">Compacité :</span><br><span style="color:#062134;font-weight:500;">' + helpers.get_pretty_num(polygon?.properties.Im) + '</span>' : "";

                // Close other search popup
                if (search_popup !== null) {
                    search_popup.remove();
                    search_popup = null;
                }

                // Create new search popup
                search_popup = new mapboxgl.Popup({ offset: 10 })
                    // search_popup = new mapboxgl.Popup()
                    // .setLngLat(coordinates)
                    .setLngLat(e.lngLat)
                    .setHTML('<div style="font-family:Manrope; color:#062134;">' + txt_label + txt_land_area + txt_land_compacity + '</div>')
                    .addTo(map.current);
            }
        } catch (error) {
            console.error("ERR", error);
            // setErrorPage(error?.message);
        }
    }


    // _________ LANDS POLYGONS 2

    // LANDS POLYGONS : Hide
    const landPolygons2_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerLandsPolys2')) {
            map.current.removeLayer('layerLandsPolys2');
        }
        // Remove source
        if (map.current.getSource('sourceLandsPolys2')) {
            map.current.removeSource('sourceLandsPolys2');
        }
    }

    // LANDS POLYGONS : Show
    const landPolygons2_Show = () => {
        if (capacity?.city && capacity.city !== null && capacity.city !== "fetching" && capacity.city?.bounds) {
            // Fetch land polygons
            fetch(capacity.city["url_lands"])
                .then(res => res.json())
                .then(
                    (result) => {
                        // console.log("-FETCHING LANDS- : Success", result);
                        // Remove Layer and Source if already loaded
                        landPolygons2_Hide();
                        // Add source to map
                        map.current.addSource('sourceLandsPolys2', {
                            'type': 'geojson',
                            'data': result,
                            'generateId': true
                        });
                        // Add layers to map
                        map.current.addLayer({
                            'id': 'layerLandsPolys2',
                            'type': 'fill',
                            'source': 'sourceLandsPolys2',
                            'layout': {
                                'visibility': 'visible'
                            },
                            'paint': {
                                // 'fill-color': [
                                //     'case',
                                //     ['boolean', ['feature-state', 'clicked'], false],
                                //     '#062134',
                                //     '#888888'
                                // ],
                                'fill-color': '#888888',
                                // 'fill-opacity': 0.4,
                                'fill-opacity': [
                                    'case',
                                    ['boolean', ['get', 'notMatched'], true],
                                    0.1,
                                    0.4, //all other cases
                                ],
                                'fill-outline-color': 'rgba(6, 33, 52, 1)'
                            }
                        }, layer_get_position('layerLandsPolys2'));
                        // Add labels
                        setTimeout(() => {
                            landLabels_Show();
                        }, 400)
                    },
                    (error) => {
                        // console.log("Error fetching lands2", error);
                    }
                )
        }
    }



    // _________ LANDS LABELS

    // LAND LABELS : Hide
    const landLabels_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerLandsLabels')) {
            map.current.removeLayer('layerLandsLabels');
        }
        // Remove source
        if (map.current.getSource('sourceLandsLabels')) {
            map.current.removeSource('sourceLandsLabels');
        }
    }

    // LAND LABELS : Show
    const landLabels_Show = () => {
        // Available only at step 0
        if (capacity?.step === 0 || mapLayers?.cityLands?.isVisible === true) {

            // Remove labels
            // landLabels_Hide();

            // Check if zoom is big enought and if land polygons are displayed (because it is performing a query on them)
            if (zoom >= 17 && (map.current.getLayer('layerLandsPolys') || map.current.getLayer('layerLandsPolys2'))) {
                var visibleLands = [];
                if (map.current.getLayer('layerLandsPolys')) {
                    visibleLands = map.current.queryRenderedFeatures({
                        layers: ['layerLandsPolys']
                    });
                }
                else if (map.current.getLayer('layerLandsPolys2')) {
                    visibleLands = map.current.queryRenderedFeatures({
                        layers: ['layerLandsPolys2']
                    });
                }
                var labelList = [];
                var treatedIds = [];
                for (var i = 0; i < visibleLands.length; i++) {
                    // Check if not seen already
                    if (treatedIds.indexOf(visibleLands[i]["id"]) === -1) {
                        // Get centroïd with turf
                        // var centroid = turf.centerOfMass(visibleLands[i]["geometry"]);
                        // centroid.properties.label = visibleLands[i]["properties"]["section"] + "-" + visibleLands[i]["properties"]["numero"];
                        // labelList.push(centroid);
                        // treatedIds.push(visibleLands[i]["id"]);
                        // Get controid with polylabel
                        var centroid = {
                            type: "Feature",
                            geometry: {
                                type: "Point",
                                coordinates: polylabel(visibleLands[i]["geometry"]["coordinates"])
                            },
                            properties: {
                                label: visibleLands[i]["properties"]["section"] + "-" + visibleLands[i]["properties"]["numero"]
                            }
                        }
                        labelList.push(centroid);
                        treatedIds.push(visibleLands[i]["id"]);
                    }
                }
                var labelData = {
                    'type': 'FeatureCollection',
                    'features': labelList
                }
                // Check if labels are already displayed => update source data
                if (map.current.getLayer('layerLandsLabels')) {
                    map.current.getSource('sourceLandsLabels').setData(labelData);
                }
                // If labels not alreday displayed => add source & layer
                else {
                    // Add source
                    map.current.addSource('sourceLandsLabels', {
                        'type': 'geojson',
                        'data': labelData
                    });
                    // Add Layer
                    map.current.addLayer({
                        'id': 'layerLandsLabels',
                        'type': 'symbol',
                        'source': 'sourceLandsLabels',
                        'layout': {
                            'text-field': ['get', 'label'],
                            'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
                            'text-radial-offset': 0.5,
                            'text-justify': 'auto',
                            'text-size': 12,
                        },
                        paint: {
                            "text-color": "#062134"
                        }
                    }, layer_get_position('layerLandsLabels'));
                }
            }
            // If zoom is not big enought => hide land labels
            else {
                landLabels_Hide();
            }
        }
    }

    // __________ USER LOCATION

    // USER LOCATION : Hide
    const userLocation_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerUserLocation')) {
            map.current.removeLayer('layerUserLocation');
        }
        // Remove source
        if (map.current.getSource('sourceUserLocation')) {
            map.current.removeSource('sourceUserLocation');
        }
    }
    const userLocation_Show = (position) => {
        // Remove if exists already
        userLocation_Hide();
        if (position?.coords?.latitude && position?.coords?.longitude) {
            // Add source
            map.current.addSource('sourceUserLocation', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [{
                        "type": "Feature",
                        "properties": position?.coords,
                        "geometry": {
                            "type": "Point",
                            "coordinates": [position?.coords?.longitude, position?.coords?.latitude],
                        }
                    }],
                }
            });
            // Add layer points
            map.current.addLayer({
                'id': 'layerUserLocation',
                'type': 'circle',
                'source': 'sourceUserLocation',
                'paint': {
                    'circle-radius': 7,
                    'circle-color': '#062134',
                    'circle-stroke-width': 2,
                    'circle-stroke-color': "#FFF",
                },
                filter: ['in', '$type', 'Point']
            }, layer_get_position('layerUserLocation'));
            // Fly
            map.current.flyTo({
                center: [position?.coords?.longitude, position?.coords?.latitude],
                zoom: 17,
                essential: true
            });
        }
    }

    // __________ UNION POLYGON

    // UNION POLY : Hide
    const unionPolygon_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerUnionPolys')) {
            map.current.removeLayer('layerUnionPolys');
        }
        // Remove source
        if (map.current.getSource('sourceUnionPolys')) {
            map.current.removeSource('sourceUnionPolys');
        }
    }

    // UNION POLY : Show
    const unionPolygon_Show = () => {
        if (capacity?.landBase?.union?.outerLine) {
            // Set union color
            var union_color = "#062134";
            if (capacity.landBase.union.outerLine.type === "FeatureCollection") {
                union_color = "#DD1C1A";
            }
            // Remove if exists already
            unionPolygon_Hide();
            // Add source
            var union_poly_data = {
                type: 'FeatureCollection',
                features: [
                    capacity.landBase.union.geometry,
                ],
            };
            if (capacity.landBase.union.geometry.type === "FeatureCollection") {
                union_poly_data = capacity.landBase.union.geometry;
            }
            map.current.addSource('sourceUnionPolys', {
                type: 'geojson',
                data: union_poly_data
            });
            // Add layer
            map.current.addLayer({
                id: 'layerUnionPolys',
                type: 'fill',
                source: 'sourceUnionPolys',
                paint: {
                    'fill-color': union_color,
                    'fill-opacity': 0.4,
                },
            }, layer_get_position('layerUnionPolys'));
        }
    }

    // __________ UNION DASH LINES

    // UNION DASH LINES : Hide
    const unionDashLines_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerUnionDashLines')) {
            map.current.removeLayer('layerUnionDashLines');
        }
        // Remove source
        if (map.current.getSource('sourceUnionDashLines')) {
            map.current.removeSource('sourceUnionDashLines');
        }
    }

    // UNION DASH LINES : Show
    const unionDashLines_Show = () => {
        // Remove if exists already
        unionDashLines_Hide();
        // Add source
        var union_data = {
            type: 'FeatureCollection',
            features: [
                capacity.landBase.union.outerLine,
            ],
        };
        var union_color = "#062134";
        if (capacity.landBase.union.outerLine.type === "FeatureCollection") {
            union_data = capacity.landBase.union.outerLine;
            union_color = "#DD1C1A";
        }
        map.current.addSource('sourceUnionDashLines', {
            type: 'geojson',
            data: union_data
        });
        // Add layer
        map.current.addLayer({
            id: 'layerUnionDashLines',
            type: 'line',
            source: 'sourceUnionDashLines',
            layout: {
                'line-cap': 'round',
                'line-join': 'round'
            },
            paint: {
                'line-color': union_color,
                'line-width': 2.5,
                'line-dasharray': [2, 2]
            },
            filter: ['in', '$type', 'LineString']
        }, layer_get_position('layerUnionDashLines'));
    }


    // __________ UNION LINES

    // UNION LINES : Hide
    const unionLines_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerUnionLines')) {
            map.current.removeLayer('layerUnionLines');
        }
        // Remove source
        if (map.current.getSource('sourceUnionLines')) {
            map.current.removeSource('sourceUnionLines');
        }
    }

    // UNION LINES : Show
    const unionLines_Show = () => {
        if (capacity?.landBase?.union?.outerLine) {
            // Remove if exists already
            unionLines_Hide();
            // Add source
            var union_data = {
                type: 'FeatureCollection',
                features: [
                    capacity.landBase.union.outerLine,
                ],
            };
            map.current.addSource('sourceUnionLines', {
                type: 'geojson',
                data: union_data
            });
            // Add layer
            map.current.addLayer({
                id: 'layerUnionLines',
                type: 'line',
                source: 'sourceUnionLines',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#062134',
                    'line-width': 2.5,
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerUnionLines'));
        }
    }


    // __________ DIVISION POLYGON

    // DIVISION POLY : Hide
    const divisionPolygon_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerDivisionPolys')) {
            map.current.removeLayer('layerDivisionPolys');
        }
        // Remove source
        if (map.current.getSource('sourceDivisionPolys')) {
            map.current.removeSource('sourceDivisionPolys');
        }
        // Remove markers
        for (var i = 0; i < divisionMarkers.length; i++) {
            divisionMarkers[i].remove();
        }
    }

    // UNION POLY : Show
    const divisionPolygon_Show = () => {
        if (capacity?.landBase?.division?.division_areas?.features) {
            // Set union color
            var division_color = "#062134";
            // Remove if exists already
            divisionPolygon_Hide();
            // Add source
            var division_poly_data = {
                type: 'FeatureCollection',
                features: [
                    capacity.landBase.division.division_areas,
                ],
            };
            if (capacity.landBase.division.division_areas.type === "FeatureCollection") {
                division_poly_data = JSON.parse(JSON.stringify(capacity.landBase.division.division_areas));
            }
            // Sort to get red under blue
            var features_blue = [];
            var features_red = [];
            division_poly_data.features.forEach(feature => {
                if (feature?.properties?.isTaken === true) {
                    features_blue.push(feature);
                }
                else {
                    features_red.push(feature);
                }
            })
            var fetaures_sorted = features_blue.concat(features_red);
            division_poly_data.features = fetaures_sorted;
            map.current.addSource('sourceDivisionPolys', {
                type: 'geojson',
                data: division_poly_data
            });
            // Add layer
            map.current.addLayer({
                id: 'layerDivisionPolys',
                type: 'fill',
                source: 'sourceDivisionPolys',
                paint: {
                    'fill-color': '#DD1C1A',
                    'fill-opacity': [
                        'case',
                        ['boolean', ['get', 'isTaken'], true],
                        0,
                        0.2, //all other cases
                    ],
                },
            }, layer_get_position('layerDivisionPolys'));
            // Set markers & pop-up
            var markers = [];
            for (var i = 0; i < capacity.landBase.division.division_areas.features.length; i++) {
                var area_color = "#062134";
                if (capacity.landBase.division.division_areas.features[i]?.properties?.isTaken === false) {
                    area_color = "#DD1C1A";
                }
                var area_label = capacity.landBase.division.division_areas.features[i]?.properties?.label || "D";
                var area_value = capacity.landBase.division.division_areas.features[i]?.properties?.area ? "<br />" + helpers.get_pretty_num(capacity.landBase.division.division_areas.features[i].properties.area.toFixed(0)) + " m²" : "";
                var midPoint = turf.centroid(capacity.landBase.division.division_areas.features[i]);
                var el = document.createElement('div');
                el.className = 'marker';
                el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;color:#FFF;background-color:' + area_color + ';border-radius:6px;text-align:center"><span style=""><strong>' + area_label + '</strong>' + area_value + '</span></div>';
                markers.push(new mapboxgl.Marker(el, { offset: [0, -20] })
                    .setLngLat(turf.getCoord(midPoint))
                    // .setPopup(new mapboxgl.Popup({ offset: 15 })
                    //     .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Limite N°' + (i + 1) + '</strong></span><br><span style="font-size:12px;font-weight: 600;color: grey;">Longueur :</span><br><span style="color:#062134;font-weight:500;">' + (capacity.landBase.union.bounds_groups[i].length).toFixed(2) + ' m</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Type :</span><br><span style="color:#062134;font-weight:500;">' + type + '</span></p>'))
                    .addTo(map.current))
            }
            divisionMarkers = markers;
            console.log("DIVISION MARKER 002");
        }
    }

    // __________ DIVISION LINES

    // DIVISION LINES : Hide
    const divisionLines_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerDivisionLines')) {
            map.current.removeLayer('layerDivisionLines');
        }
        // Remove source
        if (map.current.getSource('sourceDivisionLines')) {
            map.current.removeSource('sourceDivisionLines');
        }
    }

    // DIVISION LINES : Show
    const divisionLines_Show = () => {
        if (capacity?.landBase?.division?.division_areas?.features) {
            // Remove if exists already
            divisionLines_Hide();
            // Create outer lines
            var features = [];
            capacity.landBase.division.division_areas.features.forEach(div_zone => {
                features.push(turf.polygonToLine(div_zone));
            })
            // Add source
            var division_line_data = {
                type: 'FeatureCollection',
                features: features,
            };
            map.current.addSource('sourceDivisionLines', {
                type: 'geojson',
                data: division_line_data
            });
            // Add layer
            map.current.addLayer({
                id: 'layerDivisionLines',
                type: 'line',
                source: 'sourceDivisionLines',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    // 'line-color': '#062134',
                    'line-color': [
                        'case',
                        ['boolean', ['get', 'isTaken'], true],
                        '#062134',
                        '#DD1C1A', //all other cases
                    ],
                    'line-width': 1.1,
                    // 'line-dasharray': [2, 2]
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerDivisionLines'));
        }
    }


    // __________ UNION LINES TYPE

    // UNION LINES TYPE : Hide
    const unionLinesType_Hide = () => {
        // Remove layer
        if (map.current.getLayer('layerUnionLinesType')) {
            map.current.removeLayer('layerUnionLinesType');
        }
        if (map.current.getLayer('layerUnionLinesTypeCircle')) {
            map.current.removeLayer('layerUnionLinesTypeCircle');
        }
        // Remove source
        if (map.current.getSource('sourceUnionLinesType')) {
            map.current.removeSource('sourceUnionLinesType');
        }
        // Remove markers
        for (var i = 0; i < boundsMarkers.length; i++) {
            boundsMarkers[i].remove();
        }
    }

    // UNION LINES TYPE : Show
    const unionLinesType_Show = () => {
        // Remove if exists already
        unionLinesType_Hide();
        // Create source
        var union_data = {
            type: 'FeatureCollection',
            features: [],
        };
        var bounds_markers = [];
        for (var i = 0; i < capacity.landBase.union.bounds_groups.length; i++) {
            var coordinates = [];
            for (var j = 0; j < capacity.landBase.union.bounds_groups[i].bounds.length; j++) {
                if (j === 0) {
                    coordinates.push(capacity.landBase.union.bounds[capacity.landBase.union.bounds_groups[i].bounds[j]].start_coord_global);
                }
                coordinates.push(capacity.landBase.union.bounds[capacity.landBase.union.bounds_groups[i].bounds[j]].end_coord_global);
            }
            union_data.features.push({
                "type": "Feature",
                "properties": capacity.landBase.union.bounds_groups[i].context_analysis.result,
                "geometry": {
                    "type": "LineString",
                    "coordinates": coordinates
                }
            });
            union_data.features.push({
                "type": "Feature",
                "geometry": {
                    "type": "Point",
                    "coordinates": capacity.landBase.union.bounds_groups[i].start_coord_global,
                }
            });

            // Set markers & pop-up
            var color = "#00a097";
            var type = capacity.landBase.union.bounds_types[capacity.landBase.union.bounds_groups[i].context_analysis.result.type].subtypes[capacity.landBase.union.bounds_groups[i].context_analysis.result.subtype].label;
            if (capacity.landBase.union.bounds_groups[i].context_analysis.result.type === "separation") {
                color = "#004861";
                // type = "Limite séparative";
            }
            else if (capacity.landBase.union.bounds_groups[i].context_analysis.result.type === "way") {
                color = "#007384";
                // type = "Limite sur voie";
            }
            var el = document.createElement('div');
            el.className = 'marker';
            el.innerHTML = '<div style="cursor:pointer;padding:4px 8px;font-family:Manrope;color:#FFF;background-color:' + color + ';border-radius:6px"><span style="">Limite N°' + (i + 1) + '</span></div>';
            bounds_markers.push(new mapboxgl.Marker(el)
                .setLngLat(capacity.landBase.union.bounds_groups[i].middle_coord_global)
                .setPopup(new mapboxgl.Popup({ offset: 15 })
                    .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Limite N°' + (i + 1) + '</strong></span><br><span style="font-size:12px;font-weight: 600;color: grey;">Longueur :</span><br><span style="color:#062134;font-weight:500;">' + (capacity.landBase.union.bounds_groups[i].length).toFixed(2) + ' m</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Type :</span><br><span style="color:#062134;font-weight:500;">' + type + '</span></p>'))
                .addTo(map.current))
        }
        setBoundsMarkers(bounds_markers);

        // Add source
        map.current.addSource('sourceUnionLinesType', {
            type: 'geojson',
            data: union_data
        });
        // Add layer lines
        map.current.addLayer({
            id: 'layerUnionLinesType',
            type: 'line',
            source: 'sourceUnionLinesType',
            layout: {
                'line-cap': 'round',
                'line-join': 'round'
            },
            paint: {
                'line-color': [
                    'match',
                    ['get', 'type'],
                    'separation',
                    '#004861',
                    'way',
                    '#007384',
                    /* default */ '#00a097'
                ],
                'line-width': 3.3,
            },
            filter: ['in', '$type', 'LineString']
        }, layer_get_position('layerUnionLinesType'));
        // Add layer points
        // map.current.addLayer({
        //     'id': 'layerUnionLinesTypeCircle',
        //     'type': 'circle',
        //     'source': 'sourceUnionLinesType',
        //     'paint': {
        //         'circle-radius': 5.3,
        //         'circle-color': '#062134'
        //     },
        //     filter: ['in', '$type', 'Point']
        // });
    }


    // __________ TOPO 3D

    // TOPO 3D : Hide
    const topo3D_Hide = () => {
        if (map.current.getLayer('layer3D_land')) {
            map.current.removeLayer('layer3D_land');
        }
    }

    // TOPO 3D : Show
    const topo3D_Show = () => {
        if (capacity?.landBase?.topo && capacity.landBase.topo !== "fetching") {
            // Remove if data already loaded
            topo3D_Hide();
            var alti = capacity.landBase.topo;
            // Set point 0
            var point0 = capacity.landBase.union.bbox.origin;
            // Georeferencing
            var modelOrigin = point0;
            var modelAltitude = 0;
            var modelRotate = [0, 0, 0];
            var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
                modelOrigin,
                modelAltitude
            );
            // Set position, rotation and scale
            var modelTransform = {
                translateX: modelAsMercatorCoordinate.x,
                translateY: modelAsMercatorCoordinate.y,
                translateZ: modelAsMercatorCoordinate.z,
                rotateX: modelRotate[0],
                rotateY: modelRotate[1],
                rotateZ: modelRotate[2],
                scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
            };
            // Set THREE
            // var THREE = window.THREE;
            // Create the custom layer
            var customLayer = {
                id: 'layer3D_land',
                type: 'custom',
                renderingMode: '3d',
                onAdd: function (map, gl) {
                    this.camera = new THREE.Camera();
                    this.scene = new THREE.Scene();
                    // Create lights
                    var directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
                    directionalLight1.position.set(-10, -10, 20).normalize();
                    this.scene.add(directionalLight1);
                    // var directionalLight2 = new THREE.DirectionalLight(0xffffff);
                    // directionalLight2.position.set(0, 70, 100).normalize();
                    // this.scene.add(directionalLight2);
                    var ambientLight = new THREE.AmbientLight(0xffffff);
                    this.scene.add(ambientLight);

                    // Create buffer geometry
                    var geometry = new THREE.BufferGeometry();
                    // Create vertices
                    var vertices_list = [];

                    // // Create grid of points inside the union at SPACING m each
                    // var spacing = Math.max(0.0025, ((capacity.landBase.union.area / 800) / 1000));
                    // var extent = [capacity.landBase.union.bbox.coordinates[0][0][0], capacity.landBase.union.bbox.coordinates[0][0][1], capacity.landBase.union.bbox.coordinates[0][2][0], capacity.landBase.union.bbox.coordinates[0][2][1]];
                    // // var mask_poly = capacity.landBase.union.geometry;
                    // var mask_poly = turf.buffer(capacity.landBase.union.geometry, 0.002, { units: 'kilometers' });
                    // // var grid = turf.pointGrid(extent, spacing, { units: 'kilometers', mask: mask_poly });
                    // var grid = turf.pointGrid(extent, spacing, { units: 'kilometers' });
                    // // Add bounds points to grid
                    // for (var i = 0; i < capacity.landBase.union.bounds.length; i++) {
                    //     grid.features.push({
                    //         "type": "Feature",
                    //         "properties": {},
                    //         "geometry": {
                    //             "type": "Point",
                    //             "coordinates": capacity.landBase.union.bounds[i].start_coord_global
                    //         }
                    //     })
                    // }

                    // Create turf grid element from matrix data
                    var grid = {
                        type: 'FeatureCollection',
                        features: [],
                    };
                    if (capacity?.landBase?.topo?.matrix && capacity.landBase.topo.matrix.length > 0) {
                        for (var i = 0; i < capacity.landBase.topo.matrix.length; i++) {
                            if (capacity.landBase.topo.matrix[i]?.cg && capacity.landBase.topo.matrix[i].cg.length === 2 && capacity.landBase.topo.matrix[i]?.z) {
                                grid.features.push(turf.point(capacity.landBase.topo.matrix[i].cg, { elevation: capacity.landBase.topo.matrix[i].cg, id: i }));
                            }
                        }
                    }
                    // Set id to each grid element
                    // for (var i = 0; i < grid.features.length; i++) {
                    //     grid.features[i].properties.id = i;
                    // }
                    // Create triangulated network from grid
                    var tin = turf.tin(grid, "id");
                    for (var i = 0; i < tin.features.length; i++) {
                        // Vertice a
                        vertices_list.push(alti.matrix[tin.features[i].properties.a].cl[0]);
                        vertices_list.push(alti.matrix[tin.features[i].properties.a].cl[1]);
                        // vertices_list.push(alti.matrix[tin.features[i].properties.a].elevation - alti.point_min_all[2]);
                        vertices_list.push(Math.max(alti.matrix[tin.features[i].properties.a].z - alti.point_min[2], 0.02));
                        // Vertice b
                        vertices_list.push(alti.matrix[tin.features[i].properties.b].cl[0]);
                        vertices_list.push(alti.matrix[tin.features[i].properties.b].cl[1]);
                        // vertices_list.push(alti.matrix[tin.features[i].properties.b].elevation - alti.point_min_all[2]);
                        vertices_list.push(Math.max(alti.matrix[tin.features[i].properties.b].z - alti.point_min[2], 0.02));
                        // Vertice c
                        vertices_list.push(alti.matrix[tin.features[i].properties.c].cl[0]);
                        vertices_list.push(alti.matrix[tin.features[i].properties.c].cl[1]);
                        // vertices_list.push(alti.matrix[tin.features[i].properties.c].elevation - alti.point_min_all[2]);
                        vertices_list.push(Math.max(alti.matrix[tin.features[i].properties.c].z - alti.point_min[2], 0.02));
                    }
                    var vertices = new Float32Array(vertices_list);
                    // Set vertices to geometry
                    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
                    // Compute vertex normals
                    geometry.computeVertexNormals();
                    // Create material
                    var material = new THREE.MeshPhongMaterial({
                        color: '#7c8d4c',
                        side: THREE.DoubleSide
                        // transparent: true,
                        // opacity: 0.4
                    });
                    // Create mesh
                    var mesh = new THREE.Mesh(geometry, material);
                    // Add to scene
                    // this.scene.add(mesh);
                    // Add edges
                    // var edges = new THREE.EdgesGeometry(geometry);
                    // var line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: '#7c8d4c' }));
                    // this.scene.add(line);

                    // Create 3D extrusion of land
                    // Get local coordinates
                    var land_coords = [];
                    var land = capacity.landBase.union;
                    for (var i = 0; i < land["geometry"]["geometry"]["coordinates"][0].length; i++) {
                        var deltaX = (turf.distance(turf.point(land["geometry"]["geometry"]["coordinates"][0][i]), turf.point([point0[0], land["geometry"]["geometry"]["coordinates"][0][i][1]])) * 1000);
                        if (land["geometry"]["geometry"]["coordinates"][0][i][0] < point0[0]) {
                            deltaX = -deltaX;
                        }
                        var deltaY = (turf.distance(turf.point(land["geometry"]["geometry"]["coordinates"][0][i]), turf.point([land["geometry"]["geometry"]["coordinates"][0][i][0], point0[1]])) * 1000);
                        if (land["geometry"]["geometry"]["coordinates"][0][i][1] < point0[1]) {
                            deltaY = -deltaY;
                        }
                        land_coords.push([deltaX, deltaY]);
                    }
                    // Shape and Geometry
                    var landShape = new THREE.Shape();
                    landShape.moveTo(land_coords[0][0], land_coords[0][1]);
                    for (var i = 0; i < land_coords.length; i++) {
                        landShape.lineTo(land_coords[i][0], land_coords[i][1]);
                    }
                    var landExtrudeSettings = {
                        steps: 1,
                        depth: 100,
                        bevelEnabled: false
                    };
                    var landGeometry = new THREE.ExtrudeGeometry(landShape, landExtrudeSettings);
                    var landMaterial = new THREE.MeshPhongMaterial({
                        color: '#7c8d4c',
                    });
                    var landMesh = new THREE.Mesh(landGeometry, landMaterial);
                    // this.scene.add(landMesh);


                    // CSG PART
                    // Update matrix
                    mesh.updateMatrix();
                    landMesh.updateMatrix();
                    // Perform boolean operation
                    var csgMesh = CSG.subtract(landMesh, mesh);

                    this.scene.add(csgMesh);


                    // Add map
                    this.map = map;
                    // use the Mapbox GL JS map canvas for three.js
                    this.renderer = new THREE.WebGLRenderer({
                        canvas: map.getCanvas(),
                        context: gl,
                        antialias: true
                    });
                    this.renderer.autoClear = false;
                },
                render: function (gl, matrix) {
                    var rotationX = new THREE.Matrix4().makeRotationAxis(
                        new THREE.Vector3(1, 0, 0),
                        modelTransform.rotateX
                    );
                    var rotationY = new THREE.Matrix4().makeRotationAxis(
                        new THREE.Vector3(0, 1, 0),
                        modelTransform.rotateY
                    );
                    var rotationZ = new THREE.Matrix4().makeRotationAxis(
                        new THREE.Vector3(0, 0, 1),
                        modelTransform.rotateZ
                    );
                    var m = new THREE.Matrix4().fromArray(matrix);
                    var l = new THREE.Matrix4()
                        .makeTranslation(
                            modelTransform.translateX,
                            modelTransform.translateY,
                            modelTransform.translateZ
                        )
                        .scale(
                            new THREE.Vector3(
                                modelTransform.scale,
                                -modelTransform.scale,
                                modelTransform.scale
                            )
                        )
                        .multiply(rotationX)
                        .multiply(rotationY)
                        .multiply(rotationZ);

                    this.camera.projectionMatrix = m.multiply(l);
                    this.renderer.resetState()
                    this.renderer.render(this.scene, this.camera);
                    // this.map.triggerRepaint();
                }
            };
            map.current.addLayer(customLayer, layer_get_position('layer3D_land'));
        }
    }

    // __________ TOPO POINTS

    // TOPO POINTS : Hide
    const topoPoints_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerTopoPoints_CoutourLines')) {
            map.current.removeLayer('layerTopoPoints_CoutourLines');
        }
        // Remove source
        if (map.current.getSource('sourceTopoPoints_CoutourLines')) {
            map.current.removeSource('sourceTopoPoints_CoutourLines');
        }
        // Remove markers
        for (var i = 0; i < contourLinesMarkers.length; i++) {
            contourLinesMarkers[i].remove();
        }
    }

    const topoPoints_Show = () => {

        if (capacity?.landBase?.topo && capacity.landBase.topo !== "fetching" && capacity?.landBase?.topo?.profile?.elevations) {
            // Remove if data already loaded
            topoPoints_Hide();

            var contourLinesMarkers = [];

            var contourLines_color = "#5E707C";
            var coutourLines_points_data = {
                type: 'FeatureCollection',
                features: [],
            };
            if (capacity?.landBase?.topo?.matrix && capacity.landBase.topo.matrix.length > 0 && capacity?.landBase?.union?.bounds) {
                var matrix_length = capacity.landBase.topo.matrix.length - capacity.landBase.union.bounds.length;
                for (var i = 0; i < matrix_length; i++) {
                    if (capacity.landBase.topo.matrix[i]?.cg && capacity.landBase.topo.matrix[i].cg.length === 2 && capacity.landBase.topo.matrix[i]?.z) {
                        coutourLines_points_data.features.push(turf.point(capacity.landBase.topo.matrix[i].cg, { elevation: capacity.landBase.topo.matrix[i]?.z, position: capacity.landBase.topo.matrix[i]?.pos }));
                        // coutourLines_points_data.features.push(turf.point(capacity.landBase.topo.matrix[i].cg, { elevation: Math.random() * 28 }));
                    }
                }
            }
            var breaks_main = [0];
            var breaks_sub = [0];
            var breaks_sub2 = [0];
            if (capacity?.landBase?.topo?.point_min && capacity.landBase.topo.point_min.length === 3 && capacity?.landBase?.topo?.point_max && capacity.landBase.topo.point_max.length === 3) {
                var break_min = Math.floor(capacity.landBase.topo.point_min[2]);
                var break_max = Math.ceil(capacity.landBase.topo.point_max[2]);
                var offset = 0.003;
                for (var i = break_min; i < break_max; i++) {
                    breaks_main.push(i - offset);
                    for (var j = 1; j < 10; j++) {
                        if (j === 5) {
                            breaks_sub2.push(i + (j / 10) - offset);
                        }
                        else {
                            breaks_sub.push(i + (j / 10) - offset);
                        }
                    }
                }
                breaks_main.push(break_max - offset);
            }
            var coutourLinesMain_data = turf.isolines(coutourLines_points_data, breaks_main, { commonProperties: { type: "main" } });
            console.log("ISOLINES main", coutourLinesMain_data);
            var coutourLinesSub_data = turf.isolines(coutourLines_points_data, breaks_sub, { commonProperties: { type: "sub" } });
            console.log("ISOLINES sub", coutourLinesSub_data);
            var coutourLinesSub2_data = turf.isolines(coutourLines_points_data, breaks_sub2, { commonProperties: { type: "sub2" } });
            console.log("ISOLINES sub2", coutourLinesSub2_data);

            var all_points = [];
            var all_markers_data = [];
            coutourLines_points_data.features.forEach(point => {
                // if (turf.booleanPointInPolygon(point, capacity?.landBase?.union?.geometry)) {
                if (point?.properties?.position === "in") {
                    all_points.push(point);
                    all_markers_data.push({
                        nature: "contourpoint",
                        coords: point.geometry.coordinates,
                        value: point?.properties?.elevation
                    })
                }
            })
            for (var i = (capacity.landBase.topo.matrix.length - capacity.landBase.union.bounds.length); i < capacity.landBase.topo.matrix.length; i++) {
                if (capacity.landBase.topo.matrix[i]?.cg && capacity.landBase.topo.matrix[i].cg.length === 2 && capacity.landBase.topo.matrix[i]?.z) {
                    all_markers_data.push({
                        nature: "contourborne",
                        coords: capacity.landBase.topo.matrix[i].cg,
                        value: capacity.landBase.topo.matrix[i].z
                    })
                }
            }

            var all_buffers = [];
            var all_list = [].concat(coutourLinesSub2_data.features.concat(coutourLinesSub_data.features.concat(coutourLinesMain_data.features)));
            all_list.forEach(line => {
                console.log("LINE", line);
                // Extract line coords
                var lines_coords_list = [];
                if (line?.geometry?.type === "MultiLineString" && line?.geometry?.coordinates && line.geometry.coordinates.length > 0) {
                    line.geometry.coordinates.forEach(line_coords => {
                        if (line_coords.length > 1) {
                            lines_coords_list.push(line_coords);
                        }
                    })
                }
                else if (line?.geometry?.type === "LineString" && line?.geometry?.coordinates && line.geometry.coordinates.length > 0) {
                    lines_coords_list.push(line.geometry.coordinates);
                }

                // Create single lines with cleaned coords
                var all_lines = [];
                lines_coords_list.forEach(coords => {
                    all_lines.push(turf.cleanCoords(turf.lineString(coords)));
                })

                // Create small buffer of lines
                all_lines.forEach(cleaned_line => {
                    var width = 0.00002;
                    if (line?.properties?.type === "main") { width = 0.00008 }
                    else if (line?.properties?.type === "sub2") { width = 0.00004 }
                    var buffer = turf.buffer(cleaned_line, width, { steps: 2 });
                    var intersection = turf.intersect(buffer, capacity?.landBase?.union?.geometry);
                    if (intersection !== null) {
                        all_buffers.push(intersection);
                        // Set marker
                        var longest_part = {
                            length: 0,
                            start: null,
                            end: null,
                        }
                        for (var i = 1; i < intersection.geometry.coordinates[0].length; i++) {
                            var distance = turf.distance(turf.point(intersection.geometry.coordinates[0][i - 1]), turf.point(intersection.geometry.coordinates[0][i]))
                            if (distance > longest_part.length) {
                                longest_part.length = distance;
                                longest_part.start = i - 1;
                                longest_part.end = i;
                            }
                        }
                        if (longest_part.start !== null && longest_part.end !== 0) {
                            var bearing = Math.round(turf.bearing(turf.point(intersection.geometry.coordinates[0][longest_part.start]), turf.point(intersection.geometry.coordinates[0][longest_part.end])));
                            if (bearing >= 0) { bearing = bearing - 90 }
                            else { bearing = bearing + 90 }
                            all_markers_data.push({
                                nature: "contourline",
                                type: line?.properties?.type || "sub",
                                coords: turf.midpoint(turf.point(intersection.geometry.coordinates[0][longest_part.start]), turf.point(intersection.geometry.coordinates[0][longest_part.end])).geometry.coordinates,
                                bearing: bearing,
                                value: line?.properties?.elevation
                            })
                        }
                    }
                })
            })
            console.log("BUFFERS", all_buffers);
            console.log("MARKERS", all_markers_data);


            var coutourLines_data = {
                type: 'FeatureCollection',
                // features: [].concat(coutourLinesMain_data.features.concat(coutourLinesSub_data.features.concat(coutourLinesSub2_data.features))),
                features: all_buffers,
            };
            console.log("ISOLINES all", coutourLines_data);

            // Add source
            map.current.addSource('sourceTopoPoints_CoutourLines', {
                type: 'geojson',
                data: coutourLines_data
            });
            // Add layer polys
            map.current.addLayer({
                id: 'layerTopoPoints_CoutourLines',
                type: 'fill',
                source: 'sourceTopoPoints_CoutourLines',
                paint: {
                    'fill-color': contourLines_color,
                    'fill-opacity': 1,
                },
                filter: ['in', '$type', 'Polygon']
            }, layer_get_position('layerTopoPoints_Line'));
            // Add markers
            all_markers_data.forEach((marker_data, marker_index) => {
                var el = document.createElement('div');
                el.className = 'marker';
                if (marker_data.nature === "contourline") {
                    if (marker_data.type === "main") {
                        el.innerHTML = '<div style="padding:4px 7px;font-family:Manrope;line-height:13px;font-size:12px;font-weight:600;text-align:center;color:#FFF;background-color:' + contourLines_color + ';border-radius:6px;transform:rotateZ(' + marker_data.bearing + 'deg);">' + marker_data.value.toFixed(2) + '</div>';
                    }
                    else {
                        el.innerHTML = '<div style="padding:3px 6px;font-family:Manrope;line-height:12px;font-size:11px;text-align:center;color:#FFF;background-color:' + contourLines_color + ';border-radius:6px;transform:rotateZ(' + marker_data.bearing + 'deg);">' + marker_data.value.toFixed(2) + '</div>';
                    }
                    contourLinesMarkers.push(new mapboxgl.Marker(el)
                        .setLngLat(marker_data.coords)
                        .addTo(map.current))
                }
                else if (marker_data.nature === "contourpoint") {
                    el.innerHTML = '<div style="cursor:pointer;background-color:' + contourLines_color + ';border-radius:6px;width:5px;height:5px"></div>';
                    contourLinesMarkers.push(new mapboxgl.Marker(el)
                        .setLngLat(marker_data.coords)
                        .setPopup(new mapboxgl.Popup({ offset: 5 })
                            .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Point topo</strong></span><br /><span style="font-size:12px;font-weight: 600;color: grey;">Latitude :</span><br><span style="color:#062134;font-weight:500;">' + marker_data.coords[1].toFixed(6) + '</span><br /><span style="font-size:12px;font-weight: 600;color: grey;">Longitude :</span><br><span style="color:#062134;font-weight:500;">' + marker_data.coords[0].toFixed(6) + '</span><br /><span style="font-size:12px;font-weight: 600;color: grey;">Altitude :</span><br><span style="color:#062134;font-weight:500;">' + marker_data.value + ' m NGF</span></p>'))
                        .addTo(map.current))
                }
                else if (marker_data.nature === "contourborne") {
                    el.innerHTML = '<div style="cursor:pointer;background-color:#062134;border-radius:8px;width:8px;height:8px"></div>';
                    contourLinesMarkers.push(new mapboxgl.Marker(el)
                        .setLngLat(marker_data.coords)
                        .setPopup(new mapboxgl.Popup({ offset: 7 })
                            .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Borne topo</strong></span><br /><span style="font-size:12px;font-weight: 600;color: grey;">Latitude :</span><br><span style="color:#062134;font-weight:500;">' + marker_data.coords[1].toFixed(6) + '</span><br /><span style="font-size:12px;font-weight: 600;color: grey;">Longitude :</span><br><span style="color:#062134;font-weight:500;">' + marker_data.coords[0].toFixed(6) + '</span><br /><span style="font-size:12px;font-weight: 600;color: grey;">Altitude :</span><br><span style="color:#062134;font-weight:500;">' + marker_data.value + ' m NGF</span></p>'))
                        .addTo(map.current))
                }
            })
            setContourLinesMarkers(contourLinesMarkers);

        }
    }

    // __________ TOPO PROFILE

    // TOPO PROFILE : Hide
    const topoProfile_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layer3D_landProfile')) {
            map.current.removeLayer('layer3D_landProfile');
        }
        if (map.current.getLayer('layerTopoProfile_Line')) {
            map.current.removeLayer('layerTopoProfile_Line');
        }
        if (map.current.getLayer('layerTopoProfile_Points')) {
            map.current.removeLayer('layerTopoProfile_Points');
        }
        // Remove source
        if (map.current.getSource('sourceTopoProfile')) {
            map.current.removeSource('sourceTopoProfile');
        }
    }

    // TOPO PROFILE : Show
    const topoProfile_Show = () => {
        // Remove if data already loaded
        topoProfile_Hide();
        if (capacity?.landBase?.topo && capacity.landBase.topo !== "fetching" && capacity?.landBase?.topo?.profile?.elevations) {

            // _____ 2D VERSION
            // Create source with one line and all points
            var profile_data = {
                type: 'FeatureCollection',
                features: [],
            };
            profile_data.features.push({
                "type": "Feature",
                "properties": {},
                "geometry": {
                    "type": "LineString",
                    "coordinates": [[capacity.landBase.topo.profile.elevations[0].lon, capacity.landBase.topo.profile.elevations[0].lat], [capacity.landBase.topo.profile.elevations[capacity.landBase.topo.profile.elevations.length - 1].lon, capacity.landBase.topo.profile.elevations[capacity.landBase.topo.profile.elevations.length - 1].lat]]
                }
            });
            for (var i = 0; i < capacity.landBase.topo.profile.elevations.length; i++) {
                var point_type = "";
                if (capacity.landBase.topo.profile.elevations[i].lon === capacity.landBase.topo.point_max[0] && capacity.landBase.topo.profile.elevations[i].z === capacity.landBase.topo.point_max[2]) {
                    point_type = "max";
                }
                else if (capacity.landBase.topo.profile.elevations[i].lon === capacity.landBase.topo.point_min[0] && capacity.landBase.topo.profile.elevations[i].z === capacity.landBase.topo.point_min[2]) {
                    point_type = "min";
                }
                profile_data.features.push({
                    "type": "Feature",
                    "properties": { type: point_type },
                    "geometry": {
                        "type": "Point",
                        "coordinates": [capacity.landBase.topo.profile.elevations[i].lon, capacity.landBase.topo.profile.elevations[i].lat],
                    }
                });
            }
            // Add source
            map.current.addSource('sourceTopoProfile', {
                type: 'geojson',
                data: profile_data
            });
            // Add layer lines
            map.current.addLayer({
                id: 'layerTopoProfile_Line',
                type: 'line',
                source: 'sourceTopoProfile',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#062134',
                    'line-width': 2.3,
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerTopoProfile_Line'));
            // Add layer points
            map.current.addLayer({
                'id': 'layerTopoProfile_Points',
                'type': 'circle',
                'source': 'sourceTopoProfile',
                'paint': {
                    'circle-radius': 4,
                    'circle-color': [
                        'match',
                        ['get', 'type'],
                        'min',
                        '#0000FF',
                        'max',
                        '#FF0000',
                    /* default */ '#062134'
                    ]
                },
                filter: ['in', '$type', 'Point']
            }, layer_get_position('layerTopoProfile_Points'));

            // _____ 3D VERSION
            // // Set point 0
            // var point0 = capacity.landBase.union.bbox.origin;
            // // Georeferencing
            // var modelOrigin = point0;
            // var modelAltitude = 0;
            // var modelRotate = [0, 0, 0];
            // var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
            //     modelOrigin,
            //     modelAltitude
            // );
            // // Set position, rotation and scale
            // var modelTransform = {
            //     translateX: modelAsMercatorCoordinate.x,
            //     translateY: modelAsMercatorCoordinate.y,
            //     translateZ: modelAsMercatorCoordinate.z,
            //     rotateX: modelRotate[0],
            //     rotateY: modelRotate[1],
            //     rotateZ: modelRotate[2],
            //     scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
            // };
            // // Set THREE
            // // var THREE = window.THREE;
            // // Create the custom layer
            // var customLayer = {
            //     id: 'layer3D_landProfile',
            //     type: 'custom',
            //     renderingMode: '3d',
            //     onAdd: function (map, gl) {
            //         this.camera = new THREE.Camera();
            //         this.scene = new THREE.Scene();
            //         // Create lights
            //         var directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
            //         directionalLight1.position.set(-10, -10, 20).normalize();
            //         this.scene.add(directionalLight1);
            //         // var directionalLight2 = new THREE.DirectionalLight(0xffffff);
            //         // directionalLight2.position.set(0, 70, 100).normalize();
            //         // this.scene.add(directionalLight2);
            //         var ambientLight = new THREE.AmbientLight(0xffffff);
            //         this.scene.add(ambientLight);

            //         // Loop th all profile points
            //         for (var i = 0; i < capacity.landBase.topo.profile.elevations.length; i++) {
            //             // POINT PART
            //             var color = "#062134";
            //             if (i === 0) { color = "#FF0000" }
            //             else if (i === capacity.landBase.topo.profile.elevations.length - 1) { color = "#0000FF" }
            //             // Create buffer geometry
            //             var geometry = new THREE.SphereGeometry(0.2, 32, 16);
            //             // Create material
            //             var material = new THREE.MeshPhongMaterial({
            //                 color: color,
            //                 side: THREE.DoubleSide
            //                 // transparent: true,
            //                 // opacity: 0.4
            //             });
            //             // Create mesh
            //             var mesh = new THREE.Mesh(geometry, material);
            //             // Move mech to exact position
            //             // var local_coords = map_helpers.mercator_to_local([capacity.landBase.topo.profile.elevations[i].lon, capacity.landBase.topo.profile.elevations[i].lat], capacity.landBase.union.bbox.origin);
            //             var local_coords = capacity.landBase.topo.profile.elevations[i].coords_local;
            //             mesh.position.set(local_coords[0], local_coords[1], (capacity.landBase.topo.profile.elevations[i].z - capacity.landBase.topo.point_min[2]));
            //             // Add to scene
            //             this.scene.add(mesh);

            //             // LINE PART
            //             const points_line = [new THREE.Vector3(local_coords[0], local_coords[1], 0), new THREE.Vector3(local_coords[0], local_coords[1], (capacity.landBase.topo.profile.elevations[i].z - capacity.landBase.topo.point_min[2]))];
            //             const geometry_line = new THREE.BufferGeometry().setFromPoints(points_line);
            //             const material_line = new THREE.LineBasicMaterial({ color: "#062134" });
            //             const line = new THREE.Line(geometry_line, material_line);
            //             this.scene.add(line);

            //         }


            //         // Add map
            //         this.map = map;
            //         // use the Mapbox GL JS map canvas for three.js
            //         this.renderer = new THREE.WebGLRenderer({
            //             canvas: map.getCanvas(),
            //             context: gl,
            //             antialias: true
            //         });
            //         this.renderer.autoClear = false;
            //     },
            //     render: function (gl, matrix) {
            //         var rotationX = new THREE.Matrix4().makeRotationAxis(
            //             new THREE.Vector3(1, 0, 0),
            //             modelTransform.rotateX
            //         );
            //         var rotationY = new THREE.Matrix4().makeRotationAxis(
            //             new THREE.Vector3(0, 1, 0),
            //             modelTransform.rotateY
            //         );
            //         var rotationZ = new THREE.Matrix4().makeRotationAxis(
            //             new THREE.Vector3(0, 0, 1),
            //             modelTransform.rotateZ
            //         );
            //         var m = new THREE.Matrix4().fromArray(matrix);
            //         var l = new THREE.Matrix4()
            //             .makeTranslation(
            //                 modelTransform.translateX,
            //                 modelTransform.translateY,
            //                 modelTransform.translateZ
            //             )
            //             .scale(
            //                 new THREE.Vector3(
            //                     modelTransform.scale,
            //                     -modelTransform.scale,
            //                     modelTransform.scale
            //                 )
            //             )
            //             .multiply(rotationX)
            //             .multiply(rotationY)
            //             .multiply(rotationZ);

            //         this.camera.projectionMatrix = m.multiply(l);
            //         this.renderer.resetState()
            //         this.renderer.render(this.scene, this.camera);
            //         // this.map.triggerRepaint();
            //     }
            // };
            // map.current.addLayer(customLayer);
        }
    }


    // ___________ TREES

    // TREES : Hide
    const trees_Hide = () => {
        var source_layer_id = map_helpers_trees.get_ids();
        map_remove_sources_and_layers(source_layer_id);
    }

    // TREES : Show
    const trees_Show = () => {
        // Check if trees data to show
        if (capacity?.landBase?.trees?.items || capacity.landBase.trees.items.length > 0) {
            var source_layer_data = map_helpers_trees.get_data(capacity);
            map_add_sources_and_layers(source_layer_data);
        }
    }


    // __________ GLOBAL SOURCES AND LAYERS MANAGEMENT
    const map_add_sources_and_layers = (sources_layers) => {
        map_remove_sources_and_layers(sources_layers);
        if (sources_layers?.sources && sources_layers.sources.length > 0) {
            map_add_sources(sources_layers.sources);
            if (sources_layers?.layers && sources_layers.layers.length > 0) {
                map_add_layers(sources_layers.layers);
            }
        }
        if (sources_layers?.markers && sources_layers.markers.length > 0) {
            map_add_markers(sources_layers.markers);
        }
        if (sources_layers?.bbox && sources_layers.bbox.length >= 1 && sources_layers.bbox[0].length === 5) {
            flyTo_FitBbox(sources_layers.bbox, 0.0003);
        }
    }
    const map_remove_sources_and_layers = (sources_layers) => {
        if (sources_layers?.layers && sources_layers.layers.length > 0) {
            map_remove_layers(sources_layers.layers);
        }
        if (sources_layers?.sources && sources_layers.sources.length > 0) {
            map_remove_sources(sources_layers.sources);
        }
        if (sources_layers?.markers && sources_layers.markers.length > 0) {
            map_remove_markers(sources_layers.markers);
        }
    }
    const map_add_sources = (source_list) => {
        source_list.forEach(source_i => {
            if (source_i?.id && source_i?.value) {
                map.current.addSource(source_i.id, source_i.value);
            }
        })
    }
    const map_add_layers = (layer_list) => {
        layer_list.forEach(layer_i => {
            if (layer_i?.id && layer_i?.source) {
                map.current.addLayer(layer_i, layer_get_position(layer_i.id));
            }
        })
    }
    const map_add_markers = (marker_list) => {
        marker_list.forEach(marker_i => {
            marker_i.addTo(map.current);
        })
    }
    const map_remove_sources = (source_list) => {
        source_list.forEach(source_i => {
            if (source_i?.id && map.current.getSource(source_i.id)) {
                map.current.removeSource(source_i.id);
            }
        })
    }
    const map_remove_layers = (layer_list) => {
        layer_list.forEach(layer_i => {
            if (layer_i?.id && map.current.getLayer(layer_i.id)) {
                map.current.removeLayer(layer_i.id);
            }
        })
    }
    const map_remove_markers = (marker_list) => {
        marker_list.forEach(marker_i => {
            marker_i.remove();
        })
    }

    // __________ RISKS EARTHQUAKE

    // RISKS EARTHQUAKE : Hide
    const risksEarthquake_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerRisksEarthquake')) {
            map.current.removeLayer('layerRisksEarthquake');
        }
        // Remove source
        if (map.current.getSource('sourceRisksEarthquake')) {
            map.current.removeSource('sourceRisksEarthquake');
        }
    }
    // RISKS EARTHQUAKE : Show
    const risksEarthquake_Show = () => {
        // Remove if data already loaded
        risksEarthquake_Hide();
        // Add source
        map.current.addSource('sourceRisksEarthquake', {
            'type': 'raster',
            // use the tiles option to specify a WMS tile source URL
            // https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/
            'tiles': [
                'https://ws.carmencarto.fr/WMS/168/zonage_sismique?LAYERS=Zonage_sismique_Communes&TRANSPARENT=TRUE&SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&STYLES=&FORMAT=image%2Fpng&SRS=EPSG%3A3857&bbox={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'
            ],
            'tileSize': 256
        });
        // Add layer
        map.current.addLayer(
            {
                'id': 'layerRisksEarthquake',
                'type': 'raster',
                'source': 'sourceRisksEarthquake',
                'paint': {}
            },
            layer_get_position('layerRisksEarthquake')
        );
        // Set zoom
        map.current.flyTo({
            zoom: 12.3,
            essential: true
        });
    }


    // __________ RISKS CLAY

    // RISKS CLAY : Hide
    const risksClay_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerRisksClay')) {
            map.current.removeLayer('layerRisksClay');
        }
        // Remove source
        if (map.current.getSource('sourceRisksClay')) {
            map.current.removeSource('sourceRisksClay');
        }
    }
    // RISKS CLAY : Show
    const risksClay_Show = () => {
        // Remove if data already loaded
        risksClay_Hide();
        // Add source
        map.current.addSource('sourceRisksClay', {
            'type': 'raster',
            // use the tiles option to specify a WMS tile source URL
            // https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/
            'tiles': [
                'https://mapsref.brgm.fr/wxs/geoservices/risques?LAYERS=ALEARG_REALISE&TRANSPARENT=TRUE&SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&STYLES=&FORMAT=image%2Fpng&SRS=EPSG%3A3857&bbox={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'
            ],
            'tileSize': 256
        });
        // Add layer
        map.current.addLayer(
            {
                'id': 'layerRisksClay',
                'type': 'raster',
                'source': 'sourceRisksClay',
                'paint': {}
            },
            layer_get_position('layerRisksClay')
        );
        // Set zoom
        map.current.flyTo({
            zoom: 12.3,
            essential: true
        });
    }


    // __________ RISKS PIPES

    // RISKS PIPES : Hide
    const risksPipes_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerRisksPipes_Lines')) {
            map.current.removeLayer('layerRisksPipes_Lines');
        }
        if (map.current.getLayer('layerRisksPipes_Polys')) {
            map.current.removeLayer('layerRisksPipes_Polys');
        }
        if (map.current.getLayer('layerRisksPipes_Buffer')) {
            map.current.removeLayer('layerRisksPipes_Buffer');
        }
        // Remove source
        if (map.current.getSource('sourceRisksPipes')) {
            map.current.removeSource('sourceRisksPipes');
        }
        // Remove source
        if (map.current.getSource('sourceRisksPipes_Buffer')) {
            map.current.removeSource('sourceRisksPipes_Buffer');
        }
        // Remove markers
        for (var i = 0; i < pipesMarkers.length; i++) {
            pipesMarkers[i].remove();
        }

        // Fit view to union
        if (capacity?.landBase?.union) {
            if (!map.current.getLayer('layerRisksBasias_Buffer')) {
                flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.00006);
            }
        }
    }

    // RISKS PIPES : Show
    const risksPipes_Show = () => {
        if (capacity?.risks?.pipe && capacity.risks.pipe.length > 0) {
            // Remove if data already loaded
            risksPipes_Hide();

            var pipes_markers = [];

            // Create buffer circle
            var buffered = turf.buffer(turf.point(capacity.landBase.union.center.geometry.coordinates), 1, { steps: 100 });
            var buffered_line = turf.polygonToLine(buffered);
            // Add source
            map.current.addSource('sourceRisksPipes_Buffer', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [buffered_line],
                }
            });
            // Add layer lines
            map.current.addLayer({
                id: 'layerRisksPipes_Buffer',
                type: 'line',
                source: 'sourceRisksPipes_Buffer',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#062134',
                    'line-width': 2.2,
                    'line-dasharray': [4, 3]
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerRisksPipes_Buffer'));
            // Add marker
            var destination = turf.destination(turf.point(capacity.landBase.union.center.geometry.coordinates), 1, 180);
            var el = document.createElement('div');
            el.className = 'marker';
            el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;line-height:14px;text-align:center;color:#FFF;background-color:#062134;border-radius:6px">Rayon de 1000m</div>';
            pipes_markers.push(new mapboxgl.Marker(el)
                .setLngLat(destination.geometry.coordinates)
                .addTo(map.current))
            // Fit view
            var bbox = turf.bboxPolygon(turf.bbox(buffered));
            flyTo_FitBbox(bbox.geometry.coordinates, 0.0003);

            // Create source with all lines
            var profile_data = {
                type: 'FeatureCollection',
                features: [],
            };
            for (var i = 0; i < capacity.risks.pipe.length; i++) {
                // Add lines
                var line = turf.lineString(capacity.risks.pipe[i].coordinates);
                profile_data.features.push(line);
                // Add polygon
                var polygon = turf.lineToPolygon(line);
                profile_data.features.push(polygon);
                // Add marker
                var intersection = turf.intersect(polygon, buffered);
                var center = turf.centerOfMass(polygon);
                if (intersection !== null) {
                    center = turf.centerOfMass(intersection);
                }
                var el = document.createElement('div');
                el.className = 'marker';
                el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;line-height:14px;text-align:center;color:#FFF;background-color:#FF0000;border-radius:6px">Canalisation de<br><strong>' + capacity.risks.pipe[i].type + '</strong></div>';
                pipes_markers.push(new mapboxgl.Marker(el)
                    .setLngLat(center.geometry.coordinates)
                    .addTo(map.current))
            }
            setPipesMarkers(pipes_markers);
            // Add source
            map.current.addSource('sourceRisksPipes', {
                type: 'geojson',
                data: profile_data
            });
            // Add layer polys
            map.current.addLayer({
                id: 'layerRisksPipes_Polys',
                type: 'fill',
                source: 'sourceRisksPipes',
                paint: {
                    'fill-color': '#FF0000',
                    'fill-opacity': 0.2,
                },
                filter: ['in', '$type', 'Polygon']
            }, layer_get_position('layerRisksPipes_Polys'));
            // Add layer lines
            map.current.addLayer({
                id: 'layerRisksPipes_Lines',
                type: 'line',
                source: 'sourceRisksPipes',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#FF0000',
                    'line-width': 3,
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerRisksPipes_Lines'));
        }
    }

    // __________ RISKS BASIAS

    // RISKS BASIAS : Hide
    const risksBasias_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerRisksBasias_Lines')) {
            map.current.removeLayer('layerRisksBasias_Lines');
        }
        if (map.current.getLayer('layerRisksBasias_Polys')) {
            map.current.removeLayer('layerRisksBasias_Polys');
        }
        if (map.current.getLayer('layerRisksBasias_Buffer')) {
            map.current.removeLayer('layerRisksBasias_Buffer');
        }
        // Remove source
        if (map.current.getSource('sourceRisksBasias')) {
            map.current.removeSource('sourceRisksBasias');
        }
        // Remove source
        if (map.current.getSource('sourceRisksBasias_Buffer')) {
            map.current.removeSource('sourceRisksBasias_Buffer');
        }
        // Remove markers
        for (var i = 0; i < basiasMarkers.length; i++) {
            basiasMarkers[i].remove();
        }

        // Fit view to union
        if (capacity?.landBase?.union) {
            if (!map.current.getLayer('layerRisksPipes_Buffer')) {
                flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.00006);
            }
        }
    }

    // RISKS BASIAS : Show
    const risksBasias_Show = () => {
        if (capacity?.risks?.basias && capacity.risks.basias.length > 0) {
            // Remove if data already loaded
            risksBasias_Hide();

            var basias_markers = [];

            // Create buffer circle
            var buffered = turf.buffer(turf.point(capacity.landBase.union.center.geometry.coordinates), 1, { steps: 100 });
            var buffered_line = turf.polygonToLine(buffered);
            // Add source
            map.current.addSource('sourceRisksBasias_Buffer', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [buffered_line],
                }
            });
            // Add layer lines
            map.current.addLayer({
                id: 'layerRisksBasias_Buffer',
                type: 'line',
                source: 'sourceRisksBasias_Buffer',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#062134',
                    'line-width': 2.2,
                    'line-dasharray': [4, 3]
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerRisksBasias_Buffer'));
            // Add marker
            var destination = turf.destination(turf.point(capacity.landBase.union.center.geometry.coordinates), 1, 180);
            var el = document.createElement('div');
            el.className = 'marker';
            el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;line-height:14px;text-align:center;color:#FFF;background-color:#062134;border-radius:6px">Rayon de 1000m</div>';
            basias_markers.push(new mapboxgl.Marker(el)
                .setLngLat(destination.geometry.coordinates)
                .addTo(map.current))
            // Fit view
            var bbox = turf.bboxPolygon(turf.bbox(buffered));
            flyTo_FitBbox(bbox.geometry.coordinates, 0.0003);

            // Create source with all lines
            var profile_data = {
                type: 'FeatureCollection',
                features: [],
            };
            for (var i = 0; i < capacity.risks.basias.length; i++) {
                // Add polygon
                // console.log("CREATING POLYGON", capacity.risks.basias[i]);
                if (capacity.risks.basias[i].geom.type === "MultiPolygon") {
                    for (var j = 0; j < capacity.risks.basias[i].geom.coordinates.length; j++) {
                        var polygon = turf.polygon(capacity.risks.basias[i].geom.coordinates[j]);
                        profile_data.features.push(polygon);
                        var line = turf.polygonToLine(polygon);
                        profile_data.features.push(line);
                    }
                }
                else {
                    var polygon = turf.polygon(capacity.risks.basias[i].geom.coordinates);
                    profile_data.features.push(polygon);
                    var line = turf.polygonToLine(polygon);
                    profile_data.features.push(line);
                }
                // Add marker
                var center = turf.centerOfMass(polygon);
                var el = document.createElement('div');
                var label = "Site pollué";
                if (capacity.risks.basias[i].nom !== null) {
                    label = label + "<br><strong>" + capacity.risks.basias[i].nom + "</strong>"
                }
                el.className = 'marker';
                el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;line-height:14px;text-align:center;color:#FFF;background-color:#FF0000;border-radius:6px">' + label + '</div>';
                basias_markers.push(new mapboxgl.Marker(el)
                    .setLngLat(center.geometry.coordinates)
                    .addTo(map.current))
            }
            setBasiasMarkers(basias_markers);
            // Add source
            map.current.addSource('sourceRisksBasias', {
                type: 'geojson',
                data: profile_data
            });
            // Add layer polys
            map.current.addLayer({
                id: 'layerRisksBasias_Polys',
                type: 'fill',
                source: 'sourceRisksBasias',
                paint: {
                    'fill-color': '#FF0000',
                    'fill-opacity': 0.2,
                },
                filter: ['in', '$type', 'Polygon']
            }, layer_get_position('layerRisksBasias_Polys'));
            // Add layer lines
            map.current.addLayer({
                id: 'layerRisksBasias_Lines',
                type: 'line',
                source: 'sourceRisksBasias',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#FF0000',
                    'line-width': 3,
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerRisksBasias_Lines'));
        }
    }

    // __________ ZONE URBA
    // ZONE URBA : Hide
    const zoneUrba_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerZoneUrba_Polys')) {
            map.current.removeLayer('layerZoneUrba_Polys');
        }
        // Remove source
        if (map.current.getSource('sourceZoneUrba')) {
            map.current.removeSource('sourceZoneUrba');
        }
        // Remove markers
        for (var i = 0; i < zoneUrbaMarkers.length; i++) {
            zoneUrbaMarkers[i].remove();
        }

        // Fit view to union
        // if (capacity?.landBase?.union) {
        //     if (!map.current.getLayer('layerRisksPipes_Buffer')) {
        //         flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.00006);
        //     }
        // }
    }
    // ZONE URBA : Show
    const zoneUrba_Show = async (zoneUrba_list) => {
        if (capacity?.rules?.gpu_data?.zoneUrba_all && capacity.rules.gpu_data.zoneUrba_all.length > 0) {
            // Remove if data already loaded
            zoneUrba_Hide();

            var zoneUrba_markers = [];

            // Fit view
            // var bbox = turf.bboxPolygon(turf.bbox(buffered));
            // flyTo_FitBbox(bbox.geometry.coordinates, 0.0003);

            // Create full filtered list
            var zoneUrba_all_full = [];
            // Get ZoneUrba
            for (var i = 0; i < capacity.rules.gpu_data.zoneUrba_all.length; i++) {
                if (zoneUrba_list.includes(capacity.rules.gpu_data.zoneUrba_all[i]?.properties?.id)) {
                    zoneUrba_all_full.push(capacity.rules.gpu_data.zoneUrba_all[i]);
                }
            }
            // Get ZoneUrba2
            if (capacity?.rules?.gpu_data?.zoneUrba2_all && capacity.rules.gpu_data.zoneUrba2_all.length > 0) {
                for (var i = 0; i < capacity.rules.gpu_data.zoneUrba2_all.length; i++) {
                    if (zoneUrba_list.includes(capacity.rules.gpu_data.zoneUrba2_all[i]?.properties?.id)) {
                        zoneUrba_all_full.push(capacity.rules.gpu_data.zoneUrba2_all[i]);
                    }
                }
            }
            // Get ZoneCity
            if (capacity?.rules?.gpu_data?.zoneCity_all && capacity.rules.gpu_data.zoneCity_all.length > 0) {
                for (var i = 0; i < capacity.rules.gpu_data.zoneCity_all.length; i++) {
                    if (zoneUrba_list.includes(capacity.rules.gpu_data.zoneCity_all[i]?.properties?.id) && capacity.rules.gpu_data.zoneCity_all[i]?.properties?.inseecode.length > 0) {
                        console.log("FETCHING CITY", capacity.rules.gpu_data.zoneCity_all[i]);
                        for (var j = 0; j < capacity.rules.gpu_data.zoneCity_all[i]?.properties?.inseecode.length; j++) {
                            var data = await zoneUrba_getCity(capacity.rules.gpu_data.zoneCity_all[i].properties.inseecode[j]);
                            if (data?.features) {
                                data.features.forEach(feature => {
                                    var feature_light = {
                                        geometry: feature?.geometry || null,
                                        properties: {
                                            libelle: capacity.rules.gpu_data.zoneCity_all[i]?.properties?.libelle
                                        }
                                    }
                                    zoneUrba_all_full.push(feature_light);
                                })
                            }
                        }
                    }
                }
            }

            console.log("zoneUrba_all_full", zoneUrba_all_full);

            // Create source
            var profile_data = {
                type: 'FeatureCollection',
                features: [],
            };
            for (var i = 0; i < zoneUrba_all_full.length; i++) {
                var polygon_color = randomHexColorFromString(zoneUrba_all_full[i]?.properties?.libelle || "test");
                // Treat multiPoly
                if (zoneUrba_all_full[i]?.geometry?.type === "MultiPolygon") {
                    // Split multiPoly as poly
                    for (var j = 0; j < zoneUrba_all_full[i].geometry.coordinates[0].length; j++) {
                        // Add polygon
                        var polygon = turf.polygon([zoneUrba_all_full[i].geometry.coordinates[0][j]], { zone_color: polygon_color });
                        profile_data.features.push(polygon);
                        // Add marker
                        var center = turf.centerOfMass(polygon);
                        var el = document.createElement('div');
                        var label = "<strong>" + zoneUrba_all_full[i]?.properties?.libelle || "test" + "</strong>";
                        el.className = 'marker';
                        el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;line-height:14px;text-align:center;color:#FFF;background-color:' + polygon_color + ';border-radius:6px">' + label + '</div>';
                        zoneUrba_markers.push(new mapboxgl.Marker(el)
                            .setLngLat(center.geometry.coordinates)
                            .addTo(map.current))
                    }
                }
                // Treat FeaturCol
                else if (zoneUrba_all_full[i]?.geometry?.type === "FeatureCollection") {
                    // Loop th all features
                    for (var k = 0; k < zoneUrba_all_full[i]?.geometry?.features.length; k++) {
                        if (zoneUrba_all_full[i]?.geometry?.features[k]?.geometry?.type === "MultiPolygon") {
                            // Add the feature
                            // profile_data.features.push(zoneUrba_all_full[i].geometry.features[k]);
                            // Flatten each
                            turf.flattenEach(zoneUrba_all_full[i].geometry.features[k], (currentFeature) => {
                                if (currentFeature?.geometry?.type === "Polygon") {
                                    // Add polygon
                                    profile_data.features.push({ ...currentFeature, properties: { zone_color: polygon_color } });
                                    // Add marker
                                    var center = turf.centerOfMass(currentFeature);
                                    var el = document.createElement('div');
                                    var label = "<strong>" + zoneUrba_all_full[i]?.properties?.libelle || "test" + "</strong>";
                                    if (currentFeature?.properties?.value) {
                                        label = label + "<strong> : " + currentFeature?.properties?.value + "</strong>";
                                    }
                                    el.className = 'marker';
                                    el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;line-height:14px;text-align:center;color:#FFF;background-color:' + polygon_color + ';border-radius:6px">' + label + '</div>';
                                    zoneUrba_markers.push(new mapboxgl.Marker(el)
                                        .setLngLat(center.geometry.coordinates)
                                        .addTo(map.current))
                                }
                            });
                        }
                    }
                }
            }
            setZoneUrbaMarkers(zoneUrba_markers);
            // Add source
            map.current.addSource('sourceZoneUrba', {
                type: 'geojson',
                data: profile_data
            });
            // Add layer polys
            map.current.addLayer({
                id: 'layerZoneUrba_Polys',
                type: 'fill',
                source: 'sourceZoneUrba',
                paint: {
                    // 'fill-color': '#062134',
                    'fill-color': ['get', 'zone_color'],
                    'fill-opacity': 0.2,
                },
                filter: ['in', '$type', 'Polygon']
            }, layer_get_position('layerZoneUrba_Polys'));
            // Add layer lines
            // map.current.addLayer({
            //     id: 'layerRisksBasias_Lines',
            //     type: 'line',
            //     source: 'sourceRisksBasias',
            //     layout: {
            //         'line-cap': 'round',
            //         'line-join': 'round'
            //     },
            //     paint: {
            //         'line-color': '#FF0000',
            //         'line-width': 3,
            //     },
            //     filter: ['in', '$type', 'LineString']
            // }, layer_get_position('layerRisksBasias_Lines'));
        }
    }
    const randomHexColorFromString = (string) => {
        // Get hash from string
        var hash = 0
        for (var i = 0; i < string.length; i++) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
            hash = hash & hash;
        }
        var color = '#';
        for (var i = 0; i < 3; i++) {
            var value = (hash >> (i * 8)) & 255;
            color += ('00' + value.toString(16)).substr(-2);
        }
        return color;
    }
    const zoneUrba_getCity = async (inseecode) => {
        try {
            var json_url = "https://cadastre.data.gouv.fr/bundler/cadastre-etalab/communes/" + inseecode + "/geojson/communes";
            const res = await fetch(json_url, { "method": "get" });
            const result = await res.json();
            console.log("-FETCHING CITY- : Success", result);
            if (result?.features.length > 0) {
                return result
            }
            return null
        } catch (error) {
            console.log("-FETCHING CITY- : Error", error);
            return null
        }
    }


    // __________ OSM BUILDINGS

    // OSM BUILDINGS : Hide
    const OSMBuildings_Hide = () => {
        if (isMapLoaded) {
            map.current.setLayoutProperty('building-extrusion', 'visibility', 'none');
        }
    }

    // OSM BUILDINGS : Show
    const OSMBuildings_Show = () => {
        if (isMapLoaded) {
            map.current.setLayoutProperty('building-extrusion', 'visibility', 'visible');
        }
    }


    // __________ IGN BUILDINGS

    // IGN BUILDINGS : Set Interactions
    const IGNBuildings_SetInteractions = (type) => {
        map.current.on('mouseenter', 'layerIGNBuildings_Click_' + type, () => { map.current.getCanvas().style.cursor = 'pointer' });
        map.current.on('mouseleave', 'layerIGNBuildings_Click_' + type, () => { map.current.getCanvas().style.cursor = '' });
        map.current.on('click', 'layerIGNBuildings_Click_' + type, (e) => { IGNBuildings_Click(e) });
    }

    // IGN BUILDINGS : Cancel Interactions
    const IGNBuildings_CancelInteractions = (type) => {
        map.current.off('mouseenter', 'layerIGNBuildings_Click_' + type, () => { map.current.getCanvas().style.cursor = 'pointer' });
        map.current.off('mouseleave', 'layerIGNBuildings_Click_' + type, () => { map.current.getCanvas().style.cursor = '' });
        map.current.off('click', 'layerIGNBuildings_Click_' + type, (e) => { IGNBuildings_Click(e) });
    }

    // IGN BUILDINGS : Click
    const IGNBuildings_Click = (e) => {
        // console.log("CLICKED", e.features[0]);
        var title = "";
        if (e.features[0].properties?.label) {
            title = '<span style="color:#062134;font-size:15px;font-weight:700"><strong>' + e.features[0].properties.label + '</strong></span><br>';
        }
        var type = "";
        if (e.features[0].properties?.ign_properties) {
            var ign_properties = JSON.parse(e.features[0].properties.ign_properties);
            if (ign_properties?.usage_1) {
                type = '<span style="font-size:12px;font-weight: 600;color: grey;">Typologie :</span><br><span style="color:#062134;font-weight:500;">' + ign_properties.usage_1 + '</span><br>';
            }
        }
        new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML('<p style="font-family:Manrope">' + title + type + '<span style="font-size:12px;font-weight: 600;color: grey;">Emprise au sol :</span><br><span style="color:#062134;font-weight:500;">' + e.features[0].properties.area.toFixed(2) + ' m²</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Hauteur max :</span><br><span style="color:#062134;font-weight:500;">' + e.features[0].properties.height_max.toFixed(1) + ' m</span></p>')
            .addTo(map.current);
    }

    // IGN BUILDINGS : Hide
    const IGNBuildings_Hide = (type) => {
        // Remove layers
        if (map.current.getLayer('layerIGNBuildings_' + type)) {
            map.current.removeLayer('layerIGNBuildings_' + type);
        }
        if (map.current.getLayer('layerIGNBuildings_Click_' + type)) {
            map.current.removeLayer('layerIGNBuildings_Click_' + type);
        }
        // Remove source
        if (map.current.getSource('sourceIGNBuildings_' + type)) {
            map.current.removeSource('sourceIGNBuildings_' + type);
        }
    }
    const IGNBuildingsNoLand_Hide = () => {
        var source_layer_id = map_helpers_buildings.get_ids("noland");
        map_remove_sources_and_layers(source_layer_id);
    }
    const IGNBuildingsLandKeep_Hide = () => {
        var source_layer_id = map_helpers_buildings.get_ids("land_keep");
        map_remove_sources_and_layers(source_layer_id);
    }
    const IGNBuildingsLandErase_Hide = () => {
        var source_layer_id = map_helpers_buildings.get_ids("land_erase");
        map_remove_sources_and_layers(source_layer_id);
    }



    // IGN BUILDINGS : Show
    const IGNBuildingsNoLand_Show = () => {
        if (capacity?.landBase?.buildings?.buildings && capacity.landBase.buildings.buildings.length > 0) {
            var source_layer_data = map_helpers_buildings.get_data(capacity, "noland");
            map_add_sources_and_layers(source_layer_data);
        }
    }
    const IGNBuildingsLandKeep_Show = () => {
        if (capacity?.landBase?.buildings?.buildings && capacity.landBase.buildings.buildings.length > 0) {
            var source_layer_data = map_helpers_buildings.get_data(capacity, "land_keep", highlightedCombination);
            map_add_sources_and_layers(source_layer_data);
        }
    }
    const IGNBuildingsLandErase_Show = () => {
        if (capacity?.landBase?.buildings?.buildings && capacity.landBase.buildings.buildings.length > 0) {
            var source_layer_data = map_helpers_buildings.get_data(capacity, "land_erase");
            map_add_sources_and_layers(source_layer_data);
        }
    }

    // const IGNBuildings_Show = (type) => {
    //     // Remove if data already loaded
    //     IGNBuildings_Hide(type);


    //     // Set point 0
    //     var point0 = capacity.landBase.union.bbox.origin;
    //     // Georeferencing
    //     var modelOrigin = point0;
    //     var modelAltitude = 0;
    //     var modelRotate = [0, 0, 0];
    //     var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
    //         modelOrigin,
    //         modelAltitude
    //     );
    //     // Set position, rotation and scale
    //     var modelTransform = {
    //         translateX: modelAsMercatorCoordinate.x,
    //         translateY: modelAsMercatorCoordinate.y,
    //         translateZ: modelAsMercatorCoordinate.z,
    //         rotateX: modelRotate[0],
    //         rotateY: modelRotate[1],
    //         rotateZ: modelRotate[2],
    //         scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
    //     };
    //     // Set THREE
    //     // var THREE = window.THREE;
    //     // Create the custom layer
    //     var customLayer = {
    //         id: 'layerIGNBuildings_' + type,
    //         type: 'custom',
    //         renderingMode: '3d',
    //         onAdd: function (map, gl) {
    //             this.camera = new THREE.Camera();
    //             this.scene = new THREE.Scene();
    //             // Create lights
    //             var directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
    //             // directionalLight1.position.set(-100, 20, -100).normalize();
    //             directionalLight1.position.x = -10;
    //             directionalLight1.position.y = -10;
    //             directionalLight1.position.z = 20;
    //             this.scene.add(directionalLight1);

    //             var directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    //             // directionalLight1.position.set(-100, 20, -100).normalize();
    //             directionalLight2.position.x = 10;
    //             directionalLight2.position.y = 10;
    //             directionalLight2.position.z = 20;
    //             this.scene.add(directionalLight2);

    //             var ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    //             this.scene.add(ambientLight);



    //             // Loop th all buildings
    //             for (var j = 0; j < capacity.landBase.buildings.buildings.length; j++) {
    //                 var building = capacity.landBase.buildings.buildings[j];
    //                 // Check type
    //                 var test_type = false;
    //                 // if (type === "land" && capacity.landBase.buildings.buildings_land.osm.includes(building.properties.id_osm)) {
    //                 // if (type === "land" && capacity.landBase.buildings.buildings_land.includes(j)) {
    //                 if (type === "land" && building?.properties?.demolition === true) {
    //                     test_type = true;
    //                 }
    //                 // if (type === "noland" && !capacity.landBase.buildings.buildings_land.includes(j)) {
    //                 if (type === "noland" && !building?.properties?.demolition === true) {
    //                     test_type = true;
    //                 }
    //                 if (!test_type) {
    //                     continue;
    //                 }
    //                 // Get local coordinates
    //                 var building_coords_global = building["geometry"]["coordinates"][0];
    //                 if (building["geometry"]["type"] === "MultiPolygon") {
    //                     building_coords_global = building["geometry"]["coordinates"][0][0];
    //                 }
    //                 var building_coords = [];
    //                 for (var i = 0; i < building_coords_global.length; i++) {
    //                     var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
    //                     if (building_coords_global[i][0] < point0[0]) {
    //                         deltaX = -deltaX;
    //                     }
    //                     var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
    //                     if (building_coords_global[i][1] < point0[1]) {
    //                         deltaY = -deltaY;
    //                     }
    //                     building_coords.push([deltaX, deltaY]);
    //                 }
    //                 // Shape and Geometry
    //                 var landShape = new THREE.Shape();
    //                 landShape.moveTo(building_coords[0][0], building_coords[0][1]);
    //                 for (var i = 0; i < building_coords.length; i++) {
    //                     landShape.lineTo(building_coords[i][0], building_coords[i][1]);
    //                 }
    //                 var height = building?.properties?.height || 1;
    //                 if (building?.properties?.height_max > height) {
    //                     height = building.properties.height_max;
    //                 }
    //                 var landExtrudeSettings = {
    //                     steps: 1,
    //                     depth: height,
    //                     bevelEnabled: false
    //                 };
    //                 var landGeometry = new THREE.ExtrudeGeometry(landShape, landExtrudeSettings);
    //                 var color = '#F1F1F1';
    //                 var opacity = 1;
    //                 if (building?.properties?.demolition === true) {
    //                     opacity = 0.2;
    //                 }
    //                 // if (building.properties.type === "matched") {
    //                 //     color = 'green';
    //                 // }
    //                 // else if (building.properties.type === "osm") {
    //                 //     color = 'blue';
    //                 // }
    //                 // else if (building.properties.type === "ign") {
    //                 //     color = 'yellow';
    //                 // }
    //                 var landMaterial = new THREE.MeshLambertMaterial({
    //                     color: color,
    //                     transparent: true,
    //                     opacity: opacity,
    //                     side: THREE.DoubleSide,
    //                     // wireframe: true
    //                 });
    //                 var landMesh = new THREE.Mesh(landGeometry, landMaterial);
    //                 this.scene.add(landMesh);
    //                 // Edges
    //                 if (building?.properties?.demolition === true) {
    //                     var edgesGeometry = new THREE.EdgesGeometry(landGeometry);
    //                     var edgesMesh = new THREE.LineSegments(edgesGeometry, new THREE.LineDashedMaterial({ color: "#F5F5F5", dashSize: 1, gapSize: 0.6 }));
    //                     edgesMesh.computeLineDistances();
    //                     this.scene.add(edgesMesh);
    //                 }

    //                 // CREATE ROOF MESH
    //                 // if (building?.properties?.height_max) {
    //                 //     try {

    //                 //         var skeleton_coords = [...building_coords];
    //                 //         skeleton_coords.splice(-1);
    //                 //         // SKELETON
    //                 //         const skeleton = SkeletonBuilder.BuildFromGeoJSON([[
    //                 //             skeleton_coords,
    //                 //             // [[0, 0], [100, 0], [100, 50], [0, 50]],    // outer
    //                 //             // [[50, 30], [70, 30], [70, 20], [50, 20]],  // inner
    //                 //         ]]);
    //                 //         console.log("skeleton", skeleton);

    //                 //         // Get skeletyon points list
    //                 //         var skeleton_points = [];
    //                 //         var point_id = 0;
    //                 //         var elevation_max = 0;
    //                 //         for (const [pointPos, distance] of skeleton.Distances) {
    //                 //             var coords_local = [pointPos.X, pointPos.Y];
    //                 //             var coords_global = map_helpers.local_to_mercator(coords_local, capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix)
    //                 //             var turf_point = turf.point(coords_global);
    //                 //             // var elevation_new = building_levels[level_index].elevation + ((angle / 45) * distance);
    //                 //             var elevation_new = building?.properties?.height_max;
    //                 //             turf_point.properties.id = point_id;
    //                 //             turf_point.properties.elevation = elevation_new;
    //                 //             turf_point.properties.coords_local = coords_local;
    //                 //             skeleton_points.push(turf_point);

    //                 //             if (elevation_new > elevation_max) {
    //                 //                 elevation_max = elevation_new;
    //                 //             }

    //                 //             point_id++;
    //                 //         }
    //                 //         console.log("skeleton_points", skeleton_points);

    //                 //         // Get skeleton polygons
    //                 //         var skeleton_polys = [];
    //                 //         for (const edgeResult of skeleton.Edges) {
    //                 //             const poly = edgeResult.Polygon;
    //                 //             var skeleton_poly_points = [];
    //                 //             for (var sk_point_index = 0; sk_point_index < poly.length; sk_point_index++) {
    //                 //                 // Get point id
    //                 //                 var sk_poly_id = null;
    //                 //                 for (var sk_pointlist_index = 0; sk_pointlist_index < skeleton_points.length; sk_pointlist_index++) {
    //                 //                     if (poly[sk_point_index].X === skeleton_points[sk_pointlist_index].properties.coords_local[0] && poly[sk_point_index].Y === skeleton_points[sk_pointlist_index].properties.coords_local[1]) {
    //                 //                         sk_poly_id = sk_pointlist_index;
    //                 //                         break;
    //                 //                     }
    //                 //                 }
    //                 //                 skeleton_poly_points.push(sk_poly_id);
    //                 //             }
    //                 //             var skeleton_poly = {
    //                 //                 points: skeleton_poly_points,
    //                 //             }
    //                 //             skeleton_polys.push(skeleton_poly);
    //                 //         }
    //                 //         console.log("skeleton_polys", skeleton_polys);

    //                 //         // Get grid
    //                 //         var skeleton_tin = [];
    //                 //         skeleton_polys.forEach(poly => {
    //                 //             var points_list = [];
    //                 //             var coords_global = [];
    //                 //             poly.points.forEach(point_id => {
    //                 //                 if (point_id !== null) {
    //                 //                     points_list.push(skeleton_points[point_id]);
    //                 //                     coords_global.push(skeleton_points[point_id].geometry.coordinates);
    //                 //                 }
    //                 //             })
    //                 //             coords_global.push(coords_global[0]);
    //                 //             if (points_list.length >= 3) {
    //                 //                 var skeleton_polygon = turf.polygon([coords_global]);
    //                 //                 var tin = turf.tin({ features: points_list }, "id");
    //                 //                 // Check if poly is inside skeleton polygon
    //                 //                 var tin_filtered = [];
    //                 //                 for (var tin_index = 0; tin_index < tin.features.length; tin_index++) {
    //                 //                     var centroid = turf.centroid(tin.features[tin_index]);
    //                 //                     var isInside = turf.booleanPointInPolygon(centroid, skeleton_polygon);
    //                 //                     if (isInside === true) {
    //                 //                         tin_filtered.push(tin.features[tin_index]);
    //                 //                     }
    //                 //                 }
    //                 //                 skeleton_tin = skeleton_tin.concat(tin_filtered);
    //                 //             }
    //                 //         })
    //                 //         console.log("skeleton_tin", skeleton_tin);

    //                 //         // Create 3D shape
    //                 //         var vertices_list = [];
    //                 //         for (var i = 0; i < skeleton_tin.length; i++) {
    //                 //             // Vertice a
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.coords_local[0]);
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.coords_local[1]);
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.elevation);
    //                 //             // Vertice b
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.coords_local[0]);
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.coords_local[1]);
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.elevation);
    //                 //             // Vertice c
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.coords_local[0]);
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.coords_local[1]);
    //                 //             vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.elevation);
    //                 //         }
    //                 //         // console.log("tin_to_recreate", tin_to_recreate);
    //                 //         var vertices = new Float32Array(vertices_list);
    //                 //         // Set vertices to geometry
    //                 //         var geometry = new THREE.BufferGeometry();
    //                 //         geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    //                 //         // Compute vertex normals
    //                 //         geometry.computeVertexNormals();
    //                 //         // Create material
    //                 //         var material = new THREE.MeshPhongMaterial({
    //                 //             color: '#F1F1F1',
    //                 //             side: THREE.DoubleSide
    //                 //             // transparent: true,
    //                 //             // opacity: 0.4
    //                 //         });
    //                 //         // Create mesh
    //                 //         var mesh = new THREE.Mesh(geometry, material);
    //                 //         // console.log("mesh", mesh);

    //                 //         // Check if higher than buildabale max height
    //                 //         // if (elevation_max > capacity?.buildable?.volume?.parameters?.max_height + 0.02) {
    //                 //         // }
    //                 //         // else {
    //                 //         this.scene.add(mesh);
    //                 //         // Create edges
    //                 //         var edgesGeometry = new THREE.EdgesGeometry(mesh.geometry);
    //                 //         var edgesMesh = new THREE.LineSegments(edgesGeometry, new THREE.LineBasicMaterial({ color: "grey" }));
    //                 //         // console.log("edgesMesh", edgesMesh);
    //                 //         this.scene.add(edgesMesh);
    //                 //         // }

    //                 //     } catch (error) {
    //                 //         console.error(error);
    //                 //     }
    //                 // }
    //             }


    //             // Ambient Occlusion
    //             // var composer = new EffectComposer(this.renderer);
    //             // var renderPass = new RenderPass(this.scene, this.camera);
    //             // composer.addPass(renderPass);
    //             // var saoPass = new SAOPass(this.scene, this.camera);
    //             // composer.addPass(saoPass);
    //             // const outputPass = new OutputPass();
    //             // composer.addPass(outputPass);


    //             // Add map
    //             this.map = map;
    //             // use the Mapbox GL JS map canvas for three.js
    //             this.renderer = new THREE.WebGLRenderer({
    //                 canvas: map.getCanvas(),
    //                 context: gl,
    //                 antialias: true
    //             });
    //             // this.renderer.shadowMap.enabled = true;
    //             // this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    //             this.renderer.autoClear = false;
    //         },
    //         render: function (gl, matrix) {
    //             var rotationX = new THREE.Matrix4().makeRotationAxis(
    //                 new THREE.Vector3(1, 0, 0),
    //                 modelTransform.rotateX
    //             );
    //             var rotationY = new THREE.Matrix4().makeRotationAxis(
    //                 new THREE.Vector3(0, 1, 0),
    //                 modelTransform.rotateY
    //             );
    //             var rotationZ = new THREE.Matrix4().makeRotationAxis(
    //                 new THREE.Vector3(0, 0, 1),
    //                 modelTransform.rotateZ
    //             );
    //             var m = new THREE.Matrix4().fromArray(matrix);
    //             var l = new THREE.Matrix4()
    //                 .makeTranslation(
    //                     modelTransform.translateX,
    //                     modelTransform.translateY,
    //                     modelTransform.translateZ
    //                 )
    //                 .scale(
    //                     new THREE.Vector3(
    //                         modelTransform.scale,
    //                         -modelTransform.scale,
    //                         modelTransform.scale
    //                     )
    //                 )
    //                 .multiply(rotationX)
    //                 .multiply(rotationY)
    //                 .multiply(rotationZ);

    //             this.camera.projectionMatrix = m.multiply(l);
    //             this.renderer.resetState();
    //             this.renderer.render(this.scene, this.camera);
    //             // this.map.triggerRepaint();
    //         }
    //     };
    //     map.current.addLayer(customLayer, layer_get_position('layerIGNBuildings_' + type));

    //     // Add Clickable layer
    //     // Add source
    //     map.current.addSource('sourceIGNBuildings_' + type, {
    //         'type': 'geojson',
    //         'data': {
    //             type: 'FeatureCollection',
    //             features: capacity.landBase.buildings.buildings,
    //         }
    //     });
    //     // _Add layer
    //     var filter_list = [];
    //     if (capacity?.landBase?.buildings?.buildings && capacity.landBase.buildings.buildings.length > 0 && capacity?.landBase?.buildings?.buildings_land && capacity.landBase.buildings.buildings_land.length > 0) {
    //         capacity.landBase.buildings.buildings_land.forEach(item => {
    //             if (!filter_list.includes(capacity.landBase.buildings.buildings[item].properties.id)) {
    //                 filter_list.push(capacity.landBase.buildings.buildings[item].properties.id);
    //             }
    //         });
    //     }

    //     var filter = ['match', ['get', 'id'], filter_list, true, false];
    //     if (type === "noland") {
    //         filter = ['match', ['get', 'id'], filter_list, false, true];
    //     }
    //     // console.log("filter", filter);
    //     map.current.addLayer({
    //         id: 'layerIGNBuildings_Click_' + type,
    //         type: 'fill',
    //         source: 'sourceIGNBuildings_' + type,
    //         paint: {
    //             'fill-color': 'red',
    //             'fill-opacity': 0,
    //         },
    //         filter: filter
    //     }, layer_get_position('layerIGNBuildings_Click_' + type));






    //     // // Get url
    //     // let offset = 0.001;
    //     // // let offset = 0;
    //     // let data_url = "https://wxs.ign.fr/choisirgeoportail/geoportail/wfs?SERVICE=WFS&REQUEST=GetFeature&TYPENAME=BDTOPO_V3:batiment&OUTPUTFORMAT=application/json&BBOX=" + (capacity.landBase.union.bbox.coordinates[0][0][0] - offset).toString() + "," + (capacity.landBase.union.bbox.coordinates[0][0][1] - offset).toString() + "," + (capacity.landBase.union.bbox.coordinates[0][2][0] + offset).toString() + "," + (capacity.landBase.union.bbox.coordinates[0][2][1] + offset).toString() + ",EPSG:4326";

    //     // // _____ CADASTRE VERSION
    //     // let data_url_cadastre = "https://cadastre.data.gouv.fr/bundler/cadastre-etalab/communes/" + capacity.city.inseecode + "/geojson/batiments";
    //     // // Add source
    //     // map.current.addSource('sourceIGNBuildings', {
    //     //     'type': 'geojson',
    //     //     'data': data_url_cadastre
    //     // });
    //     // // Add layer
    //     // map.current.addLayer({
    //     //     'id': 'layerIGNBuildings',
    //     //     'type': 'fill-extrusion',
    //     //     'source': 'sourceIGNBuildings',
    //     //     'minzoom': 15,
    //     //     'paint': {
    //     //         // Get the `fill-extrusion-color` from the source `color` property.
    //     //         // 'fill-extrusion-color': 'red',
    //     //         'fill-extrusion-color': 'orange',
    //     //         // Get `fill-extrusion-height` from the source `height` property.
    //     //         'fill-extrusion-height': 5,
    //     //         // Get `fill-extrusion-base` from the source `base_height` property.
    //     //         'fill-extrusion-base': 0,
    //     //         // Make extrusions slightly opaque to see through indoor walls.
    //     //         'fill-extrusion-opacity': 1
    //     //     },
    //     // });


    //     // // Fetch
    //     // fetch(data_url)
    //     //     .then(res => res.json())
    //     //     .then(
    //     //         (result) => {

    //     // _____ 2D VERSION

    //     // Add id inside properties
    //     // for (var i = 0; i < result.features.length; i++) {
    //     //     result.features[i].properties.id = result.features[i].id;
    //     // }

    //     // // Add source
    //     // map.current.addSource('sourceIGNBuildings', {
    //     //     'type': 'geojson',
    //     //     // 'data': data_url
    //     //     'data': result
    //     // });

    //     // // Add layer
    //     // map.current.addLayer({
    //     //     'id': 'layerIGNBuildings',
    //     //     'type': 'fill-extrusion',
    //     //     'source': 'sourceIGNBuildings',
    //     //     'minzoom': 15,
    //     //     'paint': {
    //     //         // Get the `fill-extrusion-color` from the source `color` property.
    //     //         // 'fill-extrusion-color': 'red',
    //     //         'fill-extrusion-color': [
    //     //             'case',
    //     //             ['boolean', ['in',
    //     //                 ['get', 'id'],
    //     //                 ["literal", ['test', 'ok', 'batiment.BATIMENT0000000297089169']]]],
    //     //             '#062134',
    //     //             '#F1F1F1'
    //     //         ],
    //     //         // Get `fill-extrusion-height` from the source `height` property.
    //     //         'fill-extrusion-height': ['get', 'hauteur'],
    //     //         // Get `fill-extrusion-base` from the source `base_height` property.
    //     //         'fill-extrusion-base': 0,
    //     //         // Make extrusions slightly opaque to see through indoor walls.
    //     //         'fill-extrusion-opacity': 1
    //     //     },
    //     //     // 'filter': ['in', ['get', 'id'], ["literal", ['test', 'ok', 'batiment.BATIMENT0000000297089169']]]
    //     //     'filter': ['match', ['get', 'id'], ['test', 'ok', 'batiment.BATIMENT0000000297089169'], false, true]
    //     // });



    //     //         // _____ 3D VERSION

    //     //         // Set point 0
    //     //         var point0 = capacity.landBase.union.bbox.origin;
    //     //         // Georeferencing
    //     //         var modelOrigin = point0;
    //     //         var modelAltitude = 0;
    //     //         var modelRotate = [0, 0, 0];
    //     //         var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
    //     //             modelOrigin,
    //     //             modelAltitude
    //     //         );
    //     //         // Set position, rotation and scale
    //     //         var modelTransform = {
    //     //             translateX: modelAsMercatorCoordinate.x,
    //     //             translateY: modelAsMercatorCoordinate.y,
    //     //             translateZ: modelAsMercatorCoordinate.z,
    //     //             rotateX: modelRotate[0],
    //     //             rotateY: modelRotate[1],
    //     //             rotateZ: modelRotate[2],
    //     //             scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
    //     //         };
    //     //         // Set THREE
    //     //         // var THREE = window.THREE;
    //     //         // Create the custom layer
    //     //         var customLayer = {
    //     //             id: 'layerIGNBuildings',
    //     //             type: 'custom',
    //     //             renderingMode: '3d',
    //     //             onAdd: function (map, gl) {
    //     //                 this.camera = new THREE.Camera();
    //     //                 this.scene = new THREE.Scene();
    //     //                 // Create lights
    //     //                 var directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
    //     //                 directionalLight1.position.set(-10, -10, 20).normalize();
    //     //                 this.scene.add(directionalLight1);
    //     //                 // var directionalLight2 = new THREE.DirectionalLight(0xffffff);
    //     //                 // directionalLight2.position.set(0, 70, 100).normalize();
    //     //                 // this.scene.add(directionalLight2);
    //     //                 var ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    //     //                 this.scene.add(ambientLight);

    //     //                 // Loop th inside buildings to create list
    //     //                 var inside_buildings = [];
    //     //                 for (var j = 0; j < capacity.landBase.buildings.length; j++) {
    //     //                     inside_buildings.push(capacity.landBase.buildings[j].id);
    //     //                 }

    //     //                 // Loop th all buildings
    //     //                 for (var j = 0; j < result.features.length; j++) {
    //     //                     var building = result.features[j];
    //     //                     // Inside check
    //     //                     var isInsideLand = false;
    //     //                     if (inside_buildings.includes(building.id)) {
    //     //                         isInsideLand = true;
    //     //                     }
    //     //                     // Get local coordinates
    //     //                     var building_coords = [];
    //     //                     for (var i = 0; i < building["geometry"]["coordinates"][0][0].length; i++) {
    //     //                         var deltaX = (turf.distance(turf.point(building["geometry"]["coordinates"][0][0][i]), turf.point([point0[0], building["geometry"]["coordinates"][0][0][i][1]])) * 1000);
    //     //                         if (building["geometry"]["coordinates"][0][0][i][0] < point0[0]) {
    //     //                             deltaX = -deltaX;
    //     //                         }
    //     //                         var deltaY = (turf.distance(turf.point(building["geometry"]["coordinates"][0][0][i]), turf.point([building["geometry"]["coordinates"][0][0][i][0], point0[1]])) * 1000);
    //     //                         if (building["geometry"]["coordinates"][0][0][i][1] < point0[1]) {
    //     //                             deltaY = -deltaY;
    //     //                         }
    //     //                         building_coords.push([deltaX, deltaY]);
    //     //                     }
    //     //                     // Shape and Geometry
    //     //                     var landShape = new THREE.Shape();
    //     //                     landShape.moveTo(building_coords[0][0], building_coords[0][1]);
    //     //                     for (var i = 0; i < building_coords.length; i++) {
    //     //                         landShape.lineTo(building_coords[i][0], building_coords[i][1]);
    //     //                     }
    //     //                     var landExtrudeSettings = {
    //     //                         steps: 1,
    //     //                         depth: building.properties.hauteur,
    //     //                         bevelEnabled: false
    //     //                     };
    //     //                     var landGeometry = new THREE.ExtrudeGeometry(landShape, landExtrudeSettings);
    //     //                     var color = '#F1F1F1';
    //     //                     if (isInsideLand) { color = 'red' }
    //     //                     var landMaterial = new THREE.MeshPhongMaterial({
    //     //                         color: color,
    //     //                     });
    //     //                     var landMesh = new THREE.Mesh(landGeometry, landMaterial);
    //     //                     this.scene.add(landMesh);
    //     //                 }


    //     //                 // Add map
    //     //                 this.map = map;
    //     //                 // use the Mapbox GL JS map canvas for three.js
    //     //                 this.renderer = new THREE.WebGLRenderer({
    //     //                     canvas: map.getCanvas(),
    //     //                     context: gl,
    //     //                     antialias: true
    //     //                 });
    //     //                 this.renderer.autoClear = false;
    //     //             },
    //     //             render: function (gl, matrix) {
    //     //                 var rotationX = new THREE.Matrix4().makeRotationAxis(
    //     //                     new THREE.Vector3(1, 0, 0),
    //     //                     modelTransform.rotateX
    //     //                 );
    //     //                 var rotationY = new THREE.Matrix4().makeRotationAxis(
    //     //                     new THREE.Vector3(0, 1, 0),
    //     //                     modelTransform.rotateY
    //     //                 );
    //     //                 var rotationZ = new THREE.Matrix4().makeRotationAxis(
    //     //                     new THREE.Vector3(0, 0, 1),
    //     //                     modelTransform.rotateZ
    //     //                 );
    //     //                 var m = new THREE.Matrix4().fromArray(matrix);
    //     //                 var l = new THREE.Matrix4()
    //     //                     .makeTranslation(
    //     //                         modelTransform.translateX,
    //     //                         modelTransform.translateY,
    //     //                         modelTransform.translateZ
    //     //                     )
    //     //                     .scale(
    //     //                         new THREE.Vector3(
    //     //                             modelTransform.scale,
    //     //                             -modelTransform.scale,
    //     //                             modelTransform.scale
    //     //                         )
    //     //                     )
    //     //                     .multiply(rotationX)
    //     //                     .multiply(rotationY)
    //     //                     .multiply(rotationZ);

    //     //                 this.camera.projectionMatrix = m.multiply(l);
    //     //                 this.renderer.resetState()
    //     //                 this.renderer.render(this.scene, this.camera);
    //     //                 // this.map.triggerRepaint();
    //     //             }
    //     //         };
    //     //         map.current.addLayer(customLayer);


    //     //     },
    //     //     (error) => {
    //     //         console.log("Error fetching BNDB", error);
    //     //     }
    //     // )
    // }

    // _____ CONTEXT MERIMEE

    // MERIMEE : SHOW
    const contextMerimee_Show = () => {
        if (capacity?.context?.merimee && capacity.context.merimee.length > 0) {
            // Remove if data already loaded
            contextMerimee_Hide();

            var merimee_markers = [];

            // Create buffer circle
            var buffered = turf.buffer(turf.point(capacity.landBase.union.center.geometry.coordinates), 0.5, { steps: 100 });
            var buffered_line = turf.polygonToLine(buffered);
            // Add source
            map.current.addSource('sourceContextMerimee_Buffer', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [buffered_line],
                }
            });
            // Add layer lines
            map.current.addLayer({
                id: 'layerContextMerimee_Buffer',
                type: 'line',
                source: 'sourceContextMerimee_Buffer',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#062134',
                    'line-width': 2.2,
                    'line-dasharray': [4, 3]
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerContextMerimee_Buffer'));
            // Add marker
            var destination = turf.destination(turf.point(capacity.landBase.union.center.geometry.coordinates), 0.5, 180);
            var el = document.createElement('div');
            el.className = 'marker';
            el.innerHTML = '<div style="padding:4px 8px;font-family:Manrope;line-height:14px;text-align:center;color:#FFF;background-color:#062134;border-radius:6px">Rayon de 500m</div>';
            merimee_markers.push(new mapboxgl.Marker(el)
                .setLngLat(destination.geometry.coordinates)
                .addTo(map.current))
            // Fit view
            var bbox = turf.bboxPolygon(turf.bbox(buffered));
            flyTo_FitBbox(bbox.geometry.coordinates, 0.0003);


            // Add markers
            for (var i = 0; i < capacity.context.merimee.length; i++) {
                var color = "#062134";
                var el = document.createElement('div');
                var coords = [null];
                if (capacity.context.merimee[i]?.fields?.coordonnees) {
                    coords = capacity.context.merimee[i].fields.coordonnees;
                }
                if (capacity.context.merimee[i]?.fields?.p_coordonnees) {
                    coords = capacity.context.merimee[i].fields.p_coordonnees;
                }
                if (capacity.context.merimee[i]?.fields?.coordonnees_au_format_wgs84) {
                    coords = capacity.context.merimee[i].fields.coordonnees_au_format_wgs84;
                }
                if (coords[0] === null) { continue }
                var item_label = capacity?.context?.merimee[i]?.fields?.appellation_courante || capacity?.context?.merimee[i]?.fields?.titre_editorial_de_la_notice;
                el.className = 'marker';
                el.innerHTML = '<div style="cursor:pointer;padding:4px 8px;font-family:Manrope;color:#FFF;background-color:' + color + ';border-radius:6px"><span style="">' + item_label + '</span></div>';
                merimee_markers.push(new mapboxgl.Marker(el)
                    .setLngLat([coords[1], coords[0]])
                    .setPopup(new mapboxgl.Popup({ offset: 15 })
                        .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>' + item_label + '</strong></span><br><span style="font-size:12px;font-weight: 600;color: grey;">Statut juridique :</span><br><span style="color:#062134;font-weight:500;">' + (capacity.context.merimee[i]?.fields?.statut || capacity.context.merimee[i]?.fields?.statut_juridique_de_l_edifice) + '</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Protection :</span><br><span style="color:#062134;font-weight:500;">' + (capacity.context.merimee[i]?.fields?.date_de_protection || capacity.context.merimee[i]?.fields?.date_et_typologie_de_la_protection) + '</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Fiche détaillée :</span><br><span style="background-color:#1ecd97;color:#FFF;font-weight:500;font-size:11px;padding:0px 6px;border-radius:8px"><a href="https://www.pop.culture.gouv.fr/notice/merimee/' + capacity.context.merimee[i].fields.reference + '" target="_blank">Plateforme Ouverte du Patrimoine</a></span></p>'))
                    .addTo(map.current))
            }
            setMerimeeMarkers(merimee_markers);

        }
    }

    // MERIMEE : HIDE
    const contextMerimee_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerContextMerimee_Buffer')) {
            map.current.removeLayer('layerContextMerimee_Buffer');
        }
        // Remove source
        if (map.current.getSource('sourceContextMerimee_Buffer')) {
            map.current.removeSource('sourceContextMerimee_Buffer');
        }
        // Remove markers
        for (var i = 0; i < merimeeMarkers.length; i++) {
            merimeeMarkers[i].remove();
        }

        // Fit view to union
        if (capacity?.landBase?.union) {
            // if (!map.current.getLayer('layerRisksBasias_Buffer')) {
            flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.00006);
            // }
        }
    }

    // _____ CONTEXT SITADEL

    // SITADEL : SHOW
    const contextSitadel_Show = () => {
        if (capacity?.context?.sitadel && capacity.context.sitadel.length > 0) {
            // Remove if data already loaded
            contextSitadel_Hide();

            var sitadel_markers = [];
            var sitadel_coords_list = [];
            var sitadel_coords_list_check = [];
            // Add markers
            if (capacity?.context?.sitadel && capacity?.context?.sitadel.length > 0) {
                for (var i = 0; i < capacity.context.sitadel.length; i++) {
                    if (capacity.context.sitadel[i]?.coords && capacity.context.sitadel[i]?.coords.length === 2) {
                        // Coords
                        var sitadel_coords = capacity.context.sitadel[i]?.coords;
                        if (sitadel_coords_list_check.includes(sitadel_coords.join("_"))) {
                            sitadel_coords[0] = sitadel_coords[0] + 0.0001;
                        }
                        if (sitadel_coords_list_check.includes(sitadel_coords.join("_"))) {
                            sitadel_coords[0] = sitadel_coords[0] + 0.0001;
                        }
                        if (sitadel_coords_list_check.includes(sitadel_coords.join("_"))) {
                            sitadel_coords[0] = sitadel_coords[0] + 0.0001;
                        }
                        if (sitadel_coords_list_check.includes(sitadel_coords.join("_"))) {
                            sitadel_coords[0] = sitadel_coords[0] + 0.0001;
                        }
                        if (sitadel_coords_list_check.includes(sitadel_coords.join("_"))) {
                            sitadel_coords[0] = sitadel_coords[0] + 0.0001;
                        }
                        sitadel_coords_list.push(sitadel_coords);
                        sitadel_coords_list_check.push(sitadel_coords.join("_"));
                        // Status
                        var sitadel_status = "";
                        if (capacity.context.sitadel[i]?.status === 2) {
                            sitadel_status = '<br><span style="font-size:12px;font-weight: 600;color: grey;">Statut :</span><br><span style="color:#062134;font-weight:500;">Autorisé' + (capacity.context.sitadel[i]?.date_aut ? (" le " + helpers.get_pretty_date_dd_mm_yyyy_from_yyy_mm_dd(capacity.context.sitadel[i]?.date_aut)) : "") + '</span>';
                        }
                        else if (capacity.context.sitadel[i]?.status === 4) {
                            sitadel_status = '<br><span style="font-size:12px;font-weight: 600;color: grey;">Statut :</span><br><span style="color:#062134;font-weight:500;">Annulé</span>';
                        }
                        else if (capacity.context.sitadel[i]?.status === 5) {
                            sitadel_status = '<br><span style="font-size:12px;font-weight: 600;color: grey;">Statut :</span><br><span style="color:#062134;font-weight:500;">Commencé' + (capacity.context.sitadel[i]?.date_start ? (" le " + helpers.get_pretty_date_dd_mm_yyyy_from_yyy_mm_dd(capacity.context.sitadel[i]?.date_start)) : "") + '</span>';
                        }
                        else if (capacity.context.sitadel[i]?.status === 6) {
                            sitadel_status = '<br><span style="font-size:12px;font-weight: 600;color: grey;">Statut :</span><br><span style="color:#062134;font-weight:500;">Terminé' + (capacity.context.sitadel[i]?.date_end ? (" le " + helpers.get_pretty_date_dd_mm_yyyy_from_yyy_mm_dd(capacity.context.sitadel[i]?.date_end)) : "") + '</span>';
                        }
                        // Demandeur
                        var sitadel_dem = "";
                        if (capacity.context.sitadel[i]?.dem_label) {
                            sitadel_dem = '<br><span style="font-size:12px;font-weight: 600;color: grey;">Demandeur :</span><br><span style="color:#062134;font-weight:500;">' + capacity.context.sitadel[i]?.dem_label + '</span>';
                        }
                        // Nature
                        var sitadel_nat = "";
                        var sitadel_nat_txt = "";
                        if (capacity.context.sitadel[i]?.nature2 && capacity.context.sitadel[i]?.nature2 > 0 && capacity.context.sitadel[i]?.nature2 <= 6) {
                            const dau_nature2 = ["Nouvelle construction", "Transformation sans impact sur la surface", "Transformation avec extension de surface", "Transformation avec diminution de surface", "Extension de surface sans transformation", "Diminution de surface sans transformation"];
                            sitadel_nat_txt = dau_nature2[capacity.context.sitadel[i]?.nature2 - 1];
                        }
                        else if (capacity.context.sitadel[i]?.nature && capacity.context.sitadel[i]?.nature > 0 && capacity.context.sitadel[i]?.nature <= 2) {
                            const dau_nature = ["Nouvelle construction", "Travaux sur construction existante"];
                            sitadel_nat_txt = dau_nature[capacity.context.sitadel[i]?.nature - 1];
                        }
                        if (capacity.context.sitadel[i]?.dest && capacity.context.sitadel[i]?.dest > 0 && capacity.context.sitadel[i]?.dest <= 9) {
                            const dau_dest = ["Habitation", "Hébergement hôtelier", "Bureaux", "Commerce", "Artisanat", "Industrie", "Agriculture", "Entrepôt", "Service public ou d'intérêt collectif"];
                            sitadel_nat_txt = sitadel_nat_txt + "<br>" + dau_dest[capacity.context.sitadel[i]?.dest - 1];
                        }
                        if (sitadel_nat_txt !== "") {
                            sitadel_nat = '<br><span style="font-size:12px;font-weight: 600;color: grey;">Nature et Destination :</span><br><span style="color:#062134;font-weight:500;">' + sitadel_nat_txt + '</span>';
                        }
                        var el = document.createElement('div');
                        var sitadel_id = "marker_sitadel_" + capacity.context.sitadel[i]?.id;
                        el.className = 'marker';
                        el.innerHTML = '<div id="' + sitadel_id + '" style="cursor:pointer;height:28px;width:28px;font-size:14px;background-color:#062134;color:#FFF;border:solid 2px #FFF;border-radius:28px;display:flex;justify-content:center;align-items:center;text-align:center">' + capacity.context.sitadel[i].type + '</div>';
                        el.addEventListener('click', function (e) {
                            setHighlightedSitadel(e.target.id.substring(7));
                        });
                        // el.innerHTML = '<div style="padding:6px 8px 2px 8px;background-color:#062134;border-radius:6px;display:flex;justify-content:center;gap:4px"><div>' + img + '</div><div style="font-family:Manrope;color:#FFF;">BUS</div></div>';
                        sitadel_markers.push(new mapboxgl.Marker(el)
                            .setLngLat(sitadel_coords)
                            .setPopup(new mapboxgl.Popup({ offset: 18 })
                                .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>' + (capacity.context.sitadel[i]?.type === "PC" ? "Permis de construire" : capacity.context.sitadel[i]?.type === "DP" ? "Déclaration Préalable" : capacity.context.sitadel[i]?.type === "PA" ? "Permis d'Aménager" : "") + '<br>' + (capacity.context.sitadel[i]?.id || "") + '</strong></span>' + sitadel_status + '<br><span style="font-size:12px;font-weight: 600;color: grey;">Adresse :</span><br><span style="color:#062134;font-weight:500;">' + (capacity.context.sitadel[i]?.address || "NC") + '</span></p>'))
                            .addTo(map.current))
                    }
                }
            }
            // Add all markers
            setSitadelMarkers(sitadel_markers);


            // Fit view
            if (sitadel_coords_list.length > 0) {
                var multiPt = turf.multiPoint(sitadel_coords_list);
                var bbox = turf.bboxPolygon(turf.bbox(multiPt));
                flyTo_FitBbox(bbox.geometry.coordinates, 0.001);
            }
        }
    }

    // SITADEL : HIDE
    const contextSitadel_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerContextSitadel_Buffer')) {
            map.current.removeLayer('layerContextSitadel_Buffer');
        }
        // Remove source
        if (map.current.getSource('sourceContextSitadel_Buffer')) {
            map.current.removeSource('sourceContextSitadel_Buffer');
        }
        // Remove markers
        for (var i = 0; i < sitadelMarkers.length; i++) {
            sitadelMarkers[i].remove();
        }

        // Fit view to union
        if (capacity?.landBase?.union) {
            // if (!map.current.getLayer('layerRisksBasias_Buffer')) {
            flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.00006);
            // }
        }
    }



    // _____ CONTEXT ISODISTANCE

    // ISODISTANCE : SHOW
    const contextIsodistance_Show = () => {
        // Remove
        contextIsodistance_Hide();

        var isodistance_markers = [];

        // Create source
        var data = {
            type: 'FeatureCollection',
            features: [
                {
                    "type": "Feature",
                    "properties": {
                        type: "car"
                    },
                    "geometry": capacity.context.iso_distance.car_5.geometry
                },
                {
                    "type": "Feature",
                    "properties": {
                        type: "car"
                    },
                    "geometry": turf.polygonToLine(turf.polygon(capacity.context.iso_distance.car_5.geometry.coordinates)).geometry
                },
                {
                    "type": "Feature",
                    "properties": {
                        type: "walk"
                    },
                    "geometry": capacity.context.iso_distance.walk_10.geometry
                },
                {
                    "type": "Feature",
                    "properties": {
                        type: "walk"
                    },
                    "geometry": turf.polygonToLine(turf.polygon(capacity.context.iso_distance.walk_10.geometry.coordinates)).geometry
                },
            ],
        };
        // Add source
        map.current.addSource('sourceContextIsodistance', {
            type: 'geojson',
            data: data
        });
        // Add layer polys
        map.current.addLayer({
            id: 'layerContextIsodistance_Polys',
            type: 'fill',
            source: 'sourceContextIsodistance',
            paint: {
                'fill-color': [
                    'match',
                    ['get', 'type'],
                    'car',
                    '#008089',
                    'walk',
                    '#004e65',
                    /* default */ '#062134'
                ],
                'fill-opacity': 0.2,
            },
            filter: ['in', '$type', 'Polygon']
        }, layer_get_position('layerContextIsodistance_Polys'));
        // Add layer lines
        map.current.addLayer({
            id: 'layerContextIsodistance_Lines',
            type: 'line',
            source: 'sourceContextIsodistance',
            layout: {
                'line-cap': 'round',
                'line-join': 'round'
            },
            paint: {
                'line-color': [
                    'match',
                    ['get', 'type'],
                    'car',
                    '#008089',
                    'walk',
                    '#004e65',
                    /* default */ '#062134'
                ],
                'line-width': 2.2,
                'line-dasharray': [2, 2]
            },
            filter: ['in', '$type', 'LineString']
        }, layer_get_position('layerContextIsodistance_Lines'));
        // Add markers
        var bbox = turf.bboxPolygon(turf.bbox(capacity.context.iso_distance.car_5.geometry));
        var midpoint = turf.midpoint(turf.point([bbox.bbox[0], bbox.bbox[1]]), turf.point([bbox.bbox[2], bbox.bbox[1]]));
        var snapped_car = turf.nearestPointOnLine(turf.polygonToLine(turf.polygon(capacity.context.iso_distance.car_5.geometry.coordinates)), midpoint);
        var snapped_walk = turf.nearestPointOnLine(turf.polygonToLine(turf.polygon(capacity.context.iso_distance.walk_10.geometry.coordinates)), midpoint);
        // CAR marker
        var img = '<svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_412_13)"><path d="M17.9116 6.22306L17.4543 5.07967L16.6433 3.05208C15.9471 1.31177 14.2865 0.1875 12.4119 0.1875H7.2443C5.37012 0.1875 3.70915 1.31177 3.01294 3.05208L2.20199 5.07967L1.74463 6.22306C0.763184 6.59619 0.0625 7.53857 0.0625 8.65104V10.6042C0.0625 11.2601 0.313151 11.8521 0.713542 12.3103V14.5104C0.713542 15.2294 1.29663 15.8125 2.01562 15.8125H3.31771C4.0367 15.8125 4.61979 15.2294 4.61979 14.5104V13.2083H15.0365V14.5104C15.0365 15.2294 15.6195 15.8125 16.3385 15.8125H17.6406C18.3596 15.8125 18.9427 15.2294 18.9427 14.5104V12.3103C19.3431 11.8525 19.5938 11.2605 19.5938 10.6042V8.65104C19.5938 7.53857 18.8931 6.59619 17.9116 6.22306V6.22306ZM5.43075 4.01929C5.72738 3.27791 6.44556 2.79167 7.2443 2.79167H12.4119C13.2107 2.79167 13.9289 3.27791 14.2255 4.01929L15.0365 6.04688H4.61979L5.43075 4.01929ZM3.31771 10.596C2.53646 10.596 2.01562 10.0768 2.01562 9.29801C2.01562 8.51921 2.53646 8 3.31771 8C4.09896 8 5.27083 9.16821 5.27083 9.94702C5.27083 10.7258 4.09896 10.596 3.31771 10.596ZM16.3385 10.596C15.5573 10.596 14.3854 10.7258 14.3854 9.94702C14.3854 9.16821 15.5573 8 16.3385 8C17.1198 8 17.6406 8.51921 17.6406 9.29801C17.6406 10.0768 17.1198 10.596 16.3385 10.596V10.596Z" fill="white"/></g><defs><clipPath id="clip0_412_13"><rect width="19.6875" height="16" fill="white"/></clipPath></defs></svg>';
        var el = document.createElement('div');
        el.className = 'marker';
        el.innerHTML = '<div style="padding:6px 8px 2px 8px;background-color:#008089;border-radius:6px;display:flex;justify-content:center;gap:4px"><div>' + img + '</div><div style="font-family:Manrope;color:#FFF;">5 minutes en voiture</div></div>';
        isodistance_markers.push(new mapboxgl.Marker(el)
            .setLngLat(snapped_car.geometry.coordinates)
            .addTo(map.current))
        // WALK marker
        var img = '<svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_411_11)"><path d="M6.34582 3.11676C7.1544 3.11676 7.81042 2.46073 7.81042 1.65213C7.81042 0.843532 7.1544 0.1875 6.34582 0.1875C5.53724 0.1875 4.88123 0.843532 4.88123 1.65213C4.88123 2.46073 5.53724 3.11676 6.34582 3.11676ZM9.22925 7.66626L8.51831 7.30621L8.22234 6.40912C7.7738 5.04824 6.52279 4.09623 5.10397 4.09318C4.00552 4.09013 3.39832 4.40136 2.25716 4.86211C1.59809 5.12757 1.05802 5.63104 0.740689 6.27181L0.536256 6.68679C0.298259 7.1689 0.490487 7.75475 0.969532 7.9958C1.44553 8.23686 2.02526 8.04157 2.26631 7.55947L2.47074 7.14449C2.57754 6.9309 2.75451 6.76307 2.9742 6.67459L3.79193 6.34505L3.32814 8.19719C3.16948 8.83186 3.34035 9.5062 3.78278 9.99136L5.61047 11.9869C5.83016 12.228 5.98578 12.5178 6.06511 12.8321L6.62348 15.0687C6.75469 15.5905 7.2856 15.9109 7.80737 15.7797C8.32913 15.6485 8.64951 15.1176 8.51831 14.5958L7.84093 11.8801C7.7616 11.5658 7.60598 11.2729 7.3863 11.0349L5.99798 9.51841L6.52279 7.42216L6.69061 7.92562C6.85233 8.41689 7.20017 8.82271 7.65786 9.05461L8.36879 9.41466C8.84479 9.65572 9.42452 9.46043 9.66557 8.97833C9.90052 8.49927 9.70829 7.90732 9.22925 7.66626V7.66626ZM2.24495 11.9595C2.14731 12.2066 2.00085 12.4294 1.81168 12.6155L0.286054 14.1442C-0.0953513 14.5256 -0.0953513 15.145 0.286054 15.5264C0.667459 15.9079 1.28381 15.9079 1.66522 15.5264L3.47765 13.714C3.66378 13.5278 3.81024 13.3051 3.91093 13.0579L4.32285 12.0266C2.63551 10.1866 3.14202 10.7511 2.87656 10.388L2.24495 11.9595V11.9595Z" fill="white"/></g><defs><clipPath id="clip0_411_11"><rect width="9.75" height="16" fill="white"/></clipPath></defs></svg>';
        var el = document.createElement('div');
        el.className = 'marker';
        el.innerHTML = '<div style="padding:6px 8px 2px 8px;background-color:#004e65;border-radius:6px;display:flex;justify-content:center;gap:4px"><div>' + img + '</div><div style="font-family:Manrope;color:#FFF;">10 minutes à pied</div></div>';
        isodistance_markers.push(new mapboxgl.Marker(el)
            .setLngLat(snapped_walk.geometry.coordinates)
            .addTo(map.current))
        // TRANSIT markers
        if (capacity?.landBase?.location?.nearby?.transit_station && capacity?.landBase?.location?.nearby?.transit_station.length > 0) {
            var img = '<svg width="12" height="16" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_619_20)"><path d="M5.94 0C3.57125 0 2.42 0.41875 2.42 1.28C2.42 1.45625 2.56375 1.6 2.74 1.6C2.91625 1.6 3.06 1.45625 3.06 1.28C3.06 1.1225 3.37875 0.865 4.37 0.73L4.64 2.31C2.0525 2.6 0.5 3.73625 0.5 4.8V12.25C0.5 13.2362 1.19375 14.0687 2.14 14.32L0.8 15.44C0.66625 15.5525 0.6475 15.7462 0.76 15.88C0.8225 15.955 0.91 16 1 16C1.07125 16 1.15 15.97 1.21 15.92L3.03 14.4H8.85L10.67 15.92C10.73 15.97 10.8087 16 10.88 16C10.97 16 11.0575 15.955 11.12 15.88C11.2325 15.7462 11.2137 15.5525 11.08 15.44L9.74 14.32C10.6862 14.0687 11.38 13.2362 11.38 12.25V4.8C11.38 3.73625 9.8275 2.6 7.24 2.31L7.51 0.73C8.50125 0.865 8.82 1.1225 8.82 1.28C8.82 1.45625 8.9625 1.6 9.14 1.6C9.3175 1.6 9.46 1.45625 9.46 1.28C9.46 0.41875 8.30875 0 5.94 0ZM5.94 0.64C6.2875 0.64 6.58625 0.6525 6.86 0.67L6.59 2.26C6.3775 2.24875 6.16375 2.24 5.94 2.24C5.71625 2.24 5.5025 2.24875 5.29 2.26L5.02 0.67C5.29375 0.6525 5.5925 0.64 5.94 0.64ZM4.02 4.16H7.86C8.0375 4.16 8.18 4.30375 8.18 4.48V5.12H3.7V4.48C3.7 4.30375 3.84375 4.16 4.02 4.16ZM2.74 5.76H9.14C9.49375 5.76 9.78 6.02625 9.78 6.35V8.69C9.78 9.01375 9.49375 9.28 9.14 9.28H2.74C2.38625 9.28 2.1 9.01375 2.1 8.69V6.35C2.1 6.02625 2.38625 5.76 2.74 5.76ZM3.06 10.88C3.59 10.88 4.02 11.31 4.02 11.84C4.02 12.37 3.59 12.8 3.06 12.8C2.53 12.8 2.1 12.37 2.1 11.84C2.1 11.31 2.53 10.88 3.06 10.88ZM8.82 10.88C9.35 10.88 9.78 11.31 9.78 11.84C9.78 12.37 9.35 12.8 8.82 12.8C8.29 12.8 7.86 12.37 7.86 11.84C7.86 11.31 8.29 10.88 8.82 10.88Z" fill="white"/></g><defs><clipPath id="clip0_619_20"><rect width="12" height="16" fill="white"/></clipPath></defs></svg>';
            for (var i = 0; i < capacity.landBase.location.nearby.transit_station.length; i++) {
                if (capacity.landBase.location.nearby.transit_station[i]?.coordinates) {
                    var url = "https://www.google.com/maps/search/?api=1&query=" + capacity.landBase.location.nearby.transit_station[i]?.coordinates[0] + "%2C" + capacity.landBase.location.nearby.transit_station[i]?.coordinates[1] + "&query_place_id=" + capacity.landBase.location.nearby.transit_station[i]?.place_id + "&hl=fr";
                    var distance = Math.round(turf.distance(capacity.landBase.union.center, turf.point(capacity.landBase.location.nearby.transit_station[i]?.coordinates)) * 1000);
                    var el = document.createElement('div');
                    el.className = 'marker';
                    el.innerHTML = '<div style="cursor:pointer;height:28px;width:28px;background-color:#062134;border:solid 2px #FFF;border-radius:28px;display:flex;justify-content:center;align-items:center">' + img + '</div>';
                    // el.innerHTML = '<div style="padding:6px 8px 2px 8px;background-color:#062134;border-radius:6px;display:flex;justify-content:center;gap:4px"><div>' + img + '</div><div style="font-family:Manrope;color:#FFF;">BUS</div></div>';
                    isodistance_markers.push(new mapboxgl.Marker(el)
                        .setLngLat(capacity.landBase.location.nearby.transit_station[i]?.coordinates)
                        .setPopup(new mapboxgl.Popup({ offset: 15 })
                            .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Arrêt de transport en commun</strong></span><br><span style="font-size:12px;font-weight: 600;color: grey;">Nom :</span><br><span style="color:#062134;font-weight:500;">' + capacity.landBase.location.nearby.transit_station[i]?.name + '</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Distance du terrain :</span><br><span style="color:#062134;font-weight:500;">' + distance + ' m</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Détails :</span><br><span style="background-color:#1ecd97;color:#FFF;font-weight:500;font-size:11px;padding:0px 6px;border-radius:8px"><a href=' + url + ' target="_blank">Ouvrir dans Google Maps</a></span></p>'))
                        .addTo(map.current))
                }
            }
        }
        // DOCTOR markers
        if (capacity?.landBase?.location?.nearby?.doctor && capacity?.landBase?.location?.nearby?.doctor.length > 0) {
            var img = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_620_22)"><path d="M13.4521 4.50986H11.9544V2.92029C11.9544 1.78392 11.0577 0.859863 9.91827 0.859863H6.08242C4.94241 0.859863 4.04603 1.78392 4.04603 2.92029V4.50986H2.54801C1.52722 4.50986 0.700195 5.29796 0.700195 6.3157V13.3137C0.700195 14.3311 1.52722 15.1557 2.54801 15.1557H13.4521C14.4726 15.1557 15.3002 14.3311 15.3002 13.3134V6.3157C15.3002 5.29796 14.4726 4.50986 13.4521 4.50986ZM5.2627 2.92029C5.2627 2.49476 5.65568 2.07653 6.08242 2.07653H9.91827C10.3438 2.07653 10.7377 2.49476 10.7377 2.92029V4.50986H5.2627V2.92029ZM11.346 11.2015H9.21686V13.3307H6.78353V11.2015H4.65436V8.7682H6.78353V6.63903H9.21686V8.7682H11.346V11.2015Z" fill="white"/></g><defs><clipPath id="clip0_620_22"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>';
            for (var i = 0; i < capacity.landBase.location.nearby.doctor.length; i++) {
                if (capacity.landBase.location.nearby.doctor[i]?.coordinates) {
                    var url = "https://www.google.com/maps/search/?api=1&query=" + capacity.landBase.location.nearby.doctor[i]?.coordinates[0] + "%2C" + capacity.landBase.location.nearby.doctor[i]?.coordinates[1] + "&query_place_id=" + capacity.landBase.location.nearby.doctor[i]?.place_id + "&hl=fr";
                    var distance = Math.round(turf.distance(capacity.landBase.union.center, turf.point(capacity.landBase.location.nearby.doctor[i]?.coordinates)) * 1000);
                    var el = document.createElement('div');
                    el.className = 'marker';
                    el.innerHTML = '<div style="cursor:pointer;height:28px;width:28px;background-color:#062134;border:solid 2px #FFF;border-radius:28px;display:flex;justify-content:center;align-items:center">' + img + '</div>';
                    // el.innerHTML = '<div style="padding:6px 8px 2px 8px;background-color:#062134;border-radius:6px;display:flex;justify-content:center;gap:4px"><div>' + img + '</div><div style="font-family:Manrope;color:#FFF;">BUS</div></div>';
                    isodistance_markers.push(new mapboxgl.Marker(el)
                        .setLngLat(capacity.landBase.location.nearby.doctor[i]?.coordinates)
                        .setPopup(new mapboxgl.Popup({ offset: 15 })
                            .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Etablissement de santé</strong></span><br><span style="font-size:12px;font-weight: 600;color: grey;">Nom :</span><br><span style="color:#062134;font-weight:500;">' + capacity.landBase.location.nearby.doctor[i]?.name + '</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Distance du terrain :</span><br><span style="color:#062134;font-weight:500;">' + distance + ' m</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Détails :</span><br><span style="background-color:#1ecd97;color:#FFF;font-weight:500;font-size:11px;padding:0px 6px;border-radius:8px"><a href=' + url + ' target="_blank">Ouvrir dans Google Maps</a></span></p>'))
                        .addTo(map.current))
                }
            }
        }
        // SCHOOL markers
        if (capacity?.landBase?.location?.nearby?.school && capacity?.landBase?.location?.nearby?.school.length > 0) {
            var img = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 11.4999C7.91296 11.4999 7.82743 11.4772 7.75188 11.434L3.375 8.93242C3.33697 8.91046 3.29382 8.89891 3.24991 8.89893C3.20599 8.89894 3.16286 8.91053 3.12484 8.93251C3.08682 8.9545 3.05527 8.98611 3.03336 9.02417C3.01144 9.06222 2.99994 9.10538 3 9.14929V11.4999C2.99993 11.5892 3.02374 11.6768 3.06896 11.7537C3.11419 11.8307 3.17918 11.8941 3.25719 11.9374L7.75719 14.4374C7.83146 14.4787 7.91503 14.5003 8 14.5003C8.08497 14.5003 8.16854 14.4787 8.24281 14.4374L12.7428 11.9374C12.8208 11.8941 12.8858 11.8307 12.931 11.7537C12.9763 11.6768 13.0001 11.5892 13 11.4999V9.14929C13.0001 9.10538 12.9886 9.06222 12.9666 9.02417C12.9447 8.98611 12.9132 8.9545 12.8752 8.93251C12.8371 8.91053 12.794 8.89894 12.7501 8.89893C12.7062 8.89891 12.663 8.91046 12.625 8.93242L8.24813 11.434C8.17257 11.4772 8.08704 11.4999 8 11.4999Z" fill="white"/><path d="M15.4971 5.9531C15.4971 5.9531 15.4971 5.9506 15.4971 5.94966C15.489 5.8704 15.4621 5.79423 15.4186 5.72748C15.3751 5.66073 15.3163 5.60533 15.2471 5.56591L8.2471 1.56591C8.17154 1.52272 8.08601 1.5 7.99898 1.5C7.91194 1.5 7.82641 1.52272 7.75085 1.56591L0.750851 5.56591C0.674347 5.60965 0.610762 5.67284 0.566539 5.74906C0.522315 5.82529 0.499023 5.91185 0.499023 5.99997C0.499023 6.0881 0.522315 6.17466 0.566539 6.25088C0.610762 6.32711 0.674347 6.39029 0.750851 6.43404L7.75085 10.434C7.82641 10.4772 7.91194 10.4999 7.99898 10.4999C8.08601 10.4999 8.17154 10.4772 8.2471 10.434L14.4059 6.91497C14.4154 6.90948 14.4262 6.90659 14.4372 6.9066C14.4482 6.90661 14.4589 6.90952 14.4685 6.91503C14.478 6.92055 14.4859 6.92847 14.4913 6.93801C14.4968 6.94755 14.4996 6.95836 14.4996 6.96935V11.4859C14.4996 11.755 14.7065 11.9859 14.9755 11.9993C15.0431 12.0026 15.1107 11.9921 15.1741 11.9685C15.2376 11.9449 15.2955 11.9086 15.3445 11.8619C15.3935 11.8152 15.4325 11.7591 15.4592 11.6968C15.4858 11.6346 15.4996 11.5677 15.4996 11.5V5.99997C15.4996 5.98432 15.4987 5.96867 15.4971 5.9531Z" fill="white"/></svg>';
            for (var i = 0; i < capacity.landBase.location.nearby.school.length; i++) {
                if (capacity.landBase.location.nearby.school[i]?.coordinates) {
                    var url = "https://www.google.com/maps/search/?api=1&query=" + capacity.landBase.location.nearby.school[i]?.coordinates[0] + "%2C" + capacity.landBase.location.nearby.school[i]?.coordinates[1] + "&query_place_id=" + capacity.landBase.location.nearby.school[i]?.place_id + "&hl=fr";
                    var distance = Math.round(turf.distance(capacity.landBase.union.center, turf.point(capacity.landBase.location.nearby.school[i]?.coordinates)) * 1000);
                    var el = document.createElement('div');
                    el.className = 'marker';
                    el.innerHTML = '<div style="cursor:pointer;height:28px;width:28px;background-color:#062134;border:solid 2px #FFF;border-radius:28px;display:flex;justify-content:center;align-items:center">' + img + '</div>';
                    // el.innerHTML = '<div style="padding:6px 8px 2px 8px;background-color:#062134;border-radius:6px;display:flex;justify-content:center;gap:4px"><div>' + img + '</div><div style="font-family:Manrope;color:#FFF;">BUS</div></div>';
                    isodistance_markers.push(new mapboxgl.Marker(el)
                        .setLngLat(capacity.landBase.location.nearby.school[i]?.coordinates)
                        .setPopup(new mapboxgl.Popup({ offset: 15 })
                            .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Etablissement scolaire</strong></span><br><span style="font-size:12px;font-weight: 600;color: grey;">Nom :</span><br><span style="color:#062134;font-weight:500;">' + capacity.landBase.location.nearby.school[i]?.name + '</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Distance du terrain :</span><br><span style="color:#062134;font-weight:500;">' + distance + ' m</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Détails :</span><br><span style="background-color:#1ecd97;color:#FFF;font-weight:500;font-size:11px;padding:0px 6px;border-radius:8px"><a href=' + url + ' target="_blank">Ouvrir dans Google Maps</a></span></p>'))
                        .addTo(map.current))
                }
            }
        }
        // GROCERY markers
        if (capacity?.landBase?.location?.nearby?.grocery && capacity?.landBase?.location?.nearby?.grocery.length > 0) {
            var img = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.0322 0.217138C14.0322 0.217138 12.4288 0.169628 11.7889 0.793715C11.1737 1.39359 10.9196 1.85196 10.9196 3.10002H0.194336L1.80244 8.65182C1.80648 8.66024 1.81383 8.6683 1.81821 8.6766C2.08851 9.43457 2.79507 9.98047 3.63803 10.0122C3.64149 10.0137 3.6538 10.0189 3.6538 10.0189H11.1493V10.2487C11.1493 10.2487 11.1502 10.596 10.9196 11.0572C10.6889 11.5185 10.5741 11.7487 9.65154 11.7487H2.15605C1.00289 11.7487 1.00289 13.4784 2.15605 13.4784H3.53893H3.6538H9.3047H9.41956H9.53668C10.9205 13.4784 11.9565 12.6707 12.3024 11.8635C12.6484 11.0563 12.6493 10.3658 12.6493 10.3658V10.0189V3.10002C12.6493 2.49623 12.9129 1.94687 13.4556 1.94687H14.0907C14.5684 1.94687 14.9556 1.55964 14.9556 1.082C14.9556 0.604367 14.5684 0.217138 14.0907 0.217138H14.0322ZM9.41956 13.4784C8.72767 13.4784 8.26641 13.9397 8.26641 14.6316C8.26641 15.3234 8.72767 15.7847 9.41956 15.7847C10.1115 15.7847 10.5727 15.3234 10.5727 14.6316C10.5727 13.9397 10.1115 13.4784 9.41956 13.4784ZM3.6538 13.4784C2.9619 13.4784 2.50064 13.9397 2.50064 14.6316C2.50064 15.3234 2.9619 15.7847 3.6538 15.7847C4.34569 15.7847 4.80695 15.3234 4.80695 14.6316C4.80695 13.9397 4.34569 13.4784 3.6538 13.4784Z" fill="white"/></svg>';
            for (var i = 0; i < capacity.landBase.location.nearby.grocery.length; i++) {
                if (capacity.landBase.location.nearby.grocery[i]?.coordinates) {
                    var url = "https://www.google.com/maps/search/?api=1&query=" + capacity.landBase.location.nearby.grocery[i]?.coordinates[0] + "%2C" + capacity.landBase.location.nearby.grocery[i]?.coordinates[1] + "&query_place_id=" + capacity.landBase.location.nearby.grocery[i]?.place_id + "&hl=fr";
                    var distance = Math.round(turf.distance(capacity.landBase.union.center, turf.point(capacity.landBase.location.nearby.grocery[i]?.coordinates)) * 1000);
                    var el = document.createElement('div');
                    el.className = 'marker';
                    el.innerHTML = '<div style="cursor:pointer;height:28px;width:28px;background-color:#062134;border:solid 2px #FFF;border-radius:28px;display:flex;justify-content:center;align-items:center">' + img + '</div>';
                    // el.innerHTML = '<div style="padding:6px 8px 2px 8px;background-color:#062134;border-radius:6px;display:flex;justify-content:center;gap:4px"><div>' + img + '</div><div style="font-family:Manrope;color:#FFF;">BUS</div></div>';
                    isodistance_markers.push(new mapboxgl.Marker(el)
                        .setLngLat(capacity.landBase.location.nearby.grocery[i]?.coordinates)
                        .setPopup(new mapboxgl.Popup({ offset: 15 })
                            .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Magasin d\'alimentation</strong></span><br><span style="font-size:12px;font-weight: 600;color: grey;">Nom :</span><br><span style="color:#062134;font-weight:500;">' + capacity.landBase.location.nearby.grocery[i]?.name + '</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Distance du terrain :</span><br><span style="color:#062134;font-weight:500;">' + distance + ' m</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Détails :</span><br><span style="background-color:#1ecd97;color:#FFF;font-weight:500;font-size:11px;padding:0px 6px;border-radius:8px"><a href=' + url + ' target="_blank">Ouvrir dans Google Maps</a></span></p>'))
                        .addTo(map.current))
                }
            }
        }
        // PARK markers
        if (capacity?.landBase?.location?.nearby?.park && capacity?.landBase?.location?.nearby?.park.length > 0) {
            var img = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_621_29)"><path d="M15.4782 14.9567H14.6518L13.0947 13.3914L14.6518 11.8262H15.4782C15.7663 11.8262 16 11.5927 16 11.3045C16 11.0162 15.7663 10.7827 15.4782 10.7827C11.95 10.7827 9.91928 10.7827 9.21737 10.7827C8.92916 10.7827 8.69563 11.0162 8.69563 11.3045C8.69563 11.5927 8.92916 11.8262 9.21737 11.8262H10.0656L11.6227 13.3914L10.0656 14.9567H7.30434V11.4479C6.75931 11.3863 6.23103 11.2295 5.73912 10.9814C5.24722 11.2295 4.71894 11.3863 4.17391 11.4479V14.9567H0.52175C0.233531 14.9567 0 15.1902 0 15.4784C0 15.7666 0.233531 16.0001 0.52175 16.0001C13.5228 16.0001 2.35347 16.0001 15.4783 16.0001C15.7663 16.0001 16 15.7666 16 15.4784C16 15.1902 15.7663 14.9567 15.4782 14.9567ZM11.5377 11.8262H13.1797L12.3587 12.6516L11.5377 11.8262ZM11.5377 14.9567L12.3587 14.1313L13.1797 14.9567H11.5377Z" fill="white"/><path d="M9.38791 3.49506C9.30534 1.55384 7.70041 0 5.73912 0C3.7815 0 2.17934 1.54825 2.09134 3.48447C0.807344 4.09231 0 5.42609 0 6.78259C0 9.72197 3.32403 11.4702 5.73912 9.76731C8.15584 11.4714 11.4782 9.72034 11.4782 6.78259C11.4782 5.37025 10.6527 4.09425 9.38791 3.49506Z" fill="white"/></g><defs><clipPath id="clip0_621_29"><rect width="16" height="16" fill="white"/></clipPath></defs></svg>';
            for (var i = 0; i < capacity.landBase.location.nearby.park.length; i++) {
                if (capacity.landBase.location.nearby.park[i]?.coordinates) {
                    var url = "https://www.google.com/maps/search/?api=1&query=" + capacity.landBase.location.nearby.park[i]?.coordinates[0] + "%2C" + capacity.landBase.location.nearby.park[i]?.coordinates[1] + "&query_place_id=" + capacity.landBase.location.nearby.park[i]?.place_id + "&hl=fr";
                    var distance = Math.round(turf.distance(capacity.landBase.union.center, turf.point(capacity.landBase.location.nearby.park[i]?.coordinates)) * 1000);
                    var el = document.createElement('div');
                    el.className = 'marker';
                    el.innerHTML = '<div style="cursor:pointer;height:28px;width:28px;background-color:#062134;border:solid 2px #FFF;border-radius:28px;display:flex;justify-content:center;align-items:center">' + img + '</div>';
                    // el.innerHTML = '<div style="padding:6px 8px 2px 8px;background-color:#062134;border-radius:6px;display:flex;justify-content:center;gap:4px"><div>' + img + '</div><div style="font-family:Manrope;color:#FFF;">BUS</div></div>';
                    isodistance_markers.push(new mapboxgl.Marker(el)
                        .setLngLat(capacity.landBase.location.nearby.park[i]?.coordinates)
                        .setPopup(new mapboxgl.Popup({ offset: 15 })
                            .setHTML('<p style="font-family:Manrope"><span style="color:#062134;font-size:15px;font-weight:700"><strong>Parc</strong></span><br><span style="font-size:12px;font-weight: 600;color: grey;">Nom :</span><br><span style="color:#062134;font-weight:500;">' + capacity.landBase.location.nearby.park[i]?.name + '</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Distance du terrain :</span><br><span style="color:#062134;font-weight:500;">' + distance + ' m</span><br><span style="font-size:12px;font-weight: 600;color: grey;">Détails :</span><br><span style="background-color:#1ecd97;color:#FFF;font-weight:500;font-size:11px;padding:0px 6px;border-radius:8px"><a href=' + url + ' target="_blank">Ouvrir dans Google Maps</a></span></p>'))
                        .addTo(map.current))
                }
            }
        }
        // Add all markers
        setIsodistanceMarkers(isodistance_markers);
        // Fit view
        flyTo_FitBbox(bbox.geometry.coordinates, 0.0003);

    }

    // ISODISTANCE : HIDE
    const contextIsodistance_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerContextIsodistance_Polys')) {
            map.current.removeLayer('layerContextIsodistance_Polys');
        }
        if (map.current.getLayer('layerContextIsodistance_Lines')) {
            map.current.removeLayer('layerContextIsodistance_Lines');
        }
        // Remove source
        if (map.current.getSource('sourceContextIsodistance')) {
            map.current.removeSource('sourceContextIsodistance');
        }

        // Remove markers
        for (var i = 0; i < isodistanceMarkers.length; i++) {
            isodistanceMarkers[i].remove();
        }

        // Fit view to union
        if (capacity?.landBase?.union) {
            // if (!map.current.getLayer('layerRisksBasias_Buffer')) {
            flyTo_FitBbox(capacity.landBase.union.bbox.coordinates, 0.00006);
            // }
        }
    }



    // _____ DVF

    // DVF : Hide
    // ISODISTANCE : HIDE
    const contextDVF_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerContextDVF_Points')) {
            map.current.removeLayer('layerContextDVF_Points');
        }
        if (map.current.getLayer('layerContextDVF_Clusters')) {
            map.current.removeLayer('layerContextDVF_Clusters');
        }
        if (map.current.getLayer('layerContextDVF_ClusterLabels')) {
            map.current.removeLayer('layerContextDVF_ClusterLabels');
        }
        // Remove source
        if (map.current.getSource('sourceContextDVF')) {
            map.current.removeSource('sourceContextDVF');
        }

        // Remove markers
        for (var i = 0; i < dvfMarkers.length; i++) {
            dvfMarkers[i].remove();
        }

        // Remove popup
        if (dvf_popup !== null) {
            dvf_popup.remove();
            dvf_popup = null;
        }
    }

    // DVF : Show
    const contextDVF_Show = () => {
        // Remove
        contextDVF_Hide();

        // CLUSTER VERSION

        // Create source
        var data = {
            type: 'FeatureCollection',
            features: []
        }
        var transactions_grouped_list = {};
        Object.keys(capacity.context.dvf.dvf_transactions).forEach(id_transaction => {
            if (capacity.context.dvf.dvf_transactions[id_transaction].coordinates.length > 0 && (capacity.context.dvf.dvf_transactions[id_transaction].coordinates[0][0] !== null && capacity.context.dvf.dvf_transactions[id_transaction].coordinates[0][1] !== null)) {
                var coords = capacity.context.dvf.dvf_transactions[id_transaction].coordinates[0][0].toFixed(5).toString() + "," + capacity.context.dvf.dvf_transactions[id_transaction].coordinates[0][1].toFixed(5).toString();
                if (!(coords in transactions_grouped_list)) {
                    transactions_grouped_list[coords] = {
                        transactions: []
                    };
                }
                transactions_grouped_list[coords].transactions.push(capacity.context.dvf.dvf_transactions[id_transaction]);

                // data.features.push(turf.point(capacity.context.dvf.dvf_transactions[id_transaction].coordinates[0], { id_transaction: id_transaction }));
            }
        });

        Object.keys(transactions_grouped_list).forEach(transaction_coords => {
            data.features.push(turf.point(transactions_grouped_list[transaction_coords].transactions[0].coordinates[0], { transactions: transactions_grouped_list[transaction_coords].transactions, nb: transactions_grouped_list[transaction_coords].transactions.length }));
        });

        // Add a new source from our GeoJSON data and
        // set the 'cluster' option to true. GL-JS will
        // add the point_count property to your source data.
        map.current.addSource('sourceContextDVF', {
            type: 'geojson',
            data: data,
            cluster: true,
            clusterMaxZoom: 18, // Max zoom to cluster points on
            clusterRadius: 30 // Radius of each cluster when clustering points (defaults to 50)
        });

        map.current.addLayer({
            id: 'layerContextDVF_Clusters',
            type: 'circle',
            source: 'sourceContextDVF',
            filter: ['has', 'point_count'],
            paint: {
                // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
                // with three steps to implement three types of circles:
                //   * Blue, 20px circles when point count is less than 100
                //   * Yellow, 30px circles when point count is between 100 and 750
                //   * Pink, 40px circles when point count is greater than or equal to 750
                // 'circle-color': [
                //     'step',
                //     ['get', 'point_count'],
                //     '#51bbd6',
                //     100,
                //     '#f1f075',
                //     750,
                //     '#f28cb1'
                // ],
                'circle-color': '#062134',
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    20,
                    20,
                    24,
                    30,
                    26,
                    40,
                    28,
                    50,
                    30,
                    60,
                    40
                ],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
            }
        }, layer_get_position('layerContextDVF_Clusters'));

        map.current.addLayer({
            id: 'layerContextDVF_ClusterLabels',
            type: 'symbol',
            source: 'sourceContextDVF',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
            paint: {
                'text-color': '#FFF'
            }
        }, layer_get_position('layerContextDVF_ClusterLabels'));

        map.current.addLayer({
            id: 'layerContextDVF_Points',
            type: 'circle',
            source: 'sourceContextDVF',
            filter: ['!', ['has', 'point_count']],
            paint: {
                'circle-color': '#062134',
                'circle-radius': 6,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
            }
        }, layer_get_position('layerContextDVF_Points'));

























        // var dvf_markers = [];

        // // Create source
        // var data = {
        //     type: 'FeatureCollection',
        //     features: []
        // }
        // for (var i = 0; i < capacity.context.dvf.dvf_data.length; i++) {
        //     if (capacity.context.dvf.dvf_data[i].coordinates[0] !== null && capacity.context.dvf.dvf_data[i].coordinates[1] !== null) {
        //         data.features.push(turf.point(capacity.context.dvf.dvf_data[i].coordinates));
        //     }
        // }

        // // Add source
        // map.current.addSource('sourceContextDVF', {
        //     type: 'geojson',
        //     data: data,
        //     cluster: true,
        //     clusterMaxZoom: 16, // Max zoom to cluster points on
        //     clusterRadius: 50 // Radius of each cluster when clustering points (defaults to 50)
        // });

        // // Add layers
        // map.current.addLayer({
        //     id: 'layerContextDVF_Points',
        //     type: 'circle',
        //     source: 'sourceContextDVF',
        //     // filter: ['!', ['has', 'point_count']],
        //     paint: {
        //         'circle-color': '#062134',
        //         'circle-radius': 4,
        //         'circle-stroke-width': 1,
        //         'circle-stroke-color': '#fff'
        //     }
        // });

        // // Add markers
        // for (var i = 0; i < capacity.context.dvf.dvf_data.length; i++) {
        //     var color = "#062134";
        //     var el = document.createElement('div');
        //     var land_section = capacity.context.dvf.dvf_data[i].id_parcelle.substring(8, 10);
        //     if (land_section.substring(0, 1) === "0") { land_section = land_section.substring(1) };
        //     var land_numero = parseInt(capacity.context.dvf.dvf_data[i].id_parcelle.substring(10, capacity.context.dvf.dvf_data[i].id_parcelle.length));
        //     var land_label = "Parcelle " + land_section + "-" + land_numero.toString();
        //     var label = capacity.context.dvf.dvf_data[i].mutations.length + " transaction";
        //     if (capacity.context.dvf.dvf_data[i].mutations.length > 1) { label = capacity.context.dvf.dvf_data[i].mutations.length + " transactions" };

        //     var mutations = '';
        //     for (var j = 0; j < capacity.context.dvf.dvf_data[i].mutations.length; j++) {
        //         var title = capacity.context.dvf.dvf_data[i].mutations[j].nature_mutation;
        //         if (capacity.context.dvf.dvf_data[i].mutations[j].type_local !== "None") {
        //             title = title + " • " + capacity.context.dvf.dvf_data[i].mutations[j].type_local;
        //         }
        //         var description = "";
        //         if (!isNaN(parseInt(capacity.context.dvf.dvf_data[i].mutations[j].nombre_pieces_principales)) && parseInt(capacity.context.dvf.dvf_data[i].mutations[j].nombre_pieces_principales) > 0) {
        //             description = description + '<div><div style="font-size:12px;line-height:13px;color:grey;">Nombre de pièces :</div><div>' + parseInt(capacity.context.dvf.dvf_data[i].mutations[j].nombre_pieces_principales) + '</div></div>';
        //         }
        //         if (!isNaN(parseFloat(capacity.context.dvf.dvf_data[i].mutations[j].surface_reelle_bati))) {
        //             description = description + '<div><div style="font-size:12px;line-height:13px;color:grey;">Surface de la construction :</div><div>' + parseFloat(capacity.context.dvf.dvf_data[i].mutations[j].surface_reelle_bati).toFixed(2) + ' m²</div></div>';
        //         }
        //         if (!isNaN(parseFloat(capacity.context.dvf.dvf_data[i].mutations[j].surface_terrain))) {
        //             description = description + '<div><div style="font-size:12px;line-height:13px;color:grey;">Surface du terrain :</div><div>' + parseFloat(capacity.context.dvf.dvf_data[i].mutations[j].surface_terrain).toFixed(2) + ' m²</div></div>';
        //         }
        //         if (!isNaN(parseFloat(capacity.context.dvf.dvf_data[i].mutations[j].valeur_fonciere))) {
        //             description = description + '<div><div style="font-size:12px;line-height:13px;color:grey;">Montant de la transaction :</div><div>' + helpers.get_pretty_num(parseFloat(capacity.context.dvf.dvf_data[i].mutations[j].valeur_fonciere).toFixed(2)) + ' €</div></div>';
        //         }
        //         mutations = mutations + '<div style="margin-top:6px;border-top: 1px solid rgb(211, 211, 211); font-family:Manrope; color:#062134"><div style="margin-top:4px;font-size:12px;line-height:13px;font-style: italic;">' + (capacity.context.dvf.dvf_data[i].mutations[j].date_mutation).split("-").reverse().join("/") + '</div><div style="color:#062134;font-size:14px;line-height:16px;font-weight:600">' + title + '</div>' + description + '<div>ID : ' + capacity.context.dvf.dvf_data[i].mutations[j].id_mutation + '</div></div>';
        //     }

        //     el.className = 'marker';
        //     el.innerHTML = '<div style="cursor:pointer;padding:4px 8px;font-family:Manrope;color:#FFF;background-color:' + color + ';border-radius:6px"><span style="">' + label + '</span></div>';
        //     dvf_markers.push(new mapboxgl.Marker(el)
        //         .setLngLat(capacity.context.dvf.dvf_data[i].coordinates)
        //         .setPopup(new mapboxgl.Popup({ offset: 15 })
        //             .setHTML('<div style="font-family:Manrope; color:#062134; margin-bottom: 4px"><span style="font-size:15px;font-weight:700"><strong>' + land_label + '</strong></span><br>' + label + ' :<br></div><div class="scroll_custom" style="max-height:200px;overflow-y:auto">' + mutations + '</div>'))
        //         .addTo(map.current))
        // }
        // setDvfMarkers(dvf_markers);
    }

    // DVF : Set Interactions
    const contextDVF_SetInteractions = () => {
        map.current.on('mouseenter', 'layerContextDVF_Clusters', () => { map.current.getCanvas().style.cursor = 'pointer' });
        map.current.on('mouseleave', 'layerContextDVF_Clusters', () => { map.current.getCanvas().style.cursor = '' });
        map.current.on('click', 'layerContextDVF_Clusters', (e) => { contextDVF_Click_Cluster(e) });
        map.current.on('mouseenter', 'layerContextDVF_Points', () => { map.current.getCanvas().style.cursor = 'pointer' });
        map.current.on('mouseleave', 'layerContextDVF_Points', () => { map.current.getCanvas().style.cursor = '' });
        map.current.on('click', 'layerContextDVF_Points', (e) => { contextDVF_Click_Point(e) });
    }

    // DVF : Cluster click
    const contextDVF_Click_Cluster = (e) => {
        const features = map.current.queryRenderedFeatures(e.point, {
            layers: ['layerContextDVF_Clusters']
        });
        const clusterId = features[0].properties.cluster_id;
        map.current.getSource('sourceContextDVF').getClusterExpansionZoom(
            clusterId,
            (err, zoom) => {
                if (err) return;

                map.current.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom
                });
            }
        );
    }

    // DVF : Point click
    const contextDVF_Click_Point = (e) => {
        // console.log("CLICKED", e.features[0]);
        var transactions = JSON.parse(e.features[0].properties.transactions);
        // console.log("TRANSACTIONS", transactions);
        const coordinates = e.features[0].geometry.coordinates.slice();
        var popup_label = e.features[0].properties.nb.toString() + " transactions";
        if (e.features[0].properties.nb === 1) {
            popup_label = "1 transaction";
        }
        var popup_transactions = '';
        transactions.forEach(transaction => {
            var type = transaction.nature;
            var value = helpers.get_pretty_num(transaction.value);
            var date = (transaction.date).split("-").reverse().join("/");
            var local_list = '<div style="margin-bottom:2px">';
            transaction.local.forEach(local => {
                // if (local_list !== "") {
                //     local_list = local_list + "<br>";
                // }
                local_list = local_list + '<div style="font-size:13px;line-height:14px;margin-bottom:2px;">- ' + local.type;
                if (local.rooms !== null && local.rooms > 0) {
                    local_list = local_list + " (" + local.rooms + "p)";
                }
                if (local.area !== null && local.area > 0) {
                    local_list = local_list + " de " + local.area + " m²";
                }
                local_list = local_list + '</div>';
            });
            if (transaction.land_area > 0) {
                // if (local_list !== "") {
                //     local_list = local_list + "<br>";
                // }
                local_list = local_list + '<div style="font-size:13px;line-height:14px;margin-bottom:2px;">- Terrain de ' + transaction.land_area + ' m²</div>';
            }
            local_list = local_list + '</div>';

            popup_transactions = popup_transactions + '<div style="margin-top:12px;border-top: 1px solid rgb(211, 211, 211); font-family:Manrope; color:#062134"><div style="color:grey;margin-top:4px;margin-bottom:4px;font-size:12px;line-height:12px;">' + date + '</div><div style="color:#062134;font-size:16px;line-height:16px;font-weight:600">' + type + '</div><div style="color:#062134;font-size:14px;line-height:16px;font-weight:600;margin-bottom:4px;">' + value + ' €</div>' + local_list + '</div>';
        });
        dvf_popup = new mapboxgl.Popup({ offset: 10 })
            .setLngLat(coordinates)
            .setHTML('<div style="font-family:Manrope; color:#062134;"><span style="font-size:15px;font-weight:700"><strong>' + popup_label + '</strong></span></div><div class="scroll_custom" style="max-height:200px;overflow-y:auto">' + popup_transactions + '</div>')
            .addTo(map.current);
    }

    // _____ COMMUNITIES

    const contextCommoditiesCarPark_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_car_park.get_ids());
    }
    const contextCommoditiesCarPark_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_car_park.get_data(capacity));
    }

    const contextCommoditiesCarCharging_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_charging.get_ids());
    }
    const contextCommoditiesCarCharging_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_charging.get_data(capacity));
    }

    const contextCommoditiesTransport_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_transport.get_ids());
    }
    const contextCommoditiesTransport_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_transport.get_data(capacity));
    }

    const contextCommoditiesTrain_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_train.get_ids());
    }
    const contextCommoditiesTrain_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_train.get_data(capacity));
    }

    const contextCommoditiesBike_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_bike.get_ids());
    }
    const contextCommoditiesBike_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_bike.get_data(capacity));
    }

    const contextCommoditiesHospital_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_hospital.get_ids());
    }
    const contextCommoditiesHospital_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_hospital.get_data(capacity));
    }

    const contextCommoditiesDoctor_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_doctor.get_ids());
    }
    const contextCommoditiesDoctor_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_doctor.get_data(capacity));
    }

    const contextCommoditiesPharmacy_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_pharmacy.get_ids());
    }
    const contextCommoditiesPharmacy_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_pharmacy.get_data(capacity));
    }

    const contextCommoditiesBaby_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_edu_baby.get_ids());
    }
    const contextCommoditiesBaby_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_edu_baby.get_data(capacity));
    }

    const contextCommoditiesPrimarySchool_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_edu_prim.get_ids());
    }
    const contextCommoditiesPrimarySchool_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_edu_prim.get_data(capacity));
    }

    const contextCommoditiesSecondarySchool_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_edu_sec.get_ids());
    }
    const contextCommoditiesSecondarySchool_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_edu_sec.get_data(capacity));
    }

    const contextCommoditiesUniversity_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_edu_sup.get_ids());
    }
    const contextCommoditiesUniversity_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_edu_sup.get_data(capacity));
    }

    const contextCommoditiesMall_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_mall.get_ids());
    }
    const contextCommoditiesMall_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_mall.get_data(capacity));
    }

    const contextCommoditiesSupermarket_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_supermarket.get_ids());
    }
    const contextCommoditiesSupermarket_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_supermarket.get_data(capacity));
    }

    const contextCommoditiesParc_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_parc.get_ids());
    }
    const contextCommoditiesParc_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_parc.get_data(capacity));
    }

    const contextCommoditiesRestaurant_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_restaurant.get_ids());
    }
    const contextCommoditiesRestaurant_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_restaurant.get_data(capacity));
    }

    const contextCommoditiesCulture_Hide = () => {
        map_remove_sources_and_layers(map_helpers_commodities_culture.get_ids());
    }
    const contextCommoditiesCulture_Show = () => {
        map_add_sources_and_layers(map_helpers_commodities_culture.get_data(capacity));
    }



    // Zones Urba
    const zonesUrba_Hide = () => {
        // Remove layers
        if (map.current.getLayer('layerZonesUrbaPoly')) {
            map.current.removeLayer('layerZonesUrbaPoly');
        }
        if (map.current.getLayer('layerZonesUrbaLabel')) {
            map.current.removeLayer('layerZonesUrbaLabel');
        }
        // Remove source
        if (map.current.getSource('sourceZonesUrbaPoly')) {
            map.current.removeSource('sourceZonesUrbaPoly');
        }
    }

    const zonesUrba_Show = () => {
        if (capacity?.rules?.gpu_data?.document?.title) {
            // Add source
            map.current.addSource('sourceZonesUrbaPoly', {
                'type': 'raster',
                // use the tiles option to specify a WMS tile source URL
                // https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/
                'tiles': [
                    // 'https://ws.carmencarto.fr/WMS/168/zonage_sismique?LAYERS=Zonage_sismique_Communes&TRANSPARENT=TRUE&SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&STYLES=&FORMAT=image%2Fpng&SRS=EPSG%3A3857&bbox={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'
                    // 'https://mapsref.brgm.fr/wxs/geoservices/risques?LAYERS=ALEARG_REALISE&TRANSPARENT=TRUE&SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&STYLES=&FORMAT=image%2Fpng&SRS=EPSG%3A3857&bbox={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'
                    //'https://wxs-gpu.mongeoportail.ign.fr/externe/i9ytmrb6tgtq5yfek781ntqi/wms/v?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng&TRANSPARENT=true&transparent=true&version=1.1.1&format=image%2Fpng&LAYERS=zone_secteur&WIDTH=256&HEIGHT=256&CRS=EPSG%3A3857&STYLES=&BBOX={bbox-epsg-3857}'
                    'https://data.geopf.fr/wms-v/ows?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng&TRANSPARENT=true&transparent=true&version=1.1.1&format=image%2Fpng&tiled=true&LAYERS=du%2Cpsmv&WIDTH=512&HEIGHT=512&CRS=EPSG%3A3857&STYLES=&BBOX={bbox-epsg-3857}'
                ],
                'tileSize': 512
            });
            // Add layer
            map.current.addLayer(
                {
                    'id': 'layerZonesUrbaPoly',
                    'type': 'raster',
                    'source': 'sourceZonesUrbaPoly',
                    'paint': {
                        'raster-opacity': 0.6
                    }
                },
                layer_get_position('layerZonesUrbaPoly')
            );
            // Set zoom
            map.current.flyTo({
                zoom: 13.5,
                essential: true
            });


            // // Fetch land polygons
            // fetch("https://apicarto.ign.fr/api/gpu/zone-urba?partition=" + capacity?.rules?.gpu_data?.document?.name)
            //     .then(res => res.json())
            //     .then(
            //         (result) => {
            //             console.log("-FETCHING URBA ZONES- : Success", result);
            //             // Remove Layer and Source if already loaded
            //             zonesUrba_Hide();
            //             // Add source to map
            //             map.current.addSource('sourceZonesUrbaPoly', {
            //                 'type': 'geojson',
            //                 'data': result,
            //                 'generateId': true
            //             });
            //             // Add layers to map
            //             map.current.addLayer({
            //                 'id': 'layerZonesUrbaPoly',
            //                 'type': 'fill',
            //                 'source': 'sourceZonesUrbaPoly',
            //                 'layout': {
            //                     'visibility': 'visible'
            //                 },
            //                 'paint': {
            //                     'fill-color': [
            //                         'case',
            //                         ['==', ['get', 'typezone'], 'U'],
            //                         '#E60000',
            //                         ['==', ['get', 'typezone'], 'N'],
            //                         '#56AA02',
            //                         ['==', ['get', 'typezone'], 'A'],
            //                         '#FFFF00',
            //                         ['==', ['get', 'typezone'], 'AUc'],
            //                         '#FF6565',
            //                         ['==', ['get', 'typezone'], 'AUs'],
            //                         '#FECCBE',
            //                         '#888888'
            //                     ],
            //                     // 'fill-color': '#888888',
            //                     'fill-opacity': 0.4,
            //                     'fill-outline-color': 'rgba(6, 33, 52, 1)'
            //                 }
            //             }, 'building-extrusion');
            //             // Add labels layer
            //             map.current.addLayer({
            //                 'id': 'layerZonesUrbaLabel',
            //                 'type': 'symbol',
            //                 'source': 'sourceZonesUrbaPoly',
            //                 'layout': {
            //                     'text-field': ['get', 'libelle'],
            //                     'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
            //                     'text-radial-offset': 0.5,
            //                     'text-justify': 'auto',
            //                     'text-size': 12,
            //                 },
            //                 paint: {
            //                     "text-color": "#062134"
            //                 }
            //             });

            //             // Set zoom
            //             map.current.flyTo({
            //                 zoom: 12.3,
            //                 essential: true
            //             });
            //         },
            //         (error) => {
            //             console.log("Error fetching zones urba", error);
            //         }
            //     )
        }
    }

    // RULES

    useEffect(() => {
        if (ruleUpdateTracker !== 0 && layerRules === true) {
            // console.log("!!!!!! OFFICIAL RULE CHANGE MAP", layerRules);
            // rules_Show();
        }
    }, [ruleUpdateTracker]);

    useEffect(() => {
        if (layerRules === true) {
            // console.log("!!!!!! OFFICIAL HIGHLIGHTED RULE CHANGE MAP", highlightedRule);
            rules_Show();
        }
    }, [highlightedRule]);

    useEffect(() => {
        // console.log("!!!!!! OFFICIAL HIDDEN RULE CHANGE MAP", hiddenRule);
        if (layerRules === true) {
            rules_Show();
        }
    }, [hiddenRule]);

    const rules_update = () => {
        rules_Show();
    }

    const rules_Hide = () => {
        if (map.current.getLayer('layerRules')) {
            map.current.removeLayer('layerRules');
        }
    }

    const rules_Show = () => {
        // const loader = new THREE.ObjectLoader();
        // Check if data already loaded
        rules_Hide();
        // Set point 0
        // var point0 = capacity.landBase.union.bbox.origin;
        var point0 = capacity.landBase.union.center.geometry.coordinates;
        // Georeferencing
        var modelOrigin = point0;
        var modelAltitude = 0;
        var modelRotate = [0, 0, 0];
        var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
            modelOrigin,
            modelAltitude
        );
        // Set position, rotation and scale
        var modelTransform = {
            translateX: modelAsMercatorCoordinate.x,
            translateY: modelAsMercatorCoordinate.y,
            translateZ: modelAsMercatorCoordinate.z,
            rotateX: modelRotate[0],
            rotateY: modelRotate[1],
            rotateZ: modelRotate[2],
            scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
        };
        // Set THREE
        // var THREE = window.THREE;
        // Create the custom layer
        var customLayer = {
            id: 'layerRules',
            type: 'custom',
            renderingMode: '3d',
            onAdd: function (map, gl) {
                this.camera = new THREE.Camera();
                this.scene = new THREE.Scene();

                // Create lights
                // var directionalLight1 = new THREE.DirectionalLight(0xffffff);
                // directionalLight1.position.set(0, -70, 100).normalize();
                // this.scene.add(directionalLight1);
                // var directionalLight2 = new THREE.DirectionalLight(0xffffff);
                // directionalLight2.position.set(0, 70, 100).normalize();
                // this.scene.add(directionalLight2);
                // var ambientLight = new THREE.AmbientLight(0xffffff);
                // this.scene.add(ambientLight);
                var directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.4);
                directionalLight1.position.set(-10, -10, 20).normalize();
                this.scene.add(directionalLight1);
                var directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
                directionalLight2.position.set((capacity.landBase.union.bbox.lengthH + 10), -10, 20).normalize();
                this.scene.add(directionalLight2);
                var directionalLight3 = new THREE.DirectionalLight(0xffffff, 0.4);
                directionalLight3.position.set((capacity.landBase.union.bbox.lengthH / 2), (capacity.landBase.union.bbox.lengthV + 10), 20).normalize();
                this.scene.add(directionalLight3);
                var ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
                this.scene.add(ambientLight);

                // Get rules
                var rules = [];
                capacity.rules.ruleset.forEach(ruleset => {
                    ruleset.items.forEach(item => {
                        if (item.type === "rule") {
                            var ruleModel = { ...item };
                            // ruleModel.interpreted = node_helpers.nodeInterpretor(item.data.composition.nodes[0].data.source, capacity);
                            rules.push(ruleModel);
                        }
                    })
                })
                // console.log("rules", rules);

                // Add objects to map
                rules.forEach(rule => {
                    if (rule?.interpreted) {
                        // Parse rule
                        rule.interpreted.forEach(element => {
                            if (element?.object) {
                                // JSON Object => bugged with current ThreeJS version
                            }
                            else if (element?.type === "Mesh" || element?.type === "LineSegments") {
                                if (highlightedRule === null || highlightedRule === "all") {
                                    element.material.opacity = 0.6;
                                }
                                else if (highlightedRule === rule.id) {
                                    element.material.opacity = 0.7;
                                }
                                else {
                                    element.material.opacity = 0.1;
                                }
                                if (hiddenRule === rule.id) {
                                    element.material.opacity = 0.02;
                                }
                                element.material.transparent = true;
                                element.material.depthWrite = false;
                                this.scene.add(element);
                            }
                        })
                    }
                })

                // Add map
                this.map = map;
                // use the Mapbox GL JS map canvas for three.js
                this.renderer = new THREE.WebGLRenderer({
                    canvas: map.getCanvas(),
                    context: gl,
                    antialias: true
                });
                this.renderer.autoClear = false;
            },
            render: function (gl, matrix) {
                var rotationX = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(1, 0, 0),
                    modelTransform.rotateX
                );
                var rotationY = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 1, 0),
                    modelTransform.rotateY
                );
                var rotationZ = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 0, 1),
                    modelTransform.rotateZ
                );
                var m = new THREE.Matrix4().fromArray(matrix);
                var l = new THREE.Matrix4()
                    .makeTranslation(
                        modelTransform.translateX,
                        modelTransform.translateY,
                        modelTransform.translateZ
                    )
                    .scale(
                        new THREE.Vector3(
                            modelTransform.scale,
                            -modelTransform.scale,
                            modelTransform.scale
                        )
                    )
                    .multiply(rotationX)
                    .multiply(rotationY)
                    .multiply(rotationZ);

                this.camera.projectionMatrix = m.multiply(l);
                this.renderer.resetState()
                this.renderer.render(this.scene, this.camera);
                // this.map.triggerRepaint();
            }
        };
        map.current.addLayer(customLayer, layer_get_position('layerRules'));
    }



    // BUILDABLE VOLUME

    useEffect(() => {
        if (volumeUpdateTracker !== 0 && layerBuildableVolume === true) {
            buildableVolume_Show();
        }
    }, [volumeUpdateTracker]);

    const buildableVolume_Hide = () => {
        if (map.current.getLayer('layerBuildableVolume')) {
            map.current.removeLayer('layerBuildableVolume');
        }
    }

    const buildableVolume_Show = () => {
        // Check if data already loaded
        buildableVolume_Hide();
        // Set point 0
        // var point0 = capacity.landBase.union.bbox.origin;
        var point0 = capacity.landBase.union.center.geometry.coordinates;
        // Georeferencing
        var modelOrigin = point0;
        var modelAltitude = 0;
        var modelRotate = [0, 0, 0];
        var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
            modelOrigin,
            modelAltitude
        );
        // Set position, rotation and scale
        var modelTransform = {
            translateX: modelAsMercatorCoordinate.x,
            translateY: modelAsMercatorCoordinate.y,
            translateZ: modelAsMercatorCoordinate.z,
            rotateX: modelRotate[0],
            rotateY: modelRotate[1],
            rotateZ: modelRotate[2],
            scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
        };
        // Set THREE
        // var THREE = window.THREE;
        // Create the custom layer
        var customLayer = {
            id: 'layerBuildableVolume',
            type: 'custom',
            renderingMode: '3d',
            onAdd: function (map, gl) {
                this.camera = new THREE.Camera();
                this.scene = new THREE.Scene();

                // Create lights
                // var directionalLight1 = new THREE.DirectionalLight(0xffffff);
                // directionalLight1.position.set(0, -70, 100).normalize();
                // this.scene.add(directionalLight1);
                // var directionalLight2 = new THREE.DirectionalLight(0xffffff);
                // directionalLight2.position.set(0, 70, 100).normalize();
                // this.scene.add(directionalLight2);
                // var ambientLight = new THREE.AmbientLight(0xffffff);
                // this.scene.add(ambientLight);
                var directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.4);
                directionalLight1.position.set(-10, -10, 20).normalize();
                this.scene.add(directionalLight1);
                var directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
                directionalLight2.position.set((capacity.landBase.union.bbox.lengthH + 10), -10, 20).normalize();
                this.scene.add(directionalLight2);
                var directionalLight3 = new THREE.DirectionalLight(0xffffff, 0.4);
                directionalLight3.position.set((capacity.landBase.union.bbox.lengthH / 2), (capacity.landBase.union.bbox.lengthV + 10), 20).normalize();
                this.scene.add(directionalLight3);
                var ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
                this.scene.add(ambientLight);

                // Get buildable volume objects
                var buildableVolumeObjects = capacity?.buildable?.volume?.interpreted;
                if (capacity?.buildable?.volume?.interpreted && capacity?.buildable?.volume?.interpreted.length > 0) {
                    buildableVolumeObjects.forEach(bvObject => {
                        if (bvObject?.type === "Mesh" || bvObject?.type === "LineSegments") {
                            this.scene.add(bvObject);
                        }
                    })
                }

                // Add map
                this.map = map;
                // use the Mapbox GL JS map canvas for three.js
                this.renderer = new THREE.WebGLRenderer({
                    canvas: map.getCanvas(),
                    context: gl,
                    antialias: true
                });
                this.renderer.autoClear = false;
            },
            render: function (gl, matrix) {
                var rotationX = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(1, 0, 0),
                    modelTransform.rotateX
                );
                var rotationY = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 1, 0),
                    modelTransform.rotateY
                );
                var rotationZ = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 0, 1),
                    modelTransform.rotateZ
                );
                var m = new THREE.Matrix4().fromArray(matrix);
                var l = new THREE.Matrix4()
                    .makeTranslation(
                        modelTransform.translateX,
                        modelTransform.translateY,
                        modelTransform.translateZ
                    )
                    .scale(
                        new THREE.Vector3(
                            modelTransform.scale,
                            -modelTransform.scale,
                            modelTransform.scale
                        )
                    )
                    .multiply(rotationX)
                    .multiply(rotationY)
                    .multiply(rotationZ);

                this.camera.projectionMatrix = m.multiply(l);
                this.renderer.resetState()
                this.renderer.render(this.scene, this.camera);
                // this.map.triggerRepaint();
            }
        };
        map.current.addLayer(customLayer, layer_get_position('layerBuildableVolume'));
    }


    // COMBINATIONS

    const combinationsSelected_Hide = () => {
        var source_layer_id = map_helpers_combinations.get_ids();
        map_remove_sources_and_layers(source_layer_id);
    }

    const combinationsSelected_Show = () => {
        if (highlightedCombination !== null && highlightedCombination.length > 0) {
            var source_layer_data = map_helpers_combinations.get_data(capacity, highlightedCombination);
            map_add_sources_and_layers(source_layer_data);
        }
    }

    useEffect(() => {
        // console.log("!!!!!! OFFICIAL HIGHLIGHTEDCOMBINATION CHANGE MAP", highlightedCombination);
        // if (highlightedCombination !== null && highlightedCombination.length > 0) {
        //     combination_Show(highlightedCombination);
        // }
        // else {
        //     combination_Hide();
        // }

        // Update 3D
        if (highlightedCombination !== null && highlightedCombination.length > 0) {
            combinationsSelected_Show();
            if (layerIGNBuildingsLand_keep === true) {
                // Update Existing 3D layer
                IGNBuildingsLandKeep_Show();
            }
        }

    }, [highlightedCombination]);


    const combination_Hide = () => {
        // Check if data already loaded
        if (map.current.getLayer('layer3Dbuilding')) {
            map.current.removeLayer('layer3Dbuilding');
        }
    }

    const combination_Show = (highlightedCombination) => {

        // var building_levels = capacity?.buildable?.combinations[0][highlightedCombination].mainLines_levels;
        // console.log("building_levels", building_levels);

        combination_Hide();

        // Set point 0
        // var point0 = capacity.landBase.union.bbox.origin;
        var point0 = capacity.landBase.union.center.geometry.coordinates;
        // Georeferencing
        var modelOrigin = point0;
        var modelAltitude = 0;
        var modelRotate = [0, 0, 0];
        var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
            modelOrigin,
            modelAltitude
        );
        // Set position, rotation and scale
        var modelTransform = {
            translateX: modelAsMercatorCoordinate.x,
            translateY: modelAsMercatorCoordinate.y,
            translateZ: modelAsMercatorCoordinate.z,
            rotateX: modelRotate[0],
            rotateY: modelRotate[1],
            rotateZ: modelRotate[2],
            scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
        };
        // Set THREE
        // var THREE = window.THREE;
        // Create the custom layer
        var customLayer = {
            id: 'layer3Dbuilding',
            type: 'custom',
            renderingMode: '3d',
            onAdd: function (map, gl) {
                this.camera = new THREE.Camera();
                this.scene = new THREE.Scene();
                // Create lights
                var directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
                directionalLight1.position.set(-10, -10, 20).normalize();
                this.scene.add(directionalLight1);
                // var directionalLight2 = new THREE.DirectionalLight(0xffffff);
                // directionalLight2.position.set(0, 70, 100).normalize();
                // this.scene.add(directionalLight2);
                var ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
                this.scene.add(ambientLight);

                // Loop on each combi selected
                highlightedCombination.forEach((highlightedCombination_item, highlightedCombination_index) => {

                    const letters = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"];
                    var combi_group_id = 0;
                    var combi_id = 0;
                    var indexOf_raise = highlightedCombination_item.id.indexOf("raise");
                    var indexOf_zone = highlightedCombination_item.id.indexOf("-");
                    if (indexOf_raise > -1) {
                        combi_group_id = highlightedCombination_item.id.substring(6, indexOf_zone);
                        combi_id = highlightedCombination_item.id.substring(indexOf_zone + 1);
                    }
                    else if (indexOf_zone > -1) {
                        combi_group_id = letters.indexOf(highlightedCombination_item.id.substring(0, indexOf_zone));
                        combi_id = highlightedCombination_item.id.substring(indexOf_zone + 1);
                    }
                    else {
                        combi_id = highlightedCombination_item.id;
                    }
                    var combination = capacity?.buildable?.combinations[combi_group_id][combi_id];

                    // Create test elements
                    // if (combination?.test?.skeletonLine?.lines) {


                    //     combination?.test?.skeletonLine?.lines.forEach(line => {

                    //         if (line?.geometry?.type === "LineString") {
                    //             var points = [];

                    //             // Get local coordinates
                    //             var building_coords_global = line.geometry.coordinates;
                    //             var building_coords_local = [];
                    //             for (var i = 0; i < building_coords_global.length; i++) {
                    //                 var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                    //                 if (building_coords_global[i][0] < point0[0]) {
                    //                     deltaX = -deltaX;
                    //                 }
                    //                 var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                    //                 if (building_coords_global[i][1] < point0[1]) {
                    //                     deltaY = -deltaY;
                    //                 }
                    //                 building_coords_local.push([deltaX, deltaY]);
                    //             }
                    //             for (var i = 0; i < building_coords_local.length; i++) {
                    //                 points.push( new THREE.Vector3( building_coords_local[i][0], building_coords_local[i][1], 0 ) );
                    //             }

                    //             const geometry = new THREE.BufferGeometry().setFromPoints( points );

                    //             const material = new THREE.LineBasicMaterial({
                    //                 color: 0x0000ff
                    //             });

                    //             const line_mesh = new THREE.Line( geometry, material );
                    //             this.scene.add(line_mesh);
                    //         }
                    //     })

                    // }

                    // if (combination?.test?.skeletonLine?.buffers) {

                    //     combination?.test?.skeletonLine?.buffers.forEach(buffer => {

                    //         if (buffer.geometry.type === "Polygon") {

                    //             // Get local coordinates
                    //             var building_coords_global = buffer.geometry.coordinates[0];
                    //             var building_coords_local = [];
                    //             for (var i = 0; i < building_coords_global.length; i++) {
                    //                 var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                    //                 if (building_coords_global[i][0] < point0[0]) {
                    //                     deltaX = -deltaX;
                    //                 }
                    //                 var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                    //                 if (building_coords_global[i][1] < point0[1]) {
                    //                     deltaY = -deltaY;
                    //                 }
                    //                 building_coords_local.push([deltaX, deltaY]);
                    //             }

                    //             // Create Shape
                    //             var building_shape = new THREE.Shape();
                    //             building_shape.moveTo(building_coords_local[0][0], building_coords_local[0][1]);
                    //             for (var i = 0; i < building_coords_local.length; i++) {
                    //                 building_shape.lineTo(building_coords_local[i][0], building_coords_local[i][1]);
                    //             }

                    //             // Create Shape Holes
                    //             if (buffer.geometry.coordinates.length > 1) {
                    //                 for (var hole_index = 1; hole_index < buffer.geometry.coordinates.length; hole_index++) {
                    //                     // Get local coordinates
                    //                     var building_coords_global = buffer.geometry.coordinates[hole_index];
                    //                     var building_coords_local = [];
                    //                     for (var i = 0; i < building_coords_global.length; i++) {
                    //                         var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                    //                         if (building_coords_global[i][0] < point0[0]) {
                    //                             deltaX = -deltaX;
                    //                         }
                    //                         var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                    //                         if (building_coords_global[i][1] < point0[1]) {
                    //                             deltaY = -deltaY;
                    //                         }
                    //                         building_coords_local.push([deltaX, deltaY]);
                    //                     }

                    //                     var building_shape_hole = new THREE.Shape();
                    //                     building_shape_hole.moveTo(building_coords_local[0][0], building_coords_local[0][1]);
                    //                     for (var i = 0; i < building_coords_local.length; i++) {
                    //                         building_shape_hole.lineTo(building_coords_local[i][0], building_coords_local[i][1]);
                    //                     }
                    //                     building_shape.holes.push(building_shape_hole);
                    //                 }
                    //             }

                    //             // Create geometry
                    //             var building_extrude_settings = {
                    //                 steps: 1,
                    //                 depth: 0.2,
                    //                 bevelEnabled: false
                    //             };
                    //             var building_geometry = new THREE.ExtrudeGeometry(building_shape, building_extrude_settings);

                    //             // Create mesh
                    //             var building_material = new THREE.MeshPhongMaterial({
                    //                 color: '#F1F1F1',
                    //                 transparent: true,
                    //                 opacity: 0.7,
                    //                 side: THREE.DoubleSide
                    //             });
                    //             var building_mesh = new THREE.Mesh(building_geometry, building_material);

                    //             // Add mesh to scene
                    //             this.scene.add(building_mesh);

                    //         }


                    //     })

                    // }

                    // if (combination?.test?.skeletonLine?.bufferLines) {

                    //     combination?.test?.skeletonLine?.bufferLines.forEach(line => {

                    //         if (line?.geometry?.type === "LineString") {
                    //             var points = [];

                    //             // Get local coordinates
                    //             var building_coords_global = line.geometry.coordinates;
                    //             var building_coords_local = [];
                    //             for (var i = 0; i < building_coords_global.length; i++) {
                    //                 var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                    //                 if (building_coords_global[i][0] < point0[0]) {
                    //                     deltaX = -deltaX;
                    //                 }
                    //                 var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                    //                 if (building_coords_global[i][1] < point0[1]) {
                    //                     deltaY = -deltaY;
                    //                 }
                    //                 building_coords_local.push([deltaX, deltaY]);
                    //             }
                    //             for (var i = 0; i < building_coords_local.length; i++) {
                    //                 points.push( new THREE.Vector3( building_coords_local[i][0], building_coords_local[i][1], 0 ) );
                    //             }

                    //             const geometry = new THREE.BufferGeometry().setFromPoints( points );

                    //             const material = new THREE.LineBasicMaterial({
                    //                 color: 0xff0000
                    //             });

                    //             const line_mesh = new THREE.Line( geometry, material );
                    //             this.scene.add(line_mesh);
                    //         }
                    //     })

                    // }


                    // Create buildings
                    // if (combination?.buildings && combination?.buildings.length > 1) {
                    if (combination?.buildings && combination?.buildings.length > 0) {
                        // console.log("ERROR no buildings on combination", combination);
                        // return

                        for (var building_index = 0; building_index < combination.buildings.length; building_index++) {
                            var building = combination.buildings[building_index];

                            // Check if levels exists
                            if (!building?.levels || building?.levels.length <= 0) {
                                console.log("ERROR no levels on building", building);
                                break;
                            }

                            // Loop th all levels of building
                            for (var level_index = 0; level_index < building.levels.length; level_index++) {
                                var level = building.levels[level_index];

                                // Check if polygon is type "Polygon"
                                if (!level?.polygon || !(level?.polygon?.geometry?.type === "Polygon" || level?.polygon?.geometry?.type === "MultiPolygon")) {
                                    console.log("ERROR no polygon on level", level);
                                    break;
                                }

                                var polygons = [level.polygon.geometry.coordinates];
                                if (level?.polygon?.geometry?.type === "MultiPolygon") {
                                    polygons = level.polygon.geometry.coordinates;
                                }

                                polygons.forEach(polygon_coords => {

                                    // Get local coordinates
                                    var building_coords_global = polygon_coords[0];
                                    var building_coords_local = [];
                                    for (var i = 0; i < building_coords_global.length; i++) {
                                        var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                                        if (building_coords_global[i][0] < point0[0]) {
                                            deltaX = -deltaX;
                                        }
                                        var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                                        if (building_coords_global[i][1] < point0[1]) {
                                            deltaY = -deltaY;
                                        }
                                        building_coords_local.push([deltaX, deltaY]);
                                    }

                                    // _____ Create mesh
                                    var building_shape = new THREE.Shape();
                                    building_shape.moveTo(building_coords_local[0][0], building_coords_local[0][1]);
                                    for (var i = 0; i < building_coords_local.length; i++) {
                                        building_shape.lineTo(building_coords_local[i][0], building_coords_local[i][1]);
                                    }

                                    // _____ Create Shape Holes
                                    if (polygon_coords.length > 1) {
                                        for (var hole_index = 1; hole_index < polygon_coords.length; hole_index++) {
                                            // Get local coordinates
                                            var building_coords_global = polygon_coords[hole_index];
                                            var building_coords_local = [];
                                            for (var i = 0; i < building_coords_global.length; i++) {
                                                var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                                                if (building_coords_global[i][0] < point0[0]) {
                                                    deltaX = -deltaX;
                                                }
                                                var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                                                if (building_coords_global[i][1] < point0[1]) {
                                                    deltaY = -deltaY;
                                                }
                                                building_coords_local.push([deltaX, deltaY]);
                                            }

                                            var building_shape_hole = new THREE.Shape();
                                            building_shape_hole.moveTo(building_coords_local[0][0], building_coords_local[0][1]);
                                            for (var i = 0; i < building_coords_local.length; i++) {
                                                building_shape_hole.lineTo(building_coords_local[i][0], building_coords_local[i][1]);
                                            }
                                            building_shape.holes.push(building_shape_hole);
                                        }
                                    }

                                    var building_elevation = level.elevations[0];
                                    // var building_elevation = 0;
                                    // if (level_index > 0) {
                                    //     building_elevation = building.levels[level_index - 1].elevation;
                                    // }
                                    // If raise and level 0 => adapt to existing height
                                    if (building?.parameters?.height_raise > 0 && level_index === 0) {
                                        building_elevation = building.parameters.height_raise;
                                    }

                                    var building_height = level.elevations[1] - building_elevation;
                                    // var building_height = level.elevation - building_elevation;

                                    var building_extrude_settings = {
                                        steps: 1,
                                        depth: building_height,
                                        bevelEnabled: false
                                    };
                                    var building_geometry = new THREE.ExtrudeGeometry(building_shape, building_extrude_settings);
                                    if (building_elevation > 0) {
                                        building_geometry.translate(0, 0, building_elevation);
                                    }
                                    var building_opacity = 0.2;
                                    if (highlightedCombination_item.section === null || highlightedCombination_item.section === 0 || highlightedCombination_item.section === building_index + 1) {
                                        var building_opacity = 1;
                                    }
                                    var building_material = new THREE.MeshPhongMaterial({
                                        color: '#F1F1F1',
                                        transparent: building_opacity === 1 ? false : true,
                                        opacity: building_opacity,
                                        side: THREE.DoubleSide,
                                        // depthWrite: false
                                    });
                                    var building_mesh = new THREE.Mesh(building_geometry, building_material);
                                    this.scene.add(building_mesh);
                                    var building_mesh_edges = new THREE.LineSegments(new THREE.EdgesGeometry(building_mesh.geometry), new THREE.LineBasicMaterial({
                                        color: 'grey',
                                        transparent: true,
                                        opacity: building_opacity
                                    }));
                                    this.scene.add(building_mesh_edges);


                                    // _____ Create roof
                                    if (level_index === building.levels.length - 1 && capacity?.buildable?.volume?.parameters?.roof_angle > 0) {
                                        try {
                                            var skeleton_coords = [...building_coords_local];
                                            skeleton_coords.splice(-1);
                                            // SKELETON
                                            const skeleton = SkeletonBuilder.BuildFromGeoJSON([[
                                                skeleton_coords,
                                                // [[0, 0], [100, 0], [100, 50], [0, 50]],    // outer
                                                // [[50, 30], [70, 30], [70, 20], [50, 20]],  // inner
                                            ]]);
                                            // console.log("skeleton", skeleton);

                                            // Get skeletyon points list
                                            var angle = capacity?.buildable?.volume?.parameters?.roof_angle;
                                            var skeleton_points = [];
                                            var point_id = 0;
                                            var elevation_max = 0;
                                            for (const [pointPos, distance] of skeleton.Distances) {
                                                var coords_local = [pointPos.X, pointPos.Y];
                                                var coords_global = map_helpers.local_to_mercator(coords_local, capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix)
                                                var turf_point = turf.point(coords_global);
                                                var elevation_new = building.levels[level_index].elevations[1] + ((angle / 45) * distance);
                                                turf_point.properties.id = point_id;
                                                turf_point.properties.elevation = elevation_new;
                                                turf_point.properties.coords_local = coords_local;
                                                skeleton_points.push(turf_point);

                                                if (elevation_new > elevation_max) {
                                                    elevation_max = elevation_new;
                                                }

                                                point_id++;
                                            }
                                            // console.log("skeleton_points", skeleton_points);

                                            // Get skeleton polygons
                                            var skeleton_polys = [];
                                            for (const edgeResult of skeleton.Edges) {
                                                const poly = edgeResult.Polygon;
                                                var skeleton_poly_points = [];
                                                for (var sk_point_index = 0; sk_point_index < poly.length; sk_point_index++) {
                                                    // Get point id
                                                    var sk_poly_id = null;
                                                    for (var sk_pointlist_index = 0; sk_pointlist_index < skeleton_points.length; sk_pointlist_index++) {
                                                        if (poly[sk_point_index].X === skeleton_points[sk_pointlist_index].properties.coords_local[0] && poly[sk_point_index].Y === skeleton_points[sk_pointlist_index].properties.coords_local[1]) {
                                                            sk_poly_id = sk_pointlist_index;
                                                            break;
                                                        }
                                                    }
                                                    skeleton_poly_points.push(sk_poly_id);
                                                }
                                                var skeleton_poly = {
                                                    points: skeleton_poly_points,
                                                }
                                                skeleton_polys.push(skeleton_poly);
                                            }
                                            // console.log("skeleton_polys", skeleton_polys);

                                            // Get grid
                                            var skeleton_tin = [];
                                            skeleton_polys.forEach(poly => {
                                                var points_list = [];
                                                var coords_global = [];
                                                poly.points.forEach(point_id => {
                                                    if (point_id !== null) {
                                                        points_list.push(skeleton_points[point_id]);
                                                        coords_global.push(skeleton_points[point_id].geometry.coordinates);
                                                    }
                                                })
                                                coords_global.push(coords_global[0]);
                                                if (points_list.length >= 3) {
                                                    var skeleton_polygon = turf.polygon([coords_global]);
                                                    var tin = turf.tin({ features: points_list }, "id");
                                                    // Check if poly is inside skeleton polygon
                                                    var tin_filtered = [];
                                                    for (var tin_index = 0; tin_index < tin.features.length; tin_index++) {
                                                        var centroid = turf.centroid(tin.features[tin_index]);
                                                        var isInside = turf.booleanPointInPolygon(centroid, skeleton_polygon);
                                                        if (isInside === true) {
                                                            tin_filtered.push(tin.features[tin_index]);
                                                        }
                                                    }
                                                    skeleton_tin = skeleton_tin.concat(tin_filtered);
                                                }
                                            })
                                            // console.log("skeleton_tin", skeleton_tin);

                                            // Create 3D shape
                                            var vertices_list = [];
                                            for (var i = 0; i < skeleton_tin.length; i++) {
                                                // Vertice a
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.coords_local[0]);
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.coords_local[1]);
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.elevation);
                                                // Vertice b
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.coords_local[0]);
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.coords_local[1]);
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.elevation);
                                                // Vertice c
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.coords_local[0]);
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.coords_local[1]);
                                                vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.elevation);
                                            }
                                            // console.log("vertices_list", vertices_list);
                                            var vertices = new Float32Array(vertices_list);
                                            // Set vertices to geometry
                                            var geometry = new THREE.BufferGeometry();
                                            geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
                                            // Compute vertex normals
                                            geometry.computeVertexNormals();
                                            // Create material
                                            var material = new THREE.MeshPhongMaterial({
                                                color: '#F1F1F1',
                                                side: THREE.DoubleSide,
                                                transparent: building_opacity === 1 ? false : true,
                                                opacity: building_opacity,
                                            });
                                            // Create mesh
                                            var mesh = new THREE.Mesh(geometry, material);
                                            // console.log("mesh", mesh);

                                            // Check if higher than buildabale max height
                                            // if (elevation_max > capacity?.buildable?.volume?.parameters?.max_height + 0.02) {
                                            // }
                                            // else {
                                            this.scene.add(mesh);
                                            // Create edges
                                            var edgesGeometry = new THREE.EdgesGeometry(mesh.geometry);
                                            var edgesMesh = new THREE.LineSegments(edgesGeometry, new THREE.LineBasicMaterial({ color: "grey", transparent: true, opacity: building_opacity }));
                                            // console.log("edgesMesh", edgesMesh);
                                            this.scene.add(edgesMesh);
                                            // }

                                        } catch (error) {
                                            console.log("ERROR CREATING ROOF", error);
                                        }
                                    }


                                })


                            }
                        }
                    }


                    // Create underground
                    var isUgVisible = true;
                    for (var hl_i = 0; hl_i < highlightedCombination.length; hl_i++) {
                        if (highlightedCombination[hl_i].section !== -1) {
                            isUgVisible = false;
                            break
                        }
                    }

                    if (isUgVisible === true && combination?.undergrounds && combination?.undergrounds.length > 0) {

                        // Create land
                        if (highlightedCombination_index === 0) {

                            // Check if land is polygon
                            if (capacity?.landBase?.union?.geometry?.geometry?.type === "Polygon") {

                                // Get local coordinates
                                var building_coords_global = capacity.landBase.union.geometry.geometry.coordinates[0];
                                var building_coords_local = [];
                                for (var i = 0; i < building_coords_global.length; i++) {
                                    var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                                    if (building_coords_global[i][0] < point0[0]) {
                                        deltaX = -deltaX;
                                    }
                                    var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                                    if (building_coords_global[i][1] < point0[1]) {
                                        deltaY = -deltaY;
                                    }
                                    building_coords_local.push([deltaX, deltaY]);
                                }

                                // Create mesh
                                var building_shape = new THREE.Shape();
                                building_shape.moveTo(building_coords_local[0][0], building_coords_local[0][1]);
                                for (var i = 0; i < building_coords_local.length; i++) {
                                    building_shape.lineTo(building_coords_local[i][0], building_coords_local[i][1]);
                                }
                                var building_extrude_settings = {
                                    steps: 1,
                                    depth: 0.1,
                                    bevelEnabled: false
                                };
                                // var building_geometry = new THREE.ExtrudeGeometry(building_shape, building_extrude_settings);
                                var building_geometry = new THREE.ShapeGeometry(building_shape);
                                var building_material = new THREE.MeshPhongMaterial({
                                    color: '#062134',
                                    transparent: true,
                                    opacity: 0.4,
                                    side: THREE.DoubleSide,
                                    depthWrite: false
                                });
                                var building_mesh = new THREE.Mesh(building_geometry, building_material);
                                this.scene.add(building_mesh);

                                // Create edges
                                var building_mesh_edges = new THREE.LineSegments(new THREE.EdgesGeometry(building_mesh.geometry), new THREE.LineBasicMaterial({
                                    color: "#062134",
                                    linewidth: 12,
                                }));
                                this.scene.add(building_mesh_edges);

                            }

                        }


                        // Loop th all undergrounds
                        combination.undergrounds.forEach(underground => {

                            // Loop th all levels
                            for (var level_index = 0; level_index < underground.levels.length; level_index++) {
                                var level = underground.levels[level_index];

                                // Get polygon list
                                var ug_polygons_coords = [];
                                if (level.polygon.geometry.type === "Polygon") {
                                    ug_polygons_coords.push(level.polygon.geometry.coordinates);
                                }
                                else if (level.polygon.geometry.type === "MultiPolygon") {
                                    level.polygon.geometry.coordinates.forEach(coords => {
                                        ug_polygons_coords.push(coords);
                                    })
                                }

                                // Loop th all polygons coords
                                ug_polygons_coords.forEach(ug_polygon => {

                                    // Get local coordinates
                                    var building_coords_global = ug_polygon[0];
                                    var building_coords_local = [];
                                    for (var i = 0; i < building_coords_global.length; i++) {
                                        var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                                        if (building_coords_global[i][0] < point0[0]) {
                                            deltaX = -deltaX;
                                        }
                                        var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                                        if (building_coords_global[i][1] < point0[1]) {
                                            deltaY = -deltaY;
                                        }
                                        building_coords_local.push([deltaX, deltaY]);
                                    }

                                    // Create mesh
                                    var building_shape = new THREE.Shape();
                                    building_shape.moveTo(building_coords_local[0][0], building_coords_local[0][1]);
                                    for (var i = 0; i < building_coords_local.length; i++) {
                                        building_shape.lineTo(building_coords_local[i][0], building_coords_local[i][1]);
                                    }
                                    var building_extrude_settings = {
                                        steps: 1,
                                        depth: Math.abs(level.elevations[1] - level.elevations[0]),
                                        bevelEnabled: false
                                    };
                                    var building_geometry = new THREE.ExtrudeGeometry(building_shape, building_extrude_settings);
                                    building_geometry.translate(0, 0, (level.elevations[1] - 0.01));
                                    var building_material = new THREE.MeshPhongMaterial({
                                        color: '#BDBDBD',
                                        transparent: false,
                                        // opacity: 0.4
                                        // side: THREE.DoubleSide,
                                        // depthWrite: false
                                    });
                                    var building_mesh = new THREE.Mesh(building_geometry, building_material);
                                    this.scene.add(building_mesh);

                                    // Create edges
                                    var building_mesh_edges = new THREE.LineSegments(new THREE.EdgesGeometry(building_mesh.geometry), new THREE.LineBasicMaterial({ color: "#5b5a5a" }));
                                    // var building_mesh_edges = new THREE.LineSegments(new THREE.EdgesGeometry(building_mesh.geometry), new THREE.LineDashedMaterial({
                                    //     color: "grey",
                                    //     linewidth: 1,
                                    //     scale: 1,
                                    //     dashSize: 3,
                                    //     gapSize: 1,
                                    // }));
                                    this.scene.add(building_mesh_edges);

                                    // Create holes


                                })


                            }


                        })

                        // // Loop th all levels
                        // for (var level_index = 0; level_index < combination.undergrounds.levels.length; level_index++) {
                        //     var level = combination.parking.sub.levels[level_index];

                        //     // Check if polygon is type "Polygon"
                        //     if (!level?.polygon || level?.polygon?.geometry?.type !== "Polygon") {
                        //         console.log("ERROR no polygon on level", level);
                        //         break;
                        //     }

                        //     // Get local coordinates
                        //     var building_coords_global = level.polygon.geometry.coordinates[0];
                        //     var building_coords_local = [];
                        //     for (var i = 0; i < building_coords_global.length; i++) {
                        //         var deltaX = (turf.distance(turf.point(building_coords_global[i]), turf.point([point0[0], building_coords_global[i][1]])) * 1000);
                        //         if (building_coords_global[i][0] < point0[0]) {
                        //             deltaX = -deltaX;
                        //         }
                        //         var deltaY = (turf.distance(turf.point(building_coords_global[i]), turf.point([building_coords_global[i][0], point0[1]])) * 1000);
                        //         if (building_coords_global[i][1] < point0[1]) {
                        //             deltaY = -deltaY;
                        //         }
                        //         building_coords_local.push([deltaX, deltaY]);
                        //     }

                        //     // Create mesh
                        //     var building_shape = new THREE.Shape();
                        //     building_shape.moveTo(building_coords_local[0][0], building_coords_local[0][1]);
                        //     for (var i = 0; i < building_coords_local.length; i++) {
                        //         building_shape.lineTo(building_coords_local[i][0], building_coords_local[i][1]);
                        //     }
                        //     var building_extrude_settings = {
                        //         steps: 1,
                        //         depth: level.height,
                        //         bevelEnabled: false
                        //     };
                        //     var building_geometry = new THREE.ExtrudeGeometry(building_shape, building_extrude_settings);
                        //     building_geometry.translate(0, 0, (level.elevation - level.height));
                        //     var building_material = new THREE.MeshPhongMaterial({
                        //         color: '#BDBDBD',
                        //         // transparent: false,
                        //         // opacity: 0.4
                        //         side: THREE.DoubleSide
                        //     });
                        //     var building_mesh = new THREE.Mesh(building_geometry, building_material);
                        //     this.scene.add(building_mesh);
                        //     // var building_mesh_edges = new THREE.LineSegments(new THREE.EdgesGeometry(building_mesh.geometry), new THREE.LineBasicMaterial({ color: "grey" }));
                        //     var building_mesh_edges = new THREE.LineSegments(new THREE.EdgesGeometry(building_mesh.geometry), new THREE.LineDashedMaterial({
                        //         color: "grey",
                        //         linewidth: 1,
                        //         scale: 1,
                        //         dashSize: 3,
                        //         gapSize: 1,
                        //     }));
                        //     this.scene.add(building_mesh_edges);

                        //     // Create holes


                        // }

                    }





                    // for (var level_index = 1; level_index < building_levels.length; level_index++) {
                    //     for (var footprint_index = 0; footprint_index < building_levels[level_index].footprints.length; footprint_index++) {
                    //         if (building_levels[level_index].footprints[footprint_index].geometry.type === "Polygon") {
                    //             // Get bbox local coordinates
                    //             var bbox_coords_global = building_levels[level_index].footprints[footprint_index].geometry.coordinates[0];
                    //             var bbox_coords = [];
                    //             for (var i = 0; i < bbox_coords_global.length; i++) {
                    //                 var deltaX = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([point0[0], bbox_coords_global[i][1]])) * 1000);
                    //                 if (bbox_coords_global[i][0] < point0[0]) {
                    //                     deltaX = -deltaX;
                    //                 }
                    //                 var deltaY = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([bbox_coords_global[i][0], point0[1]])) * 1000);
                    //                 if (bbox_coords_global[i][1] < point0[1]) {
                    //                     deltaY = -deltaY;
                    //                 }
                    //                 bbox_coords.push([deltaX, deltaY]);
                    //             }
                    //             // console.log("bbox local coords: ", bbox_coords);

                    //             // CREATE LEVEL MESH
                    //             var bboxShape = new THREE.Shape();
                    //             bboxShape.moveTo(bbox_coords[0][0], bbox_coords[0][1]);
                    //             for (var i = 0; i < bbox_coords.length; i++) {
                    //                 bboxShape.lineTo(bbox_coords[i][0], bbox_coords[i][1]);
                    //             }
                    //             var bboxExtrudeSettings = {
                    //                 steps: 1,
                    //                 depth: building_levels[level_index].elevation - building_levels[level_index - 1].elevation,
                    //                 bevelEnabled: false
                    //             };
                    //             var bboxGeometry = new THREE.ExtrudeGeometry(bboxShape, bboxExtrudeSettings);
                    //             bboxGeometry.translate(0, 0, building_levels[level_index - 1].elevation);
                    //             // bboxGeometry.translate(0, 0, building_levels[level_index - 1].elevation - 6);
                    //             var bboxMaterial = new THREE.MeshPhongMaterial({
                    //                 color: '#F1F1F1',
                    //                 // transparent: false,
                    //                 // opacity: 0.4
                    //             });
                    //             var bboxMesh = new THREE.Mesh(bboxGeometry, bboxMaterial);

                    //             // CREATE HOLES
                    //             if (building_levels[level_index].footprints[footprint_index].geometry.coordinates.length > 1) {
                    //                 console.log("HOLES");
                    //                 // Create hole mesh
                    //                 var hole_index = 1;
                    //                 var bbox_coords_global = building_levels[level_index].footprints[footprint_index].geometry.coordinates[hole_index];
                    //                 var bbox_coords = [];
                    //                 for (var i = 0; i < bbox_coords_global.length; i++) {
                    //                     var deltaX = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([point0[0], bbox_coords_global[i][1]])) * 1000);
                    //                     if (bbox_coords_global[i][0] < point0[0]) {
                    //                         deltaX = -deltaX;
                    //                     }
                    //                     var deltaY = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([bbox_coords_global[i][0], point0[1]])) * 1000);
                    //                     if (bbox_coords_global[i][1] < point0[1]) {
                    //                         deltaY = -deltaY;
                    //                     }
                    //                     bbox_coords.push([deltaX, deltaY]);
                    //                 }
                    //                 // console.log("bbox local coords: ", bbox_coords);
                    //                 // Shape and Geometry
                    //                 var bboxShape = new THREE.Shape();
                    //                 bboxShape.moveTo(bbox_coords[0][0], bbox_coords[0][1]);
                    //                 for (var i = 0; i < bbox_coords.length; i++) {
                    //                     bboxShape.lineTo(bbox_coords[i][0], bbox_coords[i][1]);
                    //                 }
                    //                 var bboxExtrudeSettings = {
                    //                     steps: 1,
                    //                     depth: building_levels[level_index].elevation - building_levels[level_index - 1].elevation,
                    //                     bevelEnabled: false
                    //                 };
                    //                 var holeGeometry = new THREE.ExtrudeGeometry(bboxShape, bboxExtrudeSettings);
                    //                 holeGeometry.translate(0, 0, building_levels[level_index - 1].elevation);
                    //                 // bboxGeometry.translate(0, 0, building_levels[level_index - 1].elevation - 6);
                    //                 var holeMaterial = new THREE.MeshPhongMaterial({
                    //                     color: '#FF0000',
                    //                     // transparent: false,
                    //                     // opacity: 0.4
                    //                 });
                    //                 var holeMesh = new THREE.Mesh(holeGeometry, holeMaterial);
                    //                 // CSG operation
                    //                 bboxMesh.updateMatrix();
                    //                 holeMesh.updateMatrix();
                    //                 var interceptorMesh = CSG.subtract(bboxMesh, holeMesh);
                    //                 this.scene.add(interceptorMesh);
                    //                 bboxMesh = interceptorMesh;
                    //             }

                    //             this.scene.add(bboxMesh);
                    //             // Edges
                    //             var edgesGeometry = new THREE.EdgesGeometry(bboxMesh.geometry);
                    //             var edgesMesh = new THREE.LineSegments(edgesGeometry, new THREE.LineBasicMaterial({ color: "grey" }));
                    //             this.scene.add(edgesMesh);



                    //             // CREATE ROOF MESH
                    //             if (level_index === building_levels.length - 1 && capacity?.buildable?.volume?.parameters?.roof_angle > 0) {
                    //                 var skeleton_coords = [...bbox_coords];
                    //                 skeleton_coords.splice(-1);
                    //                 // SKELETON
                    //                 const skeleton = SkeletonBuilder.BuildFromGeoJSON([[
                    //                     skeleton_coords,
                    //                     // [[0, 0], [100, 0], [100, 50], [0, 50]],    // outer
                    //                     // [[50, 30], [70, 30], [70, 20], [50, 20]],  // inner
                    //                 ]]);
                    //                 // console.log("skeleton", skeleton);

                    //                 // Get skeletyon points list
                    //                 var angle = capacity?.buildable?.volume?.parameters?.roof_angle;
                    //                 var skeleton_points = [];
                    //                 var point_id = 0;
                    //                 var elevation_max = 0;
                    //                 for (const [pointPos, distance] of skeleton.Distances) {
                    //                     var coords_local = [pointPos.X, pointPos.Y];
                    //                     var coords_global = map_helpers.local_to_mercator(coords_local, capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix)
                    //                     var turf_point = turf.point(coords_global);
                    //                     var elevation_new = building_levels[level_index].elevation + ((angle / 45) * distance);
                    //                     turf_point.properties.id = point_id;
                    //                     turf_point.properties.elevation = elevation_new;
                    //                     turf_point.properties.coords_local = coords_local;
                    //                     skeleton_points.push(turf_point);

                    //                     if (elevation_new > elevation_max) {
                    //                         elevation_max = elevation_new;
                    //                     }

                    //                     point_id++;
                    //                 }
                    //                 // console.log("skeleton_points", skeleton_points);

                    //                 // Get skeleton polygons
                    //                 var skeleton_polys = [];
                    //                 for (const edgeResult of skeleton.Edges) {
                    //                     const poly = edgeResult.Polygon;
                    //                     var skeleton_poly_points = [];
                    //                     for (var sk_point_index = 0; sk_point_index < poly.length; sk_point_index++) {
                    //                         // Get point id
                    //                         var sk_poly_id = null;
                    //                         for (var sk_pointlist_index = 0; sk_pointlist_index < skeleton_points.length; sk_pointlist_index++) {
                    //                             if (poly[sk_point_index].X === skeleton_points[sk_pointlist_index].properties.coords_local[0] && poly[sk_point_index].Y === skeleton_points[sk_pointlist_index].properties.coords_local[1]) {
                    //                                 sk_poly_id = sk_pointlist_index;
                    //                                 break;
                    //                             }
                    //                         }
                    //                         skeleton_poly_points.push(sk_poly_id);
                    //                     }
                    //                     var skeleton_poly = {
                    //                         points: skeleton_poly_points,
                    //                     }
                    //                     skeleton_polys.push(skeleton_poly);
                    //                 }
                    //                 // console.log("skeleton_polys", skeleton_polys);

                    //                 // Get grid
                    //                 var skeleton_tin = [];
                    //                 skeleton_polys.forEach(poly => {
                    //                     var points_list = [];
                    //                     var coords_global = [];
                    //                     poly.points.forEach(point_id => {
                    //                         if (point_id !== null) {
                    //                             points_list.push(skeleton_points[point_id]);
                    //                             coords_global.push(skeleton_points[point_id].geometry.coordinates);
                    //                         }
                    //                     })
                    //                     coords_global.push(coords_global[0]);
                    //                     if (points_list.length >= 3) {
                    //                         var skeleton_polygon = turf.polygon([coords_global]);
                    //                         var tin = turf.tin({ features: points_list }, "id");
                    //                         // Check if poly is inside skeleton polygon
                    //                         var tin_filtered = [];
                    //                         for (var tin_index = 0; tin_index < tin.features.length; tin_index++) {
                    //                             var centroid = turf.centroid(tin.features[tin_index]);
                    //                             var isInside = turf.booleanPointInPolygon(centroid, skeleton_polygon);
                    //                             if (isInside === true) {
                    //                                 tin_filtered.push(tin.features[tin_index]);
                    //                             }
                    //                         }
                    //                         skeleton_tin = skeleton_tin.concat(tin_filtered);
                    //                     }
                    //                 })
                    //                 // console.log("skeleton_tin", skeleton_tin);

                    //                 // Create 3D shape
                    //                 var vertices_list = [];
                    //                 for (var i = 0; i < skeleton_tin.length; i++) {
                    //                     // Vertice a
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.coords_local[0]);
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.coords_local[1]);
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.a].properties.elevation);
                    //                     // Vertice b
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.coords_local[0]);
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.coords_local[1]);
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.b].properties.elevation);
                    //                     // Vertice c
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.coords_local[0]);
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.coords_local[1]);
                    //                     vertices_list.push(skeleton_points[skeleton_tin[i].properties.c].properties.elevation);
                    //                 }
                    //                 // console.log("tin_to_recreate", tin_to_recreate);
                    //                 var vertices = new Float32Array(vertices_list);
                    //                 // Set vertices to geometry
                    //                 var geometry = new THREE.BufferGeometry();
                    //                 geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
                    //                 // Compute vertex normals
                    //                 geometry.computeVertexNormals();
                    //                 // Create material
                    //                 var material = new THREE.MeshPhongMaterial({
                    //                     color: '#F1F1F1',
                    //                     side: THREE.DoubleSide
                    //                     // transparent: true,
                    //                     // opacity: 0.4
                    //                 });
                    //                 // Create mesh
                    //                 var mesh = new THREE.Mesh(geometry, material);
                    //                 // console.log("mesh", mesh);

                    //                 // Check if higher than buildabale max height
                    //                 // if (elevation_max > capacity?.buildable?.volume?.parameters?.max_height + 0.02) {
                    //                 // }
                    //                 // else {
                    //                 this.scene.add(mesh);
                    //                 // Create edges
                    //                 var edgesGeometry = new THREE.EdgesGeometry(mesh.geometry);
                    //                 var edgesMesh = new THREE.LineSegments(edgesGeometry, new THREE.LineBasicMaterial({ color: "grey" }));
                    //                 // console.log("edgesMesh", edgesMesh);
                    //                 this.scene.add(edgesMesh);
                    //                 // }
                    //             }


                    //         }
                    //         else {
                    //             console.log("THE FOOTPRINT OF COMBI #" + highlightedCombination_item + " IS NOT A POLYGON",);
                    //             console.log("level index: " + level_index + " | footprint index: " + footprint_index);
                    //             console.log("polygon :", building_levels[level_index].footprints[footprint_index]);
                    //         }
                    //     }
                    // }


                })



                // Add map
                this.map = map;
                // use the Mapbox GL JS map canvas for three.js
                this.renderer = new THREE.WebGLRenderer({
                    canvas: map.getCanvas(),
                    context: gl,
                    antialias: true
                });
                this.renderer.autoClear = false;
            },
            render: function (gl, matrix) {
                var rotationX = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(1, 0, 0),
                    modelTransform.rotateX
                );
                var rotationY = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 1, 0),
                    modelTransform.rotateY
                );
                var rotationZ = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 0, 1),
                    modelTransform.rotateZ
                );
                var m = new THREE.Matrix4().fromArray(matrix);
                var l = new THREE.Matrix4()
                    .makeTranslation(
                        modelTransform.translateX,
                        modelTransform.translateY,
                        modelTransform.translateZ
                    )
                    .scale(
                        new THREE.Vector3(
                            modelTransform.scale,
                            -modelTransform.scale,
                            modelTransform.scale
                        )
                    )
                    .multiply(rotationX)
                    .multiply(rotationY)
                    .multiply(rotationZ);

                this.camera.projectionMatrix = m.multiply(l);
                this.renderer.resetState()
                this.renderer.render(this.scene, this.camera);
                // this.map.triggerRepaint();
            }
        };
        map.current.addLayer(customLayer, layer_get_position('layer3Dbuilding'));
    }



    // Lands
    // const [landSelection, setLandSelection] = useState({ lands: [], area: 0, union: null });



    // On section change
    // useEffect(() => {
    //     console.log('New Map Section', currentSection);
    //     map.current.flyTo({
    //         center: [
    //             -74.5 + (Math.random() - 0.5) * 10,
    //             40 + (Math.random() - 0.5) * 10
    //         ],
    //         essential: true
    //     });
    // }, [currentSection]);


    // On zoom & lat & lng & pitch change
    // useEffect(() => {
    //     landLabels_Show();
    // }, [zoom, lng, lat, pitch]);

    // Change step
    // useEffect(() => {
    //     if (capacity?.step === 1) {
    //         // Update map for step 1
    //         // Remove city lands if shown
    //         if (map.current.getLayer('layerLandsPolys')) {
    //             map.current.removeLayer('layerLandsPolys');
    //         }
    //         if (map.current.getSource('sourceLandsPolys')) {
    //             map.current.removeSource('sourceLandsPolys');
    //         }
    //         // Remove lands number if shown
    //         if (map.current.getLayer('layerLandsLabels')) {
    //             map.current.removeLayer('layerLandsLabels');
    //         }
    //         if (map.current.getSource('sourceLandsLabels')) {
    //             map.current.removeSource('sourceLandsLabels');
    //         }
    //         // Remove union bounds if shown
    //         if (map.current.getLayer('layerLandBounds')) {
    //             map.current.removeLayer('layerLandBounds');
    //         }
    //         if (map.current.getSource('sourceLandBounds')) {
    //             map.current.removeSource('sourceLandBounds');
    //         }
    //         // Remove union poly if shown
    //         if (map.current.getLayer('layerLandPoly')) {
    //             map.current.removeLayer('layerLandPoly');
    //         }
    //         if (map.current.getSource('sourceLandPoly')) {
    //             map.current.removeSource('sourceLandPoly');
    //         }
    //         // Show landbase bounds
    //         if (map.current.getLayer('layerLandbaseBounds')) {
    //             map.current.removeLayer('layerLandbaseBounds');
    //         }
    //         if (map.current.getSource('sourceLandbaseBounds')) {
    //             map.current.removeSource('sourceLandbaseBounds');
    //         }
    //         // _Add source
    //         var union_data = {
    //             type: 'FeatureCollection',
    //             features: [
    //                 capacity.landBase.union.outerLine,
    //             ],
    //         };
    //         var union_color = "#062134";
    //         if (capacity.landBase.union.outerLine.type === "FeatureCollection") {
    //             union_data = capacity.landBase.union.outerLine;
    //             union_color = "#DD1C1A";
    //         }
    //         map.current.addSource('sourceLandbaseBounds', {
    //             type: 'geojson',
    //             data: union_data
    //         });
    //         // _Add layer
    //         map.current.addLayer({
    //             id: 'layerLandbaseBounds',
    //             type: 'line',
    //             source: 'sourceLandbaseBounds',
    //             layout: {
    //                 'line-cap': 'round',
    //                 'line-join': 'round'
    //             },
    //             paint: {
    //                 'line-color': union_color,
    //                 'line-width': 2.5,
    //             },
    //             filter: ['in', '$type', 'LineString']
    //         }, 'building-extrusion');
    //         // Show landbase poly
    //         if (map.current.getLayer('layerLandbasePoly')) {
    //             map.current.removeLayer('layerLandbasePoly');
    //         }
    //         if (map.current.getSource('sourceLandbasePoly')) {
    //             map.current.removeSource('sourceLandbasePoly');
    //         }
    //         // _Add source
    //         var union_poly_data = {
    //             type: 'FeatureCollection',
    //             features: [
    //                 capacity.landBase.union.geometry,
    //             ],
    //         };
    //         if (capacity.landBase.union.geometry.type === "FeatureCollection") {
    //             union_poly_data = capacity.landBase.union.geometry;
    //         }
    //         map.current.addSource('sourceLandbasePoly', {
    //             type: 'geojson',
    //             data: union_poly_data
    //         });
    //         // _Add layer
    //         map.current.addLayer({
    //             id: 'layerLandbasePoly',
    //             type: 'fill',
    //             source: 'sourceLandbasePoly',
    //             paint: {
    //                 'fill-color': union_color,
    //                 'fill-opacity': 0.4,
    //             },
    //         }, 'building-extrusion');
    //         // Fly to
    //         map.current.fitBounds([
    //             [capacity.landBase.union.bbox.coordinates[0][3][0] - 0.00001, capacity.landBase.union.bbox.coordinates[0][3][1] - 0.00001], // southwestern corner of the bounds
    //             [capacity.landBase.union.bbox.coordinates[0][1][0] + 0.00001, capacity.landBase.union.bbox.coordinates[0][1][1] + 0.00001], // northeastern corner of the bounds
    //         ]);
    //     }
    // }, [capacity?.step]);























    // const handleLandSelectionUpdate = (result) => {
    //     var area = 0;
    //     for (var i = 0; i < result.lands.length; i++) {
    //         area += result.lands[i]["area"];
    //     }
    //     // Update hook
    //     setLandSelection({ lands: result.lands, area, union: result.union });
    //     // Show union bonds on map
    //     // Delete if exists already
    //     if (map.current.getLayer('layerLandBounds')) {
    //         map.current.removeLayer('layerLandBounds');
    //     }
    //     if (map.current.getSource('sourceLandBounds')) {
    //         map.current.removeSource('sourceLandBounds');
    //     }
    //     // Add source
    //     var union_data = {
    //         type: 'FeatureCollection',
    //         features: [
    //             result.union.outerLine,
    //         ],
    //     };
    //     var union_color = "#062134";
    //     if (result.union.outerLine.type === "FeatureCollection") {
    //         union_data = result.union.outerLine;
    //         union_color = "#DD1C1A";
    //     }
    //     map.current.addSource('sourceLandBounds', {
    //         type: 'geojson',
    //         data: union_data
    //     });
    //     // Add layer
    //     map.current.addLayer({
    //         id: 'layerLandBounds',
    //         type: 'line',
    //         source: 'sourceLandBounds',
    //         layout: {
    //             'line-cap': 'round',
    //             'line-join': 'round'
    //         },
    //         paint: {
    //             'line-color': union_color,
    //             'line-width': 2.5,
    //             'line-dasharray': [2, 2]
    //         },
    //         filter: ['in', '$type', 'LineString']
    //     });
    //     // Show union poly on map
    //     // Delete if exists already
    //     if (map.current.getLayer('layerLandPoly')) {
    //         map.current.removeLayer('layerLandPoly');
    //     }
    //     if (map.current.getSource('sourceLandPoly')) {
    //         map.current.removeSource('sourceLandPoly');
    //     }
    //     // Add source
    //     var union_poly_data = {
    //         type: 'FeatureCollection',
    //         features: [
    //             result.union.geometry,
    //         ],
    //     };
    //     if (result.union.geometry.type === "FeatureCollection") {
    //         union_poly_data = result.union.geometry;
    //     }
    //     map.current.addSource('sourceLandPoly', {
    //         type: 'geojson',
    //         data: union_poly_data
    //     });
    //     // Add layer
    //     map.current.addLayer({
    //         id: 'layerLandPoly',
    //         type: 'fill',
    //         source: 'sourceLandPoly',
    //         paint: {
    //             'fill-color': union_color,
    //             'fill-opacity': 0.4,
    //         },
    //     });
    // }
    // useEffect(() => {
    //     console.log("Update of land selection");
    //     // setLandSelectionList(landSelection);
    // }, [landSelection]);



    // const getLineSegment = (currentList, currentLine, allLines, allPoints) => {
    //     // Add line to list
    //     currentList.push(currentLine);

    //     // Get nextLine
    //     var nextLineList = allPoints[allLines[currentLine].point_end_index].linesStart;

    //     if (nextLineList.length === 0) {
    //         return [];
    //     }

    //     if (currentList.length > 0 && currentList[0] === nextLineList[0]) {
    //         return currentList;
    //     }
    //     else if (currentList.includes(nextLineList[0])) {
    //         return [];
    //     }

    //     currentList = getLineSegment(currentList, nextLineList[0], allLines, allPoints);

    //     return currentList;
    // }

    const getHigherPlanefromBuildable = (max_height, buildableMesh) => {
        var higherPlane = max_height;
        var buidable_coords = buildableMesh.geometry.attributes.position.array;
        for (var i = 0; i < buidable_coords.length; i += 9) {
            // Check if all 3 points are equals & igher than current higher plane
            if (buidable_coords[i + 2] === buidable_coords[i + 5] && buidable_coords[i + 5] === buidable_coords[i + 8] && buidable_coords[i + 8] > higherPlane) {
                higherPlane = buidable_coords[i + 8];
            }
        }
        return parseFloat(higherPlane.toFixed(2));
    }



    const getLevelBuildableData = (interceptorElevation0, interceptorElevation1, buildableMesh) => {
        // Create intersection plan
        var interceptor = getBuildableLevelInterceptor(interceptorElevation0, interceptorElevation1, buildableMesh);
        // console.log("interceptor", interceptor);

        // Perform calculations twice to optimize => 2 rounds
        var data = [];

        // Get base faces
        var interceptorCoords = interceptor?.geometry?.attributes?.position?.array;
        // console.log("interceptorCoords", interceptorCoords);
        var interceptorFaces0 = [];
        var interceptorFaces1 = [];
        var rounding = 8;
        for (var i = 0; i < interceptorCoords.length; i += 9) {
            // Check if all 3 points are on intercepetd level
            if (interceptorCoords[i + 2].toFixed(2) === interceptorElevation0.toFixed(2) && interceptorCoords[i + 5].toFixed(2) === interceptorElevation0.toFixed(2) && interceptorCoords[i + 8].toFixed(2) === interceptorElevation0.toFixed(2)) {
                // Create turf face
                var face = turf.polygon([[map_helpers.local_to_mercator([parseFloat(interceptorCoords[i].toFixed(rounding)), parseFloat(interceptorCoords[i + 1].toFixed(rounding))], capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator([parseFloat(interceptorCoords[i + 3].toFixed(rounding)), parseFloat(interceptorCoords[i + 4].toFixed(rounding))], capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator([parseFloat(interceptorCoords[i + 6].toFixed(rounding)), parseFloat(interceptorCoords[i + 7].toFixed(rounding))], capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator([parseFloat(interceptorCoords[i].toFixed(rounding)), parseFloat(interceptorCoords[i + 1].toFixed(rounding))], capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix)]]);
                // Ensure last coords are same than first
                face.geometry.coordinates[0][face.geometry.coordinates[0].length - 1] = face.geometry.coordinates[0][0];
                // Buffer to get good union when faces are milimeters separated
                face = turf.buffer(face, 0.00005, { steps: 1 });
                // Truncate
                face = turf.truncate(face);
                // Add face to list
                interceptorFaces0.push(face);
            }
            else if (interceptorElevation1 !== null && interceptorCoords[i + 2].toFixed(2) === interceptorElevation1.toFixed(2) && interceptorCoords[i + 5].toFixed(2) === interceptorElevation1.toFixed(2) && interceptorCoords[i + 8].toFixed(2) === interceptorElevation1.toFixed(2)) {
                // Create turf face
                var face = turf.polygon([[map_helpers.local_to_mercator([parseFloat(interceptorCoords[i].toFixed(rounding)), parseFloat(interceptorCoords[i + 1].toFixed(rounding))], capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator([parseFloat(interceptorCoords[i + 3].toFixed(rounding)), parseFloat(interceptorCoords[i + 4].toFixed(rounding))], capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator([parseFloat(interceptorCoords[i + 6].toFixed(rounding)), parseFloat(interceptorCoords[i + 7].toFixed(rounding))], capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator([parseFloat(interceptorCoords[i].toFixed(rounding)), parseFloat(interceptorCoords[i + 1].toFixed(rounding))], capacity.landBase.union.center.geometry.coordinates, capacity.landBase.union.bbox.matrix)]]);
                // Ensure last coords are same than first
                face.geometry.coordinates[0][face.geometry.coordinates[0].length - 1] = face.geometry.coordinates[0][0];
                // Buffer to get good union when faces are milimeters separated
                face = turf.buffer(face, 0.00005, { steps: 1 });
                // Truncate
                face = turf.truncate(face);
                // Add face to list
                interceptorFaces1.push(face);
            }
        }
        // console.log("interceptorFaces0", interceptorFaces0);
        // console.log("interceptorFaces1", interceptorFaces1);

        for (var round = 0; round < 2; round++) {
            // get elevation for this round
            var elevation = interceptorElevation0;
            var interceptorFaces = interceptorFaces0;
            if (round > 0) {
                elevation = interceptorElevation1;
                interceptorFaces = interceptorFaces1;
            }

            if (elevation === null) {
                break;
            }


            // Get union of faces
            var union = interceptorFaces[0];
            for (var i = 1; i < interceptorFaces.length; i++) {
                union = turf.union(union, interceptorFaces[i]);
            }
            // Split union if multipolygon and get a list
            if (union.geometry.type === "MultiPolygon") {
                var union2 = [];
                union.geometry.coordinates.forEach(poly => {
                    var polygon = turf.polygon(poly);
                    if (turf.area(polygon) > 1) {
                        var surface = turf.buffer(polygon, -0.00005, { steps: 1 });
                        // Check if inside land
                        var intersect = turf.intersect(capacity.landBase.union.geometry, surface);
                        if (intersect !== null) {
                            union2.push(intersect);
                        }
                    }
                })
                union = union2;
            }
            else {
                var surface = turf.buffer(union, -0.00005, { steps: 1 });
                // Check if inside land
                var intersect = turf.intersect(capacity.landBase.union.geometry, surface);
                if (intersect !== null) {
                    union = [intersect];
                }
                else {
                    union = []
                }
            }
            // console.log("union", union);

            // Simplify unions
            var buildableSurfaces = [];
            var buildableArea = 0;
            for (var j = 0; j < union.length; j++) {
                var buildableSurface = union[j];
                // Get outline
                var buildableSurfaceOutline = turf.polygonToLine(buildableSurface);
                // Split outline
                var buildableSurfaceLines = [];
                for (var i = 1; i < buildableSurfaceOutline.geometry.coordinates.length; i++) {
                    var line = turf.lineString([buildableSurfaceOutline.geometry.coordinates[i - 1], buildableSurfaceOutline.geometry.coordinates[i]]);
                    if ((turf.length(line) * 1000) >= 1) {
                        buildableSurfaceLines.push(line);
                    }
                }
                // Correct coords to erase discontinuities due to buffering
                var buildingLines = [];
                for (var i = 0; i < buildableSurfaceLines.length; i++) {
                    var last = i - 1;
                    if (i === 0) { last = buildableSurfaceLines.length - 1 }
                    // Check if end point of last is different from start point of i
                    if (buildableSurfaceLines[last].geometry.coordinates[1] !== buildableSurfaceLines[i].geometry.coordinates[0]) {
                        // Along last
                        var lastPoint = turf.destination(buildableSurfaceLines[last].geometry.coordinates[1], 0.01, turf.bearing(turf.point(buildableSurfaceLines[last].geometry.coordinates[0]), turf.point(buildableSurfaceLines[last].geometry.coordinates[1])));
                        var lastLine = turf.lineString([buildableSurfaceLines[last].geometry.coordinates[0], turf.getCoord(lastPoint)]);
                        // Along current
                        var nextPoint = turf.destination(buildableSurfaceLines[i].geometry.coordinates[0], 0.01, turf.bearing(turf.point(buildableSurfaceLines[i].geometry.coordinates[1]), turf.point(buildableSurfaceLines[i].geometry.coordinates[0])));
                        var nextLine = turf.lineString([turf.getCoord(nextPoint), buildableSurfaceLines[i].geometry.coordinates[1]]);
                        // Intersecting point
                        var intersection = turf.lineIntersect(lastLine, nextLine);
                        // Change values if intersection exists
                        if (intersection.features.length > 0) {
                            buildableSurfaceLines[last].geometry.coordinates[1] = turf.getCoord(intersection.features[0]);
                            buildableSurfaceLines[i].geometry.coordinates[0] = turf.getCoord(intersection.features[0]);
                        }
                        else {
                            // Else, Get midpoint
                            var midpoint = turf.midpoint(turf.point(buildableSurfaceLines[last].geometry.coordinates[1]), turf.point(buildableSurfaceLines[i].geometry.coordinates[0]));
                            buildableSurfaceLines[last].geometry.coordinates[1] = turf.getCoord(midpoint);
                            buildableSurfaceLines[i].geometry.coordinates[0] = turf.getCoord(midpoint);
                        }
                    }
                }
                // Simplify by mergin lines with angle between 175 & 185 deg (almost straight)
                var anglePoints = [];
                for (var i = 0; i < buildableSurfaceLines.length; i++) {
                    var last = i - 1;
                    if (last === -1) { last = buildableSurfaceLines.length - 1 }
                    var lastBearing = turf.bearing(turf.point(buildableSurfaceLines[last].geometry.coordinates[0]), buildableSurfaceLines[last].geometry.coordinates[1]);
                    var iBearing = turf.bearing(turf.point(buildableSurfaceLines[i].geometry.coordinates[1]), buildableSurfaceLines[i].geometry.coordinates[0]);
                    var angle = iBearing - lastBearing;
                    if (angle < 0) { angle = angle + 360 }
                    if (angle <= 175 || angle >= 185) {
                        anglePoints.push(buildableSurfaceLines[i].geometry.coordinates[0]);
                    }
                }
                // console.log("anglePoints", anglePoints);
                var buildableSurfaceLinesSimplified = [];
                for (var i = 0; i < anglePoints.length; i++) {
                    var next = i + 1;
                    if (next === anglePoints.length) { next = 0 }
                    buildableSurfaceLinesSimplified.push(turf.lineString([anglePoints[i], anglePoints[next]]));
                }
                if (anglePoints.length < 3) {
                    continue;
                }
                // Add buildableSurfaces
                var buildableSurfacePolygon = turf.polygon([anglePoints.concat([anglePoints[0]])]);
                var buildableSurfaceArea = turf.area(buildableSurfacePolygon);
                buildableSurfacePolygon.properties.area = buildableSurfaceArea;
                var coords_global = anglePoints.concat([anglePoints[0]]);
                var coords_local = [];
                var coords_svg = [];
                var svgWidth = 800;
                coords_global.forEach(coords => {
                    var localCoords = map_helpers.mercator_to_local(coords, capacity.landBase.union.bbox.origin);
                    coords_local.push(localCoords);
                    if (capacity.landBase.union.bbox.lengthH >= capacity.landBase.union.bbox.lengthV) {
                        var scaleFactor = svgWidth / capacity.landBase.union.bbox.lengthH;
                        var verticalOffset = (svgWidth - (capacity.landBase.union.bbox.lengthV * scaleFactor)) / 2;
                        coords_svg.push([localCoords[0] * scaleFactor, svgWidth - (localCoords[1] * scaleFactor) - verticalOffset]);
                    }
                    else {
                        var scaleFactor = svgWidth / capacity.landBase.union.bbox.lengthV;
                        var horizontalOffset = (svgWidth - (capacity.landBase.union.bbox.lengthH * scaleFactor)) / 2;
                        coords_svg.push([(localCoords[0] * scaleFactor) - horizontalOffset, svgWidth - (localCoords[1] * scaleFactor)]);
                    }
                })
                buildableSurfacePolygon.properties.coords_local = coords_local;
                buildableSurfacePolygon.properties.coords_svg = coords_svg;
                buildableArea += buildableSurfaceArea;
                buildableSurfaces.push(buildableSurfacePolygon);
            }
            // console.log("buildableSurfaces", buildableSurfaces);

            // return buildable data
            data.push({
                elevation: elevation,
                buildableSurfaces: buildableSurfaces,
                buildableArea: buildableArea
            })
        }

        return data;
    }


    const get_offsetLine_intersection_WorkingSurface = (offset, workingLine, workingSurface) => {
        var offsetLine = turf.transformScale(turf.lineOffset(workingLine, -offset / 1000), 2);
        var intersections = turf.lineIntersect(offsetLine, turf.polygonToLine(workingSurface));
        // console.log("intersections", intersections);
        if (intersections?.features && intersections.features.length >= 2 && intersections.features[0].geometry.type === "Point" && intersections.features[1].geometry.type === "Point") {
            return turf.lineString([intersections.features[0].geometry.coordinates, intersections.features[1].geometry.coordinates])
        }
    }


    const get_offsetLine_intersection_WorkingSurface2 = (workingLine, workingSurface, originalLine) => {
        var intersections = turf.lineIntersect(workingLine, turf.polygonToLine(workingSurface));
        if (intersections?.features && intersections.features.length >= 2) {
            // Get distances from start and end point
            var point_start = turf.point(originalLine.line_coords_global[0]);
            var point_end = turf.point(originalLine.line_coords_global[1]);
            var items = [];
            var distance_start_min = {
                distance: 1000,
                index: -1
            }
            var distance_end_min = {
                distance: 1000,
                index: -1
            }
            var index = 0;
            for (var i = 0; i < intersections.features.length; i++) {
                if (intersections.features[i].geometry.type === "Point") {
                    var distance_start = turf.distance(intersections.features[i], point_start);
                    if (distance_start < distance_start_min.distance) {
                        // Update
                        distance_start_min.index = index;
                        distance_start_min.distance = distance_start
                    }
                    var distance_end = turf.distance(intersections.features[i], point_end);
                    if (distance_end < distance_end_min.distance) {
                        // Update
                        distance_end_min.index = index;
                        distance_end_min.distance = distance_end
                    }
                    items.push({
                        coords: intersections.features[i].geometry.coordinates,
                        distance_start: distance_start,
                        distance_end: distance_end,
                    })
                    index++;
                }
            }

            // Check if 2 elements are not the same
            if (distance_start_min.index === distance_end_min.index) {
                // If closer to start
                if (items[distance_start_min.index].distance_start < items[distance_start_min.index].distance_end) {
                    // Reset end search
                    distance_end_min = {
                        distance: 1000,
                        index: -1
                    }
                    // Search for second closer point from end
                    items.forEach((item, index) => {
                        if (index !== distance_start_min.index && item.distance_end < distance_end_min.distance) {
                            // Update
                            distance_end_min.index = index;
                            distance_end_min.distance = item.distance_end;
                        }
                    })
                }
                // If closer to end
                else {
                    // Reset start search
                    distance_start_min = {
                        distance: 1000,
                        index: -1
                    }
                    // Search for second closer point from end
                    items.forEach((item, index) => {
                        if (index !== distance_end_min.index && item.distance_start < distance_start_min.distance) {
                            // Update
                            distance_start_min.index = index;
                            distance_start_min.distance = item.distance_start;
                        }
                    })
                }
            }

            // return turf.lineString([intersections.features[0].geometry.coordinates, intersections.features[1].geometry.coordinates])
            return turf.lineString([items[distance_start_min.index].coords, items[distance_end_min.index].coords])
        }
        else {
            return null
        }
    }

    var turf_to_show = [];

    const get_line_type = (x1, x2, y1, y2) => {
        if (x1 === x2) {
            return "vertical"
        }
        else if (y1 === y2) {
            return "horizontal"
        }
        else {
            return "linear"
        }
    }

    const get_line_coef = (line_coords) => {
        const line_coef = (line_coords[1][1] - line_coords[0][1]) / (line_coords[1][0] - line_coords[0][0]);
        return line_coef
    }

    const get_line_origin = (line_coords, line_coef) => {
        const line_origin = line_coords[1] - line_coef * line_coords[0];
        return line_origin
    }

    const get_line_length = (line_coords) => {
        // distnace = sqrt( (x1 - x2)² + (y1 - y2)² )
        const line_length = Math.sqrt(Math.pow((line_coords[0][0] - line_coords[1][0]), 2) + Math.pow((line_coords[1][1] - line_coords[0][1]), 2));
        return line_length
    }

    const get_linePerp_coef = (line_coef) => {
        const linePerp_coef = -1 / line_coef;
        return linePerp_coef
    }

    const get_line_data = (line_coords) => {
        const line_type = get_line_type(line_coords[0][0], line_coords[1][0], line_coords[0][1], line_coords[1][1]);
        var line_coef = null;
        var line_origin = null;
        var linePerp_coef = null;
        var linePerp_start_origin = null;
        var linePerp_end_origin = null;
        var line_coords_global = [map_helpers.local_to_mercator(line_coords[0], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator(line_coords[1], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix)];

        if (line_type === "linear") {
            line_coef = get_line_coef(line_coords);
            line_origin = get_line_origin(line_coords[0], line_coef);
            linePerp_coef = get_linePerp_coef(line_coef);
            linePerp_start_origin = get_line_origin(line_coords[0], linePerp_coef);
            linePerp_end_origin = get_line_origin(line_coords[1], linePerp_coef);
        }
        return {
            line_type: line_type,
            line_coef: line_coef,
            line_origin: line_origin,
            line_coords_local: line_coords,
            line_coords_global: line_coords_global,
            linePerp_coef: linePerp_coef,
            linePerp_start_origin: linePerp_start_origin,
            linePerp_end_origin: linePerp_end_origin
        }
    }

    const get_lineReversed_data = (line_data) => {
        // Get new values
        var coords_local = line_data.line_coords_local.reverse();
        var coords_global = line_data.line_coords_global.reverse();
        var perp_end_origin = line_data.linePerp_start_origin;
        var perp_start_origin = line_data.linePerp_end_origin;

        // Replace new data
        var new_data = { ...line_data, line_coords_local: coords_local, line_coords_global: coords_global, linePerp_start_origin: perp_start_origin, linePerp_end_origin: perp_end_origin };

        return new_data
    }

    // distance between two parallel lines : d = | line_origin - lineOffset_origin | / sqrt( coef² + 1 )

    const get_lineOffset_origin = (line_data, offset) => {
        var lineOffset_origin = offset * Math.sqrt(line_data.line_coef * line_data.line_coef + 1) + line_data.line_origin;
        if (line_data.line_coords_local[0][0] > line_data.line_coords_local[1][0]) {
            lineOffset_origin = -offset * Math.sqrt(line_data.line_coef * line_data.line_coef + 1) + line_data.line_origin;
        }
        return lineOffset_origin
    }

    const get_lineOffset_data = (line_data, offset, workingSurface, isStartBound, isEndBound) => {
        // workingSurface = capacity.buildable.volume.levels[1].workingSurfaces[0];
        var line_type = null;
        var line_coef = null;
        var line_origin = null;
        var line_coords = [];
        var line_coords_global = [];
        var linePerp_coef = null;
        var linePerp_start_origin = null;
        var linePerp_end_origin = null;
        var line_connexions = {
            start_inside: null,
            start_outside: null,
            end_inside: null,
            end_outside: null,
        }
        if (line_data.line_type === "linear") {
            line_type = "linear";
            line_coef = line_data.line_coef;
            line_origin = get_lineOffset_origin(line_data, offset);
            // EITHER Get coords for line that intersects working surface boundaries
            if (workingSurface) {
                var lineOffset_coords = get_line_coords_from_equation(line_coef, line_origin, 200);
                var lineOffset_coords_global = [map_helpers.local_to_mercator(lineOffset_coords[0], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator(lineOffset_coords[1], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix)];
                var lineOffset_intersected = get_offsetLine_intersection_WorkingSurface2(turf.lineString(lineOffset_coords_global), workingSurface, line_data);
                if (lineOffset_intersected === null) {
                    console.log("line is outside workingSurface");
                    return null
                }
                line_coords_global = (lineOffset_intersected).geometry.coordinates;
                line_coords = [map_helpers.mercator_to_local(line_coords_global[0], capacity.landBase.union.bbox.origin), map_helpers.mercator_to_local(line_coords_global[1], capacity.landBase.union.bbox.origin)];
                line_coords_global = [map_helpers.local_to_mercator(line_coords[0], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator(line_coords[1], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix)];

                if (isStartBound === false) {
                    console.log("Start Coord not matching bound");
                    var line_coords_start_local = get_coords_intersection_two_equations({ line_coef, line_origin }, { line_coef: line_data.linePerp_coef, line_origin: line_data.linePerp_start_origin });
                    line_coords = [line_coords_start_local, line_coords[1]];
                    var line_coords_start_global = map_helpers.local_to_mercator(line_coords_start_local, capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix);
                    line_coords_global = [line_coords_start_global, line_coords_global[1]];
                }
                if (isEndBound === false) {
                    console.log("End Coord not matching bound");
                    var line_coords_end_local = get_coords_intersection_two_equations({ line_coef, line_origin }, { line_coef: line_data.linePerp_coef, line_origin: line_data.linePerp_end_origin });
                    line_coords = [line_coords[0], line_coords_end_local];
                    var line_coords_end_global = map_helpers.local_to_mercator(line_coords_end_local, capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix);
                    line_coords_global = [line_coords_global[0], line_coords_end_global];
                }
            }
            // OR Get coords for line that is perpendicular to original
            else {
                var line_coords_start = get_coords_intersection_two_equations({ line_coef, line_origin }, { line_coef: line_data.linePerp_coef, line_origin: line_data.linePerp_start_origin });
                var line_coords_end = get_coords_intersection_two_equations({ line_coef, line_origin }, { line_coef: line_data.linePerp_coef, line_origin: line_data.linePerp_end_origin });
                line_coords = [line_coords_start, line_coords_end];
                line_coords_global = [map_helpers.local_to_mercator(line_coords[0], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator(line_coords[1], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix)];
            }



            // Get perpendicular line equation
            linePerp_coef = line_data.linePerp_coef;
            linePerp_start_origin = get_line_origin(line_coords[0], linePerp_coef);
            linePerp_end_origin = get_line_origin(line_coords[1], linePerp_coef);

        }
        return {
            line_type: line_type,
            line_coef: line_coef,
            line_origin: line_origin,
            line_coords_local: line_coords,
            line_coords_global: line_coords_global,
            // line_connexions: line_connexions,
            linePerp_coef: linePerp_coef,
            linePerp_start_origin: linePerp_start_origin,
            linePerp_end_origin: linePerp_end_origin
        }
    }

    const get_line_coords_from_equation = (line_coef, line_origin, bbox_lengthH) => {
        var start_x = -10
        var start_coords = [start_x, (line_coef * start_x) + line_origin];
        var end_x = bbox_lengthH - start_x;
        var end_coords = [end_x, (line_coef * end_x) + line_origin];
        return [start_coords, end_coords]
    }

    const get_coords_distance_from_start = (line_data, distance) => {
        // distnace = sqrt( (x1 - x2)² + (y1 - y2)² )
        // y1 = line_coef * x1 + line_origin
        // => distance² = (x1 - x2)² + (line_coef * x1 + line_origin - y2)²
        // => distance² = x1² - 2 * x1 * x2 + x2² + y1² - 2 * y1 * y2 + y2²
    }

    const get_coords_intersection_two_equations = (line1_data, line2_data) => {
        // y1 = a1 * x1 + b1 & y2 = a2 * x2 + b2 => y1 = y2 so => a1 * x1 + b1 = a2 * x2 + b2 => x (a1 - a2) = b2 - b1 => x = (b2 - b1) / (a1 - a2)
        var x = (line2_data.line_origin - line1_data.line_origin) / (line1_data.line_coef - line2_data.line_coef);
        var y = line1_data.line_coef * x + line1_data.line_origin;
        return [x, y]
    }

    const get_line_footprint = (line_data, offset, workingSurface, isStartBound, isEndBound) => {
        // Get inside line
        var lineInside = get_lineOffset_data(line_data, offset, workingSurface, isStartBound, isEndBound);
        var lineInside_type = "bounds";
        if (lineInside === null) {
            lineInside = get_lineOffset_data(line_data, offset);
            lineInside_type = "perp";
        }
        lineInside.line_side = "in";
        // Get oustide line
        var lineOutside = get_lineOffset_data(line_data, -offset, workingSurface, isStartBound, isEndBound);
        var lineOutside_type = "bounds";
        if (lineOutside === null) {
            lineOutside = get_lineOffset_data(line_data, -offset);
            lineOutside_type = "perp";
        }
        lineOutside.line_side = "out";
        lineOutside = get_lineReversed_data(lineOutside);
        // Check if there is not too big difference between in and out line length
        var lineInside_length = get_line_length(lineInside.line_coords_local);
        var lineOutside_length = get_line_length(lineOutside.line_coords_local);
        if (lineInside_length > 1.5 * lineOutside_length || lineOutside_length > 1.5 * lineInside_length) {
            console.log("Reaffect offset lines because too big difference");
            if (lineInside_type !== "perp") {
                lineInside = get_lineOffset_data(line_data, offset);
                lineInside.line_side = "in";
            }
            if (lineOutside_type !== "perp") {
                lineOutside = get_lineOffset_data(line_data, -offset);
                lineOutside.line_side = "out";
                lineOutside = get_lineReversed_data(lineOutside);
            }
        }
        // Create 2 edges to join inside and outside
        var lineEdge1 = get_line_data([lineInside.line_coords_local[1], lineOutside.line_coords_local[0]]);
        lineEdge1.line_side = "edge";
        var lineEdge2 = get_line_data([lineOutside.line_coords_local[1], lineInside.line_coords_local[0]]);
        lineEdge2.line_side = "edge";

        // Create footprint
        var footprint = {
            lines: [lineInside, lineEdge1, lineOutside, lineEdge2],
            coords_local: [lineInside.line_coords_local[0], lineInside.line_coords_local[1], lineEdge1.line_coords_local[1], lineOutside.line_coords_local[1], lineEdge2.line_coords_local[1]],
            coords_global: [lineInside.line_coords_global[0], lineInside.line_coords_global[1], lineEdge1.line_coords_global[1], lineOutside.line_coords_global[1], lineEdge2.line_coords_global[1]],
        }

        return footprint

    }

    const get_mainLine_buffer = (footprint, buffer_main, buffer_second) => {
        var buffer = get_line_footprint_buffer(footprint, buffer_main, buffer_second);
        return buffer
    }

    const get_line_footprint_buffer = (line_footprint, buffer_main, buffer_second) => {
        // Get buffers
        var mainBuffer = buffer_main;
        var secondBuffer = buffer_second || buffer_main;
        if (buffer_second === 0) {
            secondBuffer = buffer_second;
        }
        // Get lines
        var lines = [];
        var coords_global = [];
        var coords_local = [];
        for (var i = 0; i < line_footprint.lines.length; i++) {
            // Get offset depending on line side
            var offset = secondBuffer;
            if (line_footprint.lines[i].line_side === "in" || line_footprint.lines[i].line_side === "out") {
                offset = mainBuffer;
            }
            // Get offset line
            var line = get_lineOffset_data(line_footprint.lines[i], offset);
            line.line_side = line_footprint.lines[i].line_side;
            // Create join line to link line i-1 and line i
            if (i > 0) {
                var linePrevious = lines[lines.length - 1];
                if (linePrevious.line_coords_local[1] !== line.line_coords_local[0]) {
                    // JOIN LINE
                    // var lineJoin = get_line_data([linePrevious.line_coords_local[1], line.line_coords_local[0]]);
                    // lineJoin.line_side = "join";
                    // lines.push(lineJoin);
                    // coords_global.push(lineJoin.line_coords_global[1]);
                    // coords_local.push(lineJoin.line_coords_local[1]);
                    // INTERSECTION BETWEEN IN OR OUT LINE AND EDGE LINE
                    var intersectionCoordsLocal = get_coords_intersection_two_equations(linePrevious, line);
                    // Update previous line end point
                    var linePreviousNew = get_line_data([linePrevious.line_coords_local[0], intersectionCoordsLocal]);
                    linePreviousNew.line_side = linePrevious.line_side;
                    lines[lines.length - 1] = linePreviousNew;
                    // Update current line start point
                    var lineNew = get_line_data([intersectionCoordsLocal, line.line_coords_local[1]]);
                    lineNew.line_side = line.line_side;
                    line = lineNew;
                }
            }
            else {
                coords_global.push(line.line_coords_global[0]);
                coords_local.push(line.line_coords_local[0]);
            }
            // Get intersetion if last line
            if (i === line_footprint.lines.length - 1) {
                var lineNext = lines[0];
                if (lineNext.line_coords_local[0] !== line.line_coords_local[1]) {
                    // INTERSECTION BETWEEN IN OR OUT LINE AND EDGE LINE
                    var intersectionCoordsLocal = get_coords_intersection_two_equations(line, lineNext);
                    // Update previous line start point
                    var lineNextNew = get_line_data([intersectionCoordsLocal, lineNext.line_coords_local[1]]);
                    lineNextNew.line_side = lineNext.line_side;
                    lines[0] = lineNextNew;
                    // Update current line end point
                    var lineNew = get_line_data([line.line_coords_local[0], intersectionCoordsLocal]);
                    lineNew.line_side = line.line_side;
                    line = lineNew;
                }
            }
            // Push line
            lines.push(line);
            coords_global.push(line.line_coords_global[1]);
            coords_local.push(line.line_coords_local[1]);
            // Create join line to link last line with first one
            // if (i === line_footprint.lines.length - 1) {
            //     var lineNext = lines[0];
            //     if (lineNext.line_coords_local[0] !== line.line_coords_local[1]) {
            //         // JOIN LINE
            //         var lineJoin = get_line_data([line.line_coords_local[1], lineNext.line_coords_local[0]]);
            //         lineJoin.line_side = "join";
            //         lines.push(lineJoin);
            //         coords_global.push(lineJoin.line_coords_global[1]);
            //         coords_local.push(lineJoin.line_coords_local[1]);
            //     }
            // }
        }

        // Get coords
        coords_local = [];
        coords_global = [];
        for (var i = 0; i < lines.length; i++) {
            if (i === 0) {
                coords_local.push(lines[i].line_coords_local[0]);
                coords_global.push(lines[i].line_coords_global[0]);
            }
            coords_local.push(lines[i].line_coords_local[1]);
            coords_global.push(lines[i].line_coords_global[1]);
        }

        // Create footprint_offset
        var footprint_offset = {
            lines: lines,
            coords_local: coords_local,
            coords_global: coords_global,
        }

        return footprint_offset

    }

    const get_line_maxHeight = (line_data, levels) => {
        // Create turf line
        var line_turf = turf.lineString(line_data.line_coords_global);
        var line_buffer = turf.buffer(line_turf, 0.0005, { steps: 1 });

        var minArea = 8;
        var maxHeight = 0;
        var matched = false;

        // Check each level
        for (var i = levels.length - 1; i >= 0; i--) {
            for (var j = 0; j < levels[i].workingSurfaces.length; j++) {
                var intersection = turf.intersect(line_buffer, levels[i].workingSurfaces[j]);
                if (intersection !== null && turf.area(intersection) >= minArea) {
                    maxHeight = levels[i].elevation;
                    matched = true;
                    break;
                }
            }
            if (matched) { break }
        }

        return maxHeight

    }

    const get_buildingOffsetFromType = (buildingOffset_type) => {
        var value = null;

        if (buildingOffset_type?.type === "recul") {
            value = buildingOffset_type?.value
        }
        else if (buildingOffset_type?.type === "prospect" && capacity?.buildable?.volume?.parameters?.max_height) {
            value = capacity?.buildable?.volume?.parameters?.max_height / buildingOffset_type?.value
        }

        if (value === null || value === undefined) { value = 6 }

        return value
    }

    const get_subLines = (line_data, buildingWidth, buildingOffsetMain, workingSurface) => {
        var subLines = [];
        var totalOffset = buildingWidth + buildingOffsetMain;
        // Try to create 10 subLines
        for (var i = 1; i <= 10; i++) {
            var subLine = get_lineOffset_data(line_data, totalOffset, workingSurface);
            if (subLine === null) { break }
            subLine.line_footprint = get_line_footprint(subLine, (buildingWidth / 2), workingSurface);
            subLine.line_maxHeight = get_line_maxHeight(subLine, capacity.buildable.volume.levels);
            subLine.id = line_data.id + "_" + (i - 1);
            subLines.push(subLine);
            totalOffset += buildingWidth + buildingOffsetMain;
        }

        return subLines
    }

    const get_lines_all_combinations = (lines_list) => {
        var all_combinations = [];
        for (var i = 1; i <= lines_list.length; i++) {
            all_combinations = all_combinations.concat(helpers.k_combinations(lines_list, i));
        }

        return all_combinations
    }

    const get_lines_detailed_combination = (linesList, testingLinesGroup, workingSurface) => {
        var detailedLinesList = [];
        for (var i = 0; i < linesList.length; i++) {
            // Get line from Testing Lines
            var line_Index = parseInt(linesList[i].substring(linesList[i].indexOf("_") + 1));
            var line = testingLinesGroup[line_Index];
            if (line === undefined) {
                continue;
            }

            // Defines connexions
            var start_in = null;
            var start_out = null;
            var end_in = null;
            var end_out = null;
            if (linesList.length > 1) {
                // Start
                var linePrev_Index = i - 1;
                if (linePrev_Index < 0) { linePrev_Index = linesList.length - 1 }
                // Check if crossing point is inside workingSurface
                var linePrev = get_line_from_id(linesList[linePrev_Index], testingLinesGroup);
                var intersection_coords_local = get_coords_intersection_two_equations(line, linePrev);
                var intersection_coords_global = map_helpers.local_to_mercator(intersection_coords_local, capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix);
                var intersectionPoint = turf.point(intersection_coords_global);
                var intersectionTest = turf.booleanPointInPolygon(intersectionPoint, workingSurface);
                // Check if crossing point is closer to start
                var intersectionDistance_start = turf.distance(intersectionPoint, turf.point(line.line_coords_global[0])) * 1000;
                var intersectionDistance_end = turf.distance(intersectionPoint, turf.point(line.line_coords_global[1])) * 1000;
                var distanceTest = intersectionDistance_start < intersectionDistance_end;
                if (intersectionTest && distanceTest) {
                    start_in = { line_id: linesList[linePrev_Index], side: "in" };
                    start_out = { line_id: linesList[linePrev_Index], side: "out" };
                    // Check continuity
                    if (i === 0 || (linePrev.connexions.end_in?.line_id === linesList[i] && linePrev.connexions.end_out?.line_id === linesList[i])) {

                    }
                    else {
                        console.log("DISCONTINUITY", i);
                        // Correct previous ends to preserve continuity
                        linePrev.connexions.end_in = { line_id: linesList[i], side: "in" };
                        linePrev.connexions.end_out = { line_id: linesList[i], side: "out" };
                        // Change previous starts if the now are equal to ends
                        if (linePrev.connexions.start_in?.line_id === linesList[i] && linePrev.connexions.start_out?.line_id === linesList[i]) {
                            linePrev.connexions.start_in = null;
                            linePrev.connexions.start_out = null;
                        }
                    }
                }
                // End
                var lineNext_Index = i + 1;
                if (lineNext_Index > linesList.length - 1) { lineNext_Index = 0 }
                // Check if crossing point is inside workingSurface
                var lineNext = get_line_from_id(linesList[lineNext_Index], testingLinesGroup);
                var intersection_coords_local = get_coords_intersection_two_equations(line, lineNext);
                var intersection_coords_global = map_helpers.local_to_mercator(intersection_coords_local, capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix);
                var intersectionPoint = turf.point(intersection_coords_global);
                var intersectionTest = turf.booleanPointInPolygon(intersectionPoint, workingSurface);
                // Check if crossing point is closer to end
                var intersectionDistance_start = turf.distance(intersectionPoint, turf.point(line.line_coords_global[0])) * 1000;
                var intersectionDistance_end = turf.distance(intersectionPoint, turf.point(line.line_coords_global[1])) * 1000;
                var distanceTest = intersectionDistance_start >= intersectionDistance_end;
                if (intersectionTest && distanceTest) {
                    end_in = { line_id: linesList[lineNext_Index], side: "in" };
                    end_out = { line_id: linesList[lineNext_Index], side: "out" };
                }
            }


            line.connexions = {
                start_in: start_in,
                start_out: start_out,
                end_in: end_in,
                end_out: end_out,
            };
            detailedLinesList.push(line);
        }

        return detailedLinesList
    }

    const get_line_from_id = (line_id, testingLinesGroup) => {
        var line = null;
        for (var i = 0; i < testingLinesGroup.length; i++) {
            if (testingLinesGroup[i].id === line_id) {
                line = testingLinesGroup[i];
                break
            }
        }

        return line
    }

    const get_mainLine_footprint = (mainLine, workingSurface, availableSurfaces, buffers, buffer_main, takenArea, buildingWidth) => {
        var lines = [];
        var coords_local = [];
        var coords_global = [];

        var create_first_edge = true;
        var begin_out_index = mainLine.length - 1;
        var start_out_coords_local = null;

        // First loop for in side
        for (var i = 0; i < mainLine.length; i++) {
            var footprint_line_index = 0; // The "in" side line of footprint
            // Start
            var start_coords_local = [];
            var start_connexion = mainLine[i].connexions.start_in;
            if (start_connexion === null) { // If connexion is null => get line_footprint with side "in" (index = 0)
                start_coords_local = mainLine[i].line_footprint.lines[footprint_line_index].line_coords_local[0];
            }
            else if (start_connexion?.line_id === "perp") {
                var crossing_line = {
                    line_coef: mainLine[i].linePerp_coef,
                    line_origin: mainLine[i].linePerp_start_origin,
                }
                start_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line);
                if (start_connexion?.value && start_connexion.value !== 0) {
                    var basic_line = get_line_data([start_coords_local, mainLine[i].line_coords_local[0]]);
                    crossing_line = get_lineOffset_data(basic_line, -start_connexion.value);
                    start_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line);
                }
            }
            else {
                var crossing_line = get_line_from_id(start_connexion.line_id, mainLine);
                start_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line.line_footprint.lines[footprint_line_index]);
            }
            // End
            var end_coords_local = [];
            var end_connexion = mainLine[i].connexions.end_in;
            if (end_connexion === null) { // If connexion is null => get line_footprint with side "in" (index = 0)
                end_coords_local = mainLine[i].line_footprint.lines[footprint_line_index].line_coords_local[1];
            }
            else if (end_connexion?.line_id === "perp") {
                var crossing_line = {
                    line_coef: mainLine[i].linePerp_coef,
                    line_origin: mainLine[i].linePerp_end_origin,
                }
                end_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line);
                if (end_connexion?.value && end_connexion.value !== 0) {
                    var basic_line = get_line_data([end_coords_local, mainLine[i].line_coords_local[1]]);
                    crossing_line = get_lineOffset_data(basic_line, end_connexion.value);
                    end_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line);
                }
            }
            else {
                var crossing_line = get_line_from_id(end_connexion.line_id, mainLine);
                end_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line.line_footprint.lines[footprint_line_index]);
            }
            // Line
            var line = get_line_data([start_coords_local, end_coords_local]);
            if (i > 1) {
                var start_point = turf.point(line.line_coords_global[0]);
                // KINK CHECK -- Check if line is not crossing previous in lines
                var checkLine = turf.lineString(coords_global.slice(0, -1));
                var line_cross_in = turf.lineIntersect(turf.lineString(line.line_coords_global), checkLine);
                var check_line_cross_in = line_cross_in.features.length > 0;
                if (check_line_cross_in === true) {
                    console.log("CROSSING LINE", line_cross_in);
                    // TO DO : get exact line (closer intersection) + replace this line with new end point as intersection + delete lines between + create this new line with start point as intersection
                    // Get intersection point & line
                    var line_in = null;
                    var line_index = null;
                    var intersection_point = null;
                    var intersection_distance_from_start = 100000000;
                    for (var k = 1; k < checkLine.geometry.coordinates.length; k++) {
                        var intersection = turf.lineIntersect(turf.lineString(line.line_coords_global), turf.lineString([checkLine.geometry.coordinates[k - 1], checkLine.geometry.coordinates[k]]));
                        if (intersection.features.length > 0) {
                            var distance_k = turf.distance(start_point, intersection.features[0]) * 1000;
                            if (distance_k < intersection_distance_from_start) {
                                intersection_point = line_cross_in.features[0];
                                intersection_distance_from_start = distance_k;
                                line_in = lines[k - 1];
                                line_index = k - 1;
                            }
                        }
                    }

                    // Check it is coming out from footprint and not coming in (then it is a buffer concern) => if distance to crossing_offset > distance to crossing then it is kink
                    var test_offset_line = get_lineOffset_data(line_in, 0.1);
                    var intersection_offset = turf.lineIntersect(turf.lineString(line.line_coords_global), turf.lineString(test_offset_line.line_coords_global));
                    if (intersection_offset.features.length > 0) {
                        var distance_offset = turf.distance(start_point, intersection_offset.features[0]) * 1000;
                        if (distance_offset > intersection_distance_from_start) {
                            console.log("CROSSING FROM IN TO OUT");

                            // Recreate crossing "in" line
                            var crossing_line_end_coords_local = map_helpers.mercator_to_local(intersection_point.geometry.coordinates, capacity.landBase.union.bbox.origin);
                            var crossing_line = get_line_data([lines[line_index].line_coords_local[0], crossing_line_end_coords_local]);
                            crossing_line.line_side = "in";
                            lines[line_index] = crossing_line;
                            coords_local[line_index + 1] = crossing_line.line_coords_local[1];
                            coords_global[line_index + 1] = crossing_line.line_coords_global[1];

                            // Delete "in" lines between crossing and current one
                            lines.length = line_index + 1;
                            coords_local.length = line_index + 2;
                            coords_global.length = line_index + 2;

                            // Recreate the current "in" line
                            line = get_line_data([crossing_line_end_coords_local, end_coords_local]);
                        }
                        else {
                            console.log("CROSSING FROM OUT TO IN (1)");
                            console.log("INVALID FOOTPRINT");
                            return {
                                lines: [],
                                coords_local: [],
                                coords_global: [],
                                area: 0,
                            }
                        }
                    }
                    else {
                        console.log("CROSSING FROM OUT TO IN (2)");
                        console.log("INVALID FOOTPRINT");
                        return {
                            lines: [],
                            coords_local: [],
                            coords_global: [],
                            area: 0,
                        }
                    }
                }


                // BUFFER CHECK -- Check if line is not crossing previous in lines buffer
                var checkLine_buffer = turf.buffer(checkLine, buffer_main / 1000, { steps: 1 });
                // Problem if start point is outside buffer && line is crossing buffer
                start_point = turf.point(line.line_coords_global[0]);
                var check_start_outside = !turf.booleanPointInPolygon(start_point, checkLine_buffer);
                var line_cross = turf.lineIntersect(turf.lineString(line.line_coords_global), turf.polygonToLine(checkLine_buffer));
                var check_line_cross = line_cross.features.length > 0;
                if (check_start_outside === true && check_line_cross === true && mainLine[i].id.slice(-1) !== "s" && mainLine[i].id.slice(-1) !== "e") { // id check in case of closing circle
                    console.log("CROSSING BUFFER", line_cross);
                    if (i < mainLine.length - 1) {
                        // Buffer crossing before last mailLine => INVALID FOOTPRINT
                        console.log("INVALID FOOTPRINT");
                        return {
                            lines: [],
                            coords_local: [],
                            coords_global: [],
                            area: 0,
                        }

                    }
                    // Get intersection point & buffer line
                    var buffer_line_coords_global = [];
                    var intersection_line = null;
                    var intersection_point = null;
                    var intersection_distance_from_start = 100000000;
                    // for (var k = 1; k < checkLine_buffer.geometry.coordinates[0].length; k++) {
                    //     var intersection = turf.lineIntersect(turf.lineString(line.line_coords_global), turf.lineString([checkLine_buffer.geometry.coordinates[0][k - 1], checkLine_buffer.geometry.coordinates[0][k]]));
                    //     if (intersection.features.length > 0) {
                    //         var distance_k = turf.distance(start_point, intersection.features[0]) * 1000;
                    //         if (distance_k < intersection_distance_from_start) {
                    //             intersection_point = line_cross.features[0];
                    //             intersection_distance_from_start = distance_k;
                    //             buffer_line_coords_global = turf.getCoords(turf.lineString([checkLine_buffer.geometry.coordinates[0][k - 1], checkLine_buffer.geometry.coordinates[0][k]]));
                    //         }
                    //     }
                    // }
                    for (var k = 0; k < lines.length - 1; k++) {
                        var checkLine_offset = get_lineOffset_data(lines[k], buffer_main);
                        var intersection = turf.lineIntersect(turf.lineString(line.line_coords_global), turf.lineString(checkLine_offset.line_coords_global));

                        if (intersection.features.length > 0) {
                            var distance_k = turf.distance(start_point, intersection.features[0]) * 1000;
                            if (distance_k < intersection_distance_from_start) {
                                intersection_line = checkLine_offset;
                                intersection_point = line_cross.features[0];
                                intersection_distance_from_start = distance_k;
                                buffer_line_coords_global = turf.getCoords(turf.lineString([checkLine_buffer.geometry.coordinates[0][k - 1], checkLine_buffer.geometry.coordinates[0][k]]));
                            }
                        }
                    }
                    if (intersection_point !== null) {

                        // var buffer_line = get_line_data([map_helpers.mercator_to_local(buffer_line_coords_global[0], capacity.landBase.union.bbox.origin), map_helpers.mercator_to_local(buffer_line_coords_global[1], capacity.landBase.union.bbox.origin)]);
                        var buffer_line = intersection_line;

                        // Recreate "in" line
                        end_coords_local = map_helpers.mercator_to_local(intersection_point.geometry.coordinates, capacity.landBase.union.bbox.origin);
                        line = get_line_data([start_coords_local, end_coords_local]);
                        line.line_side = "in";
                        lines.push(line);
                        coords_local.push(line.line_coords_local[1]);
                        coords_global.push(line.line_coords_global[1]);

                        // Get "out" intersection
                        start_out_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[2], buffer_line);

                        // Set begin_out_index
                        begin_out_index = i;

                        break;
                    }
                }
            }

            line.line_side = "in";
            lines.push(line);
            if (i === 0) {
                coords_local.push(line.line_coords_local[0]);
                coords_global.push(line.line_coords_global[0]);
            }
            coords_local.push(line.line_coords_local[1]);
            coords_global.push(line.line_coords_global[1]);
        }

        // Second loop for out side & edges
        for (var i = begin_out_index; i >= 0; i--) {
            var footprint_line_index = 2; // The "out" side line of footprint
            // Start
            var start_coords_local = [];
            if (i === begin_out_index && start_out_coords_local != null) {
                start_coords_local = start_out_coords_local;
            }
            else {
                var start_connexion = mainLine[i].connexions.end_out; // START & END ARE INVERTED BECAUSE HERE IS WORKING CLOCKWISE
                if (start_connexion === null) { // If connexion is null => get line_footprint
                    start_coords_local = mainLine[i].line_footprint.lines[footprint_line_index].line_coords_local[0];
                }
                else if (start_connexion?.line_id === "perp") {
                    var crossing_line = {
                        line_coef: mainLine[i].linePerp_coef,
                        line_origin: mainLine[i].linePerp_end_origin,
                    }
                    start_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line);
                    if (start_connexion?.value && start_connexion.value !== 0) {
                        var basic_line = get_line_data([start_coords_local, mainLine[i].line_coords_local[1]]);
                        crossing_line = get_lineOffset_data(basic_line, -start_connexion.value);
                        start_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line);
                    }
                }
                else {
                    var crossing_line = get_line_from_id(start_connexion.line_id, mainLine);
                    start_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line.line_footprint.lines[footprint_line_index]);
                }
            }
            // End
            var end_coords_local = [];
            var end_connexion = mainLine[i].connexions.start_out; // START & END ARE INVERTED BECAUSE HERE IS WORKING CLOCKWISE
            if (end_connexion === null) { // If connexion is null => get line_footprint
                end_coords_local = mainLine[i].line_footprint.lines[footprint_line_index].line_coords_local[1];
            }
            else if (end_connexion?.line_id === "perp") {
                var crossing_line = {
                    line_coef: mainLine[i].linePerp_coef,
                    line_origin: mainLine[i].linePerp_start_origin,
                }
                end_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line);
                if (end_connexion?.value && end_connexion.value !== 0) {
                    var basic_line = get_line_data([end_coords_local, mainLine[i].line_coords_local[0]]);
                    crossing_line = get_lineOffset_data(basic_line, end_connexion.value);
                    end_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line);
                }
            }
            else {
                var crossing_line = get_line_from_id(end_connexion.line_id, mainLine);
                end_coords_local = get_coords_intersection_two_equations(mainLine[i].line_footprint.lines[footprint_line_index], crossing_line.line_footprint.lines[footprint_line_index]);
            }
            // First Edge
            // if (i === (mainLine.length - 1)) {
            if (create_first_edge === true) {
                var edgeLine = get_line_data([lines[lines.length - 1].line_coords_local[1], start_coords_local]);
                edgeLine.line_side = "edge";
                lines.push(edgeLine);
                coords_local.push(edgeLine.line_coords_local[1]);
                coords_global.push(edgeLine.line_coords_global[1]);

                create_first_edge = false;
            }
            // Line
            var line = get_line_data([start_coords_local, end_coords_local]);
            line.line_side = "out";
            lines.push(line);
            coords_local.push(line.line_coords_local[1]);
            coords_global.push(line.line_coords_global[1]);
            // Last Edge
            if (i === 0) {
                var edgeLine = get_line_data([end_coords_local, lines[0].line_coords_local[0]]);
                edgeLine.line_side = "edge";
                lines.push(edgeLine);
                coords_local.push(edgeLine.line_coords_local[1]);
                coords_global.push(edgeLine.line_coords_global[1]);
            }
        }

        // Check kinks if not closed circle
        // if (mainLine[0].id.slice(-1) !== "s" && mainLine[0].id.slice(-1) !== "e") {
        if (checkKinkedFootprint(coords_global, mainLine[0])) {
            return [{
                lines: [],
                coords_local: [],
                coords_global: [],
                area: 0,
            }]
        }
        // }

        // Create current footrint
        var currentFootrpints = [{
            lines: lines,
            coords_local: coords_local,
            coords_global: coords_global,
            area: turf.area(turf.polygon([coords_global])),
        }];

        // Compare to buffers
        if (buffers.length > 0) {
            var footprint = turf.polygon([coords_global]);
            for (var i = 0; i < buffers.length; i++) {
                if (footprint !== null) {
                    footprint = turf.difference(footprint, turf.polygon([buffers[i].coords_global]))
                }
            }
            console.log("footprint diff", footprint);
            if (footprint === null) {
                console.log("ERROR : FOOTPRINT DIFF BY BUFFERS IS NOT A POLYGON", footprint);
                return [{
                    lines: [],
                    coords_local: [],
                    coords_global: [],
                    area: 0,
                }]
            }
            else if (footprint.geometry.type === "Polygon") {
                var recreatedFootrpint = recreateFootprint(footprint, lines);
                // coords_global = recreatedFootrpint.coords_global;
                // coords_local = recreatedFootrpint.coords_local;
                // lines = recreatedFootrpint.lines;
                currentFootrpints = [{
                    lines: recreatedFootrpint.lines,
                    coords_local: recreatedFootrpint.coords_local,
                    coords_global: recreatedFootrpint.coords_global,
                    area: turf.area(turf.polygon([recreatedFootrpint.coords_global])),
                }];
            }
            else if (footprint.geometry.type === "MultiPolygon") {
                var footprintList = [];
                for (var i = 0; i < footprint.geometry.coordinates.length; i++) {
                    var footprint_i = turf.polygon(footprint.geometry.coordinates[i]);
                    var recreatedFootrpint = recreateFootprint(footprint_i, lines);
                    footprintList.push({
                        lines: recreatedFootrpint.lines,
                        coords_local: recreatedFootrpint.coords_local,
                        coords_global: recreatedFootrpint.coords_global,
                        area: turf.area(footprint_i),
                    });
                }
                // return footprintList
                currentFootrpints = footprintList;
            }
        }


        // Adapt footprint to buildable area
        for (var i = 0; i < currentFootrpints.length; i++) {
            // Get the minimized version of i footprint
            var minimizedFootrpint = minimizeFootprint(currentFootrpints[i], availableSurfaces, takenArea, buildingWidth);
            currentFootrpints[i] = minimizedFootrpint;
            takenArea += minimizedFootrpint.area;
        }

        // Send result
        return currentFootrpints
    }

    const checkKinkedFootprint = (coords_global, mainLine0) => {
        var kinks = turf.kinks(turf.polygon([coords_global]));
        if (kinks.features.length > 0) {
            console.log("KINKED POLYGON", kinks);
            // Check if closed circle
            if (mainLine0?.id && (mainLine0.id.slice(-1) === "s" || mainLine0.id.slice(-1) === "e")) {
                // If closed circle and kinks = 2 features => nominal
                if (kinks.features.length === 2) {
                    console.log("KINKED POLYGON BUT CLOSED CIRCLE");
                    return false
                }
            }

            return true
        }
        else {
            return false
        }
    }

    const minimizeFootprint = (footprint, availableSurfaces, takenArea, buildingWidth) => {
        // Test if area is not bigger than buildable area
        // If yes =>    reduce the starting segment (if close circle => minimum reducing is buffer_main)
        //              Modify combination by reducing ending segment
        // If not =>    Create sub combinations with subMainLines

        // availableSurfaces.forEach(availableSurface => {
        for (var aS = 0; aS < availableSurfaces.length; aS++) {
            var availableSurface = availableSurfaces[aS];
            if (availableSurface.properties.buildableArea < availableSurface.properties.availableArea) { // If availableSurface is fully buildable = do nothing
                // Get intersection surface between footprint and current availableSurface
                var intersection = turf.intersect(turf.polygon([footprint.coords_global]), availableSurface);
                if (intersection !== null) { // If no intersection = do nothing
                    turf_to_show.push(intersection);
                    // Get interesction area
                    var intersection_area = turf.area(intersection);
                    if (intersection_area > (availableSurface.properties.buildableArea - takenArea)) {
                        var area_to_remove = intersection_area - (availableSurface.properties.buildableArea - takenArea);
                        console.log("TOO LARGE - intersection_area is bigger than buildable area, need to remove", area_to_remove);
                        console.log("intersection_area", intersection_area);
                        console.log("full_area", turf.area(turf.polygon([footprint.coords_global])));
                        console.log("buildableArea", availableSurface.properties.buildableArea);
                        console.log("takenArea", takenArea);
                        // Check each edge if it is inside the availableSurface
                        for (var i = 0; i < footprint.lines.length; i++) {
                            if (footprint.lines[i].line_side === "edge") {
                                var line = turf.lineString(footprint.lines[i].line_coords_global);
                                var intersection_line = turf.intersect(turf.buffer(line, 0.0001, { steps: 1 }), availableSurface);
                                if (intersection_line !== null) { // Use the first edge line that intersects availableSurface
                                    // Get lines to drop
                                    var dropIndex = [];
                                    // Get previousLine
                                    var prevIndex = i - 1;
                                    if (prevIndex < 0) { prevIndex = footprint.lines.length - 1 }
                                    var prevLine = footprint.lines[prevIndex];
                                    if (prevLine.line_side !== "in" && prevLine.line_side !== "out") {
                                        // If prevLine is not a main line => try previous one
                                        dropIndex.push(prevIndex);
                                        prevIndex--;
                                        if (prevIndex < 0) { prevIndex = footprint.lines.length - 1 }
                                        prevLine = footprint.lines[prevIndex];
                                    }
                                    // Get nextLine
                                    var nextIndex = i + 1;
                                    if (nextIndex > footprint.lines.length - 1) { nextIndex = 0 }
                                    var nextLine = footprint.lines[nextIndex];
                                    if (nextLine.line_side !== "in" && nextLine.line_side !== "out") {
                                        // If nextLine is not a main line => try next one
                                        dropIndex.push(nextIndex);
                                        nextIndex++;
                                        if (nextIndex > footprint.lines.length - 1) { nextIndex = 0 }
                                        nextLine = footprint.lines[nextIndex];
                                    }
                                    // Set inLine and outLine from previous and next lines
                                    var inLine = prevLine;
                                    var outLine = nextLine;
                                    var inDirection = "start";
                                    if (inLine.line_side !== "in") { inLine = nextLine; outLine = prevLine; inDirection = "end" }
                                    console.log("inLine", inLine);
                                    console.log("outLine", outLine);
                                    // Check if removing previous inline is enought, because if not, no need to continue this combi is not viable
                                    var linePerpOrigin = inLine.linePerp_start_origin;
                                    var newStartPoint_coords = inLine.line_coords_local[0];
                                    var newStartPoint_coords_global = inLine.line_coords_global[0];
                                    if (inDirection === "end") { linePerpOrigin = inLine.linePerp_end_origin; newStartPoint_coords = inLine.line_coords_local[1]; newStartPoint_coords_global = inLine.line_coords_global[1] }
                                    var newEndPoint_coords = get_coords_intersection_two_equations({ line_coef: inLine.linePerp_coef, line_origin: linePerpOrigin }, outLine);
                                    var newEndPoint_coords_global = map_helpers.local_to_mercator(newEndPoint_coords, capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix);
                                    // var testPolygon_coords = footprint.lines[i].line_coords_global.concat([newEndPoint_coords_global, newStartPoint_coords_global, footprint.lines[i].line_coords_global[0]]);
                                    var testPolygon_coords = [inLine.line_coords_global[1], outLine.line_coords_global[0], newEndPoint_coords_global, newStartPoint_coords_global, inLine.line_coords_global[1]];
                                    // if (inDirection === "end") { testPolygon_coords = footprint.lines[i].line_coords_global.concat([newStartPoint_coords_global, newEndPoint_coords_global, footprint.lines[i].line_coords_global[0]]) }
                                    if (inDirection === "end") { testPolygon_coords = [outLine.line_coords_global[1], inLine.line_coords_global[0], newStartPoint_coords_global, newEndPoint_coords_global, outLine.line_coords_global[1]] }
                                    var removable_area = turf.area(turf.polygon([testPolygon_coords]));
                                    console.log("removable_area", removable_area);
                                    // console.log("removable_area_true", turf.area(turf.intersect(turf.polygon([testPolygon_coords]), availableSurface)));

                                    // Check if removable area is enought to get to the target area to remove
                                    if (removable_area >= area_to_remove) { // Keep going only if max removable is greater or equal to area to remove
                                        // Get length to keep to get just enought area to be valid
                                        var area_to_keep = removable_area - area_to_remove;
                                        var length_to_keep = area_to_keep / buildingWidth;
                                        // length_to_keep = 0.01;
                                        console.log("length_to_keep", length_to_keep);
                                        // Get offset perp line by offset = length to keep
                                        var linePerp = {
                                            line_coef: inLine.linePerp_coef,
                                            line_origin: linePerpOrigin,
                                            line_type: inLine.line_type,
                                            line_coords_local: [newStartPoint_coords, newEndPoint_coords]
                                        }
                                        var linePerpOffset = get_lineOffset_data(linePerp, length_to_keep);
                                        // Get new start and end coords
                                        var finalStartPoint_coords = get_coords_intersection_two_equations(linePerpOffset, inLine);
                                        var finalEndPoint_coords = get_coords_intersection_two_equations(linePerpOffset, outLine);
                                        // Get new Edge
                                        var newEdge = get_line_data([finalStartPoint_coords, finalEndPoint_coords]);
                                        // Recreate footprint by replacing the edge
                                        var newFootprintLines = [];
                                        var newFootprintCoordsLocal = [];
                                        var newFootprintCoordsGlobal = [];
                                        for (var j = 0; j < footprint.lines.length; j++) {
                                            var currentLine = footprint.lines[j];

                                            if (j === i) {
                                                currentLine = newEdge;
                                            }
                                            else if (j === prevIndex) {
                                                currentLine.line_coords_global[1] = newEdge.line_coords_global[0];
                                                currentLine.line_coords_local[1] = newEdge.line_coords_local[0];
                                            }
                                            else if (j === nextIndex) {
                                                currentLine.line_coords_global[0] = newEdge.line_coords_global[1];
                                                currentLine.line_coords_local[0] = newEdge.line_coords_local[1];
                                            }

                                            if (!dropIndex.includes(j)) {
                                                newFootprintLines.push(currentLine);
                                            }

                                            // if (j === 0) {
                                            //     newFootprintCoordsLocal.push(currentLine.line_coords_local[0]);
                                            //     newFootprintCoordsGlobal.push(currentLine.line_coords_global[0]);
                                            // }
                                            // newFootprintCoordsLocal.push(currentLine.line_coords_local[1]);
                                            // newFootprintCoordsGlobal.push(currentLine.line_coords_global[1]);
                                        }
                                        newFootprintCoordsLocal.push(newFootprintLines[0].line_coords_local[0]);
                                        newFootprintCoordsGlobal.push(newFootprintLines[0].line_coords_global[0]);
                                        for (var j = 0; j < newFootprintLines.length; j++) {
                                            newFootprintCoordsLocal.push(newFootprintLines[j].line_coords_local[1]);
                                            newFootprintCoordsGlobal.push(newFootprintLines[j].line_coords_global[1]);
                                        }
                                        var newFootprintArea = turf.area(turf.polygon([newFootprintCoordsGlobal]));
                                        footprint = {
                                            lines: newFootprintLines,
                                            coords_global: newFootprintCoordsGlobal,
                                            coords_local: newFootprintCoordsLocal,
                                            area: newFootprintArea,
                                        }

                                        // Check kinked
                                        if (checkKinkedFootprint(newFootprintCoordsGlobal, footprint.lines[0])) {
                                            footprint = {
                                                lines: [],
                                                coords_local: [],
                                                coords_global: [],
                                                area: 0,
                                            }
                                        }

                                    }
                                    else {
                                        // Make this footprint invalid
                                        footprint = {
                                            lines: [],
                                            coords_local: [],
                                            coords_global: [],
                                            area: 0,
                                        }
                                    }

                                    break
                                }
                            }
                        }
                    }
                }
            }
        }
        // })

        return footprint
    }

    const recreateFootprint = (footprint, lines) => {
        // Set in clockwise way
        footprint = turf.rewind(footprint, { reverse: true });
        // Recreate data
        var new_coords_global = footprint.geometry.coordinates[0];
        var new_coords_local = [];
        new_coords_global.forEach(coords => {
            new_coords_local.push(map_helpers.mercator_to_local(coords, capacity.landBase.union.bbox.origin));
        })
        var new_lines = [];
        for (var i = 1; i < new_coords_local.length; i++) {
            var new_line = get_line_data([new_coords_local[i - 1], new_coords_local[i]]);
            new_line.line_side = "edge";
            var new_line_turf = turf.lineString([new_coords_global[i - 1], new_coords_global[i]]);
            // Search for same parameters in full footprint to get matching lines
            for (var j = 0; j < lines.length; j++) {
                var jline_turf = turf.lineString(lines[j].line_coords_global);
                var overlap = turf.lineOverlap(new_line_turf, jline_turf, { tolerance: 0.001 });
                if (overlap.features.length > 0) {
                    new_line.line_side = lines[j].line_side;
                    break;
                }
                // if ((new_line.line_coef).toFixed(2) === (lines[j].line_coef).toFixed(2) && (new_line.line_origin).toFixed(0) === (lines[j].line_origin).toFixed(0)) {
                //     new_line.line_side = lines[j].line_side;
                //     break;
                // }
            }
            new_lines.push(new_line);
        }

        return {
            coords_global: new_coords_global,
            coords_local: new_coords_local,
            lines: new_lines,
        }
    }

    const check_combination_mainLines = (mainLines) => {
        var taken = [];
        var tested = 0;

        for (var i = 0; i < mainLines.length; i++) {
            for (var j = 0; j < mainLines[i].length; j++) {
                tested++;
                if (!taken.includes(mainLines[i][j].id)) {
                    taken.push(mainLines[i][j].id);
                }
                else {
                    console.log("INVALID MAIN LINES");
                    return false
                }
            }
        }

        if (tested === taken.length) {
            return true
        }
        else {
            console.log("INVALID MAIN LINES");
            return false
        }
    }

    const get_combination_mainLines = (combination) => {
        // Get starting lines
        var startIndexes = [];
        for (var i = 0; i < combination.length; i++) {
            if ((combination[i].connexions.start_in === null || combination[i].connexions.start_in?.line_id === "perp") && (combination[i].connexions.start_out === null || combination[i].connexions.start_out?.line_id === "perp")) {
                startIndexes.push(i);
            }
        }

        // Create main lines
        var mainLines = [];
        for (var j = 0; j < startIndexes.length; j++) {
            var mainLine = [];
            for (var i = 0; i < combination.length; i++) {
                // Get real index
                var realIndex = startIndexes[j] + i;
                if (realIndex > combination.length - 1) {
                    realIndex -= combination.length
                }
                // Add line
                mainLine.push(combination[realIndex]);
                // Check if end of line
                if ((combination[realIndex].connexions.end_in === null || combination[realIndex].connexions.end_in?.line_id === "perp") && (combination[realIndex].connexions.end_out === null || combination[realIndex].connexions.end_out?.line_id === "perp")) {
                    mainLines.push(mainLine);
                    break
                }
            }
        }

        return mainLines
    }

    const get_combination_mainLines_all = (combination, buildingWidth, workingSurface) => {
        var mainLines = get_combination_mainLines(combination);

        // Check if closed circles /!\ COULD BE A BETTER TEST => doesnt work if ther is a closed circle + other lines
        if (mainLines.length === 0 && combination.length > 2) {
            console.log("Closed circle", [...combination]);
            // Get the longest line and split into two parts
            var maxLength = 0;
            var maxIndex = 0;
            for (var i = 0; i < combination.length; i++) {
                // distance between two points : d = sqrt( (x2 - x1)² + (y2 - y1) ² )
                var lineLength = Math.sqrt(Math.pow((combination[i].line_coords_local[1][0] - combination[i].line_coords_local[0][0]), 2) + Math.pow((combination[i].line_coords_local[1][1] - combination[i].line_coords_local[0][1]), 2));
                // var lineLength = turf.distance(turf.point(combination[i].line_coords_global[0]), turf.point(combination[i].line_coords_global[1])) * 1000;
                if (lineLength > maxLength) {
                    maxLength = lineLength;
                    maxIndex = i;
                }
            }
            // Get middle point
            var middlePoint_x = combination[maxIndex].line_coords_local[0][0] + ((combination[maxIndex].line_coords_local[1][0] - combination[maxIndex].line_coords_local[0][0]) / 2);
            if (combination[maxIndex].line_coords_local[0][0] < combination[maxIndex].line_coords_local[1][0]) {
                middlePoint_x = combination[maxIndex].line_coords_local[1][0] + ((combination[maxIndex].line_coords_local[0][0] - combination[maxIndex].line_coords_local[1][0]) / 2);
            }
            var middlePoint_y = combination[maxIndex].line_coef * middlePoint_x + combination[maxIndex].line_origin;

            // Create two parts of half line
            var halfLine_start = get_line_data([combination[maxIndex].line_coords_local[0], [middlePoint_x, middlePoint_y]]);
            halfLine_start.connexions = {
                start_in: combination[maxIndex].connexions.start_in,
                start_out: combination[maxIndex].connexions.start_out,
                end_in: null,
                end_out: null,
            }
            halfLine_start.line_footprint = get_line_footprint(halfLine_start, (buildingWidth / 2), workingSurface, true, false);
            halfLine_start.line_maxHeight = get_line_maxHeight(halfLine_start, capacity.buildable.volume.levels);
            halfLine_start.id = combination[maxIndex].id + "s";

            var halfLine_end = get_line_data([[middlePoint_x, middlePoint_y], combination[maxIndex].line_coords_local[1]]);
            halfLine_end.connexions = {
                start_in: null,
                start_out: null,
                end_in: combination[maxIndex].connexions.end_in,
                end_out: combination[maxIndex].connexions.end_out,
            }
            halfLine_end.line_footprint = get_line_footprint(halfLine_end, (buildingWidth / 2), workingSurface, false, true);
            halfLine_end.line_maxHeight = get_line_maxHeight(halfLine_end, capacity.buildable.volume.levels);
            halfLine_end.id = combination[maxIndex].id + "e";

            // Update combination
            var newCombination = [];
            var maxIndex_prev = maxIndex - 1;
            if (maxIndex_prev < 0) { maxIndex_prev = combination.length - 1 }
            var maxIndex_next = maxIndex + 1;
            if (maxIndex_next >= combination.length) { maxIndex_next = 0 }
            for (var i = 0; i < combination.length; i++) {
                if (i === maxIndex_prev) {
                    var lineModif = combination[i];
                    lineModif.connexions.end_in = {
                        line_id: halfLine_start.id,
                        side: "in",
                    };
                    lineModif.connexions.end_out = {
                        line_id: halfLine_start.id,
                        side: "out",
                    };
                    newCombination.push(lineModif);
                }
                else if (i === maxIndex) {
                    newCombination.push(halfLine_start);
                    newCombination.push(halfLine_end);
                }
                else if (i === maxIndex_next) {
                    var lineModif = combination[i];
                    lineModif.connexions.start_in = {
                        line_id: halfLine_end.id,
                        side: "in",
                    };
                    lineModif.connexions.start_out = {
                        line_id: halfLine_end.id,
                        side: "out",
                    };
                    newCombination.push(lineModif);
                }
                else {
                    newCombination.push(combination[i]);
                }
            }

            // Re Create mainLines
            mainLines = get_combination_mainLines(newCombination);
        }

        // Check validity
        var check = check_combination_mainLines(mainLines);

        if (check === true) {
            return mainLines
        }
        else {
            return []
        }
    }

    const get_combination_footprint_buffer = (mainLines, workingSurface, availableSurfaces, buffer_main, buffer_second, buildingWidth) => {

        // Get footprint
        var footprints = [];
        var buffers = [];
        var takenArea = 0;
        for (var i = 0; i < mainLines.length; i++) {
            var footprint = get_mainLine_footprint(mainLines[i], workingSurface, availableSurfaces, buffers, buffer_main, takenArea, buildingWidth);
            for (var j = 0; j < footprint.length; j++) {
                if (footprint[j].lines.length > 0) {
                    footprints.push(footprint[j]);
                    takenArea += footprint[j].area;
                    buffers.push(get_mainLine_buffer(footprint[j], buffer_main, buffer_second));
                }
            }
        }

        return { footprints, buffers }

    }

    const get_combination_levels = (footprints, levels, buildingWidth) => {
        var level_list = [];
        var total_area = 0;
        // for each levels, get all footprints, intersection with all working (or available) surfaces
        levels.forEach(level => {
            var level_current = {
                elevation: level.elevation,
                footprints: [],
                area: 0,
            }
            // Create unique surface
            var surface_union = null;
            if (level.workingSurfaces.length > 0) {
                surface_union = level.workingSurfaces[0];
                if (level.workingSurfaces.length > 1) {
                    for (var i = 1; i < level.workingSurfaces.length; i++) {
                        surface_union = turf.union(surface_union, level.workingSurfaces[i]);
                    }
                }
            }

            // Create footprints
            var level_footprints = [];
            var level_area = 0;
            if (surface_union !== null) {
                footprints.forEach(footprint => {
                    var footprint_intersection = turf.intersect(surface_union, turf.polygon([footprint.coords_global]));
                    if (footprint_intersection !== null) {
                        if (footprint_intersection?.geometry?.type === "MultiPolygon") {
                            footprint_intersection.geometry.coordinates.forEach(coords => {
                                var footprint_poly = turf.polygon(coords);
                                var footprint_area = turf.area(footprint_poly);
                                if (footprint_area >= (buildingWidth * buildingWidth / 2)) {
                                    footprint_poly.properties.area = footprint_area;
                                    level_area += turf.area(footprint_intersection);
                                    level_footprints.push(footprint_poly);
                                }
                            })
                        }
                        else if (footprint_intersection?.geometry?.type === "Polygon") {
                            var footprint_area = turf.area(footprint_intersection);
                            if (footprint_area >= (buildingWidth * buildingWidth / 2)) {
                                footprint_intersection.properties.area = footprint_area;
                                level_area += turf.area(footprint_intersection);
                                level_footprints.push(footprint_intersection);
                            }
                        }
                    }
                })
            }

            // Check max area total
            if (capacity?.buildable?.volume?.parameters?.max_area_total && capacity?.buildable?.volume?.parameters?.max_area_total < (level_area + total_area)) {
                console.log("!!-!!-!!-!! LEVEL AREA BIGGER THAN AUTHORIZED BY PLU RULES", level_area + total_area);
            }

            level_current.footprints = level_footprints;
            level_current.area = level_area;

            level_list.push(level_current);
            if (level.elevation > 0.01) {
                total_area += level_area;
            }

        })

        return { level_list, total_area }
    }



    const show_building = (building_levels) => {
        // Check if data already loaded
        if (map.current.getLayer('layer3Dbuilding')) {
            map.current.removeLayer('layer3Dbuilding');
            // console.log("REMOVE 3D LAYER");
        }
        // Set point 0
        // var point0 = capacity.landBase.union.bbox.origin;
        var point0 = capacity.landBase.union.center.geometry.coordinates;
        // Georeferencing
        var modelOrigin = point0;
        var modelAltitude = 0;
        var modelRotate = [0, 0, 0];
        var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
            modelOrigin,
            modelAltitude
        );
        // Set position, rotation and scale
        var modelTransform = {
            translateX: modelAsMercatorCoordinate.x,
            translateY: modelAsMercatorCoordinate.y,
            translateZ: modelAsMercatorCoordinate.z,
            rotateX: modelRotate[0],
            rotateY: modelRotate[1],
            rotateZ: modelRotate[2],
            scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
        };
        // Set THREE
        // var THREE = window.THREE;
        // Create the custom layer
        var customLayer = {
            id: 'layer3Dbuilding',
            type: 'custom',
            renderingMode: '3d',
            onAdd: function (map, gl) {
                this.camera = new THREE.Camera();
                this.scene = new THREE.Scene();
                // Create lights
                var directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
                directionalLight1.position.set(-10, -10, 20).normalize();
                this.scene.add(directionalLight1);
                // var directionalLight2 = new THREE.DirectionalLight(0xffffff);
                // directionalLight2.position.set(0, 70, 100).normalize();
                // this.scene.add(directionalLight2);
                var ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
                this.scene.add(ambientLight);

                // Create 3D shape of bbox
                // var level_index = 1;
                for (var level_index = 1; level_index < building_levels.length; level_index++) {
                    for (var footprint_index = 0; footprint_index < building_levels[level_index].footprints.length; footprint_index++) {
                        if (building_levels[level_index].footprints[footprint_index].geometry.coordinates.length === 1) {
                            // Get bbox local coordinates
                            var bbox_coords_global = building_levels[level_index].footprints[footprint_index].geometry.coordinates[0];
                            var bbox_coords = [];
                            for (var i = 0; i < bbox_coords_global.length; i++) {
                                var deltaX = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([point0[0], bbox_coords_global[i][1]])) * 1000);
                                if (bbox_coords_global[i][0] < point0[0]) {
                                    deltaX = -deltaX;
                                }
                                var deltaY = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([bbox_coords_global[i][0], point0[1]])) * 1000);
                                if (bbox_coords_global[i][1] < point0[1]) {
                                    deltaY = -deltaY;
                                }
                                bbox_coords.push([deltaX, deltaY]);
                            }
                            // console.log("bbox local coords: ", bbox_coords);
                            // Shape and Geometry
                            var bboxShape = new THREE.Shape();
                            bboxShape.moveTo(bbox_coords[0][0], bbox_coords[0][1]);
                            for (var i = 0; i < bbox_coords.length; i++) {
                                bboxShape.lineTo(bbox_coords[i][0], bbox_coords[i][1]);
                            }
                            var bboxExtrudeSettings = {
                                steps: 1,
                                depth: building_levels[level_index].elevation - building_levels[level_index - 1].elevation,
                                bevelEnabled: false
                            };
                            var bboxGeometry = new THREE.ExtrudeGeometry(bboxShape, bboxExtrudeSettings);
                            bboxGeometry.translate(0, 0, building_levels[level_index - 1].elevation);
                            var bboxMaterial = new THREE.MeshPhongMaterial({
                                color: '#F1F1F1',
                                // transparent: false,
                                // opacity: 0.4
                            });
                            var bboxMesh = new THREE.Mesh(bboxGeometry, bboxMaterial);
                            this.scene.add(bboxMesh);
                            // Edges
                            var edgesGeometry = new THREE.EdgesGeometry(bboxGeometry);
                            var edgesMesh = new THREE.LineSegments(edgesGeometry, new THREE.LineBasicMaterial({ color: "grey" }));
                            this.scene.add(edgesMesh);
                        }
                    }
                }


                // Add map
                this.map = map;
                // use the Mapbox GL JS map canvas for three.js
                this.renderer = new THREE.WebGLRenderer({
                    canvas: map.getCanvas(),
                    context: gl,
                    antialias: true
                });
                this.renderer.autoClear = false;
            },
            render: function (gl, matrix) {
                var rotationX = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(1, 0, 0),
                    modelTransform.rotateX
                );
                var rotationY = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 1, 0),
                    modelTransform.rotateY
                );
                var rotationZ = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 0, 1),
                    modelTransform.rotateZ
                );
                var m = new THREE.Matrix4().fromArray(matrix);
                var l = new THREE.Matrix4()
                    .makeTranslation(
                        modelTransform.translateX,
                        modelTransform.translateY,
                        modelTransform.translateZ
                    )
                    .scale(
                        new THREE.Vector3(
                            modelTransform.scale,
                            -modelTransform.scale,
                            modelTransform.scale
                        )
                    )
                    .multiply(rotationX)
                    .multiply(rotationY)
                    .multiply(rotationZ);

                this.camera.projectionMatrix = m.multiply(l);
                this.renderer.resetState()
                this.renderer.render(this.scene, this.camera);
                // this.map.triggerRepaint();
            }
        };
        map.current.addLayer(customLayer, layer_get_position('layer3Dbuilding'));
    }

    const getGPU = async () => {
        // RESET GPU
        // var rules = capacity.rules;
        // rules.gpu_data = "fetching";
        // setCapacity({ ...capacity, rules: rules, toSave: { autoSave: true, action: "", value: "" } });

        // RESET TOPO
        // var landBase = capacity.landBase;
        // landBase.topo = "fetching";
        // setCapacity({ ...capacity, landBase: landBase, toSave: { autoSave: false, action: "", value: "" } });

        // SHOW MAP LAYERS
        // var layers = map.current.getStyle().layers;
        // console.log("LAYERS:", layers);

        // RESET LOCATION
        var landBase = capacity.landBase;
        landBase.location = "fetching";
        setCapacity({ ...capacity, landBase: landBase, toSave: { autoSave: true, action: "", value: "" } });

    }

    const getTopo = () => {
        console.log("DEV TEST");
        console.log("CAPA", capacity);

        // RESET GPU
        var buildable = capacity.buildable;
        buildable.combinations = "fetching";
        setCapacity({ ...capacity, buildable: buildable, toSave: { autoSave: false, action: "", value: "" } });

        return



        // console.log("map layers", map.current.getStyle().layers);
        // map.current.getStyle().layers.map((layer) => {
        //     if (layer.type === 'symbol') {
        //         map.current.setPaintProperty(layer.id, `icon-opacity`, 0.5);
        //         map.current.setPaintProperty(layer.id, `text-opacity`, 0.5);
        //     } else {
        //         map.current.setPaintProperty(layer.id, `${layer.type}-opacity`, 0.5);
        //     }
        // })

        // Safety check
        if (!capacity?.buildable?.volume?.levels && !capacity.buildable.volume.levels.length > 1) {
            return;
        }
        if (!capacity.buildable.volume.levels[1]?.workingSurfaces && !capacity.buildable.volume.levels[1].workingSurfaces.length > 0) {
            return;
        }

        // __________Working Lines
        // Create working lines
        var workingLines = []
        var workingLinesGroups = []
        capacity.buildable.volume.levels[1].workingSurfaces.forEach(workingSurface => {
            var currentWorkingLines = [];
            var currentworkingLinesGroups = [];
            for (var i = 1; i < workingSurface.geometry.coordinates[0].length; i++) {
                currentWorkingLines.push(turf.lineString([workingSurface.geometry.coordinates[0][i - 1], workingSurface.geometry.coordinates[0][i]]));
                var line_coords = [map_helpers.mercator_to_local(workingSurface.geometry.coordinates[0][i - 1], capacity.landBase.union.bbox.origin), map_helpers.mercator_to_local(workingSurface.geometry.coordinates[0][i], capacity.landBase.union.bbox.origin)];
                var line_data = get_line_data(line_coords);
                currentworkingLinesGroups.push(line_data);
            }
            workingLines.push(currentWorkingLines);
            workingLinesGroups.push(currentworkingLinesGroups);
        })
        console.log("workingLines", workingLines);
        console.log("workingLinesGroups", workingLinesGroups);


        // if (map.current.getLayer('layerWorkingLines')) {
        //     map.current.removeLayer('layerWorkingLines');
        // }
        // if (map.current.getSource('sourceWorkingLines')) {
        //     map.current.removeSource('sourceWorkingLines');
        // }
        // // _Add source
        // map.current.addSource('sourceWorkingLines', {
        //     type: 'geojson',
        //     data: {
        //         type: 'FeatureCollection',
        //         features: workingLines[0],
        //     }
        // });
        // // _Add layer
        // map.current.addLayer({
        //     id: 'layerWorkingLines',
        //     type: 'line',
        //     source: 'sourceWorkingLines',
        //     layout: {
        //         'line-cap': 'round',
        //         'line-join': 'round'
        //     },
        //     paint: {
        //         'line-color': 'orange',
        //         'line-width': 3,
        //     },
        //     filter: ['in', '$type', 'LineString']
        // });


        // __________Testing Lines
        var testingLinesTurf = [];
        const buildingWidth = capacity?.buildable?.volume?.parameters?.max_building_width || 10;
        const buildingOffsetMain_type = capacity?.buildable?.volume?.parameters?.min_offset_facade_main || { type: "recul", value: 6 };
        const buildingOffsetMain = get_buildingOffsetFromType(buildingOffsetMain_type);
        const buildingOffsetSecond_type = capacity?.buildable?.volume?.parameters?.min_offset_facade_edge || { type: "recul", value: 4 };
        const buildingOffsetSecond = get_buildingOffsetFromType(buildingOffsetSecond_type);
        const boundOffset = 0.1;
        // Create testing lines
        var testingLinesGroups = [];
        workingLinesGroups.forEach((workingLinesGroup, workingSurfaces_Index) => {
            var testingLinesGroup = [];
            var workingLine_Index = 0;
            workingLinesGroup.forEach((workingLine, index) => {
                var currentTestingLine = get_lineOffset_data(workingLine, (buildingWidth / 2) + boundOffset, capacity.buildable.volume.levels[1].workingSurfaces[workingSurfaces_Index]);
                // var currentTestingLine = get_lineOffset_data(workingLine, (buildingWidth / 2) + boundOffset);
                if (currentTestingLine === null) { return }
                currentTestingLine.line_footprint = get_line_footprint(currentTestingLine, (buildingWidth / 2), capacity.buildable.volume.levels[1].workingSurfaces[workingSurfaces_Index]);
                currentTestingLine.line_maxHeight = get_line_maxHeight(currentTestingLine, capacity.buildable.volume.levels);
                currentTestingLine.id = "line_" + workingLine_Index;
                currentTestingLine.lineParal_subLines = get_subLines(currentTestingLine, buildingWidth, buildingOffsetMain, capacity.buildable.volume.levels[1].workingSurfaces[workingSurfaces_Index]);
                testingLinesGroup.push(currentTestingLine);
                testingLinesTurf.push(turf.lineString(currentTestingLine.line_coords_global));
                workingLine_Index++;
            })
            testingLinesGroups.push(testingLinesGroup);
        })
        console.log("testingLinesGroups", testingLinesGroups);


        // __________Main Lines Combinaitions
        var mainLinesCombinationsGroups = [];
        testingLinesGroups.forEach((testingLinesGroup, testingLinesGroup_Index) => {
            var mainLinesCombinationsGroup = [];

            // Get list of all testing line ids
            var testingLines_ids = [];
            testingLinesGroup.forEach(testingLine => {
                testingLines_ids.push(testingLine.id);
            });

            // Get ids combinations
            var testingLines_ids_combinations = get_lines_all_combinations(testingLines_ids);
            console.log("testingLines_ids_combinations", testingLines_ids_combinations);


            // Get detailed combinations
            var index = 0;
            for (var i = 0; i < testingLines_ids_combinations.length; i++) {
                var linesDetailed = get_lines_detailed_combination(testingLines_ids_combinations[i], testingLinesGroup, capacity.buildable.volume.levels[1].workingSurfaces[testingLinesGroup_Index]);
                var detailed_combi_mainLines = get_combination_mainLines_all(linesDetailed, buildingWidth, capacity.buildable.volume.levels[1].workingSurfaces[testingLinesGroup_Index]);
                var detailed_combi_footprint_buffer = get_combination_footprint_buffer(detailed_combi_mainLines, capacity.buildable.volume.levels[1].workingSurfaces[testingLinesGroup_Index], capacity.buildable.volume.levels[1].availableSurfaces, buildingOffsetMain, buildingOffsetSecond, buildingWidth);
                var detailed_combi_levels_area = get_combination_levels(detailed_combi_footprint_buffer.footprints, capacity.buildable.volume.levels, buildingWidth);
                // if (detailed_combi_levels_area.total_area > 0) {
                mainLinesCombinationsGroup.push({
                    id: index,
                    id_list: testingLines_ids_combinations[i],
                    line_list: linesDetailed,
                    mainLines: detailed_combi_mainLines,
                    mainLines_footprint: detailed_combi_footprint_buffer.footprints,
                    mainLines_buffer: detailed_combi_footprint_buffer.buffers,
                    mainLines_levels: detailed_combi_levels_area.level_list,
                    mainLines_area: detailed_combi_levels_area.total_area,
                });
                index++;
                // }
            }

            mainLinesCombinationsGroups.push(mainLinesCombinationsGroup);

        })
        console.log("mainLinesCombinationsGroups", mainLinesCombinationsGroups);

        var finalCombinations = mainLinesCombinationsGroups;
        // var finalCombinations = mergeAndElagCombinations(mainLinesCombinationsGroups);
        console.log("finalCombinations", finalCombinations);

        var current_buildable = { ...capacity.buildable };
        current_buildable.combinations = finalCombinations;

        setCapacity({ ...capacity, buildable: current_buildable, toSave: { autoSave: false, action: "", value: "" } });







        if (map.current.getLayer('layertestingLinesGroups')) {
            map.current.removeLayer('layertestingLinesGroups');
        }
        if (map.current.getLayer('layerTestingFootprints')) {
            map.current.removeLayer('layerTestingFootprints');
        }
        if (map.current.getSource('sourcetestingLinesGroups')) {
            map.current.removeSource('sourcetestingLinesGroups');
        }
        // // test with negativ buffer
        // var buffer = turf.buffer(capacity.buildable.volume.levels[1].workingSurfaces[0], -(buildingWidth) / 2000);
        // console.log("buffer", buffer);
        // var bufferLines = turf.polygonToLine(buffer);
        // console.log("bufferLines", bufferLines);
        // // test with line offset of whole linear ring
        // var offset = turf.lineOffset(turf.polygonToLine(capacity.buildable.volume.levels[1].workingSurfaces[0]), -buildingWidth / 1000);
        // console.log(offset);
        // // test with local rules
        // var lineOffset_data = get_lineOffset_data(workingLinesGroups[0][2], (buildingWidth / 2) + 0.1, capacity.buildable.volume.levels[1].workingSurfaces[0]);
        // console.log("lineOffset_data", lineOffset_data);
        // var lineOffset_turf = turf.lineString(lineOffset_data.line_coords_global);
        // var maxH = get_line_maxHeight(testingLinesGroups[0][2], capacity.buildable.volume.levels);
        // console.log("max height", maxH);

        // var lineOffsetInside_data = get_lineOffset_data(lineOffset_data, (buildingWidth / 2), capacity.buildable.volume.levels[1].workingSurfaces[0]);
        // var lineOffsetInside_turf = turf.lineString(lineOffsetInside_data.line_coords_global);
        // var lineOffsetOutside_data = get_lineOffset_data(lineOffset_data, -(buildingWidth / 2), capacity.buildable.volume.levels[1].workingSurfaces[0]);
        // var lineOffsetOutside_turf = turf.lineString(lineOffsetOutside_data.line_coords_global);
        // var footprint_coords = lineOffsetInside_data.line_coords_global.concat(lineOffsetOutside_data.line_coords_global.reverse());
        // var footprint_coords_local = lineOffsetInside_data.line_coords_local.concat(lineOffsetOutside_data.line_coords_local.reverse());
        // footprint_coords.push(lineOffsetInside_data.line_coords_global[0]);
        // var lineOffset_footprint = turf.polygon([footprint_coords]);

        // var lineOffset_buffer = turf.buffer(lineOffset_footprint, 0.004, { steps: 1 });
        // var lineOffsetInsideBuffer_data = get_lineOffset_data(get_line_data([footprint_coords_local[0], footprint_coords_local[1]]), 4);
        // var lineOffsetOutsideBuffer_data = get_lineOffset_data(get_line_data([footprint_coords_local[1], footprint_coords_local[2]]), 4);
        // var lineOffsetInsideBuffer_turf = turf.lineString(lineOffsetInsideBuffer_data.line_coords_global);

        // // var line_footprint = get_line_footprint(lineOffset_data, (buildingWidth / 2));
        // var line_footprint = get_line_footprint(lineOffset_data, (buildingWidth / 2), capacity.buildable.volume.levels[1].workingSurfaces[0]);
        // console.log("line_footprint", line_footprint);
        // var line_footprint_turf = turf.polygon([line_footprint.coords_global]);
        // var line_footprint_buffer = get_line_footprint_buffer(line_footprint, 4);
        // console.log("line_footprint_buffer", line_footprint_buffer);
        // var line_footprint_buffer_turf = turf.polygon([line_footprint_buffer.coords_global]);
        // var subLines = get_subLines(lineOffset_data, buildingWidth, buildingOffsetMain, capacity.buildable.volume.levels[1].workingSurfaces[0]);
        // console.log("subLines", subLines);
        // var subLines_turf = [];
        // subLines.forEach(subLine => {
        //     if (subLine !== null) {
        //         subLines_turf.push(turf.lineString(subLine.line_coords_global));
        //     }
        // });



        // LOOP TH COMBINATIONS
        var i = 0;
        var i_max = Math.min(51, mainLinesCombinationsGroups[0].length);
        function myLoop() {
            setTimeout(function () {
                // console.log(i);
                showLines(mainLinesCombinationsGroups[0][i], i);
                i++;
                if (i < i_max) {
                    myLoop();
                }
                if (i === i_max + 1) {
                    hideLoop();
                }
            }, 400)
        }


        const hideLoop = () => {
            // Remove source and data
            if (map.current.getLayer('layerLoopLines')) {
                map.current.removeLayer('layerLoopLines');
            }
            if (map.current.getLayer('layerLoopPolygons')) {
                map.current.removeLayer('layerLoopPolygons');
            }
            if (map.current.getSource('sourceLoop')) {
                map.current.removeSource('sourceLoop');
            }
        }

        const showLines = (mainLinesCombination, index) => {
            console.log("Show", index);

            // show_building(mainLinesCombination.mainLines_levels);

            // PB : 

            // Create turf data
            // var turfList = [];
            // for (var i = 0; i < linesList.length; i++) {
            // var line_Index = parseInt(linesList[i].substring(linesList[i].indexOf("_") + 1));
            // var line = testingLinesGroups[0][line_Index];
            // var line_turf = turf.lineString(line.line_coords_global);
            // turfList.push(line_turf);
            // var footprint_tirf = turf.polygon([line.line_footprint.coords_global]);
            // turfList.push(footprint_tirf);
            // }
            var lines_turf = [];
            mainLinesCombination.line_list.forEach(line => {
                lines_turf.push(turf.lineString(line.line_coords_global));
            })
            var footprint_turf = [];
            mainLinesCombination.mainLines_footprint.forEach(footprint => {
                footprint_turf.push(turf.polygon([footprint.coords_global]));
            })

            hideLoop();

            // _Add source
            map.current.addSource('sourceLoop', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: lines_turf.concat(footprint_turf),
                }
            });

            // _Add layer
            map.current.addLayer({
                id: 'layerLoopLines',
                type: 'line',
                source: 'sourceLoop',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': 'red',
                    'line-width': 3,
                },
                filter: ['in', '$type', 'LineString']
            }, layer_get_position('layerLoopLines'));
            map.current.addLayer({
                id: 'layerLoopPolygons',
                type: 'fill',
                source: 'sourceLoop',
                paint: {
                    'fill-color': "red",
                    'fill-opacity': 0.2,
                },
                filter: ['in', '$type', 'Polygon']
            }, layer_get_position('layerLoopPolygons'));

        }

        // TESTING
        console.log("----------------------");
        var testingLines_ids = [];
        testingLinesGroups[0].forEach(testingLine => {
            testingLines_ids.push(testingLine.id);
        });
        console.log("testingLines_ids", testingLines_ids);
        var testingLines_combination_by_2 = helpers.k_combinations(testingLines_ids, 2);
        console.log("testingLines_combination_by_2", testingLines_combination_by_2);
        var all_comb = get_lines_all_combinations(testingLines_ids);
        console.log("all_comb", all_comb);
        // myLoop();
        var showIndex = 6;
        // showLines(mainLinesCombinationsGroups[0][showIndex], showIndex);
        console.log("showIndex", showIndex);


        var detailed_combi = get_lines_detailed_combination(all_comb[showIndex], testingLinesGroups[0], capacity.buildable.volume.levels[1].workingSurfaces[0]);
        console.log("detailed_combi", detailed_combi);

        var detailed_combi_mainLines = get_combination_mainLines_all(detailed_combi, buildingWidth, capacity.buildable.volume.levels[1].workingSurfaces[0]);
        console.log("detailed_combi_mainLines", detailed_combi_mainLines);

        var detailed_combi_footprint_buffer = get_combination_footprint_buffer(detailed_combi_mainLines, capacity.buildable.volume.levels[1].workingSurfaces[0], capacity.buildable.volume.levels[1].availableSurfaces, 4, 2, buildingWidth);
        console.log("detailed_combi_footprint_buffer", detailed_combi_footprint_buffer);

        var detailed_combi_levels_area = get_combination_levels(detailed_combi_footprint_buffer.footprints, capacity.buildable.volume.levels, buildingWidth);
        console.log("detailed_combi_levels", detailed_combi_levels_area.level_list);

        var footprint_turf = [];
        // footprint_turf = turf_to_show;


        console.log("footprint_turf", footprint_turf);
















        // var lineOffset_origin = get_lineOffset_origin(workingLinesGroups[0][4], (buildingWidth / 2) + 0.1);
        // console.log("lineOffset_origin", lineOffset_origin);
        // var lineOffset_coords_local = get_line_coords_from_equation(workingLinesGroups[0][4].line_coef, lineOffset_origin, capacity.landBase.union.bbox.lengthH);
        // console.log("lineOffset_coords_local", lineOffset_coords_local);
        // var lineOffset_coords_global = [map_helpers.local_to_mercator(lineOffset_coords_local[0], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix), map_helpers.local_to_mercator(lineOffset_coords_local[1], capacity.landBase.union.bbox.origin, capacity.landBase.union.bbox.matrix)];
        // console.log("lineOffset_coords_global", lineOffset_coords_global);
        // var lineOffset_turf = turf.lineString(lineOffset_coords_global);
        // console.log("lineOffset_turf", lineOffset_turf);
        // var lineOffset_intersect_turf = get_offsetLine_intersection_WorkingSurface2(lineOffset_turf, capacity.buildable.volume.levels[1].workingSurfaces[0]);
        // console.log("lineOffset_intersect_turf", lineOffset_intersect_turf);
        // Create footprint buffer around testing line
        // var footprint = turf.buffer(lineOffset_intersect_turf, buildingWidth / 2000, { steps: 1 });
        // console.log("footprint", footprint);

        // _Add source
        map.current.addSource('sourcetestingLinesGroups', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                // features: [],
                features: footprint_turf,
                // features: [turf.lineString(testingLinesGroups[0][1].line_coords_global), turf.lineString(testingLinesGroups[0][2].line_coords_global)],
                // features: [line_footprint_turf, line_footprint_buffer_turf].concat(testingLinesTurf).concat(subLines_turf),
                // features: [bufferLines, lineOffset_turf, lineOffset_footprint, lineOffset_buffer],
                // features: [bufferLines, lineOffset_intersect_turf, footprint],
                // features: [bufferLines, testingLinesGroups[0][0].offset1, testingLinesGroups[0][1].offset1, testingLinesGroups[0][2].offset1],
                // features: [testingLinesGroups[0][0].offset1],
            }
        });
        // _Add layer
        map.current.addLayer({
            id: 'layertestingLinesGroups',
            type: 'line',
            source: 'sourcetestingLinesGroups',
            layout: {
                'line-cap': 'round',
                'line-join': 'round'
            },
            paint: {
                'line-color': 'green',
                'line-width': 2,
            },
            filter: ['in', '$type', 'LineString']
        }, layer_get_position('layertestingLinesGroups'));
        // Add layer footprints
        map.current.addLayer({
            id: 'layerTestingFootprints',
            type: 'fill',
            source: 'sourcetestingLinesGroups',
            paint: {
                'fill-color': "green",
                'fill-opacity': 0.2,
            },
            filter: ['in', '$type', 'Polygon']
        }, layer_get_position('layerTestingFootprints'));






        // var buildable_edges = capacity?.buildable?.volume?.interpreted[1];
        // console.log("Buildable edges", buildable_edges);

        // var buildable_edges_coords = buildable_edges?.geometry?.attributes?.position?.array;
        // console.log("Buildable vertices", buildable_edges_coords);

        // // Create buildable points and lines
        // var buildable_edges_points = [];
        // var buildable_edges_lines = [];
        // // Loo th coords every 3 elements becaus eone point is made of 3 coords
        // for (var i = 0; i < buildable_edges_coords.length; i += 3) {
        //     // Add point
        //     buildable_edges_points.push([buildable_edges_coords[i], buildable_edges_coords[i + 1], buildable_edges_coords[i + 2]]);
        //     // Add line
        //     if (i % 2 !== 0) { // One line is made of 2 points so operation is made only if i is even
        //         buildable_edges_lines.push({
        //             point_start: [buildable_edges_coords[i - 3], buildable_edges_coords[i - 2], buildable_edges_coords[i - 1]],
        //             point_end: [buildable_edges_coords[i], buildable_edges_coords[i + 1], buildable_edges_coords[i + 2]],
        //         })
        //     }
        // }
        // console.log("Buildable points", buildable_edges_points);
        // console.log("Buildable lines", buildable_edges_lines);



        // // Get only lines at elevation 0
        // var buildable_edges_lines_level0 = [];
        // buildable_edges_lines.forEach(line => {
        //     if (line.point_start[2] === 0 && line.point_end[2] === 0) {
        //         buildable_edges_lines_level0.push(line);
        //     }
        // })
        // console.log("buildable_edges_lines_level0", buildable_edges_lines_level0);

        // // Get only points at elevation 0 & their corresponding lines
        // var buildable_edges_points_level0_check = [];
        // var buildable_edges_points_level0 = [];
        // var point_index = 0;
        // buildable_edges_points.forEach(point => {
        //     if (point[2] === 0) {
        //         // check if not already taken
        //         if (!(buildable_edges_points_level0_check.includes(point.join("|")))) {
        //             var linesStart = [];
        //             var linesEnd = [];
        //             buildable_edges_lines_level0.forEach((line, index) => {
        //                 if ((line.point_start[0] === point[0] && line.point_start[1] === point[1] && line.point_start[2] === point[2])) {
        //                     linesStart.push(index);
        //                     line.point_start_index = point_index;
        //                 }
        //                 if ((line.point_end[0] === point[0] && line.point_end[1] === point[1] && line.point_end[2] === point[2])) {
        //                     linesEnd.push(index);
        //                     line.point_end_index = point_index;
        //                 }
        //             });
        //             buildable_edges_points_level0.push({
        //                 coordinates: point,
        //                 linesStart: linesStart,
        //                 linesEnd: linesEnd,
        //             });

        //             buildable_edges_points_level0_check.push(point.join("|"));
        //             point_index++;
        //         }
        //     }
        // })
        // console.log("buildable_edges_points_level0", buildable_edges_points_level0);

        // // Try making line segments
        // var lineSegments = [];
        // var lineSegmentsCheck = [];

        // for (var i = 0; i < buildable_edges_lines_level0.length; i++) {
        //     // currentSegment = [];
        //     if (lineSegmentsCheck.includes(i)) {
        //         continue;
        //     }
        //     var currentSegment = getLineSegment([], i, buildable_edges_lines_level0, buildable_edges_points_level0);
        //     if (currentSegment.length > 0) {
        //         lineSegments.push(currentSegment);
        //         lineSegmentsCheck = lineSegmentsCheck.concat(currentSegment);
        //     }
        // }

        // console.log("lineSegments", lineSegments);

        // // Create turf polygons
        // var polygpons = [];
        // lineSegments.forEach(poly => {
        //     var localCoords = [];
        //     poly.forEach(point => {
        //         localCoords.push(map_helpers.local_to_mercator([buildable_edges_lines_level0[point].point_start[0], buildable_edges_lines_level0[point].point_start[1]], capacity.landBase.union.bbox.matrix));
        //     })
        //     localCoords.push(localCoords[0]);
        //     console.log("localCoords", localCoords);
        //     polygpons.push(turf.polygon([localCoords]));
        // })
        // console.log("polygpons", polygpons);


        // // Get higher horizontal plane of buildable volume
        // var higherPlane = getHigherPlanefromBuildable(capacity.buildable.volume.parameters.max_height, capacity.buildable.volume.interpreted[0]);
        // console.log("higherPlane", higherPlane);

        // // Get elevations
        // var levelHeight = 2.6;
        // var levelMaxNb = Math.floor(higherPlane / levelHeight);
        // console.log("levelMaxNb", levelMaxNb);
        // var elevations = [0.01];
        // for (var i = 1; i <= levelMaxNb; i++) {
        //     elevations.push(parseFloat((i * levelHeight).toFixed(2)));
        // }
        // console.log("elevations", elevations);

        // // Get buildable data for each elevation (by 2 to be optimized)
        // var levels = [];
        // for (var i = 0; i < elevations.length; i += 2) {
        //     // Get elevations to fetch
        //     var elevation0 = elevations[i];
        //     var elevation1 = null;
        //     if (i !== elevations.length - 1) {
        //         elevation1 = elevations[i + 1];
        //     }

        //     var levelBuildableData = getLevelBuildableData(elevation0, elevation1, capacity.buildable.volume.interpreted[0]);
        //     levels = levels.concat(levelBuildableData);
        // }
        // console.log("levels", levels);

        // // Get global data
        // var buildableAreaTotal = 0;
        // levels.forEach(level => {
        //     buildableAreaTotal += level.buildableArea;
        // })

        // var capa = { ...capacity };
        // capa.buildable.volume.levels = levels;
        // capa.buildable.volume.parameters.buildableArea = buildableAreaTotal;
        // capa.buildable.volume.parameters.higherPlane = higherPlane;
        // capa.buildable.volume.parameters.levelHeight = levelHeight;
        // setCapacity(capa);







        // // Remove turf data
        // if (map.current.getLayer('layerTest')) {
        //     map.current.removeLayer('layerTest');
        // }
        // if (map.current.getLayer('layerTestLine')) {
        //     map.current.removeLayer('layerTestLine');
        // }
        // if (map.current.getLayer('layerTestLine2')) {
        //     map.current.removeLayer('layerTestLine2');
        // }
        // if (map.current.getLayer('layerTestSurfaces')) {
        //     map.current.removeLayer('layerTestSurfaces');
        // }
        // if (map.current.getSource('sourceTest')) {
        //     map.current.removeSource('sourceTest');
        // }
        // if (map.current.getSource('sourceTestLine')) {
        //     map.current.removeSource('sourceTestLine');
        // }
        // if (map.current.getSource('sourceTestLine2')) {
        //     map.current.removeSource('sourceTestLine2');
        // }
        // if (map.current.getSource('sourceTestSurfaces')) {
        //     map.current.removeSource('sourceTestSurfaces');
        // }
        // // Add source
        // map.current.addSource('sourceTest', {
        //     type: 'geojson',
        //     data: {
        //         type: 'FeatureCollection',
        //         features: [],
        //         // features: union,
        //     }
        // });
        // map.current.addSource('sourceTestLine', {
        //     type: 'geojson',
        //     data: {
        //         type: 'FeatureCollection',
        //         features: [],
        //         // features: buildingLines,
        //     }
        // });
        // map.current.addSource('sourceTestSurfaces', {
        //     type: 'geojson',
        //     data: {
        //         type: 'FeatureCollection',
        //         features: levels[0].buildableSurfaces
        //     }
        // });
        // map.current.addSource('sourceTestLine2', {
        //     type: 'geojson',
        //     data: {
        //         type: 'FeatureCollection',
        //         features: [],
        //         // features: buildableSurfaceLinesSimplified,
        //     }
        // });
        // // Add layer polys
        // map.current.addLayer({
        //     id: 'layerTest',
        //     type: 'fill',
        //     source: 'sourceTest',
        //     paint: {
        //         'fill-color': "#FF0000",
        //         'fill-opacity': 0.2,
        //     },
        // });
        // map.current.addLayer({
        //     id: 'layerTestLine',
        //     type: 'line',
        //     source: 'sourceTestLine',
        //     layout: {
        //         'line-cap': 'round',
        //         'line-join': 'round'
        //     },
        //     paint: {
        //         'line-color': "#FF0000",
        //         'line-width': 2.5,
        //     },
        // });
        // map.current.addLayer({
        //     id: 'layerTestSurfaces',
        //     type: 'fill',
        //     source: 'sourceTestSurfaces',
        //     paint: {
        //         'fill-color': "#00FF00",
        //         'fill-opacity': 0.2,
        //     },
        // });
        // map.current.addLayer({
        //     id: 'layerTestLine2',
        //     type: 'line',
        //     source: 'sourceTestLine2',
        //     layout: {
        //         'line-cap': 'round',
        //         'line-join': 'round'
        //     },
        //     paint: {
        //         'line-color': "#00FF00",
        //         'line-width': 2.5,
        //         'line-dasharray': [2, 2]
        //     },
        // });











        // var topo_majic_buildings = await fetch_helpers.get_topo_majic_buildings(capacity);
        // console.log(topo_majic_buildings);
        // // Get maxHeight
        // var maxHeight = -1;
        // if (topo_majic_buildings?.buildings?.buildings_close?.data && topo_majic_buildings.buildings.buildings_close.data.length > 0) {
        //     for (var i = 0; i < topo_majic_buildings.buildings.buildings_close.data.length; i++) {
        //         if (topo_majic_buildings.buildings.buildings_close.data[i]?.properties?.height_max > maxHeight) {
        //             maxHeight = topo_majic_buildings.buildings.buildings_close.data[i]?.properties?.height_max;
        //         }
        //     }
        // }
        // console.log("maxHeight", maxHeight);


        // Create turf line elemnt of actual profile
        // var profile_line0 = turf.lineString([[capacity.landBase.topo.point_max[0], capacity.landBase.topo.point_max[1]], [capacity.landBase.topo.point_min[0], capacity.landBase.topo.point_min[1]]]);

        // // Get bearing
        // var bearing = turf.bearing(turf.point([capacity.landBase.topo.point_max[0], capacity.landBase.topo.point_max[1]]), turf.point([capacity.landBase.topo.point_min[0], capacity.landBase.topo.point_min[1]]));
        // // Get further points
        // var destination1 = turf.destination(turf.point([capacity.landBase.topo.point_max[0], capacity.landBase.topo.point_max[1]]), 0.5, bearing);
        // var destination2 = turf.destination(turf.point([capacity.landBase.topo.point_max[0], capacity.landBase.topo.point_max[1]]), -0.5, bearing);
        // // Create new line
        // var profile_line1 = turf.lineString([destination1.geometry.coordinates, destination2.geometry.coordinates]);
        // console.log(profile_line1);
        // // Create union poly
        // var polygon = turf.polygon(capacity.landBase.union.geometry.geometry.coordinates);
        // console.log(polygon);
        // // Get intersections points
        // var intersects = turf.lineIntersect(profile_line1, polygon);
        // console.log(intersects);
        // if (intersects.features.length > 1) {

        // }
        // var profile_line = turf.lineString([intersects.features[0].geometry.coordinates, intersects.features[intersects.features.length - 1].geometry.coordinates]);
        // var sampling = Math.ceil(turf.length(profile_line) / 0.0025);
        // console.log(sampling);


        // // SHOW LINE
        // // Delete if exists already
        // if (map.current.getLayer('layerProfile')) {
        //     map.current.removeLayer('layerProfile');
        // }
        // if (map.current.getSource('sourceProfile')) {
        //     map.current.removeSource('sourceProfile');
        // }
        // // _Add source
        // var data = {
        //     type: 'FeatureCollection',
        //     features: [
        //         profile_line
        //     ],
        // };
        // map.current.addSource('sourceProfile', {
        //     type: 'geojson',
        //     data: data
        // });
        // // _Add layer
        // map.current.addLayer({
        //     id: 'layerProfile',
        //     type: 'line',
        //     source: 'sourceProfile',
        //     layout: {
        //         'line-cap': 'round',
        //         'line-join': 'round'
        //     },
        //     paint: {
        //         'line-color': 'red',
        //         'line-width': 4,
        //     },
        //     filter: ['in', '$type', 'LineString']
        // });

        // // SHOW POLY
        // // Remove layer
        // if (map.current.getLayer('tGridLayer')) {
        //     map.current.removeLayer('tGridLayer');
        // }
        // // Remove source
        // if (map.current.getSource('tGridSource')) {
        //     map.current.removeSource('tGridSource');
        // }
        // // Add source to map
        // map.current.addSource('tGridSource', {
        //     'type': 'geojson',
        //     'data': {
        //         type: 'FeatureCollection',
        //         features: [
        //             polygon,
        //         ],
        //     },
        // });
        // // Add layers to map
        // map.current.addLayer({
        //     'id': 'tGridLayer',
        //     'type': 'fill',
        //     'source': 'tGridSource',
        //     'layout': {
        //         'visibility': 'visible'
        //     },
        //     'paint': {
        //         'fill-color': 'red',
        //         'fill-opacity': 0.4,
        //         'fill-outline-color': 'red'
        //     }
        // });

        // // Create api url
        // var api_url = "https://wxs.ign.fr/calcul/alti/rest/elevationLine.json?sampling=" + sampling + "&lon=" + intersects.features[0].geometry.coordinates[0] + "|" + intersects.features[intersects.features.length - 1].geometry.coordinates[0] + "&lat=" + intersects.features[0].geometry.coordinates[1] + "|" + intersects.features[intersects.features.length - 1].geometry.coordinates[1];
        // console.log(api_url);

        // Fetch
        // fetch(api_url)
        //     .then(res => res.json())
        //     .then(
        //         (result) => {
        //             console.log("ALTI result", result);
        //             // Get real max and min
        //             var point_max_new = [result.elevations[0].lon, result.elevations[0].lat, result.elevations[0].z];
        //             var point_min_new = [result.elevations[0].lon, result.elevations[0].lat, result.elevations[0].z];
        //             for (var i = 0; i < result.elevations.length; i++) {
        //                 if (result.elevations[i].z > point_max_new[2]) { point_max_new = [result.elevations[i].lon, result.elevations[i].lat, result.elevations[i].z] };
        //                 if (result.elevations[i].z < point_min_new[2]) { point_min_new = [result.elevations[i].lon, result.elevations[i].lat, result.elevations[i].z] };
        //             }
        //             var height_new = point_max_new[2] - point_min_new[2];
        //             var length_new = (turf.distance(turf.point([point_min_new[0], point_min_new[1]]), turf.point([point_max_new[0], point_max_new[1]])) * 1000);
        //             var slide_new = (height_new / length_new) * 100;
        //         },
        //         (error) => {
        //             console.log("Error fetching IGN Alti", error);
        //         }
        //     )


        // // Matching btw osm & ign ones
        // var land_poly = turf.polygon(capacity.landBase.union.geometry.geometry.coordinates);
        // var buildings = [];
        // var buildings_land = {
        //     osm: [],
        //     ign: [],
        //     index: []
        // }
        // var matched_osm = [];
        // var matched_ign = [];
        // for (var i = 0; i < capacity.landBase.buildings_osm.length; i++) {
        //     var building_osm_poly = capacity.landBase.buildings_osm[i];
        //     var building_osm_area = turf.area(building_osm_poly);
        //     // Check land intersection
        //     var land_intersection_osm = turf.intersect(building_osm_poly, land_poly);
        //     if (land_intersection_osm !== null) {
        //         var land_intersection_osm_area = turf.area(land_intersection_osm);
        //         if ((land_intersection_osm_area / building_osm_area) > 0.5) {
        //             buildings_land.osm.push(building_osm_poly.id);
        //         }
        //     }

        //     var matched = null;
        //     for (var j = 0; j < capacity.landBase.buildings_ign.length; j++) {
        //         var building_ign_poly = capacity.landBase.buildings_ign[j];
        //         var building_ign_area = turf.area(building_ign_poly);
        //         // Check land intersection
        //         if (i === 0) { // Check only on first loop of i
        //             var land_intersection_ign = turf.intersect(building_ign_poly, land_poly);
        //             if (land_intersection_ign !== null) {
        //                 var land_intersection_ign_area = turf.area(land_intersection_ign);
        //                 if ((land_intersection_ign_area / building_ign_area) > 0.5) {
        //                     buildings_land.ign.push(building_ign_poly.id);
        //                 }
        //             }
        //         }

        //         // Check area
        //         if (building_osm_area > (building_ign_area - 2) && building_osm_area < (building_ign_area + 2)) {
        //             // Check intersection
        //             var intersection = turf.intersect(building_osm_poly, building_ign_poly);
        //             if (intersection !== null && (turf.area(intersection) / building_osm_area) > 0.5) {
        //                 matched = j;
        //             }
        //         }
        //     }
        //     // Add to final list
        //     if (matched !== null) {
        //         matched_osm.push(building_osm_poly.id);
        //         matched_ign.push(capacity.landBase.buildings_ign[matched].id);
        //         buildings.push({
        //             geometry: building_osm_poly.geometry,
        //             properties: {
        //                 type: "matched",
        //                 id_osm: building_osm_poly.id,
        //                 id_ign: capacity.landBase.buildings_ign[matched].id,
        //                 area: building_osm_area,
        //                 height: capacity.landBase.buildings_ign[matched].properties.hauteur,
        //                 osm_properties: building_osm_poly.properties,
        //                 ign_properties: capacity.landBase.buildings_ign[matched].properties,
        //             },
        //             type: "Feature",
        //         });
        //     }
        // }
        // // Add unmatched buildings
        // for (var i = 0; i < capacity.landBase.buildings_osm.length; i++) {
        //     if (!matched_osm.includes(capacity.landBase.buildings_osm[i].id)) {
        //         buildings.push({
        //             geometry: capacity.landBase.buildings_osm[i].geometry,
        //             properties: {
        //                 type: "osm",
        //                 id_osm: capacity.landBase.buildings_osm[i].id,
        //                 id_ign: null,
        //                 area: turf.area(capacity.landBase.buildings_osm[i]),
        //                 height: capacity.landBase.buildings_osm[i].properties.height,
        //                 osm_properties: capacity.landBase.buildings_osm[i].properties,
        //                 ign_properties: null,
        //             },
        //             type: "Feature",
        //         });
        //     }
        // }
        // for (var i = 0; i < capacity.landBase.buildings_ign.length; i++) {
        //     if (!matched_ign.includes(capacity.landBase.buildings_ign[i].id)) {
        //         buildings.push({
        //             geometry: capacity.landBase.buildings_ign[i].geometry,
        //             properties: {
        //                 type: "ign",
        //                 id_osm: null,
        //                 id_ign: capacity.landBase.buildings_ign[i].id,
        //                 area: turf.area(capacity.landBase.buildings_ign[i]),
        //                 height: capacity.landBase.buildings_ign[matched].properties.hauteur,
        //                 osm_properties: null,
        //                 ign_properties: capacity.landBase.buildings_ign[i].properties,
        //             },
        //             type: "Feature",
        //         });
        //     }
        // }

        // // Get buildings land indexes
        // for (var i = 0; i < buildings.length; i++) {
        //     if (buildings_land.osm.includes(buildings[i].properties.id_osm) || buildings_land.ign.includes(buildings[i].properties.id_ign)) {
        //         buildings_land.index.push(i);
        //     }
        // }



        // console.log("buildings", buildings);
        // console.log("buildings land", buildings_land);


        // RESET TOPO
        // var landBase = capacity.landBase;
        // landBase.topo = "fetching";
        // setCapacity({ ...capacity, landBase: landBase, toSave: { autoSave: false, action: "", value: "" } });

        // RESET RISKS
        // setCapacity({ ...capacity, risks: "fetching", toSave: { autoSave: false, action: "", value: "" } });

        // RESET CONTEXT
        // setCapacity({ ...capacity, context: "fetching", toSave: { autoSave: true, action: "", value: "" } });

        // RESET GPU
        // var rules = capacity.rules;
        // rules.gpu_data = "fetching";
        // setCapacity({ ...capacity, rules: rules, toSave: { autoSave: true, action: "", value: "" } });




        // console.log("DVF", capacity);
        // // Create selected lands id list
        // var lands_id = [];
        // for (var i = 0; i < capacity.landBase.lands.length; i++) {
        //     lands_id.push(capacity.landBase.lands[i].properties.id);
        // }

        // var dvf_transactions = {};
        // var dvf_transactions_land = [];
        // capacity.context.dvf.dvf_list.forEach(mutation => {
        //     // Create key if not already done
        //     if (!(mutation.id_mutation in dvf_transactions)) {
        //         dvf_transactions[mutation.id_mutation] = {
        //             land_id: [],
        //             coordinates: [],
        //             date: "",
        //             nature: "",
        //             value: "",
        //             mutations: [],
        //             local: [],
        //             land_area: 0,
        //             local_area: 0,
        //         };
        //     }

        //     // Add data
        //     if (!dvf_transactions[mutation.id_mutation].land_id.includes(mutation.id_parcelle)) {
        //         dvf_transactions[mutation.id_mutation].land_id.push(mutation.id_parcelle);
        //         // Add land area
        //         if (!isNaN(parseFloat(mutation.surface_terrain))) {
        //             dvf_transactions[mutation.id_mutation].land_area += parseFloat(mutation.surface_terrain);
        //         }
        //         // Check if the land is a selected one
        //         if (lands_id.includes(mutation.id_parcelle) && !dvf_transactions_land.includes(mutation.id_mutation)) {
        //             dvf_transactions_land.push(mutation.id_mutation);
        //         }
        //     }
        //     dvf_transactions[mutation.id_mutation].coordinates.push([parseFloat(mutation.longitude), parseFloat(mutation.latitude)]);
        //     dvf_transactions[mutation.id_mutation].date = mutation.date_mutation;
        //     dvf_transactions[mutation.id_mutation].nature = mutation.nature_mutation;
        //     dvf_transactions[mutation.id_mutation].value = parseFloat(mutation.valeur_fonciere);
        //     dvf_transactions[mutation.id_mutation].mutations.push(mutation);
        //     if (mutation.type_local !== "None") {
        //         dvf_transactions[mutation.id_mutation].local.push({
        //             type: mutation.type_local,
        //             area: parseFloat(mutation.surface_reelle_bati),
        //             rooms: parseInt(mutation.nombre_pieces_principales),
        //         });
        //         if (!isNaN(parseFloat(mutation.surface_reelle_bati))) {
        //             dvf_transactions[mutation.id_mutation].local_area += parseFloat(mutation.surface_reelle_bati);
        //         }
        //     }
        // })
        // console.log("dvf_transactions", dvf_transactions);
        // console.log("dvf_transactions_land", dvf_transactions_land);


        // var dvf_stats = {};
        // for (var i = 0; i < capacity.context.dvf.dvf_data.length; i++) {
        //     var dvfdata = capacity.context.dvf.dvf_data[i];
        //     for (var j = 0; j < dvfdata.mutations.length; j++) {
        //         var mutation = dvfdata.mutations[j];
        //         var price = parseFloat(mutation.valeur_fonciere) / parseFloat(mutation.surface_reelle_bati);
        //         // if (mutation.type_local !== "None" && !isNaN(price)) {
        //         if (!isNaN(price)) {
        //             if (!dvf_stats[mutation.type_local]) {
        //                 dvf_stats[mutation.type_local] = {
        //                     sum: 0,
        //                     list_price: [],
        //                     list_id: []
        //                 };
        //             }
        //             dvf_stats[mutation.type_local].list_price.push(price);
        //             dvf_stats[mutation.type_local].sum += price;
        //             dvf_stats[mutation.type_local].list_id.push({ land_index: i, mutation_index: j });
        //         }
        //     }
        // }
        // console.log("dvf_stats", dvf_stats);

        // console.log("TREATMENT DVF", capacity);
        // var lands_id = [];
        // for (var i = 0; i < capacity.landBase.lands.length; i++) {
        //     lands_id.push(capacity.landBase.lands[i].properties.id);
        // }
        // var types = [];
        // var dvf_data = [];
        // var dvf_land = [];
        // var dvf_data_id = [];
        // for (var j = 0; j < capacity.landBase.sections.length; j++) {
        //     if (capacity?.context?.dvf[capacity.landBase.sections[j]]) {
        //         for (var i = 0; i < capacity.context.dvf[capacity.landBase.sections[j]].length; i++) {
        //             var index = dvf_data_id.indexOf(capacity.context.dvf[capacity.landBase.sections[j]][i].id_parcelle);
        //             if (index === -1) {
        //                 dvf_data_id.push(capacity.context.dvf[capacity.landBase.sections[j]][i].id_parcelle);
        //                 dvf_data.push({
        //                     id_parcelle: capacity.context.dvf[capacity.landBase.sections[j]][i].id_parcelle,
        //                     coordinates: [parseFloat(capacity.context.dvf[capacity.landBase.sections[j]][i].longitude), parseFloat(capacity.context.dvf[capacity.landBase.sections[j]][i].latitude)],
        //                     mutations: [capacity.context.dvf[capacity.landBase.sections[j]][i]]
        //                 });
        //                 // Check if the land is a selected one
        //                 if (lands_id.includes(capacity.context.dvf[capacity.landBase.sections[j]][i].id_parcelle)) {
        //                     dvf_land.push(dvf_data.length - 1);
        //                 }
        //             }
        //             else {
        //                 dvf_data[index].mutations.push(capacity.context.dvf[capacity.landBase.sections[j]][i]);
        //             }

        //             if (!types.includes(capacity.context.dvf[capacity.landBase.sections[j]][i].type_local)) {
        //                 types.push(capacity.context.dvf[capacity.landBase.sections[j]][i].type_local);
        //             }
        //         }
        //     }
        // }
        // console.log("DVF DATA", dvf_data);
        // console.log("DVF TYPES", types);
        // console.log("LANDS", lands_id);
        // console.log("DVF LANDS", dvf_land);



        // var extent = [capacity.landBase.union.bbox.coordinates[0][0][0], capacity.landBase.union.bbox.coordinates[0][0][1], capacity.landBase.union.bbox.coordinates[0][2][0], capacity.landBase.union.bbox.coordinates[0][2][1]];
        // console.log("EXTENT", extent);
        // var mask_poly = capacity.landBase.union.geometry;
        // var grid = turf.pointGrid(extent, 0.0025, { units: 'kilometers', mask: mask_poly });
        // // Add bounds points
        // for (var i = 0; i < capacity.landBase.union.bounds.length; i++) {
        //     grid.features.push({
        //         "type": "Feature",
        //         "properties": {},
        //         "geometry": {
        //             "type": "Point",
        //             "coordinates": capacity.landBase.union.bounds[i].start_coord_global
        //         }
        //     })
        // }
        // // Set if to each grid element
        // for (var i = 0; i < grid.features.length; i++) {
        //     grid.features[i].properties.id = i;
        // }
        // console.log("GRID", grid);
        // // Create triangulated network from grid
        // var tin = turf.tin(grid, "id");
        // console.log("TIN", tin);

        // // Create api url
        // var lon_list = "";
        // var lat_list = "";
        // for (var i = 0; i < grid.features.length; i++) {
        //     if (lon_list !== "") {
        //         lon_list = lon_list + "|";
        //     }
        //     lon_list = lon_list + grid.features[i].geometry.coordinates[0];
        //     if (lat_list !== "") {
        //         lat_list = lat_list + "|";
        //     }
        //     lat_list = lat_list + grid.features[i].geometry.coordinates[1];
        // }
        // var api_url = "https://wxs.ign.fr/calcul/alti/rest/elevation.json?lon=" + lon_list + "&lat=" + lat_list + "&zonly=true";

        // // Fetch
        // fetch(api_url)
        //     .then(res => res.json())
        //     .then(
        //         (result) => {
        //             console.log("ALTI result", result);
        //             // CREATE MATRIX
        //             var point_matrix = [];
        //             var point_max = [grid.features[0].geometry.coordinates[0], grid.features[0].geometry.coordinates[1], result.elevations[0]];
        //             var point_min = [grid.features[0].geometry.coordinates[0], grid.features[0].geometry.coordinates[1], result.elevations[0]];
        //             for (var i = 0; i < grid.features.length; i++) {
        //                 point_matrix.push({
        //                     coord_global: grid.features[i].geometry.coordinates,
        //                     coord_local: map_helpers.mercator_to_local(grid.features[i].geometry.coordinates, capacity.landBase.union.bbox.origin),
        //                     elevation: result.elevations[i]
        //                 });
        //                 if (result.elevations[i] > point_max[2]) { point_max = [grid.features[i].geometry.coordinates[0], grid.features[i].geometry.coordinates[1], result.elevations[i]] };
        //                 if (result.elevations[i] < point_min[2]) { point_min = [grid.features[i].geometry.coordinates[0], grid.features[i].geometry.coordinates[1], result.elevations[i]] };
        //             }
        //             var height = point_max[2] - point_min[2];
        //             var length = (turf.distance(turf.point([point_min[0], point_min[1]]), turf.point([point_max[0], point_max[1]])) * 1000);
        //             var slide = (height / length) * 100;
        //             var alti = {
        //                 tin: tin,
        //                 matrix: point_matrix,
        //                 point_max: point_max,
        //                 point_min: point_min,
        //                 height: height,
        //                 slide: slide
        //             }
        //             console.log("ALTI", alti);


        //             // set3D_land(alti);




        //         },
        //         (error) => {
        //             console.log("Error fetching IGN Alti", error);
        //         }
        //     )



        // map.current.loadImage(
        //     'https://docs.mapbox.com/mapbox-gl-js/assets/custom_marker.png',
        //     (error, image) => {
        //         if (error) throw error;
        //         map.current.addImage('custom-marker', image);

        //         // Remove layer
        //         if (map.current.getLayer('points')) {
        //             map.current.removeLayer('points');
        //         }
        //         // Remove source
        //         if (map.current.getSource('points')) {
        //             map.current.removeSource('points');
        //         }

        //         // Add a GeoJSON source with 2 points
        //         map.current.addSource('points', {
        //             'type': 'geojson',
        //             'data': grid
        //         });

        //         // Add a symbol layer
        //         map.current.addLayer({
        //             'id': 'points',
        //             'type': 'symbol',
        //             'source': 'points',
        //             'layout': {
        //                 'icon-image': 'custom-marker',
        //             }
        //         });
        //     }
        // );


        // // SHOW TRIANGULATED GRID
        // // Remove layer
        // if (map.current.getLayer('tGridLayer')) {
        //     map.current.removeLayer('tGridLayer');
        // }
        // // Remove source
        // if (map.current.getSource('tGridSource')) {
        //     map.current.removeSource('tGridSource');
        // }
        // // Add source to map
        // map.current.addSource('tGridSource', {
        //     'type': 'geojson',
        //     'data': tin,
        // });
        // // Add layers to map
        // map.current.addLayer({
        //     'id': 'tGridLayer',
        //     'type': 'fill',
        //     'source': 'tGridSource',
        //     'layout': {
        //         'visibility': 'visible'
        //     },
        //     'paint': {
        //         'fill-color': 'red',
        //         'fill-opacity': 0.4,
        //         'fill-outline-color': 'red'
        //     }
        // });
    }


    const getBuildableLevelInterceptor = (height0, height1, buildableMesh) => {
        // Set point 0
        // var point0 = capacity.landBase.union.bbox.origin;
        var point0 = capacity.landBase.union.center.geometry.coordinates;
        // Create 3D shape of bbox
        // Get bbox local coordinates
        // console.log("bbox global coords: ", capacity.landBase.union.bbox.coordinates[0]);
        // console.log("bbox global coords scaled", turf.transformScale(turf.polygon(capacity.landBase.union.bbox.coordinates), 1.01).geometry.coordinates[0]);
        var bbox_coords_global = turf.transformScale(turf.polygon(capacity.landBase.union.bbox.coordinates), 1.1).geometry.coordinates[0];
        var bbox_coords = [];
        for (var i = 0; i < bbox_coords_global.length; i++) {
            var deltaX = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([point0[0], bbox_coords_global[i][1]])) * 1000);
            if (bbox_coords_global[i][0] < point0[0]) {
                deltaX = -deltaX;
            }
            var deltaY = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([bbox_coords_global[i][0], point0[1]])) * 1000);
            if (bbox_coords_global[i][1] < point0[1]) {
                deltaY = -deltaY;
            }
            bbox_coords.push([deltaX, deltaY]);
        }
        // console.log("bbox local coords: ", bbox_coords);
        // Shape and Geometry
        var bboxShape = new THREE.Shape();
        bboxShape.moveTo(bbox_coords[0][0], bbox_coords[0][1]);
        bboxShape.lineTo(bbox_coords[0][0], bbox_coords[0][1]);
        bboxShape.lineTo(bbox_coords[1][0], bbox_coords[1][1]);
        bboxShape.lineTo(bbox_coords[2][0], bbox_coords[2][1]);
        bboxShape.lineTo(bbox_coords[3][0], bbox_coords[3][1]);
        bboxShape.lineTo(bbox_coords[4][0], bbox_coords[4][1]);
        var bboxExtrudeSettings = {
            steps: 1,
            depth: height1 - height0,
            bevelEnabled: false
        };
        var bboxGeometry = new THREE.ExtrudeGeometry(bboxShape, bboxExtrudeSettings);
        bboxGeometry.translate(0, 0, height0);
        var bboxMaterial = new THREE.MeshPhongMaterial({
            color: 'red',
            transparent: true,
            opacity: 0.4
        });
        var bboxMesh = new THREE.Mesh(bboxGeometry, bboxMaterial);

        // Get intersection
        bboxMesh.updateMatrix();
        buildableMesh.updateMatrix();
        var interceptorMesh = CSG.intersect(bboxMesh, buildableMesh);


        return interceptorMesh;
    }


    const set3D_bbox = (buildableMesh) => {
        // Check if data already loaded
        if (map.current.getLayer('layer3D')) {
            map.current.removeLayer('layer3D');
            // console.log("REMOVE 3D LAYER");
        }
        // Set point 0
        // var point0 = capacity.landBase.union.bbox.origin;
        var point0 = capacity.landBase.union.center.geometry.coordinates;
        // Georeferencing
        var modelOrigin = point0;
        var modelAltitude = 0;
        var modelRotate = [0, 0, 0];
        var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
            modelOrigin,
            modelAltitude
        );
        // Set position, rotation and scale
        var modelTransform = {
            translateX: modelAsMercatorCoordinate.x,
            translateY: modelAsMercatorCoordinate.y,
            translateZ: modelAsMercatorCoordinate.z,
            rotateX: modelRotate[0],
            rotateY: modelRotate[1],
            rotateZ: modelRotate[2],
            scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
        };
        // Set THREE
        // var THREE = window.THREE;
        // Create the custom layer
        var customLayer = {
            id: 'layer3D',
            type: 'custom',
            renderingMode: '3d',
            onAdd: function (map, gl) {
                this.camera = new THREE.Camera();
                this.scene = new THREE.Scene();
                // Create lights
                var directionalLight1 = new THREE.DirectionalLight(0xffffff);
                directionalLight1.position.set(0, -70, 100).normalize();
                this.scene.add(directionalLight1);
                var directionalLight2 = new THREE.DirectionalLight(0xffffff);
                directionalLight2.position.set(0, 70, 100).normalize();
                this.scene.add(directionalLight2);
                var ambientLight = new THREE.AmbientLight(0xffffff);
                this.scene.add(ambientLight);

                // Create 3D shape of bbox
                // Get bbox local coordinates
                // console.log("bbox global coords: ", capacity.landBase.union.bbox.coordinates[0]);
                // console.log("bbox global coords scaled", turf.transformScale(turf.polygon(capacity.landBase.union.bbox.coordinates), 1.01).geometry.coordinates[0]);
                var bbox_coords_global = turf.transformScale(turf.polygon(capacity.landBase.union.bbox.coordinates), 1.1).geometry.coordinates[0];
                // var bbox_coords_global = capacity.landBase.union.bbox.coordinates[0];
                var bbox_coords = [];
                for (var i = 0; i < bbox_coords_global.length; i++) {
                    var deltaX = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([point0[0], bbox_coords_global[i][1]])) * 1000);
                    if (bbox_coords_global[i][0] < point0[0]) {
                        deltaX = -deltaX;
                    }
                    var deltaY = (turf.distance(turf.point(bbox_coords_global[i]), turf.point([bbox_coords_global[i][0], point0[1]])) * 1000);
                    if (bbox_coords_global[i][1] < point0[1]) {
                        deltaY = -deltaY;
                    }
                    bbox_coords.push([deltaX, deltaY]);
                }
                // console.log("bbox local coords: ", bbox_coords);
                // Shape and Geometry
                var bboxShape = new THREE.Shape();
                bboxShape.moveTo(bbox_coords[0][0], bbox_coords[0][1]);
                bboxShape.lineTo(bbox_coords[0][0], bbox_coords[0][1]);
                bboxShape.lineTo(bbox_coords[1][0], bbox_coords[1][1]);
                bboxShape.lineTo(bbox_coords[2][0], bbox_coords[2][1]);
                bboxShape.lineTo(bbox_coords[3][0], bbox_coords[3][1]);
                bboxShape.lineTo(bbox_coords[4][0], bbox_coords[4][1]);
                var bboxExtrudeSettings = {
                    steps: 1,
                    depth: 100,
                    bevelEnabled: false
                };
                var bboxGeometry = new THREE.ExtrudeGeometry(bboxShape, bboxExtrudeSettings);
                bboxGeometry.translate(0, 0, 6);
                var bboxMaterial = new THREE.MeshPhongMaterial({
                    color: 'red',
                    transparent: true,
                    opacity: 0.4
                });
                var bboxMesh = new THREE.Mesh(bboxGeometry, bboxMaterial);
                // this.scene.add(bboxMesh);


                // Get intersection
                bboxMesh.updateMatrix();
                buildableMesh.updateMatrix();
                var interceptorMesh = CSG.subtract(buildableMesh, bboxMesh);
                this.scene.add(interceptorMesh);

                // Add edges
                // var bboxEdges = new THREE.EdgesGeometry(bboxGeometry);
                var bboxEdges = new THREE.EdgesGeometry(interceptorMesh.geometry);
                var bboxLine = new THREE.LineSegments(bboxEdges, new THREE.LineBasicMaterial({ color: 'red' }));
                this.scene.add(bboxLine);


                // Add map
                this.map = map;
                // use the Mapbox GL JS map canvas for three.js
                this.renderer = new THREE.WebGLRenderer({
                    canvas: map.getCanvas(),
                    context: gl,
                    antialias: true
                });
                this.renderer.autoClear = false;
            },
            render: function (gl, matrix) {
                var rotationX = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(1, 0, 0),
                    modelTransform.rotateX
                );
                var rotationY = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 1, 0),
                    modelTransform.rotateY
                );
                var rotationZ = new THREE.Matrix4().makeRotationAxis(
                    new THREE.Vector3(0, 0, 1),
                    modelTransform.rotateZ
                );
                var m = new THREE.Matrix4().fromArray(matrix);
                var l = new THREE.Matrix4()
                    .makeTranslation(
                        modelTransform.translateX,
                        modelTransform.translateY,
                        modelTransform.translateZ
                    )
                    .scale(
                        new THREE.Vector3(
                            modelTransform.scale,
                            -modelTransform.scale,
                            modelTransform.scale
                        )
                    )
                    .multiply(rotationX)
                    .multiply(rotationY)
                    .multiply(rotationZ);

                this.camera.projectionMatrix = m.multiply(l);
                this.renderer.resetState()
                this.renderer.render(this.scene, this.camera);
                // this.map.triggerRepaint();
            }
        };
        map.current.addLayer(customLayer, layer_get_position('layer3D'));
    }



    return (
        <div id="map" className={`app__map ${isHeaderBanner ? 'app__map_header_large' : 'app__map_header_small'}`}>
            {/* MAP
            {currentSection} */}
            {/* <div>Longitude: {lng} | Latitude: {lat} | Zoom: {zoom} | Pitch: {pitch} | Step : {capacity?.step} | Section : {currentSection}</div> */}
            <div ref={mapContainer} className="app__map_container" />
            {/* MAP BUTTONS */}
            {capacity?.step >= 0 &&
                <>
                    <div className={`app__map_button_container ${isSectionChatHidden ? "" : "app__map_button_container_slided"}`}>
                        <div onClick={pitchChange} className="app__map_button_single" title={pitch > 0 ? "Passer la carte en 2D" : "Passer la carte en 3D"}>
                            {pitch > 0 ? "2D" : "3D"}
                        </div>
                        {capacity?.step === 0 &&
                            <div onClick={() => { mapLocating_Switch() }} className="app__map_button_single" title={userLocation !== null ? "Centrer la carte sur la position actuelle" : "Masquer la géolocalisation"}>
                                <div className="app__map_button_img">
                                    {userLocation !== null ?
                                        <img src={ico_map_locatestop} alt="ico_map_locatestop" />
                                        :
                                        isLocating === true ?
                                            <img className="app__map_button_img_spinning" src={ico_map_locate} alt="ico_map_locate" />
                                            :
                                            <img src={ico_map_locate} alt="ico_map_locate" />
                                    }
                                </div>
                            </div>
                        }
                        {window.innerWidth > 600 &&
                            <div onClick={() => { mapExpand_Switch() }} className="app__map_button_single" title={(isSectionDataHidden === true && isSectionChatHidden === true) ? "Afficher les données et le chat" : "Afficher la carte en plein écran"}>
                                <div className="app__map_button_img"><img src={(isSectionDataHidden === true && isSectionChatHidden === true) ? ico_map_minimize : ico_map_expand} alt="ico_map_expand" /></div>
                            </div>
                        }
                        {capacity?.step > 0 && capacity?.landBase?.union?.center &&
                            <>
                                <div onClick={mapCenter} className="app__map_button_single" title="Centrer la carte sur le terrain">
                                    <div className="app__map_button_img"><img src={ico_map_center} alt="ico_map_center" /></div>
                                </div>
                                <div onClick={() => { mapRotation_Switch() }} className="app__map_button_single" title="Tourner autour du terrain">
                                    <div className="app__map_button_img">
                                        {isRotating ?
                                            <img src={ico_map_spinstop} alt="ico_map_spinstop" />
                                            :
                                            <img src={ico_map_spin} alt="ico_map_spin" />
                                        }
                                    </div>
                                </div>
                            </>
                        }
                    </div>
                    <div id="map_button_container_2" className={`app__map_button_container2 ${capacity?.step === 0 ? "app__map_button_container2_step0" : ""} ${isSectionChatHidden ? "" : "app__map_button_container_slided"}`}>
                        <div style={{ position: "relative" }}>
                            <div onClick={() => { handleLayersSwitch(); closeMeasure() }} className="app__map_button_single" title={isLayersVisible ? "Masquer les calques" : "Gérer les calques"}>
                                <div className="app__map_button_img"><img src={isLayersVisible ? ico_map_layerstop : ico_map_layers} alt="ico_map_layers" /></div>

                            </div>
                            {isLayersVisible &&
                                <div className={`app__map_layer_container ${window.innerWidth > 550 ? "scale-up-topright" : "scale-up-topleft"}`} style={{ maxHeight: (document.getElementById("map").offsetHeight - (document.getElementById("map_button_container_2").offsetTop - document.getElementById("map").offsetTop) - 10) + "px" }}>
                                    <div className="app__map_layer_title_main">Calques</div>
                                    {/* LAYERS GENERAL */}
                                    <div className="app__map_layer_title">Fond de carte</div>
                                    <div className="app__map_layer">
                                        <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch00" checked={layerSatellite} onChange={() => { layer_Toggle("satellite") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch00">Toggle</label></div>
                                        <div className="app__map_layer_label">Vue satellite</div>
                                    </div>
                                    <div className="app__map_layer">
                                        <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_osm_buildings" checked={layerOSMBuildings} onChange={() => { layer_Toggle("OSM_Buildings") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_osm_buildings">Toggle</label></div>
                                        <div className="app__map_layer_label">Bâtiments <span style={{ fontSize: "11px" }}>(Open Street Map)</span></div>
                                    </div>
                                    {/* LAYERS CADASTRE */}
                                    {capacity?.step > 0 &&
                                        <>
                                            <div className="app__sectiondata_widget_spacer_15"></div>
                                            <div className="app__map_layer_title">Données cadastrales</div>
                                            <div className="app__map_layer">
                                                <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_lands" checked={layerCityLands} onChange={() => { layer_Toggle("cityLands") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_lands">Toggle</label></div>
                                                <div className="app__map_layer_label">Parcelles</div>
                                            </div>
                                            {(capacity?.step > 0 && capacity?.landBase?.buildings?.buildings && capacity?.landBase?.buildings?.buildings?.length > 0) &&
                                                <>
                                                    <div className="app__map_layer">
                                                        <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_ign_buildings" checked={layerIGNBuildings} onChange={() => { layer_Toggle("IGN_Buildings") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_ign_buildings">Toggle</label></div>
                                                        <div className="app__map_layer_label">Bâtiments <span style={{ fontSize: "11px" }}>(Référentiel National des Bâtiments)</span></div>
                                                    </div>
                                                    <div className={`app__map_sublayer_container ${layerIGNBuildings ? "" : "app__map_sublayer_container_closed"}`}>
                                                        <div className="app__map_sublayer">
                                                            <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_ign_buildings_noland" checked={layerIGNBuildingsNoland} onChange={() => { layer_Toggle("IGN_BuildingsNoland") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_ign_buildings_noland">Toggle</label></div>
                                                            <div className="app__map_layer_label">Constructions en dehors du terrain</div>
                                                        </div>
                                                        {capacity?.buildable?.volume?.parameters?.builtArea > 0 &&
                                                            <div className="app__map_sublayer">
                                                                <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_ign_buildings_land_keep" checked={layerIGNBuildingsLand_keep} onChange={() => { layer_Toggle("IGN_BuildingsLand_keep") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_ign_buildings_land_keep">Toggle</label></div>
                                                                <div className="app__map_layer_label">Constructions sur le terrain : conservées</div>
                                                            </div>
                                                        }
                                                        {(capacity?.landBase?.buildings?.buildings_land && capacity.landBase.buildings.buildings_land.length > 0) &&
                                                            <div className="app__map_sublayer">
                                                                <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_ign_buildings_land" checked={layerIGNBuildingsLand_erase} onChange={() => { layer_Toggle("IGN_BuildingsLand_erase") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_ign_buildings_land">Toggle</label></div>
                                                                <div className="app__map_layer_label">Constructions sur le terrain{capacity?.buildable?.volume?.parameters?.builtArea > 0 ? " : démolies" : ""}</div>
                                                            </div>
                                                        }
                                                    </div>
                                                </>
                                            }
                                        </>
                                    }
                                    {/* LAYERS LANDBASE */}
                                    {capacity?.step > 0 &&
                                        <>
                                            <div className="app__sectiondata_widget_spacer_15"></div>
                                            <div className="app__map_layer_title">Données terrain</div>
                                            <div className="app__map_layer">
                                                <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch1" checked={layerUnion} onChange={() => { layer_Toggle("union") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch1">Toggle</label></div>
                                                <div className="app__map_layer_label">Terrain d'assiette</div>
                                            </div>
                                            {(capacity?.step > 0 && capacity?.landBase?.division) &&
                                                <div className="app__map_layer">
                                                    <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_division" checked={layerDivision} onChange={() => { layer_Toggle("division") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_division">Toggle</label></div>
                                                    <div className="app__map_layer_label">Division du terrain</div>
                                                </div>
                                            }
                                            {(capacity?.step > 0 && capacity?.landBase?.union?.bounds_groups && capacity?.landBase?.union?.bounds_groups.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch2" checked={layerUnionBounds} onChange={() => { layer_Toggle("boundsType") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch2">Toggle</label></div>
                                                    <div className="app__map_layer_label">Détail des limites</div>
                                                </div>
                                            }
                                            {(capacity?.step > 0 && capacity?.landBase?.topo?.matrix && capacity?.landBase?.topo?.matrix.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.land ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_topo_3D" checked={layerTopo3D} onChange={() => { layer_Toggle("topo_3D") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_topo_3D">Toggle</label></div>
                                                    <div className="app__map_layer_label">Topographie 3D</div>
                                                </div>
                                            }
                                            {(capacity?.step > 0 && capacity?.landBase?.topo?.matrix && capacity?.landBase?.topo?.matrix.length > 0 && capacity?.landBase?.topo?.elevation_mean) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.land ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_topo_points" checked={layerTopoPoints} onChange={() => { layer_Toggle("topo_points") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_topo_points">Toggle</label></div>
                                                    <div className="app__map_layer_label">Plan topo</div>
                                                </div>
                                            }
                                            {(capacity?.step > 0 && capacity?.landBase?.topo?.profile?.elevations && capacity?.landBase?.topo?.profile?.elevations.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.land ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_topo_profile" checked={layerTopoProfile} onChange={() => { layer_Toggle("topo_profile") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_topo_profile">Toggle</label></div>
                                                    <div className="app__map_layer_label">Profil altimétrique caractéristique</div>
                                                </div>
                                            }
                                            {/* {(capacity?.step > 0 && capacity?.landBase?.trees?.items && capacity?.landBase?.trees?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.land ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_trees" checked={layerTrees} onChange={() => { layer_Toggle("trees") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_trees">Toggle</label></div>
                                                    <div className="app__map_layer_label">Arbres</div>
                                                </div>
                                            } */}
                                        </>
                                    }
                                    {/* LAYERS RISKS */}
                                    {(capacity?.step > 0 && capacity?.risks && capacity?.risks?.natural) &&
                                        <>
                                            <div className="app__sectiondata_widget_spacer_15"></div>
                                            <div className="app__map_layer_title">Risques</div>
                                            {(capacity?.risks?.earthquake && capacity.risks.earthquake.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.risks ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_risks_earthquake" checked={layerRisksEarthquake} onChange={() => { layer_Toggle("risks_earthquake") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_risks_earthquake">Toggle</label></div>
                                                    <div className="app__map_layer_label">Zones de simsmicité</div>
                                                </div>
                                            }
                                            {(capacity?.risks?.clay && capacity.risks.clay.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.risks ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_risks_clay" checked={layerRisksClay} onChange={() => { layer_Toggle("risks_clay") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_risks_clay">Toggle</label></div>
                                                    <div className="app__map_layer_label">Zones d'aléa de retrait-gonflement des sols argileux</div>
                                                </div>
                                            }
                                            {(capacity?.risks?.pipe && capacity.risks.pipe.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.risks ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_risks_pipes" checked={layerRisksPipes} onChange={() => { layer_Toggle("risks_pipes") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_risks_pipes">Toggle</label></div>
                                                    <div className="app__map_layer_label">Canalisations de matières dangereuses</div>
                                                </div>
                                            }
                                            {(capacity?.risks?.basias && capacity.risks.basias.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.risks ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_risks_basias" checked={layerRisksBasias} onChange={() => { layer_Toggle("risks_basias") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_risks_basias">Toggle</label></div>
                                                    <div className="app__map_layer_label">Sites pollués</div>
                                                </div>
                                            }
                                        </>
                                    }
                                    {/* LAYERS CONTEXT */}
                                    {(capacity?.step > 0 && capacity?.context?.merimee) &&
                                        <>
                                            <div className="app__sectiondata_widget_spacer_15"></div>
                                            <div className="app__map_layer_title">Données contextuelles</div>
                                            {(capacity?.context?.dvf?.dvf_transactions && Object.keys(capacity.context.dvf.dvf_transactions).length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_dvf" checked={layerContextDVF} onChange={() => { layer_Toggle("context_dvf") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_dvf">Toggle</label></div>
                                                    <div className="app__map_layer_label">Transactions immobilières</div>
                                                </div>
                                            }
                                            {(capacity?.context?.sitadel && capacity.context.sitadel.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_sitadel" checked={layerContextSitadel} onChange={() => { layer_Toggle("context_sitadel") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_sitadel">Toggle</label></div>
                                                    <div className="app__map_layer_label">Demandes d'autorisation d'urbanisme</div>
                                                </div>
                                            }
                                            {(capacity?.context?.merimee && capacity.context.merimee.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_merimee" checked={layerContextMerimee} onChange={() => { layer_Toggle("context_merimee") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_merimee">Toggle</label></div>
                                                    <div className="app__map_layer_label">Monuments Historiques</div>
                                                </div>
                                            }
                                            {capacity?.context?.iso_distance?.car_5?.point &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_isodistance" checked={layerContextIsodistance} onChange={() => { layer_Toggle("context_isodistance") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_isodistance">Toggle</label></div>
                                                    <div className="app__map_layer_label">Périmètres de mobilité</div>
                                                </div>
                                            }
                                        </>
                                    }
                                    {/* COMMODITIES */}
                                    {(capacity?.step > 0 && capacity?.context?.commodities?.parking) &&
                                        <>
                                            <div className="app__sectiondata_widget_spacer_15"></div>
                                            <div className="app__map_layer_title">Commodités à proximité du terrain</div>
                                            {(capacity?.context?.commodities?.parking?.items && capacity?.context?.commodities?.parking?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_car_park" checked={layerContextCommoditiesCarPark} onChange={() => { layer_Toggle("context_commodities_car_park") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_car_park">Toggle</label></div>
                                                    <div className="app__map_layer_label">Stationnements</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.charging?.items && capacity?.context?.commodities?.charging?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_car_charging" checked={layerContextCommoditiesCarCharging} onChange={() => { layer_Toggle("context_commodities_car_charging") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_car_charging">Toggle</label></div>
                                                    <div className="app__map_layer_label">Bornes de chargement</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.transports_bus_tram_subway?.sum_values?.all?.stops > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_transport" checked={layerContextCommoditiesTransport} onChange={() => { layer_Toggle("context_commodities_transport") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_transport">Toggle</label></div>
                                                    <div className="app__map_layer_label">Transports en commun</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.transports_train?.items && capacity?.context?.commodities?.transports_train?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_train" checked={layerContextCommoditiesTrain} onChange={() => { layer_Toggle("context_commodities_train") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_train">Toggle</label></div>
                                                    <div className="app__map_layer_label">Gares ou haltes ferroviaires</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.transports_bike?.items && capacity?.context?.commodities?.transports_bike?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_bike" checked={layerContextCommoditiesBike} onChange={() => { layer_Toggle("context_commodities_bike") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_bike">Toggle</label></div>
                                                    <div className="app__map_layer_label">Stations de vélo</div>
                                                </div>
                                            }
                                            {((capacity?.context?.commodities?.health_hospital?.items?.hospital && capacity?.context?.commodities?.health_hospital?.items?.hospital.length > 0) || (capacity?.context?.commodities?.health_hospital?.items?.clinic && capacity?.context?.commodities?.health_hospital?.items?.clinic.length > 0) || (capacity?.context?.commodities?.health_hospital?.items?.laboratory && capacity?.context?.commodities?.health_hospital?.items?.laboratory.length > 0)) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_hospital" checked={layerContextCommoditiesHospital} onChange={() => { layer_Toggle("context_commodities_hospital") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_hospital">Toggle</label></div>
                                                    <div className="app__map_layer_label">Étabilssements de soins</div>
                                                </div>
                                            }
                                            {((capacity?.context?.commodities?.health_doctor?.items?.doctor && capacity?.context?.commodities?.health_doctor?.items?.doctor.length > 0) || (capacity?.context?.commodities?.health_doctor?.items?.nurse && capacity?.context?.commodities?.health_doctor?.items?.nurse.length > 0)) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_doctor" checked={layerContextCommoditiesDoctor} onChange={() => { layer_Toggle("context_commodities_doctor") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_doctor">Toggle</label></div>
                                                    <div className="app__map_layer_label">Cabinets médicaux</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.health_pharmacy?.items && capacity?.context?.commodities?.health_pharmacy?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_pharmacy" checked={layerContextCommoditiesPharmacy} onChange={() => { layer_Toggle("context_commodities_pharmacy") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_pharmacy">Toggle</label></div>
                                                    <div className="app__map_layer_label">Pharmacies</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.education_baby?.items && capacity?.context?.commodities?.education_baby?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_baby" checked={layerContextCommoditiesBaby} onChange={() => { layer_Toggle("context_commodities_baby") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_baby">Toggle</label></div>
                                                    <div className="app__map_layer_label">Crèches</div>
                                                </div>
                                            }
                                            {((capacity?.context?.commodities?.education_primary?.items?.maternel && capacity?.context?.commodities?.education_primary?.items?.maternel.length > 0) || (capacity?.context?.commodities?.education_primary?.items?.elementary && capacity?.context?.commodities?.education_primary?.items?.elementary.length > 0) || (capacity?.context?.commodities?.education_primary?.items?.primary && capacity?.context?.commodities?.education_primary?.items?.primary.length > 0)) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_primary_school" checked={layerContextCommoditiesPrimarySchool} onChange={() => { layer_Toggle("context_commodities_primary_school") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_primary_school">Toggle</label></div>
                                                    <div className="app__map_layer_label">Écoles</div>
                                                </div>
                                            }
                                            {((capacity?.context?.commodities?.education_secondary?.items?.medschool && capacity?.context?.commodities?.education_secondary?.items?.medschool.length > 0) || (capacity?.context?.commodities?.education_secondary?.items?.highschool && capacity?.context?.commodities?.education_secondary?.items?.highschool.length > 0)) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_secondary_school" checked={layerContextCommoditiesSecondarySchool} onChange={() => { layer_Toggle("context_commodities_secondary_school") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_secondary_school">Toggle</label></div>
                                                    <div className="app__map_layer_label">Collèges et lycées</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.education_university?.items && capacity?.context?.commodities?.education_university?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_university" checked={layerContextCommoditiesUniversity} onChange={() => { layer_Toggle("context_commodities_university") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_university">Toggle</label></div>
                                                    <div className="app__map_layer_label">Universités</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.shop?.items?.mall && capacity?.context?.commodities?.shop?.items?.mall.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_mall" checked={layerContextCommoditiesMall} onChange={() => { layer_Toggle("context_commodities_mall") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_mall">Toggle</label></div>
                                                    <div className="app__map_layer_label">Centres commerciaux</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.shop?.items?.supermarket && capacity?.context?.commodities?.shop?.items?.supermarket.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_supermarket" checked={layerContextCommoditiesSupermarket} onChange={() => { layer_Toggle("context_commodities_supermarket") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_supermarket">Toggle</label></div>
                                                    <div className="app__map_layer_label">Supermarchés</div>
                                                </div>
                                            }
                                            {(capacity?.context?.commodities?.park_garden?.items && capacity?.context?.commodities?.park_garden?.items.length > 0) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_parc" checked={layerContextCommoditiesParc} onChange={() => { layer_Toggle("context_commodities_parc") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_parc">Toggle</label></div>
                                                    <div className="app__map_layer_label">Parcs et jardins</div>
                                                </div>
                                            }
                                            {((capacity?.context?.commodities?.leisure_food?.items?.restaurant && capacity?.context?.commodities?.leisure_food?.items?.restaurant.length > 0) || (capacity?.context?.commodities?.leisure_food?.items?.bar_cafe && capacity?.context?.commodities?.leisure_food?.items?.bar_cafe.length > 0) || (capacity?.context?.commodities?.leisure_food?.items?.marketplace && capacity?.context?.commodities?.leisure_food?.items?.marketplace.length > 0)) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_restaurant" checked={layerContextCommoditiesRestaurant} onChange={() => { layer_Toggle("context_commodities_restaurant") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_restaurant">Toggle</label></div>
                                                    <div className="app__map_layer_label">Restaurants et cafés</div>
                                                </div>
                                            }
                                            {((capacity?.context?.commodities?.leisure_culture?.items?.arts_centre && capacity?.context?.commodities?.leisure_culture?.items?.arts_centre.length > 0) || (capacity?.context?.commodities?.leisure_culture?.items?.cinema && capacity?.context?.commodities?.leisure_culture?.items?.cinema.length > 0) || (capacity?.context?.commodities?.leisure_culture?.items?.conference_exhibition_centre && capacity?.context?.commodities?.leisure_culture?.items?.conference_exhibition_centre.length > 0) || (capacity?.context?.commodities?.leisure_culture?.items?.library && capacity?.context?.commodities?.leisure_culture?.items?.library.length > 0) || (capacity?.context?.commodities?.leisure_culture?.items?.music_venue && capacity?.context?.commodities?.leisure_culture?.items?.music_venue.length > 0) || (capacity?.context?.commodities?.leisure_culture?.items?.theatre && capacity?.context?.commodities?.leisure_culture?.items?.theatre.length > 0)) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.context ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_context_commodities_culture" checked={layerContextCommoditiesCulture} onChange={() => { layer_Toggle("context_commodities_culture") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_context_commodities_culture">Toggle</label></div>
                                                    <div className="app__map_layer_label">Étabilssements culturels</div>
                                                </div>
                                            }
                                        </>
                                    }
                                    {/* RULES */}
                                    {(capacity?.step > 0 && capacity?.rules?.ruleset) &&
                                        <>
                                            <div className="app__sectiondata_widget_spacer_15"></div>
                                            <div className="app__map_layer_title">Règles</div>
                                            {/* {(capacity.rules.ruleset[0].items.length > 0) && */}
                                            {(capacity?.rules?.gpu_data?.document?.title) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${user?.access?.rules ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_zones_urba" checked={layerZonesUrba} onChange={() => { layer_Toggle("zones_urba") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_zones_urba">Toggle</label></div>
                                                    <div className="app__map_layer_label">Zones PLU</div>
                                                </div>
                                            }
                                            <div className="app__map_layer">
                                                <div className={`app__map_layer_checkbox ${user?.access?.rules ? "" : "app__map_layer_checkbox_noaccess"}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_rules" checked={layerRules} onChange={() => { layer_Toggle("rules") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_rules">Toggle</label></div>
                                                <div className="app__map_layer_label">
                                                    Règles d'urbanisme
                                                    {(highlightedRule !== null && highlightedRule !== "all") &&
                                                        <>
                                                            {capacity.rules.ruleset.map(ruleset => {
                                                                return (
                                                                    ruleset.items.map(item => {
                                                                        if (item.id === highlightedRule) {
                                                                            return (
                                                                                <>
                                                                                    <br></br>
                                                                                    <span style={{ fontStyle: "italic", fontSize: "11px" }}>focus sur la règle
                                                                                        <span style={{ fontWeight: "600" }}> {item.name}</span>
                                                                                    </span>
                                                                                </>
                                                                            )
                                                                        }
                                                                    })
                                                                )
                                                            })}
                                                        </>
                                                    }
                                                </div>
                                            </div>
                                            {/* } */}
                                        </>
                                    }
                                    {/* BUILDABLE */}
                                    {(capacity?.step > 0 && capacity?.buildable) &&
                                        <>
                                            <div className="app__sectiondata_widget_spacer_15"></div>
                                            <div className="app__map_layer_title">Capacité</div>
                                            {(capacity?.buildable) &&
                                                <div className="app__map_layer">
                                                    <div className="app__map_layer_checkbox"><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_buildable_volume" checked={layerBuildableVolume} onChange={() => { layer_Toggle("buildable_volume") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_buildable_volume">Toggle</label></div>
                                                    <div className="app__map_layer_label">Volume constructible</div>
                                                </div>
                                            }
                                        </>
                                    }
                                    {/* SIMULATIONS */}
                                    {(capacity?.step > 0 && capacity?.buildable) &&
                                        <>
                                            <div className="app__sectiondata_widget_spacer_15"></div>
                                            <div className="app__map_layer_title">Simulations</div>
                                            {(capacity?.buildable) &&
                                                <div className="app__map_layer">
                                                    <div className={`app__map_layer_checkbox ${(!user?.access?.simulations || highlightedCombination === null || highlightedCombination.length === 0) ? "app__map_layer_checkbox_noaccess" : ""}`}><input className="app__map_layer_checkbox_element" type="checkbox" id="switch_layer_combinations_selected" checked={layerCombinationsSelected} onChange={() => { layer_Toggle("combinations_selected") }} /><label className="app__map_layer_checkbox_label" htmlFor="switch_layer_combinations_selected">Toggle</label></div>
                                                    <div className="app__map_layer_label">{highlightedCombination.length > 1 ? "Simulations sélectionnées" : "Simulation sélectionnée"}</div>
                                                </div>
                                            }
                                        </>
                                    }
                                    <div className="app__sectiondata_widget_spacer_10"></div>
                                </div>
                            }
                        </div>
                    </div>
                    {capacity?.step > 0 &&
                        <div id="map_button_container_3" className={`app__map_button_container3 ${isSectionChatHidden ? "" : "app__map_button_container_slided"}`}>
                            <div style={{ position: "relative" }}>
                                {mapDrawMode?.mode === null &&
                                    <>
                                        <div className="app__map_button_single" onClick={() => { handleMeasureSwitch(); setIsLayersVisible(false) }} title={isMeasureVisible ? "Masquer les outils de mesure" : "Ouvrir les outils de mesure"}>
                                            <div className="app__map_button_img"><img src={isMeasureVisible ? ico_map_measurestop : ico_map_measure} alt="ico_map_layers" /></div>
                                        </div>
                                        {isMeasureVisible &&
                                            <div className={`app__map_button_container_trans ${window.innerWidth > 550 ? "scale-up-right" : "scale-up-left"}`} style={{}}>
                                                <div className={`app__map_button_single_inside ${isMeasureLength === true ? "app__map_button_active" : ""}`} onClick={startMeasureLength}>LONGUEUR</div>
                                                <div className={`app__map_button_single_inside ${isMeasureArea === true ? "app__map_button_active" : ""}`} onClick={startMeasureArea}>SURFACE</div>
                                            </div>
                                        }
                                    </>
                                }
                            </div>
                            <link rel="stylesheet" href="https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-draw/v1.4.0/mapbox-gl-draw.css" type="text/css" />
                        </div>
                    }
                </>
            }
            {(mapDrawMode?.mode === "point" || mapDrawMode?.mode === "line" || mapDrawMode?.mode === "polygon") &&
                <div className={`app__map_drawing_info_description_mastercontainer ${isSectionDataHidden ? "app__map_drawing_info_description_mastercontainer_large" : ""}`}>
                    <div className="app__map_drawing_info_container scale-up-center">
                        {mapDrawMode?.target?.division === true ?
                            <div className="app__map_drawing_info_title"><strong>Division du terrain</strong></div>
                            :
                            <div className="app__map_drawing_info_title">Édition {mapDrawMode?.target?.rule_type === "rule" ? "de la règle" : mapDrawMode?.target?.rule_type === "condition" ? "de la condition de la règle" : mapDrawMode?.target?.rule_type === "perimeter" ? "du périmètre de la règle" : ""} <strong>{mapDrawMode?.target?.rule_label}</strong></div>
                        }
                        {mapDrawMode?.mode === "point" &&
                            <>
                                <div className="app__map_drawing_info_description_container">
                                    <div className="app__map_drawing_info_description">Pour ajouter un nouveau point, cliquez sur <strong>Ajouter un point</strong> puis cliquez sur la carte.</div>
                                    <div className="app__map_drawing_info_description">Pour modifier un point, sélectionnez-le puis déplacez-le.</div>
                                    <div className="app__map_drawing_info_description">Pour supprimer un point, sélectionnez-le puis cliquez sur <strong>Supprimer un point</strong>.</div>
                                </div>
                                <div className="app__map_drawing_info_buttons">
                                    <div className="app__map_drawing_button" onClick={() => { ruleDraw_add() }}>Ajouter un point</div>
                                    {drawSelection.length > 0 ?
                                        <div className="app__map_drawing_button" onClick={() => { ruleDraw_delete() }}>Supprimer {drawSelection.length === 1 ? "un point" : drawSelection.length + " points"}</div>
                                        :
                                        <div className="app__map_drawing_button_disabled">Supprimer un point</div>
                                    }
                                    {/* <div className="app__map_drawing_button" onClick={() => { ruleDraw_delete_all() }}>Supprimer tout</div> */}
                                </div>
                            </>
                        }
                        {mapDrawMode?.mode === "line" &&
                            <>
                                <div className="app__map_drawing_info_description_container">
                                    <div className="app__map_drawing_info_description">Pour ajouter une nouvelle ligne, cliquez sur <strong>Ajouter une ligne</strong> puis cliquez sur la carte pour placer les points composants la ligne. Double cliquez ou appuyez sur <strong>Entrée</strong> pour placer le point final.</div>
                                    <div className="app__map_drawing_info_description">Pour modifier une ligne, sélectionnez-la puis déplacez-la. Pour modifier un point de la ligne, sélectionnez la ligne, puis sélectionnez le point, et déplacez-le.</div>
                                    <div className="app__map_drawing_info_description">Pour supprimer une ligne, sélectionnez-la puis cliquez sur <strong>Supprimer une ligne</strong>.</div>
                                </div>
                                <div className="app__map_drawing_info_buttons">
                                    <div className="app__map_drawing_button" onClick={() => { ruleDraw_add() }}>Ajouter une ligne</div>
                                    {drawSelection.length > 0 ?
                                        <div className="app__map_drawing_button" onClick={() => { ruleDraw_delete() }}>Supprimer {drawSelection.length === 1 ? "une ligne" : drawSelection.length + " lignes"}</div>
                                        :
                                        <div className="app__map_drawing_button_disabled">Supprimer une ligne</div>
                                    }
                                    {/* <div className="app__map_drawing_button" onClick={() => { ruleDraw_delete_all() }}>Supprimer tout</div> */}
                                </div>
                            </>
                        }
                        {mapDrawMode?.mode === "polygon" &&
                            <>
                                <div className="app__map_drawing_info_description_container">
                                    <div className="app__map_drawing_info_description">Pour ajouter une nouvelle surface, cliquez sur <strong>Ajouter une surface</strong> puis cliquez sur la carte pour placer les points composants la surface. Double cliquez ou appuyez sur <strong>Entrée</strong> pour placer le point final.</div>
                                    <div className="app__map_drawing_info_description">Pour modifier une surface, sélectionnez-la puis déplacez-la. Pour modifier un point de la surface, sélectionnez la surface, puis sélectionnez le point, et déplacez-le.</div>
                                    <div className="app__map_drawing_info_description">Pour supprimer une surface, sélectionnez-la puis cliquez sur <strong>Supprimer une surface</strong>.</div>
                                </div>
                                <div className="app__map_drawing_info_buttons">
                                    <div className="app__map_drawing_button" onClick={() => { ruleDraw_add() }}>Ajouter une surface</div>
                                    {drawSelection.length > 0 ?
                                        <div className="app__map_drawing_button" onClick={() => { ruleDraw_delete() }}>Supprimer {drawSelection.length === 1 ? "une surface" : drawSelection.length + " surfaces"}</div>
                                        :
                                        <div className="app__map_drawing_button_disabled">Supprimer une surface</div>
                                    }
                                    {/* <div className="app__map_drawing_button" onClick={() => { ruleDraw_delete_all() }}>Supprimer tout</div> */}
                                </div>
                            </>
                        }
                        <div className="app__map_drawing_info_buttons_main">
                            {mapDrawMode?.target?.division === true ?
                                <div className={`app__map_drawing_button_main ${isDivisionValid ? "" : "app__map_drawing_button_main_invalid"}`} onClick={() => { ruleDraw_validate() }}>Valider</div>
                                :
                                <div className="app__map_drawing_button_main" onClick={() => { ruleDraw_validate() }}>Valider</div>
                            }
                            <div className="app__map_drawing_button_main_red" onClick={() => { ruleDraw_cancel() }}>Annuler</div>
                        </div>
                    </div>
                </div>
            }
            <div className={`app__map_loader_container ${isMapLoading ? 'app__map_loader_container_loading' : ''}`}>
                <div className="app__map_loader"></div>
            </div>
        </div>
    );
};

export default Map;